/**
 * The live loop, in a browser.
 *
 * Everything here is the real thing — the same packages the tests exercise.
 * Nothing is mocked, because a demo that mocks the interesting part proves
 * nothing about whether the interesting part works.
 */

import {
  APP_TEMPLATE_MODULES,
  compile, baselineGenerator, bindingFor, candidateId, deriveShape,
  makeBaselineGenerator, PARAMS, promotionCanCompile, promotionChangesRanking,
  promotionChangesAccepted,
  emptyLens, offer, preview, keep, dismiss, revert, activeSpec, acceptDirect,
  type LensState, type ObjectShape, type TastePriors, type TemplateView,
  templateShortfalls,
} from '@morph/generation';
import { assertClosedVocabulary, isProse, project, PROSE_ROLES, resolveInvocationArgs, SPEC_VERSION, VOCABULARY_VERSION } from '@morph/spec';
import type { Palette } from '@morph/spec';
import {
  compileSpec, renderHTML, BASE_CSS, RefusedLayout, surfaceOf, presentationOf,
  attachEditHost, mintGrants, labelForRole, readAnchor, readNavigationAnchor, handleOf, createViewState,
  attachPlayback, createPlaybackState, sectionAnchor, isSectionAnchor,
  createGraphViewState, FORCE_GRAPH_LIMIT, FORCE_GRAPH_PANE_BOX, FORCE_GRAPH_VAULT_BOX,
  attachDropHost, DROP_CSS,
  attachDocumentCanvas,
  ANCHOR_ATTR, OBJECT_HANDLE_KEY, nextTxnId, scopeOf,
  type BoundValues, type EditAnchor, type EditHost, type GrantSet, type IR, type ObjectHandle,
  type ViewportClass, type Scheme,
  type Rollups, type CapabilityInputs, type CapabilityInput, type ForceGraphInput,
  type IRNode,
} from '@morph/runtime';

import {
  CAPABILITIES, ROLES, ROLE_KIND, isRelationRole, validateSpec, registeredModuleIds,
  type Role, type Aggregation, type TLayoutSpec, type TNode, type DocumentContent,
  type AppTemplate,
  type Cardinality,
  type RegionPurpose,
} from '@morph/spec';
import {
  StoreEngine, rawOf, TextIndex, terms, scopeKey, openIndexedDbBlobs,
  loadDocument, saveDocument, migrateBodyToBlocks, stateOf,
  type DocumentRetirement,
  type ManifestRef, type ObjectId, type PropertyKeyId, type Scope, type Value,
  type KnownValue, type BlobId,
} from '@morph/store';
import {
  openDemoStore, SCALE_POOL_ID, type DemoPool, type DemoStore,
} from './store-setup.js';
import type { Def, Option } from './pools.js';
import { optionValue, optionTone } from './pools.js';
import { createEditBridge, type RelationStore } from './edit-bridge.js';
import { plotAxes, plotSpec, continuousPlotInputs, type PlotMark } from './plot.js';
import { calendarSpec, calendarInputs } from './calendar.js';
import { boardSpec } from './board-view.js';
import { timelineSpec, timelineInputs } from './timeline.js';
import {
  withShell, readShellChoice, writeShellChoice, mountShellControl, SHELL_OPTIONS,
  type ShellChoice,
} from './shell.js';
import {
  answerSubjectRegionScopes, membershipQuery, subjectRegions, subjectRegionScopes,
  withheldBranches,
} from './membership-query.js';
import { titleOf, wantsAside } from './panes.js';
import { lookOf, VIEW_MODULE, type LookRecord } from './looks.js';
import { composedById, composedRecord, readComposedApps, writeComposedApp,
  forgetComposedApp, lookFields, partOf, rootTemplate, effectiveParts,
  resolvePartSource, type ComposedApp } from './composed.js';
import { documentSections } from './document.js';
import {
  ACTIONS, buildRegistry, COMPOSED, parseAutomation, serializeAutomation,
} from './actions.js';
import { pickCandidate, dismissCandidate, refusedOffer, type Candidate } from './suggestion.js';
import { createBlobUrls, resolvableRoles, type BlobUrls } from './blob-urls.js';
import {
  acceptAttribute, ingestOne, roleFor, ANNOUNCE_ABOVE_BYTES, type IngestedFile,
} from './ingest.js';
import { withoutViewState } from './capability-view.js';
import { renderBootFailure } from './boot-failure.js';
import { resolveTemplateSeed } from './template-seed.js';
import { renderHub, renderHubFailure, type AcceptanceMap, type HubDestination, type HubKeptEntry } from './hub.js';
import { propertyPickerAct, propertyPickerRoles } from './property-picker.js';
import { applyPresence, createManualTaste, readFieldWidths, writeFieldWidth } from './manual-taste.js';
import { createQuiescence } from './quiescence.js';
import { browserDeriver, derivativeRoleFor, encoderProbe } from './derivatives.js';
import { attachRoleFor } from './attach-role.js';
import { PrivatePoolSuggestions, privateStringsIn } from './private-pool.js';

/** The store. Objects come from here rather than from arrays in the bundle,
 *  which is what makes creating one, editing one, and reloading the page mean
 *  anything at all. */
let db: DemoStore;

/**
 * `morph-blob:` references, turned into URLs this page made. Decision 0017.
 *
 * Held across passes rather than rebuilt per render, which is the whole reason
 * repainting an image costs nothing: a blob id IS its content, so a URL minted
 * for one can never go stale.
 */
let blobUrls: BlobUrls;

/** The pool in view. Switching pools is the demonstration: same engine, same
 *  vocabulary, different content — and the layout that comes back is a
 *  different kind of application, not the same one restyled. */
let pool: DemoPool;

/** The objects the current pass painted, each carrying the opaque handle the
 *  DOM names it by. The revert target and the `before` half of every commit are
 *  read from the store, never from here — this is what was drawn, not truth. */
let objects: BoundValues[] = [];
/** What the shell panel lists: everything in the pool, whatever the main lens is
 *  currently about. Resolved for `source: 'siblings'`. */
let siblings: BoundValues[] = [];
/** The per-region sets the last render supplied, module-scope for the same
 *  reason `objects` and `siblings` are: a click handler runs long after the
 *  render that drew what was clicked, and an object on screen may be in any of
 *  the three. */
let lastRegionSets: Record<string, readonly BoundValues[]> = {};
/** One live canvas per object section in the generated document lens. */
const documentCanvases = new Map<ObjectId, ReturnType<typeof attachDocumentCanvas>>();
/** The document block the caret was last in — which object's canvas, which
 *  block — so a ribbon's style tile knows what to convert after the press
 *  itself has moved focus to the tile. */
let lastDocBlock: { object: ObjectId; block: string } | undefined;
const canvasDocuments: ObjectId[] = [];
/**
 * Saves the canvases are making right now, and the BOUNDED wait for them.
 * See `watchPool`, and `quiescence.ts` for why the wait has a ceiling.
 */
const quiescence = createQuiescence({
  onAbandon: ({ waitedMs, inFlight }) => {
    // Said, not swallowed. Task 0112: the spin this replaced left a person
    // looking at a surface that had simply stopped answering, with nothing to
    // read. What follows this line may be mid-flight, and the notice is the
    // only thing that says so.
    say(`Still saving after ${Math.round(waitedMs / 1000)}s — `
      + `${inFlight} unfinished; what you see may be behind.`, 'bad');
  },
});
const manualTaste = createManualTaste(localStorage);
/** The private pool, and the only thing in this file allowed to read it.
 *  Decisions 0039 and 0040 — see `private-pool.ts`. */
let privatePool: PrivatePoolSuggestions;
/** Field widths are VIEW STATE, not taste — decision 0036. Per-device, restored
 *  on open, and never dependent on an evidence threshold. */
let fieldWidths = readFieldWidths(localStorage);

/**
 * A canonical surface read waits for every canvas publish already underway —
 * and gives up at a ceiling rather than waiting forever. The report it returns
 * is deliberately ignored by the ordinary callers below: they proceed with a
 * possibly mid-flight read, which is what they would have done anyway, except
 * that now the page has SAID so. `__morphDocument.quiescence` is where a
 * harness reads whether it happened.
 */
async function settled(): Promise<void> {
  await quiescence.settled();
}

/**
 * One reconciliation boundary for both a proactive tab notice and a refused
 * stale document save. The transport has already refreshed the engine before
 * calling this; the stale path does that explicitly below. A document canvas
 * keeps its local draft through its three-way merge, while every other surface
 * takes the ordinary canonical render path.
 */
async function refreshCanonicalSurfaces(): Promise<void> {
  /**
   * A remote refresh KILLS any suggestion still compiling — @Codex, P2 on
   * `18eae3f`. Another tab's write can change coverage or shape without this
   * tab commanding a derive, and a completion compiled from the pre-refresh
   * shape passed its currency checks and replaced the refreshed surface with
   * a stale offer. Invalidation is independent of re-ranking: the repaint
   * below stays a value repaint (0023 — a background event does not begin a
   * new viewing episode), but the offer was an answer to a shape that is no
   * longer on the table (0044, uniformly). This is the remote half only; a
   * person's own value write racing their own requested suggestion keeps
   * today's behavior, because hydration emits no per-object events and this
   * function is the one surface remote changes reach.
   */
  poolRequest += 1;
  await settled();
  if (documentCanvases.size === 0) {
    scheduleValue();
    return;
  }
  /**
   * Canvases first, then the ordinary repaint for everything else.
   *
   * Decision 0031: *"after canvas reconciliation, request the ordinary value
   * repaint so titles and every other generated field refresh through the
   * normal renderer."* And the half that says why it is not a shortcut: *"Do
   * not patch title text directly. That would create a second projection/render
   * path and leave the next non-canvas role stale."*
   *
   * Reconciling first means the repaint that follows transplants an island that
   * already holds the merged content, so the two do not fight over the block
   * tree.
   */
  /**
   * Inside ONE coherent read, like the projection path.
   *
   * @Codex, P2 on #166: *"During active cross-tab writing, `loadDocument`
   * performs multiple object and relation awaits against the mutable engine
   * passed here. Another commit or invalidation hydrate can land between those
   * awaits, so reconciliation may receive roots, blocks, and edges from
   * different revisions and either reject a valid document or merge a state
   * that never existed."*
   *
   * This is the path that runs WHILE another tab is writing, so it is the one
   * place a torn traversal is not hypothetical. Task 0106 built `store.read`
   * for exactly this and the canvas path was still using the raw engine.
   */
  const loaded = await db.store.read(async ({ reader }) => {
    const out: [ObjectId, DocumentContent][] = [];
    for (const document of documentCanvases.keys()) {
      const content = await loadDocument(
        reader, document, db.document.keys, db.document.manifests);
      if (content) out.push([document, content]);
    }
    return out;
  });
  for (const [document, content] of loaded) {
    const canvas = documentCanvases.get(document);
    if (!canvas) continue;
    const result = canvas.reconcile(content);
    if (result.status === 'conflict') {
      say('Not saved — the canonical document changed; your draft remains visible.', 'bad');
      continue;
    }
    /**
     * A CLEAN rebase can still carry work the store has never seen.
     *
     * @Codex, P1 on #166: *"When two tabs make non-overlapping edits before
     * either sees the other's commit, the losing save is refused `stale` and
     * `reconcile` returns `clean` with both the canonical change and the still
     * local draft. This branch only reports conflicts and never saves the clean
     * merged content, while the stale `onChange` path returns normally; the
     * draft therefore looks saved but disappears on reload unless the person
     * happens to type again."*
     *
     * That is the worst shape again: the screen says the work exists and the
     * store does not have it. A conflict at least SAYS so.
     *
     * Saved only when the merge actually moved past canonical, so a tab with
     * nothing local writes nothing — which is also what makes this converge
     * rather than ping-pong. After the write, canonical IS the merge, so the
     * notice it provokes finds nothing to save in either tab.
     */
    if (JSON.stringify(result.content) === JSON.stringify(content)) continue;
    const rebased = await saveCanvasDocument(document, result.content);
    if (!rebased.ok) {
      say(`Not saved — ${rebased.code}; your draft remains visible.`, 'bad');
    }
  }
  // And then everything that is NOT a canvas. The island is transplanted
  // through this repaint rather than rebuilt, so the person's editing session
  // survives its surroundings being replaced — which is the whole of 0031.
  scheduleValue();
}

async function saveCanvasDocument(
  document: ObjectId, content: DocumentContent, retire?: DocumentRetirement,
) {
  return quiescence.track(() => saveDocument(
    db.store, db.editor, document, content, db.document.keys, db.document.manifests, retire));
}

/**
 * Replace each generated Body field with the canonical block canvas for that
 * SAME object. The section card, heading, fold control, note marker, running
 * furniture and apparatus remain generated presentation around it.
 *
 * A body is migrated exactly once. Stable block ids are derived from the
 * document identity and the legacy paragraph ordinal; after that every edit is
 * loaded from canonical document objects and relations, never reconstructed
 * from the legacy slot.
 */
/**
 * The live canvas ISLANDS, kept across a repaint.
 *
 * Decision 0031: *"preservation applies to the canvas DOM root and its editable
 * descendants, not merely to the `DocumentCanvas` JavaScript object."* Reusing
 * the object while re-rendering its DOM looks correct and reproduces the defect
 * exactly — the object alive, the element it is bound to detached, every
 * keystroke landing where nobody reads.
 *
 * Keyed by document identity; the binding index is held beside it because the
 * anchor is half of the identity. A generated body field that moved to a
 * different node is a different surface and gets a new canvas.
 */
const canvasIslands = new Map<ObjectId, { root: Element; binding: number }>();

/**
 * How many canvases have EVER been attached, and how many destroyed.
 *
 * Decision 0031's clause 8 asks for something a census of live canvases cannot
 * answer: *"Repeated value repaints neither leak nor duplicate canvases."* A
 * map with one entry per document looks identical whether the render reused the
 * canvas or destroyed and rebuilt it — so the counter that discriminates is the
 * one that only ever goes up.
 */
const canvasLifetime = { attached: 0, destroyed: 0 };

/** Where the caret was, if it was inside a canvas, when the stage was replaced. */
let canvasCaret: { document: ObjectId; block: string; offset: number } | undefined;

/**
 * Remember the caret before the generated tree is swapped.
 *
 * The island itself survives the swap — it is moved, not rebuilt — but FOCUS
 * does not: removing the focused node from the document drops the selection,
 * even when the removal and the re-insertion are adjacent statements. So the
 * offset is read here, while the node is still live, and put back into the
 * SAME node afterwards.
 *
 * This is a restoration, and decision 0031 is explicit that a restoration is
 * the weaker result: *"Restoring plain focus after composition was cancelled
 * is a different and weaker result."* What makes the strong result reachable is
 * the staging in `render`, which removes the asynchronous window entirely —
 * not this function, which only papers over the synchronous one.
 */
function rememberCanvasCaret(): void {
  canvasCaret = undefined;
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return;
  const block = active.closest('[data-morph-document-block]');
  if (!block) return;
  const owner = [...canvasIslands.entries()]
    .find(([, island]) => island.root.contains(block));
  if (!owner) return;
  const selection = active.ownerDocument.defaultView?.getSelection();
  const offset = selection && selection.rangeCount > 0 ? selection.getRangeAt(0).startOffset : 0;
  canvasCaret = { document: owner[0], block: block.getAttribute('data-morph-document-block')!, offset };
}

/** And put it back, into the SAME node it came out of. */
function restoreCanvasCaret(): void {
  const held = canvasCaret;
  canvasCaret = undefined;
  if (!held) return;
  const island = canvasIslands.get(held.document);
  const block = island?.root.querySelector<HTMLElement>(
    `[data-morph-document-block="${held.block}"]`);
  if (!block || !block.isContentEditable) return;
  // Without scrolling, for the reason `settleCanvasFocus` gives: a caret
  // put back on a repaint must not move the window either.
  block.focus({ preventScroll: true });
  const text = block.firstChild;
  const range = block.ownerDocument.createRange();
  if (text && text.nodeType === 3) {
    range.setStart(text, Math.min(held.offset, text.textContent?.length ?? 0));
  } else range.selectNodeContents(block);
  range.collapse(true);
  const selection = block.ownerDocument.defaultView?.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
}

/**
 * Prepare the document canvases for a tree that is not on screen yet, and
 * return the one SYNCHRONOUS step that puts the live islands into it.
 *
 * Decision 0031: *"Moving the island must not leave it detached while
 * asynchronous reads or canvas creation run. The new generated surroundings may
 * be staged first, but the live island is transferred at the synchronous commit
 * boundary. No await may sit between removing the island from its old live
 * parent and inserting it into its new live parent."*
 *
 * That is the whole reason this function takes a `staged` root and hands its
 * moves back rather than performing them. Every `await` below — a canonical
 * document read per note, and a migrating write for a note that has never had
 * one — happens while the island the person is typing into is still in the
 * page. Doing the swap first and the reads afterwards, which is what this was,
 * leaves an editor detached for the length of an IndexedDB round trip: the
 * text still looks right and the keystrokes go nowhere.
 */
async function prepareDocumentCanvases(ir: IR, staged: Element): Promise<() => void> {
  const moves: (() => void)[] = [];
  canvasDocuments.splice(0);
  /**
   * NOT gated on `MAY_WRITE` any more. Decision 0028: *"Read-only rendering is
   * a mode of the canonical canvas, not a fallback to the generated scalar
   * field."*
   *
   * Returning early here was 0105's reader half. A `?viewer=reader` visitor
   * never reached the blocks and got the generated legacy `body` — prose frozen
   * at migration, while the document it claims to be had moved on. @Codex:
   * *"Read-only stale content is still wrong content."*
   *
   * Write authority decides whether the blocks are EDITABLE. It does not decide
   * which representation is rendered.
   */
  // The document view — and Word's look, whose sheet IS the document view's
  // module wearing the app's window (0055): its sections are the same
  // canvases, edited in place.
  if (state.view !== 'document' && !currentLook().documentIsTheView) {
    for (const canvas of documentCanvases.values()) { canvas.destroy(); canvasLifetime.destroyed += 1; }
    documentCanvases.clear();
    canvasIslands.clear();
    return () => {};
  }

  /**
   * EVERY BODY FIELD, not the first one in the IR — task `0199`.
   *
   * `find()` was right while the only look whose document IS the view had one
   * pane. A multi-pane look has SEVERAL, and the first in IR order is not the
   * one a person reads. Measured on `app.obsidian`, instrumented in the page
   * rather than reasoned about:
   *
   * ```
   * body field 13   rendered 0     <- find() took this one
   * body field 26   rendered 1     <- the editor's, the one on screen
   * body field 34   rendered 0
   * ```
   *
   * `candidates` was then empty, no root matched, and the loop below continued
   * for every object: the canvas never mounted and nothing said so. The note
   * rendered as a flat `contenteditable` with no element children at all — no
   * paragraph, no heading, no list, no quote — which is `0170`'s finding 6.
   *
   * AND "THE FIRST ONE THAT RENDERS" IS ALSO WRONG, which cost a drive to
   * find out. Open the search pane and field 13 — the hit card's own snippet —
   * starts rendering, so that rule then mounted the OPEN NOTE'S document into
   * the search result: the hit's text became the editor's prose and pressing
   * it stopped opening anything. Measured, with no page error to say so.
   *
   * So the producer is enumerated instead of sampled, which is this
   * repository's own rule: every body field contributes its rendered elements,
   * each carrying the node it came from, and the ANCHOR decides which one is
   * this object's document surface. The hit card's body resolves to the hit's
   * handle and the editor's to the subject's, so a snippet can never be
   * mistaken for a document.
   *
   * The positional fallback below is unchanged in CODE and not in what it
   * sees: `candidates` can now hold elements from several fields, so the
   * counts agree less often and it fires less often. That is the safe
   * direction — it exists for a lens whose fields carry no handle, and every
   * look that reaches it draws one body field, so its input is the same one
   * it always had. Saying "unchanged" without this sentence would be the
   * comment describing the fix rather than the code.
   */
  /* THE PROSE ROLE THE SPEC BOUND, not the name this file happened to know —
     @Codex's P2 on c4d1346.

     This scanned for role === 'body', so a pool whose prose is a transcript
     took the no-binding branch: no canvas, and the vault fell back to a flat
     scalar. The generator has never been so narrow — its five prose choices
     all read body ?? transcript — and @morph/spec has carried isProse and
     PROSE_ROLES all along, used by baseline.ts and edit-host.ts. The
     vocabulary existed; the host was the one place that did not ask it. That
     is 0173's standing question answered wrongly: a data source could not
     wear the Obsidian arrangement without editing this file.

     body FIRST, then any prose role, which is the generator's own preference
     spelled the same way — so a shape carrying both resolves exactly as it
     does today, and one carrying only a transcript stops falling through. ONE
     role, not the union: two prose roles in one spec are two surfaces, and a
     canvas mounted across both would hand one document two owners. */
  const proseRole = ir.nodes.find((node) => node.kind === 'field' && node.role === 'body')?.role
    ?? ir.nodes.find((node) => node.kind === 'field'
      && node.role !== undefined && isProse(node.role))?.role;
  const bodyFields = proseRole === undefined ? []
    : ir.nodes.filter((node) => node.kind === 'field' && node.role === proseRole);
  const binding = bodyFields[0];
  // `proseRole` is defined whenever a binding exists — `bodyFields` is filtered
  // on it — but the term is written out rather than asserted with a cast,
  // because the migration below indexes the subject BY this role and a cast is
  // the shape of the defect it would be hiding.
  if (!binding || proseRole === undefined) {
    for (const canvas of documentCanvases.values()) { canvas.destroy(); canvasLifetime.destroyed += 1; }
    documentCanvases.clear();
    canvasIslands.clear();
    return () => {};
  }
  /**
   * Decision 0028's ownership transfer, unblocked by decision 0030.
   *
   * This was built once and REVERTED, because retiring the scalar removed
   * `body` from the store-derived shape and the document surface had nowhere to
   * mount. The derived-role boundary now keeps the role in the shape from the
   * canonical blocks, so the scalar can go.
   */
  /* AND THE SAME ROLE FOR THE RETIREMENT. Retiring body while the canvas owns
     transcript would clear a slot nobody had migrated and leave the one that
     matters beside its own document — the two-truths state coda-row's formula
     clause already caught once. */
  const body = proseRole !== undefined ? pool?.keys[proseRole] : undefined;
  const retirement: DocumentRetirement | undefined = body ? { scalar: body } : undefined;
  const candidates = bodyFields.flatMap((node) =>
    [...staged.querySelectorAll(`[${ANCHOR_ATTR.node}="${node.i}"]`)]
      .map((element) => ({ element, node: node.i })));

  for (const [at, subject] of objects.entries()) {
    const handle = handleOf(subject);
    const document = handle && identities.get(handle);
    /**
     * By anchor where there is one, by POSITION where there cannot be.
     *
     * `render-html.ts` USED TO emit the object anchor only when an `edit`
     * option was present — *"Absent for a reader, so the renderer mints no
     * editable element"* — so a reader's fields carried a node index and
     * nothing else and the anchor branch matched nothing. A reader in a
     * COLLECTION document view therefore mounted no canvas at all and saw
     * eight titles with no prose under any of them.
     *
     * Decision 0062 removed that coupling: the handle is emitted
     * unconditionally and only the epoch waits on a grant, so the anchor
     * branch now resolves for a reader too — this call takes
     * `readNavigationAnchor`, since matching a candidate to a handle is not a
     * write. The POSITION fallback below is kept rather than deleted: it is
     * the answer for a lens whose fields genuinely carry no handle, which is
     * a shape the renderer still produces, and it is guarded on the counts
     * agreeing so it cannot fire in place of a correct match.
     *
     * It went unnoticed because the only reader guard in the repo opens after a
     * writer has selected a note, so its lens is single-object — and there
     * `completeSingleAnchor` stamps the anchor onto the root by hand.
     *
     * The candidates are in the lens's own object order, which is the same zip
     * of `query` and `projection` this demo already depends on to recover
     * identity at all. Guarded on the counts agreeing: if they do not, this is
     * not the case being handled, and mounting one note's prose under another
     * note's title is worse than mounting none.
     */
    /**
     * IDENTITY IS NOT ENOUGH WHEN THE NOTE MATCHES ITS OWN SEARCH — @Codex's
     * P2 on `cb4fa44`.
     *
     * Search the open note's own text and its hit card carries the SAME
     * object handle as the editor. Candidates are grouped in body-field
     * order and the search field precedes the editor's, so the first identity
     * match was the SNIPPET: the live canvas was transplanted into the hit
     * card, the editor was left without its document, and the hit stopped
     * opening normally. The harness searches for a different note, so nothing
     * could have seen it.
     *
     * Told by TOPOLOGY, and by a generic one rather than a look's names: a
     * snippet is an ITEM IN A LIST and a document surface is the page's own
     * body. Every look draws its list items as cards, so a candidate with a
     * card ancestor is a preview of the object and not the object's page.
     * Preferred rather than filtered, so a lens whose only body field really
     * is inside a card — a document view over a collection — still mounts.
     */
    const pageFirst = [
      ...candidates.filter((c) => c.element.closest('.m-card') === null),
      ...candidates.filter((c) => c.element.closest('.m-card') !== null),
    ];
    const chosen = (handle && pageFirst.find((candidate) =>
      // Identity matching to mount a document, not a write — decision 0062.
      readNavigationAnchor(candidate.element)?.object === handle))
      || (candidates.length === objects.length ? candidates[at] : undefined);
    if (!handle || !document || !chosen) continue;
    const root = chosen.element;

    let content: DocumentContent | undefined;
    try {
      content = await loadDocument(db.store, document, db.document.keys, db.document.manifests);
    } catch (error) {
      // An ordinary pool object has no document graph until this migration.
      // A malformed graph remains a refusal; only the canonical empty-document
      // validation identifies the not-initialised case.
      if (!(error instanceof Error)
        || !error.message.includes('a document has at least one block')) throw error;
    }
    if (!content) {
      const prefix = `document-${String(document)}-body`;
      /* THE RESOLVED PROSE ROLE, not `body` — @Codex's P1 on e02bf93, and it is
         a defect inside the repair for their own earlier P2. That repair
         taught the canvas to mount on a transcript; this line kept reading
         `subject.body`, so the first WRITABLE open of a transcript-only object
         migrated an empty string into a canonical document and then retired
         the populated transcript through `pool.keys[proseRole]` — replacing a
         person's prose with an empty graph. The read path was fine; the write
         path was the loss.

         One role decides all three: what the canvas mounts, what is migrated,
         and what is retired. Two of the three agreeing is what made this
         invisible. */
      const held = subject[proseRole];
      content = migrateBodyToBlocks(
        typeof held === 'string' ? held : '',
        (ordinal) => `${prefix}-${ordinal}`);
      /**
       * A reader does not establish the canonical graph, and must not need to
       * in order to READ.
       *
       * `migrateBodyToBlocks` is pure, so the blocks a writer would commit are
       * derived in memory and rendered read-only; nothing is written. The
       * canonical objects are still created the first time a WRITER opens the
       * note, exactly as before.
       *
       * Without this the reader route would reach a write it has no authority
       * for, and the whole render would fail — which is a worse answer than the
       * stale scalar this change exists to remove.
       */
      if (MAY_WRITE) {
        const seeded = await saveCanvasDocument(document, content, retirement);
        if (!seeded.ok) throw new Error(`migrating the document body was refused: ${seeded.code}`);
      }
    }

    // The generated field still owns section furniture such as its derived
    // footnote marker. Move that furniture beside (not inside) the editable
    // canvas, and retire only the legacy scalar editor on the field itself.
    const furniture = [...root.querySelectorAll('.m-doc-marker')];
    root.removeAttribute('contenteditable');
    root.removeAttribute('role');
    root.removeAttribute('aria-multiline');
    root.removeAttribute('data-morph-empty');

    /**
     * TRANSPLANT, when this is the same surface it was.
     *
     * Decision 0031: *"If the document identity and Body anchor are unchanged,
     * it must reuse the existing canvas instance and its root. Do not call
     * `destroy()` or `attachDocumentCanvas()`."*
     *
     * The generated furniture around it is rebuilt — that is the whole point,
     * it is how another tab's title change becomes visible — and the island is
     * moved into it. The island's nodes are the SAME nodes, which is what the
     * caret, the selection and any in-flight composition are attached to.
     */
    const held = canvasIslands.get(document);
    const existing = held?.binding === chosen.node ? documentCanvases.get(document) : undefined;
    if (existing && held) {
      // DEFERRED. `held.root` is still in the page and may still be receiving
      // keystrokes; it moves at the commit boundary below, not here.
      moves.push(() => root.replaceChildren(held.root, ...furniture));
      canvasDocuments.push(document);
      // Same content is a no-op inside `reconcile`, so a repaint driven by an
      // unrelated field does not touch the block tree at all.
      existing.reconcile(content);
      continue;
    }
    // Identity changed, or there was nothing here. Only now is a canvas built —
    // and any stale one for this document goes first.
    if (documentCanvases.get(document)) canvasLifetime.destroyed += 1;
    documentCanvases.get(document)?.destroy();
    const canvasRoot = root.ownerDocument.createElement('div');
    canvasRoot.className = 'm-document-canvas';
    root.replaceChildren(canvasRoot, ...furniture);
    canvasIslands.set(document, { root: canvasRoot, binding: chosen.node });

    const canvas = attachDocumentCanvas({
      root: canvasRoot, content, createBlockId: () => crypto.randomUUID(),
      editable: MAY_WRITE,
      edit: { object: handle, node: chosen.node, epoch, role: 'body' },
      onChange: async (next) => {
        // The first canonical save owns `body` from this point onward. Passing
        // retirement on every save also upgrades documents migrated before the
        // ownership-transfer wiring landed; clearing an absent slot is inert.
        const result = await saveCanvasDocument(document, next, retirement);
        if (result.ok) return;
        if (result.code === 'stale' && db.store instanceof StoreEngine) {
          /* AND IT SAYS SO — task `0221`. This branch used to hydrate, refresh
             and return, which drops the person's typing and repaints the title
             from the store that still holds the old text. Nothing was said:
             `onRefusal` below is what calls `say`, and this returns before the
             throw that would reach it.
             `0055` names silence as the worst shape a refused control can
             take, and this was it — the harness that found `0221` measured
             "the DOM took the text, and the store did not" and could not
             explain why nobody was told.
             SAYING is not the whole fix and the task holds the rest. A RETRY
             is what decision `0027` means by `stale` ("plan it again"), and
             `store-setup.ts` already does it for the migration; here `next` is
             content computed BEFORE the refusal, so re-planning it may
             overwrite whatever the other tab wrote to the same document. That
             needs a decision about a second writer's edit. Telling the person
             their edit did not land needs none, and is strictly better than
             what was here. */
          await db.store.hydrate({}, true);
          await refreshCanonicalSurfaces();
          say('another tab changed this document — your last edit was not saved, '
            + 'the page has been refreshed (0221)', 'bad');
          return;
        }
        throw new Error(`document save refused: ${result.code}`);
      },
      onRefusal: (error) =>
        say(error instanceof Error ? error.message : 'document save refused', 'bad'),
    });
    canvasLifetime.attached += 1;
    documentCanvases.set(document, canvas);
    canvasDocuments.push(document);
  }

  /**
   * Destroyed only for identity DELTAS, which is the narrower rule 0031 asks
   * for: *"Destroy canvases only when their document or binding disappears."*
   *
   * The old rule was "on every render", and stating the new one loosely is how
   * the next render pass quietly reinstates the defect.
   */
  const present = new Set(canvasDocuments);
  for (const [document, canvas] of [...documentCanvases]) {
    if (present.has(document)) continue;
    canvas.destroy();
    canvasLifetime.destroyed += 1;
    documentCanvases.delete(document);
    canvasIslands.delete(document);
  }

  return () => { for (const move of moves) move(); };
}

/** After the staged tree is in the page: the caret, then the initial one. */
function settleCanvasFocus(): void {
  // The caret came out of a node that is still the same node, so it goes back
  // into it rather than being re-derived from position.
  restoreCanvasCaret();

  // Opening a writable document means arriving ready to write, not arriving at
  // a record and aiming again. Do not steal an existing block selection during
  // a repaint; only place the initial caret when no canvas already owns focus.
  /**
   * AND NOT WHEN THE GESTURE DECLARED WHERE FOCUS GOES — task `0199`.
   *
   * "Arriving ready to write" is right for the document view and for Word,
   * whose sheet IS the document. It is wrong for a look whose door has its own
   * destination: `app.obsidian` opens on the ribbon's first tool, which the
   * tenth verdict asked for and `e2e/hub.mjs` measures at every door.
   *
   * Mounting the canvas in Obsidian's editor made this fire first, so the
   * caret landed in the note body and `takePendingWorkspaceFocus` then found
   * focus "somewhere real" and dropped the door's request. Measured: at the
   * door, `activeElement` was a `<p data-morph-document-block=…>` from t+400ms
   * onward, and two clauses went red — the door's own, and the keyboard one,
   * whose Shift+Tab walk had four more stops to cross.
   *
   * An outstanding request is the host saying this gesture has a destination,
   * so it wins. Nothing else changes: a repaint with no request still arrives
   * ready to write, which is the behaviour this block was written for.
   */
  if (pendingWorkspaceFocus !== undefined) return;
  if (!$('stage').querySelector('[data-morph-document-block]:focus')) {
    const first = $('stage').querySelector<HTMLElement>(
      '[data-morph-document-block][contenteditable="true"]');
    // WITHOUT scrolling: the first block sits under the contents, and a
    // focus that scrolls opens the document with its top — Word's title
    // bar and ribbon, the document view's head — off the screen. The
    // caret is placed; the page stays where a document opens, at its top.
    first?.focus({ preventScroll: true });
  }
}

/** Which object a single-object lens is over. `undefined` means "the first one
 *  in the collection", resolved per render, so the control alone still works. */
let selected: ObjectId | undefined;

/**
 * The one place `selected` is assigned, so the ingest barrier cannot be missed.
 *
 * @Codex, P2 on #184, one round after the barrier itself: *"after
 * `createObject()` fails, `creationLanded` stays false, while `openHere()` and
 * the automatic `firstInPool()` selection assign a valid `selected` object
 * without resetting it. A person can therefore follow a reference or switch to
 * a single-object view, see a valid existing object, and still have every file
 * refused here as though that object had not been created."*
 *
 * Right, and the first fix set the flag at three assignment sites — which is
 * the shape of a fix that misses the fourth. There were five. This is the
 * choke point instead: `apps/demo/test/ingest-barrier.test.ts` asserts that no
 * bare `selected =` survives outside it, so a sixth selection path fails a
 * test rather than a person's next file.
 */
function select(id: ObjectId | undefined): void {
  selected = id;
  /* AND A PENDING TAB CLOSE IS OUTRANKED — @Codex's P2 on `98376fb`, and the
     same argument that made this function the choke point in the first place.
     `tabCloseTicket` was bumped at three call sites and its own comment named
     them: a newer close, a tab activation, leaving the workspace. A person
     clicking a card, a row, an explorer note or a search hit is a fourth, and
     it went through `selectFromClick` without moving the number — so a close
     still resolving its neighbour's pool landed afterwards and replaced the
     selection they had just made.

     Here rather than there, because "three sites is the shape that goes stale
     when a fourth is added" is written about this very ticket eight lines from
     where the fourth was missing. Every selection is a newer answer to the
     question a close is asking, including the close's own — which reaches this
     line having already passed its guard, so bumping again costs nothing. */
  tabCloseTicket += 1;
  // A selection that landed answers the question the barrier asks. Clearing
  // the selection does not: `!selected` refuses on its own, and a person who
  // is looking at nothing has not established that a creation succeeded.
  if (id !== undefined) creationLanded = true;
}

type Ctx = 'reading' | 'authoring' | 'browsing' | 'comparing' | 'studying';

/**
 * Who is looking, and what they may do.
 *
 * `ACCEPTANCE.md`'s Notion row asks for **permissions**, and it is the one item
 * on that row nothing had ever addressed — the word appeared exactly once in
 * that file, in the row's own definition.
 *
 * The canon finding names the shape it needs: *"per-viewer evaluation —
 * permissions determine what exists in the output, so the same spec renders
 * differently per user."* **Differently in the OUTPUT** is the load-bearing
 * half. Morph enforced authority only at write time: a person who could not
 * write still got a lens full of `contenteditable` fields, every one of which
 * refused on the first keystroke. That is not a permission, it is a trap.
 *
 * So a reader is read-only **by absence**, which is the rule the write path
 * already works under and the one the side pane fell out of: grants are what
 * make the renderer emit an editable element, so a render with no `edit` option
 * mints none and there is nothing to refuse. Nothing is disabled, greyed, or
 * checked for — the affordance is not there, because the authority to use it
 * was not.
 *
 * `?viewer=reader`, because a permission is something you ARRIVE with. It is
 * how Notion actually hands one out — a link — rather than a control the person
 * holding it can flip, and a select in the drawer would be exactly that.
 *
 * **This is one permission, not a permission system.** There is no viewer
 * identity in the store, no per-object rule and no sharing model; `db.editor`
 * is still one authority obtained at boot. What it establishes is the shape:
 * authority reaching GENERATION rather than only the write path.
 */
function viewerMayWrite(): boolean {
  try {
    return new URLSearchParams(location.search).get('viewer') !== 'reader';
  } catch {
    // No `location` under a non-browser harness, which is a legitimate way to
    // run this — the same reason `scaleRequested` swallows it.
    return true;
  }
}

/** Read once, at boot. A permission that changed under a running page would be
 *  a different person looking at the same lens, which is not a thing a reload
 *  cannot express. */
const MAY_WRITE = viewerMayWrite();

/**
 * The authorization every write gesture asks, in ONE place.
 *
 * Until this it was asked at each gesture — the two form buttons, the drawer —
 * and the ACTION path never asked it at all. @Codex, P1 on #138:
 *
 * > *"With `?viewer=reader`, switch Tasks to single-object mode and then back
 * > to the collection: `selected` remains populated, while the newly added row
 * > buttons are still rendered … the row has no anchor and `invokeFromClick`
 * > falls back to that stale `selected`; this direct call then bypasses both
 * > the former cardinality guard and any `MAY_WRITE`/grant check and persists
 * > `status: seen` through `db.editor`."*
 *
 * Correct, and it is the answer to the question I put on the PR. Splitting
 * `writeProperty` out of `addProperty` removed a guard that was doing two jobs:
 * `state.cardinality !== 'single'` reads as a gesture guard and was also,
 * incidentally, the only thing standing between a reader and the store on that
 * path. A guard that authorizes by accident is one nobody knows they are
 * relying on — which is exactly how it came out.
 *
 * So the question is asked at the two places that matter and no others: the
 * effect boundary, where an action's writes are declared, and `writeProperty`,
 * which is the door to `db.editor`. Both, because a reader must not reach the
 * store whether the request came from an action, a form, or something added
 * next month — the same "there is no other door" argument the effect gate in
 * `@morph/spec` makes about undeclared effects.
 *
 * It SAYS so rather than failing silently. A reader who presses a control has
 * asked a question, and "nothing happened" is not an answer.
 */
function mayWrite(what: string): boolean {
  if (MAY_WRITE) return true;
  say(`this link is read-only — you cannot ${what} here`, 'bad');
  return false;
}

/**
 * The same refusal, for an EFFECT — which has to throw rather than return.
 *
 * `Effects.setProperty` returns `Promise<void>`. A refusal expressed as an
 * early return is invisible to `ActionRegistry.invoke`, which records the step
 * as successful — so `runAutomation` carries on to the next one and
 * `runStoredAutomation` announces *"ran 2 steps from stored data"* over a
 * reader whose every write was refused. @Codex, P2 on #138, and it is the worst
 * shape a refusal can take: reported as success.
 *
 * Throwing is the channel the interface already has. `invoke` catches anything
 * an action's `run` throws and converts it into `action-failed`, on the same
 * reasoning `host.ts` gives for `UndeclaredEffect`: *"a return value it can
 * ignore is not a boundary"*. So an automation stops at the refused step and
 * says which one.
 *
 * `say` still fires, so a person reaching this from a single button press gets
 * the plain sentence rather than a code.
 */
function refuseUnauthorized(what: string): void {
  if (mayWrite(what)) return;
  throw new Error(`this link is read-only — ${what} is not permitted`);
}

/** A place under Notion's look — what the topbar's back and forward move
 *  between: the page's view, or an object OPEN in its own lens. A selection
 *  on the board is not a place (the app's back does not undo a click on a
 *  card that stayed on the board), so `selected` is read only in single
 *  cardinality. */
interface NtPlace {
  readonly view: TemplateView;
  readonly selected: ObjectId | undefined;
  readonly cardinality: 'single' | 'large-set';
}
const samePlace = (a: NtPlace, b: NtPlace): boolean =>
  a.view === b.view && a.selected === b.selected && a.cardinality === b.cardinality;

const state = {
  context: 'browsing' as Ctx,
  palette: 'neutral' as Palette,
  scheme: (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') as Scheme,
  cardinality: 'large-set' as 'single' | 'large-set',
  view: 'auto' as 'auto' | 'board' | 'graph' | 'plot' | 'document' | 'calendar' | 'timeline',
  /** The NAMED APP LOOK the stage is wearing (decision 0055), set by the hub
   *  and cleared by any collection navigation. A hard constraint on
   *  generation, so it is part of the episode identity beside the view. */
  template: undefined as AppTemplate | undefined,
  /** Which pane each of a named app look's sidebars is showing, keyed by
   *  the sidebar's spec id (the left: explorer or search; the right:
   *  backlinks or graph) — host state the renderer is told; a sidebar
   *  absent here shows its first pane. */
  sidePanes: {} as Record<string, RegionPurpose>,
  /** The search pane's query — the host's, answered on every paint. */
  searchQuery: '',
  /** Whether a COLLAPSED search (a look declaring `searchTrigger`) is
   *  showing its box. Meaningless under a look that has no trigger, whose
   *  box is always there. */
  searchOpen: false,
  /** The explorer's folders a person has closed, by name. The folders are
   *  the file list's `groupBy` in the spec; which are open is host state. */
  folded: [] as string[],
  /** Which of a named look's VIEWS the page shows — Notion's Board, Table,
   *  Calendar, Timeline. A regeneration the person asked for by name. */
  templateView: 'board' as TemplateView,
  /** THE COMPOSED APP a person made, if this door opened one — its id, never
   *  its record, because the record is derived from parts that can change.
   *  `template` stays the app it is BASED on: generation is the base's, and
   *  only the presentation is composed. Undefined at one of the six doors. */
  composedId: undefined as string | undefined,
  /** The places visited under Notion's look — the topbar's back and forward.
   *  OBSERVED at each paint (every path that moves the page lands in one),
   *  never pushed by hand, so no navigation can forget to record itself;
   *  `moving` marks the paint a chevron caused, which records nothing new. */
  ntHistory: { back: [] as NtPlace[], forward: [] as NtPlace[],
    here: undefined as NtPlace | undefined, moving: false },
  /** Which of the main column's views shows: the note, or the vault's
   *  Graph view behind a tab of its own (the tenth verdict's list). */
  mainView: 'note' as 'note' | 'graph',
  /** Which mark a plot draws. A control rather than a constant, because the
   *  renderer draws four and a route that reaches one leaves the other three
   *  unreachable — an implemented capability nobody can exercise. @Codex asked
   *  for every supported mark to be reachable and measured, and it was right:
   *  three of the ten defects in this renderer were mark-specific. */
  mark: 'point' as PlotMark,
  /** Which month a calendar is showing, `YYYY-MM`, or nothing for "the one
   *  with the most in it". View state in decision 0014's sense — where the
   *  reader is, not what the layout says — so it lives here beside `mark`
   *  rather than in the spec. */
  calendarMonth: undefined as string | undefined,
  viewport: 'regular' as ViewportClass,
  /** The accepted lens, and any candidate being offered. Real LensState from
   *  @morph/generation — the same module the tests exercise. */
  lens: emptyLens() as LensState,
  /** Kept suggestions and the persisted manual-adjustment ledger meet here. */
  taste: manualTaste.taste() as TastePriors,
  learned: [] as string[],
  /**
   * Candidates the person has said "Not this" to.
   *
   * SEPARATE from `lens.dismissed`, which holds spec ids and is right to. A
   * spec id answers "have they already been shown this exact layout"; a
   * candidate key answers "have they already turned down this suggestion".
   * Those are different questions and the answers are different strings —
   * conflating them is what left `pickCandidate`'s dismissal filter dead for
   * its whole life. The key is minted in `suggestion.ts` at both ends.
   */
  dismissedCandidates: [] as readonly string[],
  /** Where the outline panel sits. A LAYOUT decision, so it shapes the spec —
   *  unlike where the user dragged it to, which is view state and never gets
   *  near one. Decision 0014 draws that line. */
  shell: readShellChoice() as ShellChoice,
};

const $ = (id: string) => document.getElementById(id)!;

/**
 * What this pool calls each of its roles.
 *
 * The pools have always declared these — Films calls `date` **Released**,
 * `author` **Director**, `duration` **Runtime**; Notes calls `quantity`
 * **Words** — and none of it ever reached the screen, because the renderer
 * derived every caption from the vocabulary term. So a note showed an empty
 * field prompting `Quantity`, which is not a thing a note has, while the name
 * for it sat in `store-setup.ts` the whole time.
 *
 * Built here rather than put in the spec: a caption is content, and the spec is
 * the one place content must never be. `renderHTML` takes it as a render input
 * beside the values it names.
 */
function labelsFor(p: DemoPool): Partial<Record<Role, string>> {
  const out: Partial<Record<Role, string>> = {};
  for (const d of p.defs) if (d.role) out[d.role] = d.label;
  return out;
}

/**
 * What an enum may be chosen from, read from the POOL rather than the lens.
 *
 * Over `pool.scope`, never over the scope being rendered, and the difference
 * is the whole feature: a single-object lens is seeded to one object, so its
 * enum values are that object's own — a chooser built from it would offer a
 * film the one country it already has, or nothing at all for a film that has
 * none, which is precisely the object that needs the chooser.
 *
 * Derived, never authored. Nobody writes a list of statuses: a pool that
 * starts using a new one offers it the moment an object carries it, and the
 * store orders them by how often they are held.
 */
async function choicesFor(p: DemoPool, shape: ObjectShape): Promise<Partial<Record<Role, readonly string[]>>> {
  const out: Partial<Record<Role, readonly string[]>> = {};
  /* THE ROLES ARE THE UNION, and the shape alone is not enough — task `0202`.
     A cleared enum leaves `shape.roles` the moment its last value goes, so a
     walk over the shape cannot reach the very role that most needs a chooser:
     the one with nothing in it. The pool's own defs know it is an enum without
     an object carrying a value, which is the same reason `declaredRoles`
     exists one layer up. */
  const roles = new Set<Role>([
    ...shape.roles.filter((r) => r.kind === 'enum').map((r) => r.role),
    ...p.defs.filter((d) => d.kind === 'enum').map((d) => d.role),
  ]);
  await Promise.all([...roles].map(async (role) => {
    const seen = await db.store.enumOptions(p.scope, role);
    /* DECLARED FIRST, IN DECLARED ORDER, then anything observed the
       declaration does not name.

       Not "fall back to declared when the store is empty", which is what a
       cleared role alone would need and is one dimension short in the way this
       family keeps repeating: a status DECLARED and never yet used by any
       object is missing from the chooser for exactly the same reason, and a
       fallback keyed on emptiness cannot see it. The union covers both.

       Declared order because `Def.options` says so — it is "a declaration,
       never a discovery", and the renderer never sorts. The observed extras
       follow in the store's own frequency order, which is what a value
       somebody typed outside the declaration deserves: offered, not ranked
       above the declared set. */
    /* FROM `p`, NOT FROM THE OPEN POOL. `declaredOptions` reads the
       module-level `pool`, and this function is called with `home` — the
       aside object's OWNER, which is a different pool whenever a note cites
       something from elsewhere. Reaching for it here would have offered the
       open pool's statuses for another pool's object.

       That is the defect the comment forty lines above this one records
       against `asideManifest`, verbatim: "I moved the READ onto the resolved
       home and left the WRITE on the open pool, so the field a person edits
       and the slot the store changes could belong to different documents."
       Caught on a hostile read of this diff, having just read that comment. */
    const declared = p.defs.find((d) => d.role === role && d.options)
      ?.options?.map(optionValue) ?? [];
    out[role] = [...declared, ...seen.filter((v) => !declared.includes(v))];
  }));
  return out;
}

/**
 * A candidate the taste model would propose, given what it has seen.
 *
 * **Each one carries typography as well as density**, and that is the whole
 * difference between this list and the one task 0045 phase 3 measured. That
 * measurement concluded "taste moves density, and density alone" and handed
 * forward an open question about whether `TastePriors` should learn to carry
 * typography. It already did: the field has always been there and
 * `baseline.ts` has always honoured it —
 *
 *     typography: req.taste?.typography ?? pickTypography(...)
 *     // Explicit taste beats inference, always.
 *
 * — and what was actually narrow was this array. Two candidates, both offering
 * density and nothing else, so the model could not learn a preference nobody
 * ever offered it. A finding about the demo's fixtures, read as a finding about
 * the taste model.
 *
 * The two directions the phase-3 bar names are exactly these two rows.
 * `editorial` is Source Serif 4 at 17px/1.62 over a 68ch measure; `compact` is
 * Inter at 14px/1.42 over 76ch. Reading and working, from one shape, switched
 * by what the person kept.
 */
const CANDIDATES: Candidate[] = [
  { taste: { density: 'airy', typography: 'editorial' },
    what: 'Larger covers, metadata on demand',
    why: 'A collection like this is opened for its covers more than its rows — larger art first, metadata when you ask for it.' },
  { taste: { density: 'dense', typography: 'compact', suppressedRoles: ['duration'] },
    what: 'Tighter rows, runtime hidden',
    why: 'Comparing years is easier in tight rows, with runtime out of the way until it matters.' },
];

/**
 * The scope the lens is over.
 *
 * A single-object lens is a scope SEEDED on the object in hand, not a slice of
 * the collection's. That is the difference between a demonstration and a
 * disappointment: coverage is then that object's own, so a property added to it
 * clears `roleCoverageFloor` and earns a field, where the collection's coverage
 * would report it at one-in-eight and the lens would gain nothing.
 *
 * The same value drives `query`, `projection` and `deriveShape`, which is the
 * store's own rule — a shape derived over one scope and rows read from another
 * describe two different collections.
 */
/**
 * The documents this pool declared, carried onto every lens derived from it.
 *
 * `Scope.across` is what makes a reference into another document resolve, and
 * every seeded lens here builds a FRESH scope rather than spreading the pool's.
 * Dropping it meant a cross-doc reference that drew on the collection vanished
 * the moment somebody opened the object holding it — the one interaction that
 * exists to look at it.
 */
function crossDocs(): Pick<Scope, 'across'> {
  return pool.scope.across?.length ? { across: pool.scope.across } : {};
}

/**
 * What each declared view-state slot currently holds, by INDEX.
 *
 * Per-person, per-device, never pooled — decision 0014's line, and the reason
 * the value lives here rather than in the spec. The renderer draws the control
 * and names which slot it is; the number it is showing is the host's business.
 *
 * Sparse on purpose: an untouched slot has no value, and a filter reading it
 * compares against `undefined`, which `testPredicate` treats as no constraint.
 * A default would be a filter nobody asked for, applied on first paint.
 */
let viewStateValues: (Value | undefined)[] = [];

/** The slots the CURRENT lens declares, so a control's index means something.
 *  Reset whenever the lens changes, because slot 2 of one lens is not slot 2
 *  of another — the index is positional and the specs are generated. */
let viewStateSlots: readonly {
  readonly name: string; readonly role: Role; readonly op: NonNullable<IR['viewState']>[number]['op'];
}[] = [];
let viewStateOwner = '';

/**
 * The generator for the ONE host that can draw a split lens.
 *
 * `splitOnViewState` is a global default in `params.ts`, and a global default
 * is the wrong shape for this: `baselineGenerator` is shared by five hosts here
 * and exactly one of them — `run()`, the stage — supplies `regionSets`.
 * `renderBacklinks` hands the renderer one projection as `objects`, and the
 * renderer's fallback is deliberately silent when `regionSets` is undefined
 * (absent means region addressing is OFF, not that a region is missing). So
 * flipping the default would give the double paint to a surface with **no
 * view-state control to fix it with** — @Codex's measurement, and the reason
 * the flag has stayed off even though every layer beneath it is finished.
 *
 * Naming the capable host inverts that. Split becomes something a host ASKS
 * for, rather than something every other host has to be immune to, and the
 * question "which hosts are safe?" stops being a standing audit with four
 * answers that can each go stale independently.
 *
 * `openOffer` compiles through this too: its candidate is handed to
 * `state.lens` and drawn by `run()`, so the host that renders it is the stage
 * whatever compiled it.
 *
 * **Still off.** The remaining gate is decision 0020 — whether the stage may
 * hold back a region whose control is unset — and the value below becomes
 * `true` when that is ruled, in this one place, with no other host reachable
 * from it.
 */
const STAGE_SPLITS_ON_VIEW_STATE = true;
const stageGenerator = makeBaselineGenerator(
  { splitOnViewState: STAGE_SPLITS_ON_VIEW_STATE });
/**
 * The private pool, and the only thing in this file allowed to read it.
 *
 * Given `stageGenerator`, not the shared one: a pool offer is drawn by `run()`
 * exactly as an ordinary offer is, so compiling it through a generator that
 * does not split on view state would lose region splitting for a reason having
 * nothing to do with the pool. @Codex caught this on #184.
 */
privatePool = new PrivatePoolSuggestions(stageGenerator);

/** The values, trimmed to the slots that exist and packed for the store.
 *  `Scope.viewState` is an array indexed by `Predicate.valueFrom`. */
function viewStateScope(): Pick<Scope, 'viewState'> {
  if (!viewStateSlots.length) return {};
  const vals = viewStateSlots.map((_, i) => viewStateValues[i]);
  // Nothing set means nothing to send — an empty array would still be a
  // declaration that this lens has view state, and the scope hash includes it.
  return vals.some((v) => v !== undefined)
    ? { viewState: vals as readonly Value[] }
    : {};
}

function queryScope(query: Scope['query'] | undefined): Pick<Scope, 'query'> {
  return query ? { query } : {};
}


function lensScope(single: boolean, query?: Scope['query']): Scope {
  if (single && selected) {
    // A referenced object may have another typeHint (a film's director is a
    // person). A seed already names the exact object, so retaining the pool's
    // type filter would turn a successful graph selection into an empty lens.
    return { manifest: pool.scope.manifest, seed: [selected], ...crossDocs(),
      ...queryScope(query), ...viewStateScope() };
  }
  // A SEARCH RESULT is an ordinary seeded scope, which is the whole design of
  // retrieval here: it gets an ordinary generated lens over it, through the
  // same path everything else takes. A result set delivered by its own private
  // route would bypass the manifest boundary and the grants with it, and the
  // search results would be the one surface where authorization did not apply.
  //
  // No `typeHint`, for the reason above it: the search ranked objects, and
  // filtering its own answer by type afterwards would drop hits it just said
  // were the best ones.
  if (found) {
    return { manifest: pool.scope.manifest, seed: found.ids, ...crossDocs(),
      ...queryScope(query), ...viewStateScope() };
  }
  return { ...pool.scope, ...queryScope(query), ...viewStateScope() };
}

/**
 * What points AT this object.
 *
 * Obsidian's backlinks panel, and it needs no new machinery: `Scope.seed`
 * already means "a lens over exactly these objects", so a backlink set is an
 * ORDINARY collection scope and gets an ordinary generated lens over it. Not a
 * panel with its own renderer — the same derive → generate → compile → render
 * path the main lens uses, which is the claim this product makes about itself.
 *
 * `typeHint` is deliberately dropped. What references a film is not necessarily
 * a film, and a backlinks panel that silently hides the wrong type of referrer
 * is worse than none.
 */
async function backlinkScope(target: ObjectId): Promise<Scope | undefined> {
  const refRoles = pool.defs.filter((d) => ROLE_KIND[d.role] === 'ref').map((d) => d.role);
  if (!refRoles.length) return undefined;

  const found = new Set<ObjectId>();
  for (const via of refRoles) {
    const r = await db.store.traverse({
      manifest: pool.manifest, from: [target], via, direction: 'in',
      depth: 1, maxNodes: 64,
    });
    for (const n of r.nodes) if (n.id !== target) found.add(n.id);
    // `truncated` is surfaced by the store rather than swallowed, and saying so
    // is the difference between "nothing else links here" and "we stopped
    // looking". But `depth` is not that: this asks for depth 1 on purpose —
    // direct referrers only — so hitting the depth bound is the normal stop,
    // and reporting it made a complete answer read as a cut-off one.
    if (r.truncated === 'max-nodes' || r.truncated === 'timeout') {
      backlinkNote = `stopped at ${r.truncated}`;
    }
  }
  if (!found.size) return undefined;
  return { manifest: pool.manifest, seed: [...found], ...crossDocs() };
}

let backlinkNote = '';

/** Render what links here as its own generated lens, under the main one. */
async function renderBacklinks(target: ObjectId | undefined): Promise<void> {
  const el = $('backlinks');
  backlinkNote = '';
  // Under a named app look the backlinks are a PANE of the generated spec
  // (decision 0055), supplied through `regionSets`; a second list under the
  // stage would be Morph's chrome showing through the app's.
  if (!target || state.template) { el.innerHTML = ''; el.hidden = true; return; }

  const scope = await backlinkScope(target);
  if (!scope) {
    el.hidden = false;
    el.innerHTML = '<div class="bl-head">Linked from</div>'
      + '<p class="bl-empty">Nothing links here yet.</p>';
    return;
  }

  const shape = await db.store.deriveShape(scope);
  const gen = await compile(
    { shape, context: 'browsing', taste: { palette: state.palette, ...state.taste } },
    baselineGenerator);
  if (!gen.ok) { el.hidden = true; return; }

  const rows = await db.store.projection(scope, { limit: 64 }, { expandRefs: 0 });
  const backlinkTotal = rows.total ?? rows.items.length;
  try {
    const ir = compileSpec(gen.spec, { viewport: state.viewport, itemCount: backlinkTotal });
    el.hidden = false;
    el.innerHTML = `<div class="bl-head">Linked from`
      + `<span class="bl-count">${backlinkTotal}</span>`
      + (backlinkNote ? `<span class="bl-note">${backlinkNote}</span>` : '')
      + `</div>`
      + renderHTML({
        ir, objects: [...rows.items], scheme: state.scheme,
        totals: { subject: backlinkTotal }, labels: labelsFor(pool),
      });
  } catch {
    // A refused backlinks lens must not take the page down with it. The main
    // lens is the product; this is context beside it.
    el.hidden = true;
  }
}

/* ---------------------------------------------------------------- retrieval */

/**
 * FINDING things, which is the Fabric row's last unbuilt item.
 *
 * A search returns a SCOPE, and that is the whole design. `Scope.seed` already
 * means "a lens over exactly these objects", so a result set is an ordinary
 * scope and gets an ordinary generated lens — the same derive → generate →
 * compile → render path the backlinks panel and the side pane take. A retrieval
 * that returned ids by its own private route would bypass the manifest
 * boundary and the grants with it, and the search results would be the one
 * surface in the product where authorization did not apply.
 *
 * The index is built from the pool's own text and rebuilt when the pool
 * changes. It is not persisted: it is a fold over objects the store already
 * holds, so a stale index is a wrong answer with nothing to warn you, and
 * rebuilding costs a pass over text the store just handed us anyway.
 */
let findIndex: TextIndex | undefined;
let findIndexFor: string | undefined;
let found: { query: string; ids: ObjectId[]; expanded: string[] } | undefined;

/** Which roles carry text worth searching. Prose first, but not only prose —
 *  a title is the most searchable thing an object has, and a caption often
 *  says what a picture is. */
const SEARCHABLE: readonly Role[] = [
  'title', 'subtitle', 'body', 'caption', 'transcript', 'author', 'origin', 'status',
];

async function buildFindIndex(): Promise<TextIndex> {
  /**
   * THE STORE REVISION IS PART OF THE KEY — @Codex, P2 on `65952e8`. Keyed
   * by pool and manifest alone, no write path ever invalidated this cache:
   * a search after editing a title kept answering from the pool as it was
   * at first search. Any commit moves the revision, so the next search
   * rebuilds; a rebuild costs one pass over the pool and only ever runs on
   * a search after a write.
   */
  let { revision } = await db.store.snapshot();
  const keyOf = (rev: unknown): string => `${pool.id}:${String(pool.manifest)}:${String(rev)}`;
  if (findIndex && findIndexFor === keyOf(revision)) return findIndex;
  // `query`, not `projection`. A projection returns opaque HANDLES, which are
  // session identities minted for a render and resolved through `identities` —
  // a map the demo fills while binding a pass. An index built out of band has
  // no pass, so every handle missed and the index came out empty: a search box
  // that answered "nothing in this pool" to every word in it. `query` returns
  // stored objects with their real ids, which is what a scope seed needs.
  //
  // The WHOLE pool, not a page — and paginated until the cursor runs out,
  // because @Codex's P2 on `65952e8` caught this comment claiming the whole
  // pool over a single `limit: 5000` read: object 5,001 was permanently
  // invisible to search while the sentence above said otherwise.
  // ONE revision per scan — @Codex, P2 on `22799c0`: the cursor is an offset
  // into a result set, so a write landing between pages makes the index skip
  // rows, duplicate rows, or mix old and new text — and the search being
  // typed right now answers from that torn scan. Re-read the revision after
  // the walk; if it moved, the walk raced a writer and runs again. Bounded:
  // under persistent churn the torn index serves one search, and the next
  // search's own revision check rebuilds it.
  let items0 = [];
  for (let attempt = 0; ; attempt += 1) {
    items0 = [];
    let cursor;
    do {
      const slice = await db.store.query(pool.scope,
        { limit: 5000, ...(cursor !== undefined ? { cursor } : {}) });
      items0.push(...slice.items);
      cursor = slice.cursor;
    } while (cursor !== undefined);
    const now = (await db.store.snapshot()).revision;
    if (now === revision || attempt >= 3) break;
    revision = now;
  }
  const items = items0.map((o) => ({
    id: o.id,
    text: SEARCHABLE
      .flatMap((r) => o.roles.get(r) ?? [])
      .map((v) => rawOf(v))
      .filter((v): v is string => typeof v === 'string')
      .join(' '),
  })).filter((it) => it.text.trim());
  findIndex = new TextIndex(items);
  findIndexFor = keyOf(revision);
  return findIndex;
}

async function runFind(query: string): Promise<void> {
  const q = query.trim();
  if (!q) { clearFind(); return; }
  const index = await buildFindIndex();
  const hits = index.search(q, 60);
  // Terms the person did not type but the pool associates with what they did.
  // Said out loud, because a result that contains none of the words you typed
  // reads as a bug unless something explains it.
  const asked = new Set(terms(q));
  const expanded = [...new Set(hits.flatMap((h) => h.matched))]
    .filter((t) => !asked.has(t));
  found = { query: q, ids: hits.map((h) => h.id), expanded };
  select(undefined);
  schedule();
}

function clearFind(): void {
  if (!found) return;
  found = undefined;
  schedule();
}

/** What the search found, in words above the document.
 *
 *  Built from TEXT NODES, never from an HTML string. The query is whatever a
 *  person typed, and interpolating it into `innerHTML` would make the one
 *  surface in this app that echoes user input back to the page an injection
 *  site. `textContent` cannot be escaped wrong because there is nothing to
 *  escape — the same reason the renderer's markup goes through `esc` and this
 *  does not go through anything at all. */
function renderFindNote(): void {
  const el = $('findnote');
  el.replaceChildren();
  if (!found) { el.hidden = true; return; }
  el.hidden = false;
  const n = found.ids.length;
  const head = document.createElement('b');
  head.textContent = n === 0
    ? `Nothing in this pool matches “${found.query}”`
    : `${n} ${n === 1 ? 'object' : 'objects'} for “${found.query}”`;
  el.append(head);
  // The sentence that makes an unexpected result legible rather than wrong: a
  // hit containing none of the typed words needs to say why it is here.
  if (found.expanded.length) {
    const also = document.createElement('b');
    also.textContent = found.expanded.slice(0, 4).join(', ');
    el.append(' — also matched on ', also, ', which this pool uses alongside it');
  }
}

/* ------------------------------------------------------------- split panes */

/**
 * A SIDE PANE is a second lens over a seeded scope, beside the first one.
 *
 * Obsidian's row asks for split panes, and the machinery was already here: the
 * backlinks panel above is a second generated lens over `Scope.seed`, painted
 * into its own container. What it was not is a place a person can put anything
 * — it only ever shows what points at the current object. This makes the same
 * path general, so two objects can be read side by side.
 *
 * Every pane is a REAL generated lens. Not a second renderer, not a simplified
 * preview: the same derive → generate → compile → render path the main lens
 * takes. A pane drawn any other way would look like the claim this product
 * makes about itself while quietly not being it.
 *
 * **The pane was read-only, and the reason recorded here for it was about a
 * different host.** It said an editable pane needs two hosts both listening on
 * `document` — *"the exact arrangement that made one Escape keystroke undo two
 * things once already"*. That is true of `drop-host.ts`, which does bind
 * `window` for pointermove, pointerup and keydown. It is not true of
 * `edit-host.ts`: every listener there is `root.addEventListener`, `isQuiescent`
 * asks `root.contains(active)`, and the only thing it reaches the document for
 * is `activeElement`, `createElement` and `createRange` — reads and factories,
 * never a listener.
 *
 * `#stage` sits inside `#paneMain` and `#paneStage` is its SIBLING, so the two
 * roots are disjoint and neither host can see the other's events.
 *
 * Everything else an editable second lens needs was already built, for other
 * reasons:
 *
 *  - **its own grants**, because `mintGrants(ir, lens)` keys every grant id on
 *    `lens:ir.hash:node` and `resolveEdit` refuses a mismatched lens outright;
 *  - **its own epoch**, because each host holds its own `pass` and compares the
 *    anchor's epoch against that rather than against a global;
 *  - **its own undo**, because `scopeOf` already returns `lens:<id>` and undo is
 *    filtered by containment;
 *  - **its own spec**, because the write path resolves authority through
 *    `specOf(lens)` over a map keyed by the spec's content hash, and the pane's
 *    spec registers in the same map under its own key.
 *
 * So the pane is editable now, and none of it needed a runtime change. What it
 * did need was `withHandle`: the pane used the raw projection row, which carries
 * no handle, so nothing in it was addressable and every write would have failed
 * to resolve an object rather than been refused.
 */
let aside: ObjectId | undefined;
/**
 * The pane's own render passes.
 *
 * The first version of this comment said a shared counter *"would make every
 * main repaint stale the pane's anchors and refuse an edit a person was in the
 * middle of"*. **Mutation-checked, and that does not happen**: the epoch an
 * anchor resolves against is the one `completeSingleAnchor` stamps on the
 * pane's root, and a main repaint neither restamps that root nor rebinds this
 * host — so `e2e/pane-edit.mjs` passed unchanged with the counters shared.
 *
 * It stays separate on a design argument rather than a measured one, and the
 * difference is worth marking: each host compares an anchor's epoch against its
 * OWN pass, so the number has to agree with whatever stamped the root it was
 * read from. Taking it from the main lens's counter couples two independent
 * render loops through a variable neither of them reads from the other, which
 * is a thing to be able to change later without checking a second loop first.
 */
let asideEpoch = 0;

/**
 * The pane's viewing episode, mirroring the stage's.
 *
 * Decision 0023 is about the CURRENT LENS, and the pane is one. @Codex's review:
 * *"an ordinary pane edit still regenerates and may move or remove fields under
 * the person, contrary to accepted decision 0023's current-lens invariant."*
 * Right, and the reason the first implementation missed it is worth naming: I
 * fixed `run()` and treated that as fixing the app, when the pane has its own
 * render path, its own host and its own generator call.
 *
 * Keyed on the pane's object, so following a reference to a different one is a
 * new episode and does derive.
 */
let paneEpisode: {
  readonly key: string;
  readonly shape: ObjectShape;
  spec?: TLayoutSpec;
} | undefined;

async function renderAside(derive = true): Promise<void> {
  const el = $('paneSide');
  const stage = $('paneStage');
  if (!aside) {
    stage.innerHTML = '';
    $('paneTitle').textContent = '';
    el.hidden = true;
    return;
  }

  /**
   * Read through the object's OWN manifest, resolved before anything else.
   *
   * The pane built its scope from the open pool's manifest, so following a
   * cross-pool reference read a note through the TASKS manifest — which
   * addresses none of its keys. Measured: the pane opened on a title of
   * "Object" with five unnamed fields and no relations at all. Decision 0049's
   * boundary doing its job, on a lens that had no business asking.
   *
   * @Codex found the declaration half of this; driving it found that the
   * READING half was wrong in the same way and for longer. Both come from one
   * assumption — that the pane's object belongs to the pool on screen — and
   * following a reference is the one gesture guaranteed to break it.
   */
  /**
   * The pane's object has its OWN pool — or none, and NONE IS NOT THE OPEN ONE.
   *
   * @Codex: *"Films → Filmmaker … the target is a `typeHint: 'person'` object
   * seeded under the Films manifest, but there is no person `DemoPool` … so
   * `renderAside` falls back to the current Films pool and passes all film
   * declarations to `declaredShape`, making the person acquire absent film
   * fields."* Reproduced: opening **René Clément** showed *No Poster* and empty
   * Released, Country, Director, Runtime, Rating and Filmmaker.
   *
   * `?? pool` read "no proven home" as "the pool on screen", which is the same
   * assumption this block was written to remove, surviving in its own fallback.
   * A person is not a film and nothing said it was — the code simply had no
   * other answer to hand.
   *
   * Two facts, kept apart:
   *
   *  - **Which document to READ through.** The person is seeded under the films
   *    manifest, so the open pool's manifest is right and the fallback is safe.
   *  - **Whose DECLARATIONS apply.** Nobody's. An object no pool owns gets its
   *    own derived shape and nothing added — which is what the pane did before
   *    any of this, and is correct for exactly this case.
   */
  const owner = await poolOf(aside);
  const home = owner ?? pool;
  /**
   * The HOME's complete scope, seeded — not its manifest plus the stage's
   * `across`.
   *
   * @Codex, driving the deployed preview: adding a `Raised by` target through a
   * note's pane *"reports `reference added`, but draws an orphan `linked
   * object` outside the Connections group"*, because `crossDocs()` is
   * stage-global. With Tasks on the stage and a Note in the pane this combined
   * the Notes manifest with Tasks' `across` (Notes), so the TASK target fell
   * outside the reading scope and projected as an unreadable value with no
   * occurrence token.
   *
   * I fixed exactly this for `resolveOccurrence` one commit ago and left the
   * READ that feeds it alone — the same repair one level too narrow, which is
   * the failure mode this branch keeps producing. A surface's manifest combined
   * with another surface's `across` is two documents' answers to one question,
   * wherever it appears.
   *
   * **WITHOUT the typeHint**, and that is not a detail. @Codex's suggested line
   * was `{ ...home.scope, seed: [aside] }`; a pool's scope carries its type
   * filter, and I applied the suggestion literally against a rule written three
   * hundred lines up this same file, beside `lensScope`:
   *
   * > *A referenced object may have another typeHint (a film's director is a
   * > person). A seed already names the exact object, so retaining the pool's
   * > type filter would turn a successful graph selection into an empty lens.*
   *
   * Which is exactly what happened: `split-panes` went from 20/20 to a pane
   * that never opened, because every object reached by following a reference
   * was filtered out of its own lens. A seed names ONE object; a type filter
   * beside it can only ever remove that object.
   */
  const { typeHint: _paneType, ...homeReads } = home.scope;
  const scope: Scope = { ...homeReads, seed: [aside] };
  const paneRead = await db.effective.read(scope, { limit: 1 }, { expandRefs: 1 });
  const derived = paneRead.shape;
  /**
   * Through `declaredShape`, like the main stage.
   *
   * This pane generated from the raw derived shape, so a task opened by
   * FOLLOWING a Parent task reference got no Parent task row and no way to add
   * one — @Codex's finding, and it is the first defect of this whole feature
   * surviving in the one surface a person reaches by following a reference.
   * Landing on a task with no parent of its own is exactly what opening a
   * Parent task does, so the pane most in need of the gesture was the pane
   * without it.
   *
   * The DENSITY is measured over the whole pool rather than over this one
   * object, for the reason the main stage records at length: over a single
   * object a density is 0 or 1, so a seeded object with no reference reports 0,
   * falls under the surfacing threshold, and gets no slot — the same failure by
   * a different route.
   */
  /**
   * The pane's object has its OWN pool, and it is often not the open one.
   *
   * @Codex: *"when the pane is opened by following a cross-pool reference, such
   * as Tasks → Background → a note … `aside` is not a member of the current
   * `pool`, yet this augments its derived shape with the current pool's
   * measurements and declarations … a note can acquire task fields plus Parent
   * task/Subtasks rows."*
   *
   * Correct, and it is worse than a cosmetic slip: `declaredShape` appends every
   * missing declared role at full coverage, so the note would have been shown
   * fields it does not have and rows for relations its own pool never declares.
   * Following a reference is the ONE gesture guaranteed to land on an object
   * from somewhere else, and this was the one path that assumed otherwise.
   *
   * Resolved by membership rather than by type name — `seed` narrows a scope to
   * named objects, the same exact test the write boundary uses.
   */
  // Declarations only where membership is PROVEN. `owner`, not `home`.
  const fresh = owner
    ? declaredShape(
      derived, (await db.effective.read(owner.scope, { limit: 0 })).shape, owner.defs)
    : derived;

  // Decision 0023, on the pane's own lens. Same two parts as the stage: the
  // shape the structural decisions read, and the spec they produce.
  const paneKey = `${aside}\u0000${state.context}\u0000${state.palette}`;
  if (derive || paneEpisode?.key !== paneKey) paneEpisode = undefined;
  if (paneEpisode === undefined) paneEpisode = { key: paneKey, shape: fresh };
  const shape = paneEpisode.shape;

  let generated = paneEpisode.spec;
  if (generated === undefined) {
    const gen = await compile(
      { shape, context: state.context, taste: { palette: state.palette, ...state.taste } },
      baselineGenerator);
    if (!gen.ok) {
      // A pane that cannot generate says so where the pane is. Silently closing
      // it would read as the click having done nothing at all.
      el.hidden = false;
      $('paneTitle').textContent = 'Side pane';
      stage.innerHTML = '<p class="bl-empty">No lens could be generated for this object.</p>';
      return;
    }
    generated = gen.spec;
    paneEpisode.spec = generated;
  }

  // Both, because `withHandle` needs the id beside the values — the same zip the
  // main lens does, and for the same reason: the DOM names an object by an
  // opaque handle while the store still resolves a real id.
  const values = paneRead.projection.items[0];
  const id = paneRead.objects.items[0]?.id;
  if (!values || !id) { aside = undefined; stage.innerHTML = ''; el.hidden = true; return; }
  const row = withHandle(values, id);

  try {
    /**
     * The pane draws relation gestures NOW, because there is finally something
     * behind them — task 0077.
     *
     * It drew none until this commit, and that was the honest state rather than
     * a shrug: applying `withRelationMutations` here once made Add and Remove
     * appear, and @Codex measured what they did — *"pressing any of the newly
     * visible controls after following a Parent task reference does nothing —
     * Add never opens a picker and Remove never writes."* **An affordance with
     * nothing behind it is worse than a missing one**, the sentence
     * `relation-affordances.test.ts` opens with, so they came back out.
     *
     * What was missing was never this line. It was that all four functions on
     * the route read module state meaning the stage. `RelationSurface` is that
     * repair; this line is what it makes safe.
     */
    const spec = withRelationMutations(generated);
    const ir = compileSpec(spec, { viewport: state.viewport, itemCount: 1 });
    el.hidden = false;
    $('paneTitle').textContent = titleOf(row as Record<string, unknown>);
    // The pane's own lens id, spec, grants and epoch. Registered in the same
    // `specs` map as the main lens: the key is the spec's content hash, so two
    // lenses collide only when they are the same layout, which is exactly when
    // sharing an entry is correct.
    const asideLens = candidateId(spec);
    specs.set(asideLens, spec);
    const handle = handleOf(row)!;
    const asideReadonly = paneRead.derived.get(id);
    const asideGrants = mintGrants(ir, asideLens, {
      knownObjects: new Set([handle]),
      ...(asideReadonly?.size
        ? { readonlyRoles: new Map([[handle, asideReadonly]]) } : {}),
    });
    /**
     * The manifest the pane's WRITES go through, recorded beside the render.
     *
     * @Codex, P1: *"the pane's `createEditBridge` still supplies
     * `manifest: () => pool.manifest` … editing a shared role such as `title`
     * therefore reports success while writing the Tasks title key onto the
     * note … note-only roles such as `body` instead refuse."*
     *
     * A data-integrity bug, and mine: I moved the READ onto the resolved home
     * and left the WRITE on the open pool, so the field a person edits and the
     * slot the store changes could belong to different documents. Reproduced —
     * the probe value appeared and then the note reopened unchanged.
     *
     * The bridge is built once at boot and reads this at call time, which is
     * the same rule `EditBridgeDeps.manifest` already states in its own
     * comment: *"read at call time, never captured."* It was being read at call
     * time off the wrong variable.
     */
    asideManifest = home.manifest;
    // The rest of what `paneSurface` reads. Recorded together, on every paint,
    // because a surface half-updated describes two different renders — and the
    // half that lags is the half nobody notices until a write lands in the
    // wrong document. `asideOwner` is `owner`, NOT `home`: an object no pool
    // owns gets no pool's target domain, the same split #120 established for
    // its fields and labels.
    asideIR = ir;
    asideLensId = asideLens;
    asideOwner = owner;
    asideRow = row;
    asideEpoch += 1;
    stage.innerHTML = renderHTML({
      // `home`, not `pool`. The same role carries a different label in every
      // pool that declares it — `relation.peer` is "Filmmaker" on a film,
      // "Links to" on a note and "Background" on a task — so drawing a note
      // with the open pool's labels called its own relation Background.
      // Measured: the stage said "Links to" and the pane said "Background" for
      // the same object.
      ir, objects: [row], scheme: state.scheme, labels: labelsFor(home),
      ...(MAY_WRITE ? { edit: { epoch: asideEpoch, grants: asideGrants } } : {}),
      choices: await choicesFor(home, shape),
      tones: tonesFor(home),
    });
    // The other half of the anchor. A single-object lens has no element that
    // carries the object, so the renderer emits the node index and the HOST
    // stamps object and epoch on the root — the main lens has always done this
    // and the pane silently did not, which is why its editable elements had a
    // `data-morph-n` and no anchor to resolve it against. Found by driving it.
    completeSingleAnchor(ir, stage, row, asideEpoch, [row]);
    // After the DOM exists, like the main lens — `bind` is also what registers
    // this lens's grants with the store, so an intent whose grant was never
    // minted fails a lookup rather than a comparison.
    if (MAY_WRITE) {
      asideHost?.bind({
        ir, lens: asideLens, epoch: asideEpoch, grants: asideGrants, objects: [row],
      });
    }
  } catch {
    // A refused pane must not take the page down with it. The main lens is the
    // product; this is a second reading of something beside it.
    el.hidden = true;
  }
}

/** Put an object in the side pane. */
function openAside(id: ObjectId): void {
  if (id === aside) return;
  aside = id;
  say('opened to the side — a second lens, generated the same way as the first');
  schedule();
}

function closeAside(): void {
  if (!aside) return;
  aside = undefined;
  schedule();
}


/** The role a rendered field is bound to, read back off the class the renderer
 *  emits. Roles contain dots, so this parses rather than using a selector. */
/**
 * A field that holds exactly ONE reference IS that reference — a click
 * anywhere in it follows, not only a click that lands on the label.
 *
 * @Codex, P2 on `87ad4e4`: Fibery's card foot is drawn as a full-width row
 * with a chevron at its end, which says "this opens something". The chevron
 * is a pseudo-element on the field, so a click on it — or on the gap it sits
 * across — resolves to the FIELD, which was neither `.m-slot-open` nor
 * `.m-ref-hit`, fell through the activation gate, and selected the card. The
 * part of the band that most looks like a destination was the only part that
 * was not one.
 *
 * The rule is general rather than a fibery special case, and true for the
 * same reason everywhere: a field carrying one reference and nothing else has
 * no other meaning a click could take. Where it carries several the ambiguity
 * is real, and the label still decides.
 *
 * It lives HERE, as one function, because the gate that decides whether to
 * ask and the resolver that answers are two places — and two copies of one
 * rule is how a chip comes to mean one thing to a mouse and another to a
 * keyboard, which this file has already paid for once.
 */
export function soleReferenceField(el: Element | null): Element | null {
  const field = el?.closest('.m-field') ?? null;
  return field && field.querySelectorAll('.m-ref-hit').length === 1 ? field : null;
}

/**
 * The role a drawn occurrence stands for, however the renderer marked it.
 *
 * TWO MARKERS, ONE READER — task `0225`. A FIELD wears `m-role-<role>`, which
 * also carries the generic role styling. A MEDIA node and a table CELL wear
 * `data-role`, because that styling would turn a `<video>` or a cell into a
 * heading — this is `render-html.ts`'s own rule, stated there three times.
 *
 * So Hide was offered for every text field and for no attachment: the rail
 * walks for `m-role-` and no media branch emitted one. Reading both HERE is
 * what makes that one change rather than several — this function is the single
 * reader, and the two call sites that select occurrences widen their query to
 * match it. A third marker would be added here and nowhere else.
 */
function roleOfField(field: Element | null): Role | undefined {
  const known = (name: string | undefined): Role | undefined =>
    name !== undefined && (ROLES as readonly string[]).includes(name) ? name as Role : undefined;
  for (const c of field?.classList ?? []) {
    if (c.startsWith('m-role-')) return known(c.slice('m-role-'.length));
  }
  return known(field?.getAttribute('data-role') ?? undefined);
}

/** Every drawn occurrence of a role, by either marker — the query that pairs
 *  with `roleOfField`. A selector list rather than a walk of everything,
 *  because the stage holds thousands of nodes on a board. */
const ROLE_OCCURRENCE = '[class*="m-role-"], [data-role]';

/** Hide and Promote are local presentation gestures until repeated ledger
 * evidence learns their role-presence preference. */
function mountManualHideControls(): void {
  if (!MAY_WRITE) return;
  const fields = [...$('stage').querySelectorAll(ROLE_OCCURRENCE)]
    .filter((field) => roleOfField(field));
  const byRole = new Map<Role, HTMLElement[]>();
  for (const field of fields) {
    const role = roleOfField(field)!;
    const occurrences = byRole.get(role) ?? [];
    occurrences.push(field as HTMLElement);
    byRole.set(role, occurrences);
  }

  /**
   * Hide, Promote, and width are ROLE gestures, not object gestures.
   *
   * @Codex's answer to `0120`, delivered inline on #184 and committed from the
   * paste. One control group per role, in a rail above the lens: *"putting a
   * copy beside every rendered occurrence made twelve identical cards differ
   * only because each carried the same controls, and consumed enough width to
   * wrap their contents onto an extra row."*
   *
   * That is the whole defect `0120` chased for a day under two other names.
   * `querySelectorAll('[class*="m-role-"]')` over a collection put up to
   * thirty-six controls on a twelve-card board; measured, `e2e/card-action.mjs`
   * read 14/18 and `e2e/plot.mjs` 47/49, both on `main`, and withholding this
   * function alone returned 18/18 and 49/49.
   *
   * The rail is also the honest model rather than only the cheap fix. Twelve
   * `Hide Status` buttons are not twelve gestures: `manualTaste.hide` takes a
   * role, and every one of them did the same thing to the same role.
   *
   * BESIDE the field is what this replaces, and its reason still holds for
   * anything that ever goes back there: a control appended INTO a field joins
   * its `textContent` — `.m-role-title` read `"Plein SoleilHide"`, which broke
   * `e2e/the-form.mjs` and put the word "Hide" inside the accessible name of
   * every value on screen.
   */
  let controls = document.querySelector<HTMLElement>('[data-manual-role-controls]');
  if (!controls) {
    controls = document.createElement('div');
    controls.dataset.manualRoleControls = '';
    controls.setAttribute('role', 'toolbar');
    controls.setAttribute('aria-label', 'Presentation gestures');
    controls.style.cssText =
      'display:flex;flex-wrap:wrap;align-items:center;gap:.45rem;margin:.55rem 0 0';
    // OFF THE READING PATH, and still one gesture away.
    //
    // Joaquín, with a screenshot of this page: "morph's main page should be
    // far better than all of the other combined". The rail was the largest
    // offence on it — four sliders and four bare `Hide` links ABOVE the
    // content, and worse on a single object, where the rail enumerates
    // every drawn role and grows to twenty controls.
    //
    // The rail MODEL is right and stays whole: one control group per role,
    // which is @Codex's own answer to task 0120, where a copy beside every
    // field put thirty-six controls on twelve cards and cost `card-action`
    // 14/18 and `plot` 47/49. What was wrong is where it sat. So it keeps
    // every knob, in one place, behind a disclosure a person opens — not
    // hidden, which would REMOVE a capability rather than tidy a surface,
    // and 0174's own bar forbids that.
    //
    // A `details` rather than a pane because it is the smallest move that
    // takes the instrumentation out of the reading path without inventing
    // furniture. The better long-term home is a sidebar pane beside the
    // outline — the `sidebar` region purpose and the pane-tab strip
    // Obsidian already draws — and that belongs with `0174`'s arrangement,
    // not smuggled in here.
    const drawer = document.createElement('details');
    drawer.dataset.manualRoleDrawer = '';
    drawer.style.cssText = 'margin:0 0 .9rem';
    const label = document.createElement('summary');
    label.textContent = 'Presentation';
    label.style.cssText =
      'cursor:pointer;font-size:12px;color:var(--text-muted);'
      + 'letter-spacing:.04em;text-transform:uppercase;user-select:none';
    drawer.append(label, controls);
    $('stage').before(drawer);
  }
  controls.replaceChildren();

  for (const [role, occurrences] of byRole) {
    const group = document.createElement('span');
    group.style.cssText = 'display:inline-flex;align-items:center;gap:.25rem';
    const label = document.createElement('span');
    label.className = 'quiet';
    label.textContent = labelsFor(pool)[role] ?? labelForRole(role);
    group.append(label);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-quiet manual-hide';
    button.textContent = 'Hide';
    button.setAttribute('aria-label', `Hide ${labelsFor(pool)[role] ?? labelForRole(role)}`);
    button.addEventListener('click', () => {
      manualTaste.hide(role);
      /* BOTH MARKERS HERE TOO — task `0225`. The discovery query above and
         this one are one pair: widening only the first offers Hide for a
         player and then fails to grey it out, which is a control that appears
         to work and does not. */
      for (const current of $('stage').querySelectorAll(ROLE_OCCURRENCE)) {
        if (roleOfField(current) === role) (current as HTMLElement).hidden = true;
      }
      const learned = manualTaste.taste();
      if (learned.suppressedRoles?.includes(role)) {
        // One role's presence, applied whole — see `applyPresence`. A winner
        // that flips must not leave the losing array stale, and a preference
        // accepted from a SUGGESTION must not be dropped on the way.
        state.taste = applyPresence(state.taste, role, learned);
        if (!state.learned.includes(`hiding ${role}`)) state.learned.push(`hiding ${role}`);
        void run();
      }
      renderTaste();
    });
    group.append(button);

    /**
     * Only where a promotion can actually change the compiled spec. `rankRoles`
     * ranks media and title/subtitle above the promoted band, so offering the
     * gesture there would persist a belief, report it, and change nothing —
     * @Codex's P2 on that delivery.
     *
     * And which spec is *the* compiled spec depends on whether they have kept
     * one. @Codex, P2 on `1a2b3b1`: *"a kept non-baseline layout can make
     * promotion effective even when this shape-only guard returns false …
     * `applyNodeLocal()` would move that field forward, but this line omits
     * the Promote button entirely."*
     *
     * Right, and it is the sibling-promotion change from the same push
     * arriving at the gate that decides whether anybody can reach it. While an
     * accepted spec exists every suggestion is a revision of it, so the
     * question is what the revision does to THEIR arrangement rather than what
     * a baseline nobody is looking at would rank.
     *
     * `promotionCanCompile` stays as a precondition rather than being replaced
     * wholesale. `applyNodeLocal` ranks any prominent role, so the accepted
     * test alone would start offering Promote on `title` and on media — a
     * wider change than this finding asks for, made silently on the way past.
     */
    const acceptedNow = state.lens.accepted?.spec;
    const promotable = acceptedNow
      ? promotionCanCompile(role) && promotionChangesAccepted(acceptedNow, role)
      : lastShape ? promotionChangesRanking(lastShape, role) : promotionCanCompile(role);
    if (promotable) {
      const promote = document.createElement('button');
      promote.type = 'button';
      promote.className = 'btn btn-quiet manual-promote';
      promote.textContent = 'Promote';
      promote.setAttribute('aria-label', `Promote ${labelsFor(pool)[role] ?? labelForRole(role)}`);
      promote.addEventListener('click', () => {
        manualTaste.promote(role);
        const learned = manualTaste.taste();
        if (learned.prominentRoles?.includes(role)) {
          // One role's presence, applied whole — see `applyPresence`.
        state.taste = applyPresence(state.taste, role, learned);
        if (!state.learned.includes(`promoting ${role}`)) state.learned.push(`promoting ${role}`);
          void run();
        } else {
          /**
           * The evidence threshold is the design (repeated gestures teach;
           * one does not), but a press that changes nothing and SAYS nothing
           * reads as a dead button — blind run 12 pressed four Promotes,
           * got zero reaction each time, and reported the affordance dead.
           * Say the honest state, in the narrator's own counted style.
           */
          const nudges = manualTaste.ledger.filter((ev) => !ev.retractedAt
            && ev.observations.some((o) => o.knob === 'role.presence'
              && o.index === role && o.value === 'prominent')).length;
          say(`noted — ${nudges} nudge${nudges === 1 ? '' : 's'} toward making `
            + `${labelsFor(pool)[role] ?? labelForRole(role)} prominent; `
            + 'repeated nudges teach the layout');
        }
        renderTaste();
      });
      group.append(promote);
    }

    /**
     * The slider is VIEW STATE, and decision 0036 is why.
     *
     * It was a taste gesture for about an hour. `role.extent` was registered
     * with a closed domain, type-checked, and compiled to nothing: `extent`
     * lives on `docked`/`floating`/`anchored` placements, the baseline emits no
     * placement, so every generated node is inline and there is no field for a
     * role-indexed extent to reach. @Codex withdrew the knob rather than bend
     * the schema to it.
     *
     * What survives is the honest half, and it is what decision 0014 asked for
     * from the start: the width is private, per-device, restored on open, and
     * owes nothing to an evidence threshold.
     */
    const extents = ['hug', 'compact', 'roomy'] as const;
    const widths = { hug: '12ch', compact: '22ch', roomy: '36ch' } as const;
    const chosen = fieldWidths[role];
    if (chosen) for (const field of occurrences) field.style.inlineSize = widths[chosen];
    const resize = document.createElement('input');
    resize.type = 'range';
    resize.className = 'manual-resize';
    resize.min = '0';
    resize.max = '2';
    resize.step = '1';
    resize.value = String(Math.max(0, extents.indexOf(chosen ?? 'compact')));
    resize.setAttribute('aria-label', `Resize ${labelsFor(pool)[role] ?? labelForRole(role)}`);
    // The bucket, not the slider's index. A screen reader hearing "1" learns
    // nothing about a control whose whole vocabulary is hug/compact/roomy.
    const announce = () => resize.setAttribute('aria-valuetext',
      extents[Number(resize.value)] ?? 'compact');
    announce();
    const applyWidth = () => {
      const extent = extents[Number(resize.value)] ?? 'compact';
      // EVERY occurrence, because one slider now stands for the role. A width
      // applied to the first card and not the other eleven is a private view
      // state that disagrees with itself.
      for (const field of occurrences) field.style.inlineSize = widths[extent];
      announce();
      return extent;
    };
    resize.addEventListener('input', applyWidth);
    resize.addEventListener('change', () => {
      fieldWidths = writeFieldWidth(localStorage, role, applyWidth());
    });
    group.append(resize);
    controls.append(group);
  }
}

/** What `subject` points at along `via`, or nothing — with the refusal said
 *  once, here, so both callers below report a dangling reference the same way. */
/**
 * WHICH OBJECT one drawn occurrence names.
 *
 * `resolveReference` answers *"what does this role point at"*, which is one
 * object — fine for a field chip, wrong for a Connections row that may hold
 * three. The Open control carries the occurrence's own opaque token and this
 * asks the store to resolve it.
 *
 * IT USED TO COUNT. The control carried `data-morph-at`, this traversed the
 * relation and took that position, and a comment here said the order was safe
 * because *"the projection that drew the row walked the same relation from the
 * same subject"* — then added, in its own words, *"that is an assumption about
 * two store reads agreeing."* @Codex showed two commits later that it does not:
 * `traverse` deduplicates edges by `from>to`, so a slot-backed reference and an
 * edge to the SAME object are two rendered occurrences and one traversal edge,
 * and the second chip opened the following target. The demo's own "one of each"
 * note is that shape.
 *
 * Writing an assumption down is not the same as checking it.
 *
 * The interim repair compared the two counts and refused when they disagreed —
 * never wrong, occasionally unavailable. That was a guard against a mistake the
 * design should not permit, and task 0078 removed the mistake: the projection
 * already minted an opaque identity per occurrence for detach, and
 * `occurrenceTarget` is the reading mirror of `relationTargetFor`. No counting
 * anywhere, so nothing left to compare.
 *
 * The token says WHICH occurrence and never that it may be read: the scope is
 * still the authority, and one no reachable manifest addresses resolves to
 * nothing exactly as it would for a write.
 */
async function resolveOccurrence(
  subject: ObjectId, token: string, scope: Scope,
): Promise<ObjectId | undefined> {
  const target = await db.store.occurrenceTarget(scope, token);
  if (target === undefined || target === subject) {
    say('that reference points at nothing the store can reach', 'bad');
    return undefined;
  }
  return target;
}

async function resolveReference(subject: ObjectId, via: Role): Promise<ObjectId | undefined> {
  const r = await db.store.traverse({
    manifest: pool.manifest, from: [subject], via, direction: 'out', depth: 1, maxNodes: 8,
  });
  const target = r.nodes.find((n) => n.id !== subject);
  if (!target) { say('that reference points at nothing the store can reach', 'bad'); return undefined; }
  return target.id;
}

/**
 * Controls that are NOT the card they sit inside.
 *
 * The transport, the fold and the relation controls each arrived at this rule
 * separately and each was a bug first: pressing play resolved the enclosing
 * card and repainted the player away; folding a section dropped focus to
 * `<body>`; pressing Add inserted a picker and then threw it away in the same
 * tick. Where a card affordance and a control overlap, THE CARD LOSES.
 *
 * One list, read by both gesture routes, because a list the mouse honours and
 * the keyboard does not is how the two stop agreeing about what is under them.
 */
const NOT_THE_CARD =
  'video, audio, summary.m-fold-summary, .m-attach, .m-detach, .m-attach-picker, '
  + 'button.m-invoke, select.m-choose, button.m-cal-nav, '
  // The SEVENTH: a look's chrome bars and every INERT item (0056). Under
  // Word the root wears the document's subject anchor, so a click on the
  // ribbon's Dictate — imagery, by decision — resolved the subject and
  // took the window to one note. Chrome is never the card, and an inert
  // item dispatches nothing: the third proof 0056 asks for, seen red here.
  + '.m-inert, .m-wd-ribbon, .m-topbar, .m-ws-status, '
  // The NINTH: a board column's fold toggle. It is a button of its own and
  // its click is the fold's; under a look whose root wears an anchor it
  // must never resolve to the card. Found by @Codex on #223.
  + '.m-group-toggle, '
  // The EIGHTH: a text field. A click into Notion's view-row search box
  // resolved the page's anchor and opened a task in place of the board —
  // the caret's own argument, which contenteditable won below, applied to
  // the input the search module draws. A caret target is never the card.
  + 'input, textarea';

/*
 * `button.m-cal-nav` is the SIXTH, found the day the calendar gained a Today
 * under Notion's look: the page region wears the subject's anchor, so a
 * month step inside it resolved the subject, took the page to `single`, and
 * the calendar came back as a grid of one object with the month it had been
 * asked for. A month step is a control on the calendar, never the card.
 */

/*
 * `select.m-choose` is the FIFTH, and it is the one the rule's own wording
 * predicted. Task 0102: clicking a Status chooser on a row took Tasks from
 * twelve rows to none and `large-set` to `single` — the collection vanished
 * mid-gesture and the next row the person meant to edit was gone. The value
 * they chose still landed, because the chooser is drawn in the single lens too,
 * so nothing refused and nothing looked broken.
 *
 * The sentence below about a select is arguing that a select is a LESS severe
 * conflict than a transport, not that a select should lose. Under
 * *"where a card affordance and a control overlap, THE CARD LOSES"* a chooser
 * is exactly the overlap being described.
 *
 * No harness could have caught it: every one reaches a chooser through
 * `page.selectOption()`, which sets the value without dispatching the click a
 * person makes. So the whole enum-editing path was exercised by a gesture
 * nobody performs — @Codex's observation about task 0101's guard, one control
 * over, and found only because that guard was being corrected.
 *
 * `button.m-invoke` is the FOURTH arrival at that rule, and it was measured
 * rather than reasoned: with a row button on every task card, pressing one
 * announced *"that button names no action"* and opened the object instead.
 *
 * The click resolved the enclosing card, switched to a single-object lens and
 * repainted — so by the time `invokeFromClick` read `data-morph-n` off the
 * button, `lastIR` was a different IR and that index was a different node. The
 * action never ran, and what the page showed was a button that navigates.
 *
 * It could not surface before this commit: the only invocations in the product
 * were the panel's three, and selecting the object a single-object lens is
 * already showing is a no-op repaint that the invoke survives. The moment a
 * button sat on a row among others, selecting meant changing the lens.
 *
 * `NOT_TO_THE_SIDE` already carried this selector for the modifier gesture, on
 * the same reasoning — *"a modifier on an invoke button ran the action AND
 * opened a pane"*. That it was not also in this list is the bug: the aside
 * route was taught the rule and the ordinary click route was not.
 */

/** And these are not the card only for the SIDE gesture: a caret keeps its
 *  keystroke, and a modifier on an invoke button ran the action AND opened a
 *  pane, because the button sits inside a card and the anchor lookup finds it. */
const NOT_TO_THE_SIDE = '[contenteditable="true"], button.m-invoke';

/**
 * WHICH OBJECT a gesture on `el` is about.
 *
 * A relation Open control means its own target; a reference chip means what the
 * reference points at; anything else means the card's own object. ONE answer,
 * used by the mouse's modifier-click and by the keyboard's Ctrl/Cmd+Enter.
 *
 * @Codex: *"a modifier-click follows the chip target while the documented
 * keyboard equivalent opens the wrong object."* A first repair let the chip
 * through the keyboard route by returning early from it — which left the
 * gesture depending on whether the browser copies modifier state onto a
 * synthesized click, and it is a no-op where it does not. The two routes ask
 * this function now, so a keyboard equivalent that is not equivalent has
 * nowhere left to come from.
 */
async function referentOf(
  el: HTMLElement | null, subject: ObjectId,
): Promise<ObjectId | undefined> {
  // An Open control inside a relation row. Its role comes from the IR, never
  // from the element — the same rule the node index and the spec id follow —
  // and its index says WHICH occurrence, because a row may hold several.
  const opener = el?.closest('.m-slot-open');
  if (opener) {
    // The occurrence's OWN token, the one thing read from the markup — and it
    // is not an address: opaque, minted by the projection this page is showing,
    // and resolved only through a scope that addresses its target. The node
    // index and the role are gone from this path because nothing here needs to
    // know which relation it is; the token already names one occurrence of one.
    const token = opener.getAttribute('data-morph-t');
    if (token) return resolveOccurrence(subject, token, surfaceFor(el).scope());
  }
  // A reference opens the thing it REFERS to, not the thing it is written on.
  // Hover previews it, click goes there — the pair Notion and Obsidian both
  // ship, and without the second half a referenced object has no way to be
  // opened at all: a person is `typeHint: 'person'`, so it never appears in a
  // films scope and nothing else can reach it.
  const chip = el?.closest('.m-ref-hit');
  if (chip) {
    const role = roleOfField(chip.closest('.m-field'));
    if (role) return resolveReference(subject, role);
  }
  // A field holding ONE reference is that reference — see `soleReferenceField`.
  const sole = soleReferenceField(el);
  if (sole) {
    const role = roleOfField(sole);
    if (role) return resolveReference(subject, role);
  }
  return subject;
}

/**
 * Land on `target` ON THE MAIN STAGE, as its own object.
 *
 * The other destination. `openAside` is this one's twin and they are the only
 * two — which object to land on is `referentOf`'s question, and keeping the two
 * apart is what stopped a chip meaning one thing to a mouse and another to a
 * keyboard.
 *
 * AND IN ITS OWN DOCUMENT. @Codex, P1: *"resolve the target's home pool before
 * assigning/scheduling the main-stage lens."* The side pane learned this two
 * commits ago and the main stage did not, so the modifier gesture opened a note
 * correctly while the plain one — the ordinary click, the common case — opened
 * the same note through the tasks manifest, tasks declarations and tasks
 * labels. The fix that reached one level too narrow, which is the other half of
 * this branch's recurring mistake.
 *
 * Following a reference across documents IS a document switch, and the pool
 * switcher was the only thing that said so. Both go through `enterPool` now.
 */
async function openHere(target: ObjectId): Promise<void> {
  const owner = await poolOf(target);
  const moved = owner !== undefined && owner !== pool;
  if (moved) enterPool(owner, target);
  else select(target);
  if (state.cardinality !== 'single') {
    state.cardinality = 'single';
    ($('cardinality') as HTMLSelectElement).value = 'single';
  }
  say(moved
    ? `followed a reference into ${pool.label} — this is that document's own lens`
    : 'followed a reference — this is the referenced object\'s own lens');
  schedule();
}

/** Open whatever `subject` points at along `via`. */
async function openReferenced(subject: ObjectId, via: Role): Promise<void> {
  const target = await resolveReference(subject, via);
  if (target) await openHere(target);
}

/** The object a single-object lens falls back to when nothing was clicked. */
async function firstInPool(): Promise<ObjectId | undefined> {
  const page = await db.store.query(pool.scope, { limit: 1 });
  return page.items[0]?.id;
}

/**
 * How many things on screen a person can actually edit.
 *
 * Counted from the DOM rather than from the grant set on purpose. A grant is
 * permission to write; an editable element is the offer of it, and the two came
 * apart once already — every binding said `read`, so `mintGrants` returned
 * nothing and the deployed lens was a picture of a database while the write
 * path underneath it was fully wired and passing its tests.
 */
/**
 * Compute every fold the compiled lens asked for, over the whole scope.
 *
 * Keyed by SOURCE, mirroring `sources`: the numbers describe a set, not a
 * position in the tree. Only the subject is folded today — the panel lists
 * titles and asks for nothing — and a source nobody asked about is simply
 * absent, which the renderer draws as nothing rather than as zero.
 *
 * Read off the IR rather than the spec, because the IR is what the renderer
 * will actually draw: a fold the compiler dropped is a fold nothing can show.
 */
async function rollupsFor(
  ir: IR, scope: Scope,
): Promise<Partial<Record<'subject' | 'siblings', Rollups>> | undefined> {
  const wanted = new Map<string, { role: Role; fn: Aggregation }>();
  const groupings = new Set<Role>();
  for (const node of Object.values(ir.nodes)) {
    if (node.kind !== 'collection' || (node.source ?? 'subject') !== 'subject') continue;
    for (const a of node.query?.aggregate ?? []) wanted.set(`${a.role}:${a.fn}`, a);
    if (node.query?.groupBy) groupings.add(node.query.groupBy);
  }
  if (wanted.size === 0 && groupings.size === 0) return undefined;

  // One Rollups value can describe one grouping. Name the first so a matching
  // board can use exact whole-scope counts and every other board can refuse to
  // mistake those counts for its own.
  const groupBy = [...groupings][0];
  const out = await db.store.aggregate(scope, [...wanted.values()],
    groupBy ? { groupBy } : {});
  // `grouped` carries the role WITH the map, so the store's own result can no
  // longer be handed through as a `Rollups` that silently loses its groups.
  const { groups, ...scopeFold } = out;
  return {
    subject: {
      ...scopeFold,
      ...(groupBy && groups ? { grouped: { by: groupBy, groups } } : {}),
    },
  };
}

function editableCount(): number {
  return document.querySelectorAll('[contenteditable="true"], .m-editable').length;
}

const graphViewState = createGraphViewState();

/** Turn an ordinary generated spec into a capability request. Role selection is
 * host-side and explicit; the module receives bindings and never guesses that
 * "title" or "relation.peer" must mean the same thing in every pool. */
function graphSpec(spec: TLayoutSpec, shape: ObjectShape): TLayoutSpec {
  const label = shape.roles.find((r) => r.role === 'title')
    ?? shape.roles.find((r) => ROLE_KIND[r.role] === 'text');
  // The demo's acceptance graph is film → director: a cast relation can fan
  // beyond the measured ceiling before the same data ever reaches Wong Kar-wai.
  // Role choice belongs to the host; the capability still sees only the closed
  // edge channel and has no knowledge of what "director" means.
  const edge = shape.roles.find((r) => r.role === 'relation.peer')
    ?? shape.roles.find((r) => ROLE_KIND[r.role] === 'ref');
  return {
    // Controls dropped with the root they drove — see `withoutViewState`.
    ...withoutViewState(spec),
    target: { ...spec.target, cardinality: 'large-set' },
    root: {
      kind: 'capability',
      id: 'pool-graph',
      capability: 'force-graph',
      version: CAPABILITIES['force-graph'].version,
      config: { depth: 1, showLabels: true },
      channels: {
        ...(label ? { label: { slot: 'graph-label', role: label.role, direction: 'read' } } : {}),
        ...(edge ? { edge: { slot: 'graph-edge', role: edge.role, direction: 'read' } } : {}),
      },
    },
  };
}

/**
 * A document surface over the same objects — the Word row of `ACCEPTANCE.md`.
 *
 * Every part of it is a SLOT holding an ordinary core primitive: the prose is a
 * field bound to a prose role, the running head is a field bound to the title,
 * the foot to the author. `document-flow` places them and draws nothing itself.
 * That is the division the schema names when it says modules contain core
 * primitives in turn, and it is only expressible since the IR started carrying
 * which slot a child came from.
 *
 * The body role is CHOSEN from the shape rather than assumed: a pool of notes
 * has `body`, a pool of films has none and its longest prose is a transcript.
 * Picking `body` unconditionally would render an empty document over half the
 * pools in the demo.
 */
/**
 * Fill every `node-slot` with a REAL generated lens for what it points at.
 *
 * `canon-notion.md` names this as one of two things decision 0003 never
 * mentions: *"the core must be recursive — a region must be able to host a
 * complete spec."* SPEC 0.9.0 lets a node-slot carry `children`; this is the
 * host filling them, and what it puts there is not a hand-written card but the
 * output of the same `compile` + `baselineGenerator` the main lens comes from.
 *
 * **The shape comes from the targets themselves.** A projection expands a ref
 * one level, so the referenced objects arrive as `RoleValues` — objects whose
 * KEYS ARE ROLES. That is already what `deriveShape` wants, with an identity
 * manifest, and it needs no second store query: the demo is describing what it
 * has rather than asking what exists.
 *
 * **`cardinality: 'single'`, deliberately.** The grammar admits everything but
 * a collection inside a node-slot, and the reason is measured: a collection
 * here renders the whole object set once per REFERENCED object, and `maxDepth`
 * lets that recur. Asking for a single-object lens is how the generator is told
 * what it is drawing rather than how it is stopped from drawing the wrong
 * thing.
 *
 * Behind `?inline=1` rather than on by default, and that is not timidity: the
 * built-in preview IS the Obsidian row's hover preview, which several harnesses
 * assert. Two ways to draw a relation is the product; replacing one with the
 * other would trade a green row for a new one.
 */
const INLINE = (() => {
  try { return new URLSearchParams(location.search).get('inline') === '1'; }
  catch { return false; }
})();

/**
 * Give a hosted spec its own id space.
 *
 * Two complete specs in one tree share a generator, so they share node names —
 * `sheet`, `body`, `hero`. The validator caught it on the first run with
 * `duplicate-id` and refused the whole page, which is the right answer and the
 * reason this exists: ids are unique per SPEC, and hosting one inside another
 * makes that two specs' worth of ids in one document.
 *
 * An anchored placement names another node BY ID, so a rename that missed those
 * would turn `duplicate-id` into `dangling-anchor` — trading a loud failure for
 * a quieter one. They are rewritten with the same function that renames the
 * node.
 *
 * Ids are capped at 64 characters, so the prefix is short and the original is
 * kept whole: a truncated id could collide with another truncated id, which is
 * the bug this is preventing.
 */
function namespaced(root: TNode, role: string): TNode {
  const tag = `x${role.replace(/[^a-z0-9]/gi, '').slice(0, 6)}`;
  const rename = (id: string) => `${tag}-${id}`.slice(0, 64);
  const walk = (n: TNode): TNode => {
    const node = { ...(n as unknown as Record<string, unknown>) };
    if (typeof node['id'] === 'string') node['id'] = rename(node['id']);
    const placement = node['placement'] as Record<string, unknown> | undefined;
    if (placement && typeof placement['anchor'] === 'string') {
      node['placement'] = { ...placement, anchor: rename(placement['anchor'] as string) };
    }
    if (node['children']) node['children'] = (node['children'] as TNode[]).map(walk);
    if (node['items']) {
      node['items'] = (node['items'] as { spec: TNode }[]).map((it) => ({ ...it, spec: walk(it.spec) }));
    }
    if (node['slots']) {
      node['slots'] = Object.fromEntries(Object.entries(node['slots'] as Record<string, TNode[]>)
        .map(([k, arr]) => [k, arr.map(walk)]));
    }
    return node as unknown as TNode;
  };
  return walk(root);
}

/**
 * Declare the two relation gestures on every slot that can honestly offer them.
 *
 * The generator emits a bare `node-slot` — task 0065's guard says so and still
 * passes — so this is the HOST deciding that a relation in this demo is
 * authorable. Decision 0016 puts the decision here on purpose: *"In this
 * version, relation mutations are executable only through a node slot's
 * `mutations` declaration."*
 *
 * Only for a relation role. `source` is a `ref` and is provenance, so a slot
 * over it is drawn and never attached to — and the validator refuses the
 * declaration anyway, which would take the whole page down rather than the one
 * slot. Checked here so a pool that surfaces `source` keeps rendering.
 *
 * Not gated on `MAY_WRITE`: a reader gets no `edit` boundary, so the renderer
 * mints no grants and draws no controls whatever the spec says. Gating twice
 * would make the spec differ between viewers, which is the thing the
 * permissions row exists to avoid — *"permissions that quietly hide content are
 * a different feature and a worse one"*, and a spec is content.
 */
function withRelationMutations(spec: TLayoutSpec): TLayoutSpec {
  let touched = false;
  const walk = (n: TNode): TNode => {
    const node = n as unknown as Record<string, unknown>;
    if (node['kind'] === 'node-slot') {
      if (!isRelationRole(node['via'] as Role)) return n;
      touched = true;
      return { ...node, mutations: ['link', 'unlink'] } as unknown as TNode;
    }
    const next: Record<string, unknown> = { ...node };
    if (node['children']) next['children'] = (node['children'] as TNode[]).map(walk);
    if (node['items']) {
      next['items'] = (node['items'] as { spec: TNode }[]).map((it) => ({ ...it, spec: walk(it.spec) }));
    }
    if (node['slots']) {
      next['slots'] = Object.fromEntries(Object.entries(node['slots'] as Record<string, TNode[]>)
        .map(([k, arr]) => [k, arr.map(walk)]));
    }
    return next as unknown as TNode;
  };
  const root = walk(spec.root as unknown as TNode);
  return touched ? ({ ...spec, root } as unknown as TLayoutSpec) : spec;
}

/**
 * CODA'S ROW BUTTON, on the rows of this demo's own lenses.
 *
 * The generator emits no `invocation`, and it should not: an invocation NAMES
 * an action from the registry, and the registry is the host's. The generator
 * has no way to know `demo.mark-seen` exists. So this is the host deciding
 * that a row in this demo is actionable — the same division `withRelationMutations`
 * makes above it and for the same reason decision 0016 gives.
 *
 * Until now the only invocations in the whole product were the three panel
 * buttons in `shell.ts`. `ACCEPTANCE.md`'s Coda row asks for *"buttons that run
 * actions"*, and a button in a panel is not what that names — Coda's live in
 * rows, beside the thing they act on. Task 0086.
 *
 * **Only where the write can actually land.** `demo.mark-seen` sets `status`,
 * so a pool whose shape does not declare that role would get a button whose
 * every press is refused — which is the thing this repo keeps writing down: an
 * affordance with nothing behind it is worse than a missing one. The check is
 * against the SHAPE the lens was generated from, which is the same authority
 * the generator used to decide what to draw.
 *
 * **Before `withShell`, deliberately.** The shell's outline is a collection
 * over `siblings`, and a button on its rows would be anchored to a sibling
 * while the page is about something else. Running first means this only ever
 * sees the generated lens.
 *
 * Not gated on `MAY_WRITE`. A reader gets no `edit` boundary, so the renderer
 * mints no grants and the button's press resolves to nothing — the spec stays
 * the same for everyone, which is the rule the permissions row states.
 */
function withRowActions(spec: TLayoutSpec, shape: ObjectShape): TLayoutSpec {
  /**
   * THE PARAMETERISED BUTTON, when the lens declares a control it can read.
   *
   * A slot index and an action are chosen together, because the pairing is a
   * TYPE question rather than a preference. `demo.mark-seen` writes `status`,
   * an enum; `pickViewStateSlot` only ever declares `number`, `score` and
   * `date` slots, since @Codex's ruling refuses enum and text. Handing the
   * existing button a slot would write a threshold into an enum property — a
   * store refusal dressed as a feature, and task 0079 records that exact shape
   * making a browser check report a working renderer as broken.
   *
   * So the button reads the slot only when the slot is ABOUT the role the
   * button writes. Anything else keeps the unparameterised action it had.
   */
  //
  // A TABLE, because the pairing is a type question and there is more than one
  // answer. `pickViewStateSlot` declares `number`, `score` and `date` slots, so
  // an action per writable kind is the shape — one entry was the version where
  // the capability existed and never fired, since every shipped pool declares a
  // date and `set-score` is the only thing that could read one.
  const WRITES: Partial<Record<string, string>> = {
    score: ACTIONS.setScore, number: ACTIONS.setScore, date: ACTIONS.setDate,
  };
  const slots = spec.viewState ?? [];
  const slotIndex = slots.findIndex((sl) =>
    WRITES[sl.role] !== undefined && shape.roles.some((r) => r.role === sl.role));
  const slotRole = slotIndex >= 0 ? slots[slotIndex]!.role : undefined;
  const parameterised = slotIndex >= 0;

  /**
   * The same rule the parameterised branch follows: `demo.mark-seen` writes
   * `status`, so a pool without that role would get a button whose every press
   * is refused.
   *
   * This briefly fell through to an object-bound Duplicate instead, under
   * decision 0032 — which was ruled on the false premise that no table in this
   * product carried a row button, and superseded by 0033 once that was
   * measured. Notes derives a table and every row of it carries the
   * parameterised button, which is the thing 0086 wanted and
   * `e2e/table-row-button.mjs` now guards.
   */
  if (!parameterised && !shape.roles.some((r) => r.role === 'status')) return spec;
  /**
   * A FUNCTION, not a value — the third time this feature has needed that
   * sentence, and the first two are already written down beside it.
   *
   * `partitioned` deep-copies its collection because *"two regions that are the
   * same tree must be two trees"*, and the generator's `where` is a function
   * because handing both regions one predicate object *"leaves the same node
   * reachable twice"*. `preScan` reports either as `cyclic — value contains a
   * cycle` and refuses the whole lens.
   *
   * I hoisted this to a const anyway. With one subject collection there is one
   * item template and one injection site, so nothing shared; with the split
   * there are two, and the Tasks pool — the only one whose shape carries
   * `status`, so the only one this injects into — rendered
   * **"The shell produced an invalid spec: cyclic"** and drew nothing at all.
   *
   * Every unit test passed. It took a browser.
   */
  const invocation = () => (parameterised
    ? { action: WRITES[slotRole!]!, effects: ['set-property'],
      args: [{ valueFrom: slotIndex }] }
    : { action: ACTIONS.markSeen, effects: ['set-property'] });
  let touched = false;
  const walk = (n: TNode): TNode => {
    const node = n as unknown as Record<string, unknown>;
    const next: Record<string, unknown> = { ...node };
    if (node['kind'] === 'collection' && Array.isArray(node['items'])) {
      // The SUBJECT only. A collection naming another set is showing objects
      // this page is not about, and a write from its row would land somewhere
      // the person is not looking.
      const subject = node['source'] === undefined || node['source'] === 'subject';
      next['items'] = (node['items'] as { spec: TNode }[]).map((it) => {
        const item = it.spec as unknown as Record<string, unknown>;
        // A card, and one that does not already declare an action. Overwriting
        // an authored invocation would be the host quietly replacing something
        // the spec said.
        if (!subject || item['kind'] !== 'card' || item['invocation'] !== undefined) {
          return { ...it, spec: walk(it.spec) };
        }
        touched = true;
        return { ...it, spec: {
          ...(walk(it.spec) as unknown as Record<string, unknown>),
          invocation: invocation(),
          // BOTH, or neither. The validator refuses an invocation without the
          // affordance and the affordance without an invocation, so a spec
          // carrying one of the two is not a spec anybody could author — and
          // the page would refuse whole rather than lose a button.
          affordances: [
            ...new Set([...(item['affordances'] as string[] ?? []), 'invoke']),
          ],
        } as unknown as TNode };
      });
      return next as unknown as TNode;
    }
    if (node['children']) next['children'] = (node['children'] as TNode[]).map(walk);
    if (node['items']) {
      next['items'] = (node['items'] as { spec: TNode }[]).map((it) => ({ ...it, spec: walk(it.spec) }));
    }
    if (node['slots']) {
      next['slots'] = Object.fromEntries(Object.entries(node['slots'] as Record<string, TNode[]>)
        .map(([k, arr]) => [k, arr.map(walk)]));
    }
    return next as unknown as TNode;
  };
  const root = walk(spec.root as unknown as TNode);
  return touched ? ({ ...spec, root } as unknown as TLayoutSpec) : spec;
}

/**
 * Direct document prose saves as it is written. A generic set-property button
 * beside that surface says the opposite, so document mode removes those
 * invocations after the shell and row transforms have both had their say.
 */
function withoutDocumentUpdateActions(spec: TLayoutSpec): TLayoutSpec {
  const walk = (n: TNode): TNode | undefined => {
    const node = n as unknown as Record<string, unknown>;
    const invocation = node['invocation'] as { effects?: readonly string[] } | undefined;
    const updates = invocation?.effects?.includes('set-property') === true;
    const next: Record<string, unknown> = { ...node };
    if (updates) {
      const bindings = node['bindings'] as readonly unknown[] | undefined;
      const emptyAction = node['kind'] === 'card' && (bindings?.length ?? 0) === 0
        && node['children'] === undefined && node['items'] === undefined && node['slots'] === undefined;
      if (emptyAction) return undefined;
      delete next['invocation'];
      next['affordances'] = ((node['affordances'] as readonly string[] | undefined) ?? [])
        .filter((affordance) => affordance !== 'invoke');
    }
    if (node['children']) next['children'] = (node['children'] as TNode[])
      .map(walk).filter((child): child is TNode => child !== undefined);
    if (node['items']) next['items'] = (node['items'] as { spec: TNode }[])
      .map((item) => ({ ...item, spec: walk(item.spec) ?? item.spec }));
    if (node['slots']) next['slots'] = Object.fromEntries(
      Object.entries(node['slots'] as Record<string, TNode[]>).map(([key, children]) =>
        [key, children.map(walk).filter((child): child is TNode => child !== undefined)]));
    return next as unknown as TNode;
  };
  return { ...spec, root: walk(spec.root as unknown as TNode)! } as unknown as TLayoutSpec;
}

async function inlineRelations(
  spec: TLayoutSpec, objects: readonly BoundValues[],
): Promise<TLayoutSpec> {
  if (!INLINE) return spec;

  /** Every object a node-slot's role points at, across the whole page. */
  const targetsFor = (role: string): Record<string, unknown>[] => {
    const out: Record<string, unknown>[] = [];
    for (const o of objects) {
      const v = (o as Record<string, unknown>)[role];
      for (const t of Array.isArray(v) ? v : [v]) {
        if (t && typeof t === 'object') out.push(t as Record<string, unknown>);
      }
    }
    return out;
  };

  const filled = new Map<string, TNode[]>();
  const collect = (n: TNode): void => {
    const node = n as unknown as Record<string, unknown>;
    if (node['kind'] === 'node-slot') { filled.set(String(node['via']), []); return; }
    for (const c of (node['children'] as TNode[] | undefined) ?? []) collect(c);
    for (const it of (node['items'] as { spec: TNode }[] | undefined) ?? []) collect(it.spec);
    for (const arr of Object.values((node['slots'] as Record<string, TNode[]>) ?? {})) {
      for (const c of arr) collect(c);
    }
  };
  collect(spec.root as unknown as TNode);
  if (filled.size === 0) return spec;

  for (const role of [...filled.keys()]) {
    const targets = targetsFor(role);
    if (!targets.length) continue;
    const roles = [...new Set(targets.flatMap((t) => Object.keys(t)))]
      .filter((r) => (ROLES as readonly string[]).includes(r));
    const shape = deriveShape(
      targets.map((properties) => ({ properties })),
      // Identity: a projected object's keys ARE its roles.
      new Map(roles.map((r) => [r, r as Role])),
    );
    const out = await compile(
      { shape: { ...shape, cardinality: 'single' },
        context: state.context,
        taste: { palette: state.palette, ...state.taste } },
      baselineGenerator);
    // A relation whose lens cannot be generated keeps the preview. Refusing the
    // whole page because one reference is awkward would be the wrong trade.
    if (out.ok) filled.set(role, [namespaced(out.spec.root as unknown as TNode, role)]);
  }

  const rewrite = (n: TNode): TNode => {
    const node = n as unknown as Record<string, unknown>;
    if (node['kind'] === 'node-slot') {
      const kids = filled.get(String(node['via'])) ?? [];
      return (kids.length ? { ...node, children: kids } : node) as unknown as TNode;
    }
    const next: Record<string, unknown> = { ...node };
    if (node['children']) next['children'] = (node['children'] as TNode[]).map(rewrite);
    if (node['items']) {
      next['items'] = (node['items'] as { spec: TNode }[]).map((it) => ({ ...it, spec: rewrite(it.spec) }));
    }
    if (node['slots']) {
      next['slots'] = Object.fromEntries(Object.entries(node['slots'] as Record<string, TNode[]>)
        .map(([k, arr]) => [k, arr.map(rewrite)]));
    }
    return next as unknown as TNode;
  };
  return { ...spec, root: rewrite(spec.root as unknown as TNode) } as unknown as TLayoutSpec;
}

function documentSpec(spec: TLayoutSpec, shape: ObjectShape): TLayoutSpec {
  const prose = shape.roles.find((r) => r.role === 'body')
    ?? shape.roles.find((r) => r.role === 'transcript')
    ?? shape.roles.find((r) => ROLE_KIND[r.role] === 'text' && r.role !== 'title');
  const title = shape.roles.find((r) => r.role === 'title');
  const author = shape.roles.find((r) => r.role === 'author')
    ?? shape.roles.find((r) => r.role === 'date');
  // A running head and foot are a REPEATED PROJECTION of the object, drawn once
  // per printed page. `read` here is a decision rather than a default: typing
  // into one of several copies of the same value has no meaning, and the copy a
  // person happened to be looking at is not a thing the store can be told
  // about.
  const field = (id: string, role: Role) => ({
    kind: 'field' as const, id,
    binding: { slot: id, role, direction: 'read' as const },
  });
  // The body is a COLLECTION, so a document is many objects rendered
  // continuously — one section per object, each named by its own title. That is
  // how Notion and Obsidian actually work, and it is what makes a table of
  // contents derivable: `compile.ts` sets `nameFrom` on a card from its title
  // binding, so a section knows what it is called without anyone authoring a
  // word.
  const sectionCard = {
    kind: 'card' as const, id: 'section',
    // `expand` makes each section a disclosure. Obsidian folds headings,
    // Notion calls them toggle blocks, Coda calls them collapsible sections —
    // one affordance, already in the vocabulary, and until now drawn as a
    // select.
    affordances: ['expand' as const],
    // Writable where the vocabulary can say how, and READ where it cannot —
    // decided by `bindingFor`, the generator's own function, rather than by a
    // literal here.
    //
    // Every binding in this spec used to be `direction: 'read'`, with no reason
    // written beside it. The effect was that a document could never be edited
    // where it was being read, whatever the shape underneath could support: a
    // person had to leave for the table view, retitle a row, and come back.
    // `e2e/document-flow.mjs` did exactly that to make the document long enough
    // to paginate, one line under a comment saying *"a harness that writes by a
    // route no person uses is testing a route no person uses"*.
    //
    // A first attempt at this set `direction: 'read-write'` by hand and the
    // validator refused the whole spec with `write-without-mutation` — which is
    // the reason to call the function rather than to write the field: a binding
    // that may be written has to say WHAT it permits, and one place already
    // knows.
    bindings: [
      ...(title ? [bindingFor('sec-t', title.role)] : []),
      ...(prose ? [bindingFor('sec-b', prose.role)] : []),
    ],
  };
  return {
    // Controls dropped with the root they drove — see `withoutViewState`.
    ...withoutViewState(spec),
    target: { ...spec.target, cardinality: 'small-set' },
    root: {
      kind: 'capability',
      id: 'doc',
      capability: 'document-flow',
      version: CAPABILITIES['document-flow'].version,
      // Asked for when the shape has something to cite, and not otherwise —
      // derived like every other decision in this spec. A document over a pool
      // whose objects reference nothing has no apparatus, which is a fact about
      // that pool rather than a missing feature.
      config: {
        columns: 1, paginated: true, toc: true,
        footnotes: shape.roles.some((r) => ROLE_KIND[r.role] === 'ref'),
      },
      ...(prose
        ? { channels: { body: { slot: 'doc-body', role: prose.role, direction: 'read' } } }
        : {}),
      slots: {
        body: [{ kind: 'collection', id: 'sections', flow: 'stack',
                 items: [{ spec: sectionCard }] }],
        ...(title ? { header: [field('doc-head', title.role)] } : {}),
        ...(author ? { footer: [field('doc-foot', author.role)] } : {}),
      },
    },
  } as unknown as TLayoutSpec;
}

/** The id `app.obsidian` gives its backlinks collection — `app-template.ts`
 *  is the authority, and this is the one string the host has to know to
 *  answer it. */
const WS_BACKLINKS_ID = 'ws-backlinks-list';
/** The search pane's results collection, answered from the siblings the
 *  host already holds — an `app-template.ts` id. */
const WS_SEARCH_ID = 'ws-search-results';

/**
 * WHICH SIDEBAR HOLDS A PANE OF THIS PURPOSE, named by the id the RENDERER
 * gave it — @Codex's P2 on `98376fb`.
 *
 * `workspace.sidePanes` is keyed by a region's own spec id, and the host has
 * no way to know that id: `ws-left` is what `app.obsidian`'s builder happens
 * to mint, and a composed presentation or a seventh look names its regions
 * differently. The ribbon's Files and Search tools wrote `sidePanes['ws-left']`
 * regardless, so under any other naming they updated a state entry nothing
 * reads and the pane they name was unreachable — silently, which is the one
 * thing a pressed control may not be.
 *
 * The renderer already publishes the answer: every pane tab carries its
 * purpose AND its sidebar's id, which is what the pane-tab handler beside this
 * one has always read. So the route is the same one `leftSide` below takes for
 * the fold — ask the LAYOUT what it drew, never a constant. It is the same
 * rule the renderer follows on the other side of the wire, where a look's
 * opening pane is matched by content and *"no id anywhere"*.
 */
/**
 * THE LOOK ON SCREEN, which is not always one of the six.
 *
 * A composed app presents as a `LookRecord` assembled from parts of other
 * looks (`composed.ts`), so every consumer that used to ask `lookOf(state.
 * template)` asks this instead. `state.template` still names the app the page
 * was GENERATED as, and nothing here changes that: composition is a
 * presentation question, and the generator's module table, shape table and
 * builder switch are all keyed by the six.
 *
 * Resolved per call rather than cached, for the reason the ticket and the
 * ribbon both learned: a cached record is a set somebody assumes is closed,
 * and a composed app's parts are editable while the door is open.
 */
function currentLook(): LookRecord {
  const made = composedById(state.composedId);
  return made ? composedRecord(made) : lookOf(state.template);
}

const sidebarHolding = (purpose: RegionPurpose): string | undefined =>
  ($('stage').querySelector(`[data-ws-pane="${purpose}"][data-ws-sidebar]`) as HTMLElement | null)
    ?.dataset.wsSidebar;

/** Depth-first search of a spec tree for a node by id. */
function findNode(node: TNode, id: string): TNode | undefined {
  if ((node as { id?: string }).id === id) return node;
  const kids = (node as { children?: TNode[] }).children
    ?? (node as { items?: { spec: TNode }[] }).items?.map((it) => it.spec)
    ?? Object.values((node as { slots?: Record<string, TNode[]> }).slots ?? {}).flat();
  for (const k of kids) { const hit = findNode(k, id); if (hit) return hit; }
  return undefined;
}

/**
 * What the workspace chrome of a named app look reads — decision 0055.
 *
 * The tab strip names the open note; the status bar counts its text. Counted
 * from the PROSE on screen rather than read off a stored number, because a
 * status bar reports the document under the cursor — Obsidian counts what you
 * typed — and the notes pool's stored "Words" is a property a person edits,
 * which the editor's properties row already shows. The two are different
 * facts and this repo has confused them before (task 0111).
 */
/**
 * THE OPEN NOTES, in the order they were first opened — task `0198`.
 *
 * This was `{current, previous}`: a fixed window of TWO, so the strip could
 * never hold three and a note opened four gestures ago was gone from it
 * without anybody closing anything. `0170`'s finding 8 reads as a count at
 * the door — one tab against the reference's two — and the count was never
 * the defect: driven, we already draw two, both backed by their object's
 * handle. The defect is that a two-slot window is not a strip.
 *
 * A LIST, and the order is FIRST-OPENED rather than most-recent: a tab that
 * jumped position when you came back to it would be a strip nobody can point
 * at. Cleared when a template is left, because the tabs belong to the
 * workspace.
 */
type OpenNote = { readonly title: string; readonly id: ObjectId };
const openNotes: { list: OpenNote[]; active: ObjectId | undefined } =
  { list: [], active: undefined };

/** The pool's size as the last paint read it — the store's total, else the
 *  page's — for chrome that states it. */
let libraryTotal = 0;

function workspaceInputs(
  ir: IR, objects: readonly BoundValues[], backlinks: number | undefined,
  searchCount?: number, boardFilterCount?: number,
): CapabilityInputs {
  const subject = objects[0];
  // THROUGH THE CHANNELS, never a hard-coded role — @Codex, P2 on ebf7b8c:
  // the accepted spec is the authority, so a spec that binds `label` or
  // `words` to another role changes what the chrome shows without this
  // adapter changing. The first version read `title` and `body` by name.
  const text = (n: IRNode, channel: string): string => {
    const role = n.channels?.[channel]?.role;
    const v = role !== undefined ? subject?.[role] : undefined;
    return typeof v === 'string' ? v : '';
  };
  const inputs = new Map<number, CapabilityInput>();
  const titleOf = (n: IRNode) => text(n, 'label');
  const title = ir.nodes.filter((n) => n.capability === 'workspace-tabs').map(titleOf)[0] ?? '';
  // The strip keeps the note opened BEFORE this one as an inactive tab —
  // Obsidian's, and the reference's, and the first deployed verdict's fifth
  // item. Remembered here, not in the spec: which tabs are open is view
  // state.
  const openId = selected;
  // Tracked by OBJECT, never gated on a stored title — @Codex, P2 on
  // 4ab498b: an unnamed note (New note; 0024 stores no title) was not
  // recorded, so the note the person had just left lost its tab until the
  // new one was named. `Untitled` is the tab's fallback copy here as
  // everywhere; and a note named later refreshes the copy its tab carries.
  /* EVERY OPEN NOTE'S TITLE, REFRESHED FROM THIS PAINT'S SIBLINGS — @Codex's
     P2 on `ee1dd16`. Only the ACTIVE note's copy was refreshed, so renaming a
     note through another view or the side pane left its inactive tab showing
     the old name until somebody activated it. That is the one behaviour
     Joaquín names directly: the same source, so what you do in one happens in
     the other.

     Through the `label` CHANNEL and never a hard-coded role, for the same
     reason `titleOf` above is — the accepted spec is the authority on which
     role a tab's copy comes from. And the cached entry is what is refreshed,
     not the render below it, so there is one place a tab's copy comes from
     rather than two that can disagree.

     A sibling with no title keeps `Untitled`, matching what is stored when a
     tab is first opened; a note absent from this paint's siblings keeps the
     copy it had, because absence is not a rename.

     NO CLAUSE YET, and the reason is written here rather than implied. The
     product has no gesture that renames a note while its tab is INACTIVE
     within one session: the editor renames the active note, and the explorer
     rows navigate. The case this fixes is the realtime one — a second browser
     tab on the same vault renaming a note the first has open behind another
     tab, which is what `attachTabInvalidation` exists for — and a control for
     it needs two contexts driven together, the shape `cross-look` already
     has. Filed as `0207`. The fix ships without it because the defect is
     Joaquín's own sentence about one source and two views, and because the
     change is small and its failure mode is a stale label rather than a
     wrong write. */
  const labelRole = ir.nodes.find((n) => n.capability === 'workspace-tabs')
    ?.channels?.['label']?.role;
  if (labelRole !== undefined) {
    const fresh = new Map<ObjectId, string>();
    for (const row of siblings) {
      const handle = (row as Record<symbol, unknown>)[OBJECT_HANDLE_KEY] as ObjectHandle | undefined;
      const id = handle !== undefined ? identities.get(handle) : undefined;
      const value = (row as Record<string, unknown>)[labelRole];
      if (id !== undefined) fresh.set(id, typeof value === 'string' && value.trim() !== ''
        ? value : 'Untitled');
    }
    // REPLACED, not mutated: `OpenNote.title` is readonly and that is a
    // contract worth keeping — a tab's copy changes by a new entry taking the
    // old one's place, which is also what happens when a note is opened.
    openNotes.list = openNotes.list.map((note) => {
      const now = fresh.get(note.id);
      return now !== undefined && now !== note.title ? { ...note, title: now } : note;
    });
  }
  // A note being CLOSED is not re-added while its neighbour's pool resolves —
  // see `closingTab`. Matched on the id AND on the live ticket, so a newer
  // close, a tab activation or leaving the workspace lifts it by itself.
  const closing = closingTab !== undefined && closingTab.ticket === tabCloseTicket
    && closingTab.id === openId;
  if (openId !== undefined && !closing) {
    const at = openNotes.list.findIndex((n) => n.id === openId);
    const entry = { title: title || 'Untitled', id: openId };
    // A note ALREADY open keeps its place and refreshes its copy — a title
    // typed after the tab appeared shows on the tab, which is what the
    // `Untitled` fallback below is for. A new one joins the end.
    if (at >= 0) openNotes.list[at] = entry; else openNotes.list.push(entry);
    openNotes.active = openId;
  }
  // In the GRAPH VIEW the note's tab steps back (pressable, by its handle)
  // and a Graph view tab is the active one — the app's own strip.
  const openHandle = openId !== undefined ? handles.get(openId) : undefined;
  const graphOn = state.mainView === 'graph' && ir.nodes.some((n) =>
    n.kind === 'capability' && n.capability === 'force-graph' && n.config?.['vault'] === true);
  /* EVERY OPEN NOTE, in the order they were opened — task `0198`. A tab whose
     object has no handle this paint is dropped rather than drawn dead: a tab
     that cannot reopen its note is the inert duplicate label finding 8
     forbids. The ACTIVE one is the note on screen, and in the graph view it
     steps back to a pressable tab with the Graph view active beside it. */
  type Tab = { readonly label: string; readonly active?: boolean; readonly handle?: ObjectHandle };
  const noteTabs: Tab[] = openNotes.list.flatMap((n): Tab[] => {
    const handle = handles.get(n.id);
    if (handle === undefined) {
      // No handle this paint: the ACTIVE note is still drawn (it is what is
      // on screen), without a close control, because the × carries the
      // handle. An inactive tab with no handle is dropped — a tab that
      // cannot reopen its note is the inert duplicate label finding 8 forbids.
      return n.id === openId && !graphOn ? [{ label: title || 'Untitled', active: true }] : [];
    }
    if (n.id === openId && !graphOn) return [{ label: title || 'Untitled', active: true, handle }];
    return [{ label: n.id === openId ? (title || 'Untitled') : n.title, handle }];
  });
  const tabs: Tab[] = [
    ...noteTabs,
    ...(graphOn ? [{ label: 'Graph view', active: true }] : []),
  ];
  // The open look, as data (0173 step 3). Every field read below used to be a
  // `state.template === 'app.<name>'` comparison at its own call site; the
  // record and the reasoning behind each value live in `looks.ts`.
  const look = currentLook();
  for (const n of ir.nodes) {
    if (n.kind !== 'capability') continue;
    if (n.capability === 'workspace-window') {
      // The look says which controls its window has; an undeclared one feeds
      // an empty row, which draws nothing rather than a row of nothing.
      inputs.set(n.i, { capability: 'workspace-window',
        window: { controls: look.windowControls ?? [] } });
      continue;
    }
    if (n.capability === 'workspace-ribbon') {
      // Which of the strip's tools SELECT a pane. The renderer resolves which
      // one is showing, against what the sidebar holds — the host does not
      // name a region here, which is the whole point of `0d83f75`'s P2.
      /* AND THE STRIP'S INVENTORY — task `0204`. The gate is EITHER, not
         `ribbonSelects` alone: a look whose strip is only commands has no
         pane selectors at all, and gating on `selects` dropped its tools on
         the floor before they could reach the renderer that has accepted
         them since `d80adab`. Both are optional and the renderer defaults
         the inventory, so a look declaring neither is unchanged. */
      if (look.ribbonSelects || look.ribbonTools) {
        inputs.set(n.i, { capability: 'workspace-ribbon', ribbon: {
          ...(look.ribbonSelects
            ? { selects: look.ribbonSelects as Record<string, RegionPurpose> } : {}),
          ...(look.ribbonTools ? { tools: look.ribbonTools } : {}),
        } });
      }
      continue;
    }
    if (n.capability === 'workspace-tabs') {
      // `create` stays gated on MAY_WRITE here rather than in the record: a
      // reader's strip has no New note, and that is the person's rights, not
      // the look's.
      inputs.set(n.i, { capability: 'workspace-tabs', tabs: { tabs,
        ...(look.tabs ? { ...(MAY_WRITE ? { create: true } : {}),
          inert: { close: look.tabs.close },
          // Task `0198`: the strip accumulates, so the × has to act. Closing
          // is a VIEW action and is not gated on a write grant — a reader may
          // tidy the strip they are reading. Only when there is more than one
          // tab to close down TO, so the last note cannot be closed into an
          // empty editor.
          /* DECLARED EITHER WAY — @Codex's P2 on `4a34aef`. This was spread
             in only when the strip held more than one tab, so `close`
             undefined meant two different things to the renderer: "this host
             cannot close tabs at all" and "this host can, but not right now".
             It drew the look's glyph inert for both, so the sole tab showed
             an 11x11 aria-hidden × that does nothing — a dead control's
             appearance without its function, in the strip `0198` built to
             stop being one. A boolean says which. */
          close: tabs.length > 1 } : {}) } });
    } else if (n.capability === 'workspace-search') {
      // The Search tool's box and count — the module's input, fed here as
      // the strip's and the bar's are (@Codex, P1 on 93d57d7). The count
      // is the hits the host answered, absent while the query is empty:
      // the vault's search pane answers a collection; Notion's view-row
      // search filters the board itself, and its count is the cards left.
      const count = searchCount ?? boardFilterCount;
      // Fabric's composer: the panel's heading and its model pill, the chip
      // that says what the composer sees (this note and the rest of the
      // library — a true count), "+ Actions" — inert (0056); the box
      // itself is the library's retrieval.
      const assistant = look.assistant;
      const fabricPanel = assistant ? {
        placeholder: assistant.placeholder, heading: assistant.heading,
        inert: [
          { label: assistant.model, box: true, menu: true, place: 'start' as const },
          // A TRUE count, so it is built here from what the library holds.
          { label: `This note + ${Math.max(0, libraryTotal - 1)} items`, box: true },
          { label: assistant.actions, glyph: '+' },
        ],
      } : {};
      // COLLAPSED where the look asks for it (0187): the trigger stands in
      // the row and the box appears when the host says it is open. A query
      // that survives a repaint keeps it open, or a paint would hide the box
      // while its filter still holds the board.
      const collapsed = look.searchTrigger ? {
        trigger: look.searchTrigger, open: state.searchOpen || state.searchQuery !== '',
      } : {};
      inputs.set(n.i, { capability: 'workspace-search',
        search: { query: state.searchQuery, ...(count !== undefined ? { count } : {}),
          ...(look.searchPlaceholder !== undefined ? { placeholder: look.searchPlaceholder } : {}),
          ...(look.searchLabel !== undefined ? { label: look.searchLabel } : {}),
          ...collapsed, ...fabricPanel } });
    } else if (n.capability === 'workspace-nav') {
      // Notion's nav sidebar, resolved by the host: the workspace is this
      // Morph; RECENT is the pool's three latest dated objects (the seed's
      // dates are all past, so "upcoming" would be an empty promise), each
      // opening its object; TEAMSPACES are the pools, the open one current.
      // Nothing drawn that does not act.
      /* FROM SIBLINGS, not `objects` — @Codex's P2 on `4a34aef`, and the
         same reason the page tree twenty lines below already gives. `objects`
         is what the LENS is about, so opening a Notion row under `single`
         cardinality left it holding one page: Recent collapsed from three
         rows to that one, or vanished entirely when the open page had no
         date. Recent is POOL navigation and has to stand still while you
         read an object, exactly as the tree under it does. */
      const dated = siblings
        .map((o) => ({ o, when: String((o['date'] ?? o['date.start'] ?? '') as string) }))
        .filter((x) => /^\d{4}-\d{2}-\d{2}/.test(x.when))
        .sort((a, b) => b.when.localeCompare(a.when))
        .slice(0, 3);
      // FAVORITES are the starred pages on the hub's shelf (the star's
      // `starred` entries, any pool), each reopening as the look it was
      // starred under, on its view — the app's Favorites, with the same
      // door the hub's shelf uses.
      const starred = readKeptLayouts().filter((k) => k.what === 'starred');
      const viewName = (v: TemplateView | undefined) => v ? v[0]!.toUpperCase() + v.slice(1) : 'Board';
      inputs.set(n.i, { capability: 'workspace-nav', nav: {
        workspace: 'Morph',
        ...(look.navFold !== undefined ? { fold: look.navFold } : {}),
        ...(look.navInert ? { inert: look.navInert } : {}),
        ...(look.navWindow ? { window: look.navWindow } : {}),
        // The tools row's Search focuses the view row's box when the page
        // has one; the foot's New page is the create door, for a writer.
        search: ir.nodes.some((m) => m.kind === 'capability' && m.capability === 'workspace-search'),
        create: MAY_WRITE,
        sections: [
          ...(starred.length ? [{ label: 'Favorites', rows: starred.map((k) => {
            const owner = db.pools.find((p) => p.id === k.poolId);
            return {
              label: owner?.label ?? k.poolId, detail: viewName(k.templateView),
              ...(owner?.icon ? { icon: owner.icon } : {}), action: `kept:${k.id ?? String(k.at)}`,
            };
          }) }] : []),
          ...(dated.length && look.recentSection ? [{ label: 'Recent', rows: dated.map(({ o, when }) => ({
            label: typeof o['title'] === 'string' ? o['title'] : 'Untitled',
            detail: when.slice(5, 10).replace('-', '/'), icon: '▪', action: `object:${handleOf(o) ?? ''}`,
          })) }] : []),
          // The open pool starts open — `applyViewState` only overrides a key
          // a person has folded — so the sidebar arrives showing where you
          // are, as the app's does. Titles only, and only what the pool
          // actually holds: a row that opens nothing is not drawn.
          { label: look.poolSection, rows: db.pools.map((p) => {
            // ONLY the open pool: the others are not in memory, and a twisty
            // on a row whose pages the host does not have would be a dead
            // control (0055), so it is drawn where the pages are and nowhere
            // else.
            //
            // From SIBLINGS, not `objects`. `objects` is what the LENS is
            // about, so under `single` cardinality it is the one open page —
            // and the sidebar's tree would collapse to a single row exactly
            // while a person is reading one, taking away every other page
            // they might go to next. `siblings` is the pool's own set in
            // either cardinality, which is what a sidebar lists. Found by
            // @Codex; the tree's first clause asserted the fold and never
            // this, so a green harness said nothing.
            const inside = look.navTree && p.id === pool.id
              ? siblings.slice(0, 6).map((o) => ({
                label: typeof o['title'] === 'string' && o['title'] ? o['title'] : 'Untitled',
                icon: '▪', action: `object:${handleOf(o) ?? ''}`,
                current: (() => { const h = handleOf(o); const id = h === undefined ? undefined : identities.get(h); return id !== undefined && id === selected; })(),
              })).filter((k) => k.action !== 'object:')
              : [];
            return {
              label: p.label, current: p.id === pool.id, action: `pool:${p.id}`,
              ...(p.icon && look.poolIcons ? { icon: p.icon } : {}),
              // TASK 0178: the pool's OWN tone, so a look's space colour is
              // a fact about the pool rather than about its row's position.
              ...(p.tone === undefined ? {} : { tone: p.tone }),
              ...(inside.length ? { children: inside, fold: `nav:${state.template}:${p.id}` } : {}),
            };
          }) },
        ],
      } });
    } else if (n.capability === 'workspace-topbar') {
      // The strip and the page actions the host BACKS (nothing drawn that
      // does not act): back and forward over the places visited under the
      // look; the star, which keeps the page on the hub's shelf and, lit,
      // takes it off; ··· with New page and the way out. The page's icon
      // is the pool's own.
      const h = state.ntHistory;
      // Word's title bar names the document and nothing else that ACTS:
      // its way out is the ribbon's Close, and it keeps no place history.
      // The rest of the bar the reference shows — AutoSave and its toggle,
      // undo and redo, the centred search, Sign in, the window controls —
      // is inert presentation under decision 0056: imagery, not controls.
      const actions = !look.topbarActions ? undefined
        : { canGoBack: h.back.length > 0, canGoForward: h.forward.length > 0,
          ...(look.topbarStar ? { kept: keptPageEntries().length > 0 } : {}),
          leave: 'Open as Morph' };
      // Each look's own inert chrome in the bar (0056) — Notion's Share and
      // comment glyph, Fabric's search and people button, Fibery's ›› and
      // close, Word's AutoSave row. The items and the reason for each are in
      // `looks.ts`; only the SVG paths moved.
      const inert = look.topbarInert;
      // Fabric's bar names the NOTE, not the library: the open item's title.
      const openTitle = objects[0]?.['title'];
      inputs.set(n.i, { capability: 'workspace-topbar', topbar: {
        // A look with a folding header carries no tab strip: the identity
        // when the sidebar is away, then the end's chrome.
        ...(look.topbarFold ? { fold: look.topbarFold, strip: false as const } : {}),
        title: look.topbarTitle === 'open'
          ? (typeof openTitle === 'string' && openTitle ? openTitle : 'Untitled') : pool.label,
        ...(pool.icon && look.topbarTitle === 'pool' ? { icon: pool.icon } : {}),
        ...(actions ? { actions } : {}),
        ...(inert ? { inert } : {}),
      } });
    } else if (n.capability === 'workspace-pagehead') {
      // A DOC's page owns more than a name (Coda): its cover, the
      // breadcrumb above the title — the workspace the page tree names —
      // and a paragraph, which is the pool's own note: real words about
      // this collection, not placeholder prose.
      const doc = look.pagehead === 'document';
      /**
       * THE HEAD NAMES ITS SUBJECT — task 0176, and it was the one thing the
       * object page got visibly wrong: a large `Tasks` H1 with the pool's icon
       * standing above the object's own title, so the prominent title was the
       * wrong one. @Codex's ruling asks for *"object page head/title"*.
       *
       * Read through the node's own `label` CHANNEL, never a hard-coded role —
       * the pattern this adapter already uses everywhere else, and @Codex's P2
       * on ebf7b8c: *"the accepted spec is the authority, so a spec that binds
       * `label` to another role changes what the chrome shows without this
       * adapter changing."* All three pagehead-bearing looks already pass that
       * channel; the host simply ignored it.
       *
       * Gated on the IR's CARDINALITY rather than on the tree: at a set the
       * channel resolves against `objects[0]`, which is the first card and not
       * the page's subject. Structure cannot tell the two apart, which is why
       * the discriminator is carried instead of inferred.
       */
      /**
       * ON AN OBJECT PAGE THE HEAD IS SILENT, because the object's own
       * EDITABLE title is the page's title — which is what Notion draws and
       * what makes 0176's rename reachable where a person looks for it.
       *
       * The first version of this fix had the head NAME the object, and that
       * only moved the defect: the page then carried two identical titles, a
       * large dead one over a small live one. Seen in a screenshot, not in a
       * clause — which is why this task's bar is judged by looking.
       *
       * Gated on the IR's CARDINALITY rather than the tree: at a set the head
       * names the pool, and structure cannot tell the two apart.
       */
      /**
       * AND THE REST OF THE HEAD IS THE POOL'S TOO — found by LOOKING at the
       * Coda row page, one commit after the title was silenced.
       *
       * Silencing the title and the icon left the pool's COVER and the pool's
       * NOTE standing on a page about one object: a banner belonging to the
       * collection above the object's name, and under it *"Carries a
       * four-value status — low enough cardinality to group by"*, which is a
       * sentence about the Tasks pool sitting on the page for one task.
       *
       * The same defect as the H1 and the icon, in two more elements — a fix
       * that reached one dimension short of the thing it named, which is this
       * repository's most-repeated shape. No clause could have caught it: the
       * head named the right thing and the field was editable. The screenshot
       * said it.
       *
       * The CRUMB stays, and stops being a literal. `'Morph'` was hard-coded
       * — the one rule this project has above the others — and on an object
       * page the honest crumb is the object's actual parent, which is the
       * pool the row was opened from.
       */
      const one = ir.cardinality === 'single';
      inputs.set(n.i, { capability: 'workspace-pagehead',
        pagehead: { title: one ? '' : pool.label,
          ...(pool.icon && !one ? { icon: pool.icon } : {}),
          ...(doc && pool.cover && !one ? { cover: pool.cover } : {}),
          ...(doc ? { crumb: one ? pool.label : 'Morph' } : {}),
          ...(doc && !one ? { words: pool.note } : {}) } });
    } else if (n.capability === 'doc-ribbon') {
      // Word's ribbon, only what the host backs: the styles gallery that
      // converts the caret's block (Normal, Heading, List — the canvas's
      // own kinds), New note (the create door), Print (the document's real
      // pagination). Bold, fonts, alignment and the rest have nothing
      // behind them and are not drawn.
      // The rest of Word's Home ribbon — the other tabs, Clipboard, Font,
      // Paragraph, Editing, Voice, Editor, Add-ins — is inert presentation
      // under decision 0056: drawn as the app draws it, imagery to a
      // keyboard and a screen reader. Each item is declared here, never a
      // renderer constant.
      const inert = (label: string, glyph?: string, more?: { big?: boolean; box?: boolean; tile?: boolean; bar?: string; menu?: boolean; sep?: boolean; break?: boolean }) =>
        ({ label, ...(glyph !== undefined ? { glyph } : {}), ...more, inert: true as const });
      // Word's Font and Paragraph glyphs, Fluent-style, 16px paths — the
      // twenty-third verdict's one remaining item: the reference's own
      // iconography and colour accents, not text approximations.
      const G = {
        grow: 'M4 18l5-12 5 12M6.5 13h5M15 9h5M17.5 6.5v5', shrink: 'M4 18l5-12 5 12M6.5 13h5M15 9h5',
        kase: 'M3 17l4-10 4 10M4.5 13h5M14 17V9M14 9c2.5-1.5 6-1 6 2v6M14 13c2-2 6-1.5 6 1',
        clear: 'M9 4h9l-6 12H6zM8 20h8M15 4l-2 5', strike: 'M6 12h12M8 8c0-2 8-2 8 0M8 16c0 2 8 2 8 0',
        sub: 'M4 6l8 10M12 6L4 16M15 18h5l-4 3', sup: 'M4 8l8 10M12 8L4 18M15 4h5l-4 3',
        fx: 'M5 18L11 6l6 12M7.5 13h7', highlight: 'M4 15l7-9 6 4-6 8H7zM14 7l2-2 3 2-2 2', color: 'M5 16L11 4l6 12M7.5 11h7',
        bullets: 'M4 6h.01M4 12h.01M4 18h.01M9 6h11M9 12h11M9 18h11', numbering: 'M4 5v3M3 8h2M3 11h2l-2 3h2M3 17h2v3H3M9 6h11M9 12h11M9 18h11',
        multi: 'M3 6h.01M6 12h.01M9 18h.01M7 6h13M10 12h10M13 18h7', outdent: 'M4 6h16M10 12h10M4 18h16M4 10l-3 2 3 2M10 12H1',
        indent: 'M4 6h16M10 12h10M4 18h16M1 10l3 2-3 2', sort: 'M4 6l3-3 3 3M7 3v12M13 8h7M13 12h5M13 16h3', pilcrow: 'M17 4H10a4 4 0 0 0 0 8h1v8M15 4v16',
        left: 'M4 6h16M4 10h10M4 14h16M4 18h10', center: 'M4 6h16M7 10h10M4 14h16M7 18h10', right: 'M4 6h16M10 10h10M4 14h16M10 18h10', justify: 'M4 6h16M4 10h16M4 14h16M4 18h16',
        spacing: 'M10 6h10M10 12h10M10 18h10M5 4v16M3 6l2-2 2 2M3 18l2 2 2-2', shading: 'M7 3l10 10-6 6L3 11zM16 14c1.5 2 3 3.5 3 5a2 2 0 0 1-4 0c0-1.5 1-3 1-5',
        borders: 'M4 4h16v16H4zM4 12h16M12 4v16', paste: 'M9 4h6v3H9zM7 6H5v15h14V6h-2M9 12h6M9 16h4', cut: 'M6 4l9 12M18 4L9 16M7 15a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM17 15a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
        copy: 'M8 8h11v13H8zM5 16V3h11', painter: 'M4 4h12v4H4zM10 8v4h4v8h-4M14 12h5v-5h-3',
      };
      inputs.set(n.i, { capability: 'doc-ribbon', ribbon: { tab: 'Home',
        tabs: ['File', 'Home', 'Insert', 'Draw', 'Design', 'Layout', 'References', 'Mailings', 'Review', 'View', 'Help'],
        right: [{ label: 'Comments', glyph: 'M4 5h16v11H9l-5 4z' }, { label: 'Editing ▾', glyph: 'M4 20h4l10-10-4-4L4 16zM13 7l4 4' }, { label: 'Share', glyph: 'M4 12v7h16v-7M12 3v12M8 7l4-4 4 4' }],
        groups: [
        { label: 'Clipboard', items: [
          inert('Paste', G.paste, { big: true, menu: true }), inert('', G.cut), inert('', G.copy), inert('', G.painter),
        ] },
        { label: 'Font', items: [
          inert('Aptos (Body)', undefined, { box: true, menu: true }), inert('10.5', undefined, { box: true, menu: true }),
          inert('', G.grow, { sep: true }), inert('', G.shrink), inert('', G.kase, { menu: true }), inert('', G.clear),
          inert('B', undefined, { break: true }), inert('I'), inert('U', undefined, { menu: true }), inert('', G.strike), inert('', G.sub), inert('', G.sup),
          inert('', G.fx, { sep: true, menu: true, bar: '#2b7cd3' }), inert('', G.highlight, { menu: true, bar: '#ffff00' }), inert('', G.color, { menu: true, bar: '#e81123' }),
        ] },
        { label: 'Paragraph', items: [
          inert('', G.bullets, { menu: true }), inert('', G.numbering, { menu: true }), inert('', G.multi, { menu: true }),
          inert('', G.outdent, { sep: true }), inert('', G.indent), inert('', G.sort, { sep: true }), inert('', G.pilcrow),
          inert('', G.left, { break: true }), inert('', G.center), inert('', G.right), inert('', G.justify),
          inert('', G.spacing, { sep: true, menu: true }), inert('', G.shading, { menu: true, bar: '#2b7cd3' }), inert('', G.borders, { menu: true }),
        ] },
        { label: 'Styles', items: MAY_WRITE ? [
          { label: 'Normal', action: 'style:paragraph', tile: true },
          inert('No Spacing', undefined, { tile: true }),
          { label: 'Heading', action: 'style:heading', tile: true },
          { label: 'List', action: 'style:list-item', tile: true },
        ] : [] },
        { label: 'Editing', items: [inert('Editing', 'M10.5 4a6.5 6.5 0 1 0 0 13a6.5 6.5 0 0 0 0-13zM15.5 15.5L20 20', { big: true })] },
        { label: 'Voice', items: [inert('Dictate', 'M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zM6 11a6 6 0 0 0 12 0M12 17v4', { big: true })] },
        { label: 'Editor', items: [inert('Editor', 'M5 4h14v16H5zM8 9h8M8 13h8M8 17h5', { big: true })] },
        { label: 'Add-ins', items: [inert('Add-ins', 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM16.5 13v7M13 16.5h7', { big: true })] },
        // The File group is the way in, out and onto paper: New note (the
        // create door), Print (real pages), Close (the look put down — this
        // pool as Morph shows it, 0024).
        { label: 'File', items: [
          ...(MAY_WRITE ? [{ label: 'New note', action: 'new' }] : []),
          { label: 'Print', action: 'print' },
          { label: 'Close', action: 'leave' },
        ] },
      ] } });
    } else if (n.capability === 'doc-actions') {
      // Buttons as blocks: the one action the host has is the create door
      // (the reference's "Add task"); the mail and Slack buttons it also
      // shows have nothing behind them here and are not drawn.
      // The table's working tools at the heading's right — Coda's filter,
      // sort and options — inert (0056): nothing behind them here.
      inputs.set(n.i, { capability: 'doc-actions',
        actions: { actions: MAY_WRITE ? [{ label: 'Add task', action: 'new' }] : [], heading: `View of ${pool.label}`,
          inert: [
            { label: 'Filter', glyph: 'M3 5h18l-7 8v6l-4-2v-4z' }, { label: 'Sort', glyph: 'M8 4v16M8 20l-3-3M8 20l3-3M16 20V4M16 4l-3 3M16 4l3 3' },
            { label: 'Options', glyph: 'M4 12h.01M12 12h.01M20 12h.01' },
          ] } });
    } else if (n.capability === 'view-switcher') {
      // The board is the template's own view; the switcher names it and
      // lights it. Other views of the same collection are the next slice.
      /* WHAT THE PAGE CAN BUILD, not only what the look CLAIMS — task `0241`.
         `views` is a composable part (`composed.ts` folds it onto `view`), so a
         Fibery base wearing Notion's view part offered four chips while
         Fibery's builder clamps calendar and timeline to the board: two chips
         that lit, wrote aria-selected, and moved the stage by nothing.
         Presentation is the DONOR's; what can be built is the BASE's, and the
         base is `state.template` — this file's own note above `currentLook`
         says the generator's module table, shape table and builder switch are
         all keyed by the six, and composition does not change that. Derived
         from the module ROW rather than from a list of app names. */
      const views: readonly TemplateView[] = look.views.filter((v) => {
        const needs = VIEW_MODULE[v];
        return needs === undefined
          || (state.template !== undefined
            && APP_TEMPLATE_MODULES[state.template].includes(needs));
      });
      // The strip's chips say only what is TRUE of this view: grouped by the
      // state's property, every card the pool's one type — so the strip is
      // declared by the look and its contents read from the spec here.
      const groupedBy = ir.nodes.find((m) => m.kind === 'collection' && m.query?.groupBy)?.query?.groupBy;
      const groupLabel = groupedBy ? (labelsFor(pool)[groupedBy] ?? groupedBy) : undefined;
      inputs.set(n.i, { capability: 'view-switcher', views: {
        views: views.map((id) => ({
          id, label: id[0]!.toUpperCase() + id.slice(1), active: state.templateView === id,
        })),
        canCreate: MAY_WRITE,
        ...(look.createMark !== undefined ? { createMark: look.createMark } : {}),
        ...(look.viewBarInert ? { inert: look.viewBarInert(state.templateView) } : {}),
        ...(look.viewBarStrip ? {
          strip: [
            ...(groupLabel ? [{ label: `${groupLabel} ▾`, glyph: 'M4 6h16M4 12h10M4 18h6' }] : []),
            { label: `Type: ${pool.typeHint[0]!.toUpperCase()}${pool.typeHint.slice(1)}`, glyph: 'M4 4h16v16H4zM8 8h8M8 12h8M8 16h5' },
          ],
        } : {}),
      } });
    } else if (n.capability === 'workspace-status') {
      // Word's bar counts the DOCUMENT — every section's prose — where
      // Obsidian's counts the note under the cursor.
      const wordsRole = n.channels?.['words']?.role;
      const prose = look.documentIsTheView && wordsRole !== undefined
        ? objects.map((o) => { const v = o[wordsRole]; return typeof v === 'string' ? v : ''; }).join('\n')
        : text(n, 'words');
      const counted = n.channels?.['words'] !== undefined;
      inputs.set(n.i, { capability: 'workspace-status',
        status: {
          ...(look.statusVault ? { vault: pool.label, format: 'Markdown' } : {}),
          ...(look.statusInert ? { inert: look.statusInert } : {}),
          // Said only when the spec bound a text to count. Code points, not
          // UTF-16 units — the runtime's `characters` derivation counts the
          // same way (@Codex, P2 on ebf7b8c).
          ...(counted ? {
            words: prose.trim() ? prose.trim().split(/\s+/).length : 0,
            // Word's bar says the words; Obsidian's the characters too.
            ...(look.statusVault ? { characters: Array.from(prose).length } : {}),
          } : {}),
          ...(backlinks === undefined ? {} : { backlinks }),
        } });
    }
  }
  /**
   * EVERY BORROWED PART WEARS THE LOOK IT CAME FROM — task `0237`.
   *
   * One pass over what the loop built, rather than a line inside each of the
   * sixteen branches: the renderer applies the wrapper generically, so the
   * host's side is generic too — or fifteen capabilities compose their DATA
   * and paint as the base, which is the shape the first build shipped.
   *
   * WHICH PART a capability draws is DERIVED from its own id, never a table:
   * `workspace-nav` is the nav, `workspace-topbar` the topbar, `view-switcher`
   * the view. Longest part first, so a short name cannot claim a capability a
   * longer one owns. A capability matching no part wears nothing, which is the
   * right answer for a module no look has an opinion about.
   *
   * The class name is derived too — `app.fibery` is `m-app-fibery`, the same
   * derivation `render-html.ts` makes on the root.
   */
  const composedNow = composedById(state.composedId);
  if (composedNow) {
    const all = readComposedApps();
    const parts = effectiveParts(composedNow, all);
    const fields = lookFields();
    const names = [...new Set(fields.map((f) => partOf(f, fields)))]
      .sort((a, b) => b.length - a.length);
    for (const [i, input] of inputs) {
      if (input instanceof Map) continue;
      const cap = String(input.capability);
      const part = names.find((name) => cap.includes(name));
      const named = part ? parts[part] : undefined;
      /* RESOLVED, not read. A part's source may be another app somebody made,
         which has no class of its own — writing `m-app-composed:xyz` would be
         refused by the renderer's sanitiser and the part would silently fail
         to paint, which is worse than not offering it. */
      const from = part && named !== undefined
        ? resolvePartSource(named, part, all) : undefined;
      if (from) inputs.set(i, { ...input, wearing: [`m-app-${from.replace(/^app\./, '')}`] });
    }
  }
  return inputs;
}

/**
 * EVERY page of the vault, through the store's own cursor at the same
 * configured boundary the siblings come from. The search and the vault
 * graph both read it; neither may stop at the 200-sibling page the
 * explorer holds (@Codex, P2 on 93d57d7 and P1 on 4195885: a vault past
 * 200 notes answered "No matches" for a note past the first page, and
 * left an isolated one off the canvas). A page's rows carry their
 * handles (`vaultRows`), so a hit or a dot opens its note through the
 * same door as an explorer row.
 */
type VaultPage = Awaited<ReturnType<typeof db.effective.read>>;
async function everyVaultPage(): Promise<VaultPage[]> {
  const pages: VaultPage[] = [];
  let cursor: string | undefined;
  do {
    const page = await db.effective.read(pool.scope, { limit: 200, ...(cursor ? { cursor } : {}) });
    pages.push(page);
    cursor = (page.objects as { cursor?: string }).cursor;
  } while (cursor);
  return pages;
}
const vaultRows = (page: VaultPage): BoundValues[] =>
  page.projection.items.map((values, i) => withHandle(values, page.objects.items[i]!.id));

/** Resolve store identities before crossing the runtime boundary, then discard
 * them. The renderer receives only opaque session handles. */
async function forceGraphInputs(
  ir: IR, seed: readonly { readonly id: ObjectId }[],
): Promise<CapabilityInputs | undefined> {
  const graphs = ir.nodes.filter((n) => n.kind === 'capability' && n.capability === 'force-graph');
  if (graphs.length === 0) return undefined;
  const inputs = new Map<number, CapabilityInput>();
  for (const node of graphs) {
    const edgeRole = node.channels?.['edge']?.role;
    const labelRole = node.channels?.['label']?.role;
    if (!edgeRole || !edgeRole.startsWith('relation.')) continue;

    /**
     * IN A PANE (`config.pane`, the app template's local graph — 0055): the
     * graph is about the ONE open note, so the traversal runs BOTH ways —
     * what it links to and what links to it, which is what Obsidian's local
     * graph shows — and the layout is computed for the pane's own box rather
     * than scaled down from the page's. The page graph is unchanged.
     *
     * THE VAULT (`config.vault`, the Graph view — the tenth verdict's list):
     * every note in the vault is a seed, one hop out from each, so the whole
     * link structure is on the canvas; laid out for the column's box, the
     * open note current so the renderer lifts it.
     */
    const local = node.config?.['pane'] === true;
    const vault = node.config?.['vault'] === true;
    // EVERY note, paged through the store's cursor — not the 200-sibling
    // page the explorer holds (@Codex, P1 on 4195885: a vault past 200
    // notes left an isolated note past the first page off the canvas, and
    // a one-hop traversal from the first page can never reach it).
    const vaultIds = vault
      ? [...new Set([
        ...seed.map((row) => row.id),
        ...(await everyVaultPage()).flatMap(vaultRows).map((v) => identities.get(handleOf(v)!)).filter((id): id is ObjectId => id !== undefined),
      ])]
      : undefined;
    const traversed = await db.store.traverse({
      manifest: pool.manifest,
      from: vaultIds ?? seed.map((row) => row.id),
      via: edgeRole,
      direction: local ? 'both' : 'out',
      depth: 1,
      maxNodes: FORCE_GRAPH_LIMIT + 1,
      timeoutMs: 40,
    });
    const graphNodes: ForceGraphInput['nodes'] = traversed.nodes.map((object) => {
      const bound = withHandle({}, object.id);
      const handle = handleOf(bound)!;
      const value = labelRole ? object.roles.get(labelRole)?.[0] : undefined;
      return { handle, label: String(value ? rawOf(value) : 'Object') };
    });
    const handleById = new Map(
      traversed.nodes.map((object) => [object.id, handleOf(withHandle({}, object.id))!] as const),
    );
    const graphEdges: ForceGraphInput['edges'] = traversed.edges.flatMap((edge) => {
      const from = handleById.get(edge.from);
      const to = handleById.get(edge.to);
      return from && to ? [{ from, to }] : [];
    });
    const positions = graphViewState.layout(
      graphNodes.map((n) => n.handle),
      graphEdges,
      local ? FORCE_GRAPH_PANE_BOX : vault ? FORCE_GRAPH_VAULT_BOX : undefined,
    );
    // The note the local graph is ABOUT — its handle, so the renderer can lift
    // it; never its id, which stays on this side of the boundary. The vault
    // graph lifts the open note the same way.
    const currentId = local || vault ? seed[0]?.id : undefined;
    const current = currentId === undefined ? undefined : handleById.get(currentId);
    const graph: ForceGraphInput = {
      nodes: graphNodes,
      edges: graphEdges,
      positions,
      /* WITHHELD WITHOUT A WRITE GRANT — task `0182`. `edit` and `bind` are
         both gated on MAY_WRITE a few hundred lines below; the module inputs
         were not, so a reader driving the vault held an epoch on every graph
         node. The handle is unaffected: identity is not permission, and
         following a node still works for a reader. */
      ...(MAY_WRITE ? { epoch } : {}),
      ...(current !== undefined ? { current } : {}),
      // `max-depth` is the declared one-hop boundary, not lost data. Only
      // resource ceilings make the result incomplete.
      truncated: traversed.truncated === 'max-nodes' || traversed.truncated === 'timeout',
    };
    inputs.set(node.i, { capability: 'force-graph', graph });
  }
  return inputs;
}

/**
 * The pool DECLARES; the digest MEASURES. One rule, read by every surface.
 *
 * Extracted because it was read by exactly one of them. @Codex:
 * *"`renderAside` generates directly from the seeded object's derived shape
 * and never applies the declaration augmentation used by `run()` …
 * consequently, a referenced task that does not already carry
 * `relation.parent` gets no Parent task slot or Add control in that pane,
 * even though this new declaration says every task may carry it."*
 *
 * Which is the first defect of this whole feature — an object that cannot be
 * given a first reference — surviving in the ONE surface a person reaches by
 * following a reference. Opening a Parent task is the likeliest way to land
 * on a task with no parent of its own, so the pane most in need of the
 * gesture was the pane without it.
 *
 * A second copy of this logic would have drifted the same way. One function,
 * two callers, the same rule — the shape `scopeForRole` already takes for the
 * picker and the write boundary.
 */
/**
 * Which pool an object actually belongs to.
 *
 * By MEMBERSHIP, through the store, rather than by matching a type name — the
 * same `seed` test the write boundary uses, and for the same reason: a name is
 * a description and membership is a fact.
 */
async function poolOf(id: ObjectId): Promise<DemoPool | undefined> {
  for (const p of db.pools) {
    const hit = await db.store.query({ ...p.scope, seed: [id] }, { limit: 1 });
    if (hit.items.length === 1) return p;
  }
  return undefined;
}

function declaredShape(
  derived: ObjectShape,
  measured: { relationDensity: number; crossDocDensity?: number;
              enumCardinality?: Record<string, number> } | undefined,
  /** WHOSE declarations. The stage passes the open pool; the pane passes the
   *  pool its own object came from, which following a reference makes a
   *  different one more often than not. */
  defs: readonly Def[],
): ObjectShape {
  if (measured === undefined) return derived;
  return { ...derived,
        // **And relation density comes from the COLLECTION too**, for exactly
        // the reason above: how many objects hold a reference is a fact about
        // the pool, and over one object it is 0 or 1 — so a newly created film
        // reported 0, fell under the surfacing threshold, and got no Filmmaker
        // row at all. The empty slot decision 0016 draws an ATTACH control into
        // was never emitted, so the one gesture that could give it a Filmmaker
        // had nowhere to appear.
        //
        // A first fix overwrote this with a flat `1` whenever the pool declared
        // any ref role. @Codex caught what that costs: it recreates AT THE HOST
        // BOUNDARY the always-surface behaviour that `tuning.test.ts` had just
        // rejected in the generator — `relationSurfaceThreshold` could no longer
        // suppress a sparse relation for any threshold below 1, and
        // `crossDocDensity` could no longer decide anything because the first
        // side of the `||` always won. The measured number was sitting one line
        // above, already fetched, already used for `enumCardinality`.
        relationDensity: measured.relationDensity,
        ...(measured.crossDocDensity !== undefined
          ? { crossDocDensity: measured.crossDocDensity } : {}),
        // And whether this pool DECLARES a relation at all, which is a
        // different question from how many of its objects carry one. Without it
        // a pool under the surfacing threshold emits no slot, and attach lives
        // only on a slot — so no object in it could ever be given a first
        // reference, and the density could never rise. Films works today only
        // because most films already have a director. Found by @Codex.
        declaresRelations: defs.some(
          (d) => d.role !== undefined && isRelationRole(d.role)),
        ...(measured.enumCardinality ? { enumCardinality: measured.enumCardinality } : {}),
        roles: [...derived.roles, ...defs
        .filter((d) => d.role !== undefined
          && !derived.roles.some((r) => r.role === d.role)
          // Relation-mutable refs only — `isRelationRole`, not `ref` kind.
          // Decision 0016 closes relation mutation to `relation.peer`,
          // `relation.parent` and `relation.child`; `source` is a ref and is
          // provenance, never relation-mutable. Offering it here would put an
          // empty region on the page with no gesture that could ever fill it,
          // which is the exact failure the original exclusion was written to
          // prevent — reintroduced for a different role. Found by @Codex.
          && (ROLE_KIND[d.role] !== 'ref' || isRelationRole(d.role))
          // `ref` USED to stay out, and the comment here nominated its own
          // repeal: *"the closed mutation vocabulary has NO mutation that
          // writes a ref, so a reference offered on a new object would be an
          // affordance that always refuses. Decision 0016 adds `link`/`unlink`
          // and this line is what should change when it lands."*
          //
          // 0016 landed. The store executes `link`, the renderer draws attach
          // and detach, a person can drive both — and this line did not change,
          // so a newly created film still had no Filmmaker row at all. The
          // condition that was written to expire outlived the thing it was
          // waiting for, which is the same shape as every stale reason this
          // repo keeps finding, in the one place that had predicted its own.
          //
          // `media` was excluded beside it and that was never the same case: a
          // media value is written by INGESTION, which this demo has — the file
          // picker attaches to the open object and derives the role from the
          // file's type. So the property was writable all along and the lens
          // simply never said the pool had one. Measured: a newly created film
          // rendered no media node at all, while an existing one rendered its
          // poster.
          )
        .map((d) => ({
          role: d.role!, kind: ROLE_KIND[d.role!], coverage: 1, keyCount: 1,
          declared: { since: new Date(0).toISOString() },
        }))] }

}

/**
 * The generated spec this VIEWING EPISODE is being painted from.
 *
 * Decision 0023, and @Codex's mechanism rather than my first one. I held the
 * SHAPE and still called `compile` on every repaint with it — which stops the
 * re-rank and violates the premise the generation package opens with:
 * *"The design compiler is invoked RARELY and never on an interaction path."*
 * Holding the spec keeps the compiler off the interaction path entirely, which
 * is what "does not regenerate" was supposed to mean.
 *
 * It also leaves coverage completely live rather than merely unread: nothing
 * downstream of the generator gets a stale number, because nothing downstream
 * is given a held shape.
 *
 * `key` is mine and is belt and braces beside `schedule`'s flag. @Codex's
 * version relies on every call site being classified correctly; a site that
 * says `false` when it means `true` would paint one pool's spec over another
 * pool's objects, which is worse than the churn this replaces. Anything that
 * changes what is on screen therefore re-derives whether or not it asked.
 */
let episode: {
  readonly key: string;
  /** Held for the STRUCTURAL decisions only — the generator's input, and the
   *  view and row-action transforms below it. `derived` and `collection` stay
   *  fresh every pass, so the property picker and the rollups still read
   *  today's numbers. Decision 0023 sanctions exactly this: *"Coverage, enum
   *  cardinality, relation density, and similar measurements may be stale with
   *  respect to ranking until the next derivation boundary. Rendered values
   *  must not be stale."* */
  readonly shape: ObjectShape;
  spec?: TLayoutSpec;
} | undefined;

async function run(derive = true) {
  /**
   * EVERY render, whoever asked for it — task `0139`.
   *
   * `morph.render.schedule` deliberately measures `schedule()`'s loop, and its
   * own comment says so: *"it is not every render — twelve call sites reach
   * `run()` directly, the bootstrap among them"*. That is the right scope for
   * what it was written for, and it is why `e2e/frame-time.mjs` could report a
   * repaint figure for the `edit` phase and none at all for `scheme`: the
   * scheme control calls `void run()` directly.
   *
   * So the two phases could not be compared on the thing that matters. This
   * measure is the broader one and is named for what it spans rather than for
   * a caller — the argument `morph.render.schedule` itself makes for its name.
   *
   * What it is expected to show, written down BEFORE the number exists so a
   * plausible mechanism cannot be quoted afterwards as if it had been
   * measured: a scheme change repaints against warm store memos and an edit
   * repaints against cold ones, because the write invalidated them. The store
   * benchmark puts that difference at 0.62ms warm against 4.26ms cold on the
   * read path, and those reads happen before the demo's own `internal` timer
   * starts, so nothing has ever seen them from the browser side.
   */
  const renderAt = typeof performance === 'object'
    && typeof performance.now === 'function' ? performance.now() : undefined;
  try {
    return await runBody(derive);
  } finally {
    if (renderAt !== undefined && typeof performance.measure === 'function') {
      try {
        if (performance.getEntriesByName('morph.render.run', 'measure').length > 8) {
          performance.clearMeasures('morph.render.run');
        }
        performance.measure('morph.render.run', { start: renderAt });
      } catch { /* no-op */ }
    }
  }
}

async function runBody(derive = true) {
  const single = state.view === 'auto' && state.cardinality === 'single';
  if (single && !selected) select(await firstInPool());
  // The currently painted/accepted lens owns membership. Generation needs the
  // unfiltered shape on first paint, but every subsequent control change must
  // send that lens's query together with its view-state values.
  // THE LENS's own scope, and when the lens has several regions it is the
  // UNFILTERED pool. `membershipQuery` returns the first region's query, so a
  // split lens narrowed `objects` — and therefore the shell's outline panel,
  // which reads `siblings` — by the FIRST region's predicate. Measured: setting
  // the threshold took the outline from twelve rows to seven, so the panel
  // listing "what is here" quietly agreed with one half of the split.
  //
  // Each region gets its own scope below. This one is what everything that is
  // not a region reads.
  const activeRegions = subjectRegions(activeSpec(state.lens) ?? lastSpec);
  const scope = lensScope(single, activeRegions.length > 1
    ? undefined
    : membershipQuery(activeSpec(state.lens) ?? lastSpec));

  // Derived by the store over the whole SCOPE rather than over whatever sample
  // was passed in, so the denominator is the collection the lens is showing and
  // the shape and the screen cannot disagree about coverage.
  /**
   * Shape, identities and values from ONE configured read. Decision 0030:
   * *"All consumers that request shape plus values use the same configured read
   * boundary. Calling raw `deriveShape` and patching values afterwards is not a
   * compatible implementation."*
   *
   * That is what this replaced — `withProjectedBodies` patched `body` onto the
   * rows after the shape had already been derived without it, so retiring the
   * stored scalar removed the role, the lens lost its field, and the canvas had
   * nowhere to mount. Derived roles now exist BEFORE generation sees the shape.
   */
  const effective = await db.effective.read(scope, { limit: 200 }, { expandRefs: 1 });
  const derived: ObjectShape = effective.shape;
  lastShape = derived;

  // The picker needs the COLLECTION's roles even while a single-object lens is
  // on screen, so it can tell a role this pool already carries from one it does
  // not. Free when the lens is the collection; one fold over the digest's
  // counters when it is not.
  const collection = state.cardinality === 'single'
    ? (await db.effective.read(pool.scope, { limit: 0 })).shape : derived;
  poolRoles = new Set(collection.roles.map((r) => r.role));

  // An object shows the fields its POOL declares, not only the ones it has
  // filled in.
  //
  // This is what makes a new note writable. A seeded single-object scope derives
  // that object's own shape, so a note with nothing but a title has a shape with
  // nothing but a title, the generator emits one binding, and the renderer draws
  // one field. There is nowhere to put a cursor, and the only way to bring a
  // body into existence was a role dropdown, a label and an Add button.
  //
  // `render-html.ts` has drawn an empty editable cell for an absent value under
  // a read-write binding for some time — "the one cell a user needs to click to
  // FILL a property was the one cell that was not there". Half the answer was
  // already here; what was missing is that no binding existed to be empty.
  //
  // The pool's declared roles at full coverage, which is the truthful reading:
  // the pool asserts every one of its objects may carry these, and coverage
  // over a single-object scope answers "does THIS one", which is the question
  // the generator's floor is for and not the question a blank field asks.
  //
  // **Cardinality comes from the COLLECTION too, for the same reason.** How
  // many distinct values a role has is a fact about the pool; over one object
  // it is at most one, and every question that reads it — is this groupable,
  // is this few enough values to be chosen from — is asking about the pool.
  // Without this a new object's enum has no counted cardinality at all, and a
  // rule that treats "unknown" as "not small" (correctly, since a truncated
  // dropdown is worse than none) then refuses the chooser on exactly the
  // object that needs one.
  const fresh: ObjectShape = state.cardinality === 'single'
    ? declaredShape(derived, collection, pool.defs)
    : derived;

  /**
   * Task 0101's measurement lives here, and the repair is a few lines further
   * down rather than in this value.
   *
   * `fresh` is honest and was used directly. Clearing one task's status takes
   * measured coverage from 12/12 to 11/12, another role outranks it, the
   * metadata budget has room for two — and every row loses the status column,
   * including the eleven the person never touched, including the control they
   * had just used. Measured: `title, status, origin` to `title, origin, author`
   * on all twelve.
   *
   * Every step of that is correct on its own, which is why the fix is not a
   * threshold. Decision 0023 moves the BOUNDARY instead: coverage stays
   * truthful — no synthetic `coverage: 1`, which 0023 rejects by name because
   * it would make the measurement lie to every other reader of it — and the
   * generator simply is not asked again until the episode ends.
   *
   * I wrote here that holding the SPEC alone left coverage fully live. That was
   * over-stated, and @Codex's review found the gap: the spec is cached, but
   * `withRowActions` and the view transforms below are handed a shape too, so
   * clearing the last populated `status` would still drop the row-action nodes
   * mid-episode. A spec frozen inside a moving frame is not a frozen lens.
   *
   * So the episode holds the shape those transforms read. What stays live is
   * everything that is about VALUES rather than structure — `derived` and
   * `collection` above, which the property picker and the rollups use.
   */
  /**
   * Selection counts ONLY where it decides what is on screen.
   *
   * This read `${selected ?? ''}` unconditionally and that was a defect, found
   * by @Codex's review and then measured. `selectFromClick` records which object
   * a click was in WITHOUT repainting — that is deliberate, so a click inside a
   * cell places a caret instead of navigating — and the value write that follows
   * therefore arrives with a different key, discards the held spec, and
   * regenerates. Which is task 0101, on the exact path a person takes:
   *
   *   without a preceding cell click   +0 compiles, columns stable
   *   with the click a person makes    +1 compile,  columns RE-RANKED
   *
   * And `e2e/stable-columns.mjs` was green throughout, because `selectOption`
   * does not click the cell first. A guard that skips the gesture is green about
   * the wrong question — @Codex spotted that its own evidence was the thing
   * making the fix look complete.
   *
   * In a collection lens the selection changes nothing about what is drawn, so
   * it does not belong in the identity. In a single-object lens it is the whole
   * subject — `scope` is seeded on it — so it does. `single` is the same
   * condition that decides that seeding, twenty lines up.
   */
  const episodeKey = `${pool.id}\u0000${state.cardinality}\u0000${state.view}`
    + `\u0000${state.template ?? ''}`
    + `\u0000${state.lens}\u0000${single ? selected ?? '' : ''}`;
  if (derive || episode?.key !== episodeKey) episode = undefined;
  if (episode === undefined) episode = { key: episodeKey, shape: fresh };
  const shape: ObjectShape = episode.shape;

  // `query` and `projection` page the same membership in the same order, so an
  // id and the values beside it describe one object. That zip is what lets the
  // DOM name an object by an opaque HANDLE while the store still resolves a
  // real id — the renderer never sees an identity, per decision 0007.
  // Ref roles expand one level, which is what node-slot renders. Both halves
  // come from the read taken above, so the zip that attaches a handle to a row
  // is a zip of one revision rather than of two calls that happened to agree.
  const rows = effective.objects;
  const slice = effective.projection;
  objects = slice.items.map((values, i) => withHandle(values, rows.items[i]!.id));

  /**
   * Per-object write provenance for this render pass.
   *
   * A collection can contain a legacy object whose Body is still a stored
   * scalar beside a migrated object whose Body is derived from canonical
   * blocks. They share one IR binding, so changing that binding to read-only
   * would unnecessarily lock the legacy row. The opaque handle already names
   * the object at every renderer/runtime boundary; the grant set uses this map
   * to suppress only the derived role on only that object.
   */
  const knownObjects = new Set<ObjectHandle>();
  const readonlyRoles = new Map<ObjectHandle, ReadonlySet<Role>>();
  const rememberDerived = (
    rendered: readonly BoundValues[], stored: typeof rows.items,
    derivedRoles: ReadonlyMap<ObjectId, ReadonlySet<Role>>,
  ): void => {
    rendered.forEach((values, index) => {
      const object = stored[index];
      const handle = handleOf(values);
      if (!handle) return;
      knownObjects.add(handle);
      const roles = object && derivedRoles.get(object.id);
      if (roles?.size) readonlyRoles.set(handle, roles);
    });
  };
  rememberDerived(objects, rows.items, effective.derived);

  // The renderer sees a PAGE; these totals are what let that page describe the
  // complete set to a screen reader and report an honest overflow count.
  const totals: Partial<Record<'subject' | 'siblings', number>> = {};
  if (rows.total !== undefined) totals.subject = rows.total;
  // The library's size, for a look's chrome that states it (Fabric's
  // composer chip): the store's total when it has one, else the page's.
  libraryTotal = rows.total ?? rows.items.length;


  // The panel's SIBLINGS. In a single-object lens `scope` is seeded to that one
  // object, so the panel — a collection inside the same spec — used to render
  // exactly one row: an outline of "what is here" that emptied the moment you
  // opened anything. It reads `source: 'siblings'` now, and this is where that
  // word gets resolved.
  //
  // Same zip as above, and for the same reason: the rows are clickable, so they
  // need the opaque handle that lets the DOM name an object without the
  // renderer ever seeing an identity.
  //
  // In a collection lens the two sets are the same query, so it is reused
  // rather than fetched twice.
  if (state.cardinality === 'single') {
    // The same configured boundary, so a sibling row shows the same `body` the
    // subject does. Two calls to the raw store would have given the panel a
    // note's stale scalar beside a stage showing its blocks.
    const siblingRead = await db.effective.read(pool.scope, { limit: 200 });
    const sibRows = siblingRead.objects;
    const sibSlice = siblingRead.projection;
    siblings = sibSlice.items.map((values, i) => withHandle(values, sibRows.items[i]!.id));
    rememberDerived(siblings, sibRows.items, siblingRead.derived);
    if (sibRows.total !== undefined) totals.siblings = sibRows.total;
  } else {
    siblings = objects;
    if (rows.total !== undefined) totals.siblings = rows.total;
  }

  // The accepted lens wins unless a candidate is being previewed. This is the
  // rule the prototype established: a candidate never overwrites what you have.
  // The shell wraps the generated lens rather than editing it: the spec the
  // generator produced is what the taste model learns from, and rewriting it in
  // place would teach the pool a layout nobody chose. With the panel off this
  // returns the same object, so the content hash — and every grant minted from
  // it — is unchanged.
  //
  // **And the automatically derived spec is accepted for the episode too.**
  // That is decision 0023 and task 0101: a value write repaints through this
  // spec instead of asking for a replacement, so the compiler stays off the
  // interaction path and the columns stay where the person left them. Only the
  // automatic base is held — a saved or candidate lens has its own explicit
  // lifecycle through offer/preview/keep and must not be cached behind it.
  // An accepted lens wins ONLY if it is a lens for the look on the stage —
  // @Codex, P1 on ebf7b8c: a generic Notes lens kept earlier took precedence
  // over the template request, so the rail was hidden for a spec with no
  // ribbon and no way back. A kept reproduction reopens by its own template
  // (0055 control 3); a generic lens waits until the person leaves the look.
  const saved = (() => {
    const s = activeSpec(state.lens);
    if (s === undefined) return undefined;
    const kept = (s as { template?: { id?: string } }).template?.id;
    if ((state.template ?? undefined) !== kept) return undefined;
    /**
     * AND IT MUST BE A LENS FOR THE SAME QUESTION — @Codex's P2 on
     * `ed4f7a7`, which is the finding above one dimension in.
     *
     * The template check was added because a generic Notes lens took
     * precedence over a template request. A KEPT LENS FOR THE SAME TEMPLATE
     * passes it, and a kept set layout is one — so opening a row, which sets
     * `state.cardinality` to 'single', reused the accepted table and the
     * single-object branch was never reached. An accepted Coda table stayed
     * a one-row table with the doc's chrome around it, and Notion's and
     * Fibery's collections did the same.
     *
     * A spec's `target.cardinality` is what it was generated to answer.
     * Where the person is now asking a different question, the saved answer
     * is not wrong — it is about something else, and generation runs. The
     * lens is not cleared: leaving the object restores the state the lens
     * was kept for and it wins again.
     *
     * 'small-set' and 'large-set' are the same question here: both are a
     * COLLECTION, the generator coerces between them by pool size, and
     * treating them as different would regenerate a kept table every time
     * the pool crossed the threshold.
     */
    const target = (s as { target?: { cardinality?: string } }).target?.cardinality;
    const asked = state.cardinality === 'single' ? 'single' : 'set';
    const answers = target === 'single' ? 'single' : 'set';
    return asked === answers ? s : undefined;
  })();
  let generated = saved ?? episode.spec;
  if (generated === undefined) {
    const gen = await compile(
      {
        shape, context: state.context, taste: { palette: state.palette, ...state.taste },
        // A named app look is asked for BY NAME, with what this runtime can
        // draw — decision 0055. The generator refuses in words when a
        // module the template needs is not registered, and that refusal is
        // shown where the lens would have been rather than papered over.
        ...(state.template
          ? { template: state.template, templateView: state.templateView, capabilities: registeredModuleIds() } : {}),
      },
      stageGenerator);
    if (!gen.ok) {
      $('stage').innerHTML = `<p class="err">Generation refused: ${gen.problems.join('; ')}</p>`;
      return;
    }
    generated = gen.spec;
    // Only the automatic base is held. A saved or candidate lens has its own
    // explicit lifecycle through offer/preview/keep and must not be cached
    // behind it — but it still reads the episode's shape above, because the
    // transforms below would otherwise move underneath it just the same.
    if (saved === undefined) episode.spec = generated;
  }
  /**
   * A BOARD, asked for by name — task 0167, decision 0051. A transform of
   * the generated spec (`query.groupBy` on the subject collection, the
   * field the generator writes when it chooses a board itself), NOT a
   * capability — the calendar's own header draws that line. When the shape
   * has no enum role there is no board to make, and the refusal is in
   * words, on the stage: blind run 12's driver burned five dead ends here
   * and the status region alone has been missed by every driver so far.
   * The stage-replacing refusal is the graph view's own precedent.
   */
  if (state.view === 'board' && boardSpec(generated, shape) === undefined) {
    $('stage').innerHTML = '<p class="err">A board groups by a role with a '
      + 'handful of named values — like a status. This pool has none; add '
      + 'such a property and a board becomes possible.</p>';
    say('a board needs a role that groups — this pool has none', 'bad');
    return;
  }
  const requested = state.view === 'graph' ? graphSpec(generated, shape)
    : state.view === 'plot' ? plotSpec(generated, shape, state.mark)
    : state.view === 'document' ? documentSpec(generated, shape)
    : state.view === 'calendar' ? calendarSpec(generated, shape)
    : state.view === 'timeline' ? timelineSpec(generated, shape)
    : state.view === 'board' ? boardSpec(generated, shape)!
    : generated;
  // A NAMED APP LOOK wears its own chrome: no outline shell around it and no
  // Update row actions inside it (0055 control 6 — Morph's generic card inside
  // Obsidian-coloured chrome is the substitution being refused). The relation
  // mutations stay: attaching a link from inside the note is Obsidian's own
  // gesture, and the vocabulary's.
  const decorated = state.template
    ? withRelationMutations(await inlineRelations(requested, objects))
    : withShell(
      withRowActions(
        withRelationMutations(await inlineRelations(requested, objects)), shape),
      state.shell);
  const shown = state.view === 'document' ? withoutDocumentUpdateActions(decorated) : decorated;

  // Validate the spec that is actually PAINTED, not the one the generator
  // returned. `compile` validates its own output, and `withShell` then wraps it
  // — so until now the tree reaching `compileSpec` had never been through the
  // validator at all, and none of `placement-in-item-template`, `duplicate-id`,
  // `dangling-anchor` or `placement-on-root` ran on it. An adversarial pass
  // walked an unvalidated placement through this gap and into the markup.
  const check = validateSpec(shown);
  if (!check.valid) {
    // Codes only, and they come from a closed set in the validator — so this
    // message cannot carry anything an author wrote.
    const codes = [...new Set(check.problems.map((p) => p.code))]
      .filter((c) => /^[a-z-]+$/.test(c)).join(', ');
    $('stage').innerHTML = `<p class="err">The shell produced an invalid spec: ${codes}</p>`;
    return;
  }

  /**
   * Internal references become URLs before anything draws them — and AFTER the
   * spec exists, which is the part that moved.
   *
   * The renderer refuses every scheme but http(s), deliberately, decision
   * `0017`, so a stored reference must never reach it. This used to run right
   * after the projection, before a spec had been chosen — so it resolved every
   * populated media role on every object whether the layout bound it or not.
   *
   * @Codex, P1 on `4261caa`: *"an ingested image or video carries both its full
   * source and its derivative into this call … a single ceiling-size source
   * therefore incurs the multi-second IndexedDB read and pins 64 MiB that the
   * derivative was introduced to avoid, and a collection repeats that cost per
   * row."* `media.thumb` exists so that a card does not pay for `media.hero`,
   * and paying for both made the derivative a second cost instead of a saving.
   *
   * So the roles come from `shown` — the tree actually about to be painted,
   * after the view transform, after `inlineRelations` generated the lenses for
   * expanded relation targets, and after the shell added the panel. Anything
   * earlier would miss a role and leave a `morph-blob:` string for the runtime
   * to refuse, which renders empty.
   *
   * `resolvableRoles` also adds the derivatives the renderer draws WITHOUT a
   * binding — the video poster is read straight off the object inside
   * `render-html.ts`'s video branch — which is the half a spec walk cannot see
   * and the half that would have cost every video its poster frame.
   *
   * Siblings resolved in the SAME pass, or `sweep()` would revoke an id that is
   * only on screen in the panel: the panel is a collection over a different
   * set, and an id counts as on screen if anything draws it. One role set
   * covers both, because the panel's spec is inside `shown`.
   *
   * Above `t0`, so an IndexedDB read does not land inside the phase timings
   * that exist to say where a frame goes.
   */
  const wanted = resolvableRoles(shown);
  [objects, siblings] = await Promise.all([
    blobUrls.resolve(objects, wanted),
    siblings === objects ? Promise.resolve(objects) : blobUrls.resolve(siblings, wanted),
  ]);

  const t0 = performance.now();
  /**
   * Where the frame actually goes, phase by phase.
   *
   * The line this feeds used to read `compiled + rendered in 57.70 ms`, and
   * `ACCEPTANCE.md` quoted that number while arguing it EXCLUDED store work —
   * *"it starts after query, projection, shape derivation and generation have
   * already run"*. The first half is true and the conclusion is not:
   * `rollupsFor` is inside this bracket, and at 100k that is an `aggregate`
   * over the whole scope — 23ms ungrouped and 92ms grouped by the store's own
   * benchmark. So the great majority of a number labelled "compiled +
   * rendered" was neither compiling nor rendering.
   *
   * Measured against it: `compileSpec` plus `renderHTML` for a 200-row page is
   * about **0.6ms** in node. The label was hiding a factor of a hundred, and
   * anybody optimising "the render" on that number would have spent the day in
   * the wrong file.
   *
   * So the phases are timed separately and all of them are reported. A single
   * total is what let the mislabel survive; three named numbers beside it
   * cannot hide which one is large.
   */
  const phase: Record<string, number> = {};
  const timed = async <T>(name: string, fn: () => T | Promise<T>): Promise<T> => {
    const start = performance.now();
    try { return await fn(); } finally { phase[name] = (phase[name] ?? 0) + (performance.now() - start); }
  };
  let html = '';
  let refused: string[] = [];
  let painted: { ir: IR; lens: string; grants: GrantSet } | undefined;
  try {
    const ir = await timed('compile', () =>
      compileSpec(shown, { viewport: state.viewport, itemCount: objects.length }));

    // The lens id is the spec's own content hash, so a re-render of an unchanged
    // layout mints the same grant ids and an edit in flight does not lose its
    // grant. The spec is remembered under it because the write path derives its
    // authority from the spec that was RENDERED — never from one a caller hands
    // in later.
    const lens = candidateId(shown);
    lastLens = lens;
    /**
     * The slots THIS lens declares.
     *
     * Recorded beside the IR, because `Predicate.valueFrom` is positional and
     * the specs are generated: slot 2 of one lens is not slot 2 of the next.
     * Held values are dropped whenever the declaration changes, since a number
     * a person typed for "Minimum score" must not silently become the value of
     * whatever slot 2 happens to be after a regeneration.
     *
     * **BEFORE the region reads, not after the paint.** This block used to sit
     * below `renderHTML`, which made the drop a frame late: `lensScope` folds
     * `viewStateScope()` into every scope it builds, so a pass that replaced
     * the lens queried each new region with the OUTGOING lens's values, still
     * indexed positionally, and only then discarded them. The rows were
     * already on screen by that point — the reset the paragraph above promises,
     * applied one paint after the paint it was written to protect. Raised by
     * @Codex on #137.
     */
    const declaredSlots = ir.viewState ?? [];
    const owner = `${String(scope.manifest)}:${lens}`;
    const sameSlots = owner === viewStateOwner
      && declaredSlots.length === viewStateSlots.length
      && declaredSlots.every((sl, i) => viewStateSlots[i]?.name === sl.name
        && viewStateSlots[i]?.role === sl.role && viewStateSlots[i]?.op === sl.op);
    if (!sameSlots) {
      viewStateOwner = owner;
      viewStateSlots = declaredSlots.map((sl) => ({
        name: sl.name, role: sl.role, op: sl.op,
      }));
      viewStateValues = [];
    }

    // ONE SCOPE PER SUBJECT REGION, which is Coda's *"a control whose value
    // drives queries in several regions"* and @Codex's ruling on 0081: *"use
    // one store scope per subject collection region, with identical scopes
    // deduplicated by `scopeKey`."* Evaluating the predicates again in the
    // renderer would create two query semantics and make store parity
    // unprovable, so each region's rows come from its own store read.
    //
    // HERE, not beside the lens query above, and the harness is why. The lens
    // query is taken from the ACCEPTED lens because it must be known before the
    // store read; the spec actually rendered is `shown`, generated after it.
    // When those differ — a candidate previewed, a lens accepted — the region
    // keys did too, and the renderer refused with
    // `no rows were supplied for subject collection "collection"`. Measured in
    // `smoke.mjs`, which is exactly what the fail-closed refusal is for: the
    // earlier version would have drawn the whole pool in a filtered region and
    // said nothing.
    //
    // So the regions come from the spec about to be drawn, and the reads happen
    // after generation rather than before it.
    let regionSets: Record<string, readonly BoundValues[]> | undefined;
    let regionTotals: Record<string, number> | undefined;
    {
      /**
       * The scopes, planned from the spec ABOUT TO BE DRAWN.
       *
       * `subjectRegionScopes` and `answerSubjectRegionScopes` are @Codex's,
       * from #136, and they are better than the loop that was here: the
       * base-vs-filtered distinction, the totals and the duplicate reads become
       * one contract with tests around it instead of an untestable closure in
       * this file. The seed is the answer the main lens already read, so a
       * region whose scope IS the lens scope costs no second query.
       *
       * What moved is WHERE they are called. On main they ran above, off
       * `activeSpec(state.lens) ?? lastSpec` — the ACCEPTED lens — while the
       * spec actually rendered is `shown`, generated after it. When those
       * differ, which is every previewed candidate and every accepted lens, the
       * region keys differed too and the renderer refused with `no rows were
       * supplied for subject collection "collection"`. Measured in `smoke.mjs`;
       * that refusal is the fail-closed one @Codex ruled for, and the earlier
       * shape would have drawn the whole pool in a filtered region and said
       * nothing.
       *
       * So: their planner, this branch's placement.
       */
      const regionScopes = subjectRegionScopes(shown, (query) => lensScope(single, query));
      /**
       * The branches the SPEC says to withhold while its control is empty.
       *
       * Read from `spec.partition`, never inferred: @Codex's ruling on 0020 is
       * that the host may execute a declared unset policy and *"must not derive
       * it by comparing queries or interpreting complementarity."*
       *
       * Dropped before the read rather than blanked after it — an answer
       * nothing will draw is a store query nobody needed. The keys go back as
       * empty sets below, because the renderer's refusal to draw a subject
       * collection it was supplied no rows for is correct and stays.
       */
      const withheld = withheldBranches(shown, (i) => viewStateValues[i] !== undefined);
      const planned = regionScopes.filter((r) => !withheld.includes(r.regionKey));
      if (regionScopes.length > 1) {
        // MEASURED, because it is N store reads and the frame breakdown is the
        // only place their cost is visible. Untimed, a split lens reported the
        // same `compile · rollups · render` a single-region one does while
        // doing strictly more work between two of them — a breakdown whose
        // parts no longer sum to the whole it is printed beside. Raised by
        // @Codex on #137. Named `regions` rather than folded into `rollups`:
        // these are scope queries, not aggregations, and merging them would
        // make a regression in one look like a regression in the other.
        const answers = await timed('regions', () => answerSubjectRegionScopes(
          planned,
          scopeKey,
          async (regionScope) => {
            const read = await db.effective.read(
              regionScope, { limit: 200 }, { expandRefs: 1 });
            // The SAME role set as the main collection and the panel.
            // @Codex, P2 on `7788a4c`: this call had no filter, so a split
            // layout rendering only `media.thumb` still read every region
            // object's full `media.hero` — the byte-proportional work the
            // narrowing removes from the main collection, reintroduced by the
            // one door of three that did not get it. Every region is drawn by
            // `shown`, so one set covers all of them.
            const items = await blobUrls.resolve(read.projection.items.map(
              (values, i) => withHandle(values, read.objects.items[i]!.id)), wanted);
            rememberDerived(items, read.objects.items, read.derived);
            return {
              items,
              ...(read.objects.total === undefined ? {} : { total: read.objects.total }),
            };
          },
          {
            scope,
            answer: {
              items: objects,
              ...(rows.total === undefined ? {} : { total: rows.total }),
            },
          },
        ));
        regionSets = { ...answers.sets,
          ...Object.fromEntries(withheld.map(
            (k) => [k, [] as readonly BoundValues[]])) };
        regionTotals = answers.totals;
      }
      // Kept for the click handlers, which run long after this render.
      lastRegionSets = regionSets ?? {};
    }

    /**
     * THE BACKLINKS PANE of a named app look — decision 0055.
     *
     * The template's spec names a subject collection for what points AT the
     * open note; what points at it is a traversal against the store, which a
     * spec names and never performs. So the host answers it here, under the
     * collection's id, exactly as it answers a split lens's regions — and
     * `regionSets` being supplied makes the renderer refuse any subject
     * collection it was not given rows for, which is the fail-closed half.
     */
    let backlinkCount: number | undefined;
    let wsBacklinksNote: string | undefined;
    let wsUnlinked: number | undefined;
    /** How many hits the search answered — for the `workspace-search`
     *  module's count; absent while the query is empty. */
    let wsSearchCount: number | undefined;
    if (state.template && selected !== undefined
        && findNode(shown.root, WS_BACKLINKS_ID)) {
      backlinkNote = '';
      const scope = await backlinkScope(selected);
      // The traversal's own word for stopping early travels with the count
      // — a partial list presented as complete is the defect (@Codex, P2 on
      // d0c42da); `renderBacklinks` already said it, and this path did not.
      if (backlinkNote) wsBacklinksNote = backlinkNote;
      const read = scope
        ? await db.effective.read(scope, { limit: 64 }, { expandRefs: 0 })
        : undefined;
      const items = read
        ? read.projection.items.map((values, i) => withHandle(values, read.objects.items[i]!.id))
        : [];
      backlinkCount = read?.objects.total ?? items.length;
      regionSets = { ...(regionSets ?? {}), [WS_BACKLINKS_ID]: items };
      regionTotals = { ...(regionTotals ?? {}), [WS_BACKLINKS_ID]: backlinkCount };
      lastRegionSets = regionSets;
      // UNLINKED mentions, counted from store-backed prose: a sibling whose
      // text carries the open note's title and which is not already linked
      // to it. Never a made-up zero (@Codex, P2 on d0c42da).
      const openTitle = objects[0]?.['title'];
      if (typeof openTitle === 'string' && openTitle.trim()) {
        const linked = new Set(read?.objects.items.map((o) => o.id) ?? []);
        const needle = openTitle.toLowerCase();
        wsUnlinked = siblings.filter((s) => {
          const h = handleOf(s);
          const id = h === undefined ? undefined : identities.get(h);
          if (id === undefined || id === selected || linked.has(id)) return false;
          const text = typeof s['body'] === 'string' ? s['body'] : '';
          return text.toLowerCase().includes(needle);
        }).length;
      }
    }
    /**
     * THE SEARCH is answered here, from the siblings the host already holds
     * — the eighth verdict's list: a Search tool with a surface behind it.
     * Title and prose, case-folded, substring; the hits keep their handles
     * so a result opens its note through the same door as an explorer row.
     * Supplied whenever the pane is in the spec, so an empty query answers
     * an empty list rather than a refusal (regionSets is fail-closed).
     */
    if (state.template && findNode(shown.root, WS_SEARCH_ID)) {
      const needle = state.searchQuery.trim().toLowerCase();
      // The PROSE the template chose — `transcript` for a shape with no
      // `body` — read from the hit card's own binding, never assumed
      // (@Codex, P2 on 93d57d7). The title is always searched.
      const hitCard = findNode(shown.root, 'ws-search-hit') as { bindings?: { slot: string; role: string }[] } | undefined;
      const proseRole = hitCard?.bindings?.find((b) => b.slot === 'ws-search-text')?.role ?? 'body';
      const matches = (s: BoundValues): boolean => {
        const t = typeof s['title'] === 'string' ? s['title'] : '';
        const b = typeof s[proseRole] === 'string' ? (s[proseRole] as string) : '';
        return t.toLowerCase().includes(needle) || b.toLowerCase().includes(needle);
      };
      // The WHOLE vault, not the sibling page the explorer holds — @Codex,
      // P2 on 93d57d7: a vault past 200 notes answered "No matches" for a
      // note the page had not reached. Paged through the store's own
      // cursor, at the same configured boundary the siblings come from, so
      // a hit carries the same handle as its explorer row.
      let hits: BoundValues[] = [];
      if (needle && currentLook().searchByMeaning) {
        // FABRIC searches by MEANING — the library's own retrieval index
        // (the find door's), which expands a query through the terms the
        // pool uses alongside it, ranked; the hits are the index's ids
        // mapped back to their rows, in the index's order.
        const index = await buildFindIndex();
        const order = new Map(index.search(state.searchQuery.trim(), 60).map((h, i) => [h.id, i] as const));
        for (const page of await everyVaultPage()) {
          const rows = vaultRows(page);
          rememberDerived(rows, page.objects.items, page.derived);
          hits = hits.concat(rows.filter((r) => { const h = handleOf(r); const id = h === undefined ? undefined : identities.get(h); return id !== undefined && order.has(id); }));
        }
        hits.sort((a, b) => (order.get(identities.get(handleOf(a)!)!) ?? 0) - (order.get(identities.get(handleOf(b)!)!) ?? 0));
      } else if (needle) {
        for (const page of await everyVaultPage()) {
          const rows = vaultRows(page);
          rememberDerived(rows, page.objects.items, page.derived);
          hits = hits.concat(rows.filter(matches));
        }
      }
      regionSets = { ...(regionSets ?? {}), [WS_SEARCH_ID]: hits };
      regionTotals = { ...(regionTotals ?? {}), [WS_SEARCH_ID]: hits.length };
      lastRegionSets = regionSets;
      wsSearchCount = needle ? hits.length : undefined;
    }

    specs.set(lens, shown);

    // Every write the spec declared, and nothing else. A binding that did not
    // say `read-write` mints no grant, so the renderer emits no editable element
    // for it — refusal by absence rather than by a check someone can forget.
    const grants = mintGrants(ir, lens, { knownObjects, readonlyRoles });
    epoch += 1;
    // The folds the SPEC asked for, computed over the whole scope by the store.
    //
    // After compiling, because the generator decides which folds a shape
    // deserves and the answer is in the IR. The renderer computes none of it:
    // it holds sixty objects at most, so a total folded there would be a page
    // presented as a sum.
    const folds = await timed('rollups', () => rollupsFor(ir, scope));
    // The open look, as data (0173 step 3) — see `looks.ts`.
    const look = currentLook();
    // Under a named app look the workspace modules AND the local graph
    // (seeded on the open note, `config.pane`) are answered together; the
    // graph's entries merge into the workspace's map, keyed by node index.
    // NOTION'S VIEW-ROW SEARCH filters the board in place: the cards the
    // query leaves, by title, case-folded; the count is what is left. A
    // value change, never a regeneration (0023) — the page is the same
    // page with fewer cards. The nav's Recent and the rest read every
    // object; only the board is narrowed.
    const boardNeedle = look.boardSearch ? state.searchQuery.trim().toLowerCase() : '';
    const boardFiltered = boardNeedle
      ? objects.filter((o) => (typeof o['title'] === 'string' ? o['title'] : '').toLowerCase().includes(boardNeedle))
      : objects;
    const boardFilterCount = boardNeedle ? boardFiltered.length : undefined;
    // THE COLUMNS IN THE PROPERTY'S DECLARED ORDER — the fifteenth verdict's
    // third item. The renderer groups first-seen and never sorts (a taste
    // decision the spec did not ask for); the host owns the declaration, so
    // under the look's board it hands the objects over in option order,
    // stably, and first-seen IS the declared order. The board only: Notion's
    // table is unsorted by default, and the calendar and timeline have no
    // columns — but the timeline's LANES do, first-seen, and its bars are
    // coloured by lane index as the board's columns are by position, so it
    // takes the same order and the two agree (the sixteenth verdict:
    // Notion-like colour inside the bars).
    const groupRole = ir.nodes.find((n) => n.kind === 'collection' && n.query?.groupBy)?.query?.groupBy;
    const laneRole = ir.nodes.find((n) => n.kind === 'capability' && n.capability === 'timeline')?.channels?.['lane']?.role;
    const orderRole = state.templateView === 'board' ? groupRole : state.templateView === 'timeline' ? laneRole : undefined;
    const boardObjects = look.boardOptionOrder && orderRole
      ? inOptionOrder(boardFiltered, orderRole) : boardFiltered;
    // FIBERY'S TYPE BADGE: every card opens with its entity's type, which is
    // the pool's typeHint — the host's fact, handed to the stylesheet as a
    // variable so the module draws it without knowing any pool.
    if (look.typeBadge) document.body.style.setProperty('--fb-badge', JSON.stringify(pool.typeHint.toUpperCase()));
    else document.body.style.removeProperty('--fb-badge');
    // NOTION'S BACK AND FORWARD: the place this paint shows, against the
    // last. A new place pushes the old onto the back stack and empties
    // forward, as a browser does; the paint a chevron caused only moves
    // "here". Leaving the look forgets the history.
    if (look.placeHistory) {
      const here: NtPlace = { view: state.templateView, cardinality: state.cardinality,
        selected: state.cardinality === 'single' ? selected : undefined };
      const h = state.ntHistory;
      if (h.here && !h.moving && !samePlace(h.here, here)) { h.back.push(h.here); h.forward = []; }
      h.moving = false;
      h.here = here;
    } else if (state.ntHistory.here) {
      state.ntHistory = { back: [], forward: [], here: undefined, moving: false };
    }
    // Fabric's composer states the library's size; a single lens holds one
    // row, so the size is read from the vault's pages when the look is on.
    if (look.assistant) {
      libraryTotal = (await everyVaultPage()).reduce((n, p) => n + p.objects.items.length, 0);
    }
    const none: CapabilityInputs = new Map();
    const capabilityInputs: CapabilityInputs | undefined = state.template
      ? new Map([
        ...workspaceInputs(ir, objects, backlinkCount, wsSearchCount, boardFilterCount),
        ...(selected !== undefined
          ? (await forceGraphInputs(ir, [{ id: selected }])) ?? none : none),
        // A look's calendar or timeline VIEW is the same module the host's
        // own views draw, answered the same way, over the board's objects.
        // Under Notion's look the calendar walks month by month and has a
        // Today, so the host answers any month and hands over its clock.
        ...(ir.nodes.some((n) => n.capability === 'calendar')
          ? calendarInputs(ir, boardObjects, MAY_WRITE ? epoch : undefined, state.calendarMonth, look.calendarToday ? localToday() : undefined) ?? none : none),
        ...(ir.nodes.some((n) => n.capability === 'timeline')
          ? timelineInputs(ir, boardObjects, MAY_WRITE ? epoch : undefined, laneRole ? declaredOptions(laneRole) : undefined) ?? none : none),
        // A look's SHEET (Word) is the document-flow module the host's own
        // document view draws, answered the same way: its sections and
        // their names, for the contents.
        ...(ir.nodes.some((n) => n.capability === 'document-flow')
          ? documentSections(ir, objects, labelsFor(pool)) ?? none : none),
      ])
      : state.view === 'graph'
      ? await forceGraphInputs(ir, rows.items)
      : state.view === 'plot'
        ? continuousPlotInputs(ir, objects, rows.total, plotAxes(shape).label)
        : state.view === 'document'
          ? documentSections(ir, objects, labelsFor(pool))
          : state.view === 'calendar'
            // The SAME `epoch` the renderer is about to stamp on every other
            // anchor — already incremented above, and handed to `renderHTML`
            // unchanged below. An event carrying a different one is a control
            // that resolves to nothing, which is why this reads the variable
            // rather than recomputing it.
            ? calendarInputs(ir, objects, MAY_WRITE ? epoch : undefined, state.calendarMonth)
            : state.view === 'timeline'
              ? timelineInputs(ir, objects, MAY_WRITE ? epoch : undefined)
              : undefined;

    // Resolved BEFORE the render callback, which is synchronous — and `timed`
    // measures the render rather than the store read that fed it.
    const choices = await choicesFor(pool, collection);
    html = await timed('render', () => renderHTML({
      ir, objects: boardObjects, sources: { siblings }, totals,
      // Absent unless the lens actually has several regions. The renderer
      // treats a supplied map as the host taking responsibility for naming
      // every subject region, and `{}` as the same defect as a missing key.
      ...(regionSets ? { regionSets } : {}),
      ...(regionTotals ? { regionTotals } : {}),
      ...(capabilityInputs ? { capabilities: capabilityInputs } : {}),
      // Spread rather than passed as `undefined`: `exactOptionalPropertyTypes`
      // draws a real distinction between "no rollups" and "rollups: undefined",
      // and the renderer's own contract is that ABSENT means draw nothing.
      ...(folds ? { rollups: folds } : {}),
      scheme: state.scheme, labels: labelsFor(pool),
      // The workspace's own facts for a named app look: the vault the
      // explorer is a tree of (0055).
      ...(state.template ? { workspace: {
        vault: pool.label,
        ...(look.vaultInert ? { inert: look.vaultInert } : {}),
        ...(wsBacklinksNote !== undefined ? { backlinksNote: wsBacklinksNote } : {}),
        ...(wsUnlinked !== undefined ? { unlinkedMentions: wsUnlinked } : {}),
        sidePanes: state.sidePanes,
        ...(look.opensPaneOver
          ? { opensPaneOver: look.opensPaneOver as Record<string, RegionPurpose> } : {}),
        ...(state.searchQuery ? { searchQuery: state.searchQuery } : {}),
        ...(state.folded.length ? { folded: state.folded } : {}),
        ...(state.mainView === 'graph' ? { mainView: 'graph' as const } : {}),
      } } : {}),
      // Absent for a reader, so the renderer mints no editable element. See
      // `viewerMayWrite`.
      ...(MAY_WRITE ? { edit: { epoch, grants } } : {}),
      choices,
      tones: tonesFor(pool),
    }));
    painted = { ir, lens, grants };
    lastIR = ir;
    // Graph is a transient capability projection over the generated lens, not
    // an accepted lens of its own. The drop host must never commit that
    // synthetic root into taste state; structural editing resumes when the
    // ordinary generated view is painted again.
    lastSpec = state.view === 'auto' ? shown : undefined;

    // Name the presentation the renderer derived. This is the line that makes
    // the claim checkable rather than decorative: nobody chose "table", the
    // shape of the content did.
    // The FIRST collection is the outline panel when the shell is on, and
    // reporting its presentation would make the demo's own claim describe the
    // sidebar instead of the content it is standing next to.
    const from = contentRootOf(ir);
    const coll = ir.nodes.find((n) => n.i >= from && n.kind === 'collection');
    const mode = coll ? presentationOf(ir, coll, { single: objects.length <= 1 }) : 'single object';
    $('derived').textContent = state.view === 'auto' ? mode : state.view;
    $('ir').textContent =
      `${ir.nodes.length} nodes · depth ${Math.max(...ir.nodes.map((n) => n.depth))} · ` +
      `hash ${ir.hash} · ${ir.tokens.typography}/${ir.tokens.density}/${ir.tokens.palette}`;
    paintChrome(ir.tokens.palette);
  } catch (e) {
    if (e instanceof RefusedLayout) {
      refused = e.violations.map((v) => `${v.code}: ${v.message}`);
    } else { throw e; }
  }
  const ms = (performance.now() - t0).toFixed(2);

  // Before the swap: the island is moved rather than rebuilt, but focus does
  // not survive its node leaving the document even for one statement.
  rememberCanvasCaret();

  /**
   * STAGED, then swapped — not written straight into the page.
   *
   * The generated tree is built in a detached element so that every canonical
   * read `prepareDocumentCanvases` makes happens while the live canvas island
   * is still in the document. Decision 0031 requires exactly that: *"No await
   * may sit between removing the island from its old live parent and inserting
   * it into its new live parent."*
   *
   * Writing `html` into `$('stage')` first — which is what this did — orphans
   * the editor for the length of an IndexedDB round trip per note. Nothing
   * looks wrong; the keystrokes in that window simply do not arrive.
   */
  const staged = document.createElement('div');
  staged.innerHTML = refused.length
    ? `<div class="refused"><strong>The runtime refused this layout.</strong>` +
      `<ul>${refused.map((r) => `<li>${r}</li>`).join('')}</ul>` +
      `<p>Refusing is the designed behaviour — a layout that would render illegibly ` +
      `is a failed layout, not one to ship slightly worse.</p></div>`
    : html;

  /**
   * Stamped on the STAGED tree, before the canvases are prepared against it.
   *
   * A single-object lens has no element carrying the object, so the host stamps
   * the anchor onto the root by hand — and `prepareDocumentCanvases` finds its
   * mount point by reading that anchor. Staging moved the preparation earlier
   * than the stamping and the reader route stopped mounting anything at all:
   * `e2e/cross-view.mjs` went 8/8 to 5/8, on three clauses that say nothing
   * about repaints.
   *
   * It is re-run against the live tree below. Stamping twice is idempotent;
   * stamping too late is not.
   */
  if (painted) completeSingleAnchor(painted.ir, staged);
  const transplant = painted
    ? await prepareDocumentCanvases(painted.ir, staged)
    : undefined;

  // --- the synchronous commit boundary. No `await` between these two lines. ---
  transplant?.();
  $('stage').replaceChildren(...staged.childNodes);

  /**
   * AFTER the DOM is replaced, and this is the second time that sentence has
   * had to move to stay true.
   *
   * It used to sit beside `painted = { ir, lens, grants }`, with a comment
   * saying exactly this — and staging then put an `await` between them.
   * @Codex, P2 on `4261caa`: *"this sweep revokes its object URL while the old
   * DOM is still mounted; `prepareDocumentCanvases()` can then await canonical
   * reads before `replaceChildren()`, leaving a decoding image or playing
   * audio/video backed by a revoked URL for up to the quiescence ceiling."*
   *
   * A revoked URL under a live `<img>` is a broken image rather than a freed
   * byte, and under a playing `<video>` it is a stall. The old comment was not
   * wrong when it was written; the code moved out from under it, which is why
   * this one names the line it must stay below rather than the phase it used to
   * follow.
   *
   * Outside the `try` as well as after the swap: a refused layout replaces the
   * stage with the refusal markup, which draws no media, so that pass's URLs
   * are exactly the ones a sweep should revoke.
   */
  blobUrls.sweep();

  // Restore only after the replacement exists. Between slots hold two typed
  // endpoints in one list value; ordinary slots hold one scalar.
  for (const el of $('stage').querySelectorAll('[data-morph-vs]')) {
    if (!(el instanceof HTMLInputElement)) continue;
    const i = Number(el.getAttribute('data-morph-vs'));
    const held = viewStateValues[i];
    if (held?.k === 'list') {
      const bound = Number(el.getAttribute('data-morph-vs-bound'));
      const value = Number.isInteger(bound) ? held.v[bound] : undefined;
      if (value) el.value = String(value.v ?? '');
    } else if (held !== undefined) {
      el.value = String((held as { v: unknown }).v ?? '');
    }
  }

  // After the DOM exists, so the host's delegated listeners are looking at the
  // pass they were told about. `bind` is also what registers this lens's grants
  // with the store, which is why an intent whose grant id was never minted fails
  // a LOOKUP rather than a comparison.
  if (painted) {
    applyViewState();
    applyFoldState();
    completeSingleAnchor(painted.ir);
    // `bind` is what REGISTERS this lens's grants with the store. A reader's
    // pass is never bound, so a forged intent fails a lookup rather than a
    // comparison — the same reasoning the comment below gives for binding.
    if (MAY_WRITE) host?.bind({
      ir: painted.ir, lens: painted.lens, epoch, grants: painted.grants,
      // The panel's rows are on screen too. An edit host that indexed only the
      // subject would have no revert target for anything painted from them.
      //
      // And so are the REGIONS'. When a lens splits around a view-state control
      // each region is answered by its own store read, so a row painted below
      // the control need not appear in `objects` at all — `objects` is the
      // lens's own projection, and region B's rows come from a different query.
      // Binding without them left every editable field in a region indexed
      // nowhere, which the header above already names the consequence of:
      // `opening` records as undefined and Escape writes an empty string over a
      // real value. Found by @Codex reviewing #137; it is the same defect
      // `sources` was introduced for, arriving by a second route.
      //
      // `lastRegionSets` rather than the local, because it is `{}` when the
      // lens has one region — spreading an empty map is the no-op the local's
      // `undefined` would have needed a branch to express.
      objects, sources: { ...lastRegionSets, siblings },
    });
  }
  mountManualHideControls();
  applyTemplateChrome();
  settleCanvasFocus();
  renderPicker();

  // Every phase, named. `e2e/frame-time.mjs` keys on "in NN ms" and on this
  // line changing, so the total keeps that shape and the breakdown follows it.
  const breakdown = ['compile', 'regions', 'rollups', 'render']
    .filter((k) => phase[k] !== undefined)
    .map((k) => `${k} ${phase[k]!.toFixed(2)}`)
    .join(' · ');
  $('timing').textContent =
    `${objects.length} objects · ${db.backend} · ${editableCount()} editable · `
    + `built in ${ms} ms`
    + (breakdown ? ` (${breakdown})` : '');
  $('spec').textContent = JSON.stringify(shown, null, 2);
  try {
    $('genome').textContent = JSON.stringify(project(shown), null, 2);
  } catch (e) {
    $('genome').textContent = `gate refused: ${String(e)}`;
  }
  renderSuggestion();
  renderTaste();
  renderFindNote();
  await renderBacklinks(single ? selected : undefined);
  await renderAside();
}

/** The suggestion surface. Focus is never stolen; Escape always reaches it;
 *  a preview never commits. Those are runtime policy, not spec. */
function renderSuggestion() {
  const el = $('suggest');
  const offered = state.lens.offered;
  if (!offered) {
    const next = pickCandidate(CANDIDATES, state.dismissedCandidates, state.taste);
    el.innerHTML = (next
      ? `<button class="trigger" id="trigger" aria-label="A layout suggestion is available">
           <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">
             <rect x="1" y="1" width="9.5" height="9.5"/><rect x="5.5" y="5.5" width="9.5" height="9.5"/>
           </svg></button>`
      : '<span class="quiet">nothing to suggest</span>') + POOL_BUTTON;
    /**
     * Handled, not fire-and-forget — the FOURTH promise on this page to be
     * found dropped at a click boundary.
     *
     * @Codex, P2 on `9bd1b63`: *"In the ordinary suggestion path, if this
     * effective read rejects — for example because its store read or derived-role
     * provider fails — the rejection escapes `openOffer()`, while the `#trigger`
     * listener invokes that async function without awaiting or catching it. The
     * person receives no refusal and the button appears inert, unlike the pool
     * suggestion path which explicitly catches and reports its failures."*
     *
     * The pool button next to it got exactly this fix one round earlier, from
     * exactly this reviewer, and its comment says the words that apply here
     * unchanged: the press is never silent — it offers or it says why not.
     * `openOffer` acquired an `await db.effective.read(...)` when the shape door
     * moved onto `0030`'s boundary, and the handler was written when there was
     * nothing in it that could reject.
     *
     * Caught here rather than inside `openOffer`, because the whole of that
     * function is the person's press: a compile that throws is as silent as a
     * read that does, and a `try` around one line would be the fix landing on
     * one of two identical doors again.
     *
     * **This has no clause, and task `0146` is that gap rather than an
     * oversight.** Nothing in the page can make an effective read reject —
     * there is no `__morphDb` — and adding a seam that could is a second door
     * onto the store, which is the thing `e2e/pool-gesture.mjs` declines to
     * build for the pool in its own words. So the claim here is argued and not
     * measured, which is worth saying out loud in the place somebody would look
     * for the clause.
     */
    if (next) {
      $('trigger').addEventListener('click', () => {
        openOffer(next).catch((why: unknown) => {
          say(`that suggestion could not be prepared: `
            + (why instanceof Error ? why.message : String(why)), 'bad');
        });
      });
    }
    wirePoolButton();
    return;
  }
  const previewing = offered.status === 'previewing';
  el.innerHTML = `
    <div class="offer${previewing ? ' previewing' : ''}">
      <div class="offer-body">
        <strong>${offered.rationale.split('—')[0]}</strong>
        <p>${offered.rationale}</p>
      </div>
      <div class="offer-actions">
        ${previewing ? '<span class="lbl">previewing</span>' : ''}
        <button class="btn" id="prevBtn">${previewing ? 'Revert' : 'Preview'}</button>
        <button class="btn btn-quiet" id="dropBtn">Not this</button>
        <button class="btn btn-primary" id="keepBtn">Keep</button>
      </div>
    </div>` + POOL_BUTTON;
  wirePoolButton();
  $('prevBtn').addEventListener('click', () => {
    state.lens = previewing ? revert(state.lens) : preview(state.lens);
    void run();
  });
  $('keepBtn').addEventListener('click', () => {
    const c = pendingCandidate;
    const pooled = pendingPoolTaste;
    state.lens = keep(state.lens);
    // Any pool request still in flight is now about a lens nobody is looking
    // at — see `poolRequest`.
    poolRequest += 1;
    contributeAcceptedLens('kept');
    if (c) {
      state.taste = { ...state.taste, ...c.taste };
      state.learned.push(c.what);
    } else if (pooled) {
      // Decision 0040's loop, closed: accepting a pooled lens is ordinary
      // personal evidence. Requesting or previewing it was not.
      state.taste = { ...state.taste, ...pooled };
      state.learned.push('a layout from the pool');
    }
    pendingPoolTaste = undefined;
    void run();
  });
  $('dropBtn').addEventListener('click', () => {
    // Both ends. The lens stops re-offering that SPEC; the picker stops
    // offering that CANDIDATE. Recording only the first is what made the
    // button dead — the same suggestion returned and was then correctly
    // refused, which looks exactly like nothing happening.
    poolRequest += 1;
    if (pendingCandidate) {
      state.dismissedCandidates = dismissCandidate(state.dismissedCandidates, pendingCandidate);
    }
    state.lens = dismiss(state.lens);
    void run();
  });
}

/**
 * The explicit gesture decision 0039 requires by name.
 *
 * *"The demo must expose an explicit 'Suggest from the pool' gesture. Invoking
 * it takes one snapshot of eligible genomes and starts one generation request …
 * Opening a document, accepting a lens, or a stranger contributing a popular
 * genome does not read the pool."* Rendered beside the ordinary trigger rather
 * than in place of it: they are different offers and a person should be able to
 * tell which one they asked for.
 */
/**
 * An ICON, the size of the trigger beside it — not a text button.
 *
 * It was a `btn btn-quiet` reading "Suggest from the pool" for about an hour,
 * and that broke `e2e/first-object.mjs`: `.rail` is a fixed-height flex row and
 * `.rail-end` is `flex:0 0 auto`, so a wide control there cannot shrink. It
 * overflowed onto `#pools` and Playwright reported the truth plainly —
 * *"<button id=\"poolSuggest\"> … intercepts pointer events"* — while trying
 * to click a pool pill.
 *
 * The accessible name is kept on `aria-label`, because that is what a person
 * using a screen reader hears and what @Codex's deployed drive checked for.
 * Three overlapping circles: the pool is other people's layouts, pooled.
 */
const POOL_BUTTON =
  '<button class="trigger" id="poolSuggest" aria-label="Suggest from the pool">'
  + '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2"'
  + ' aria-hidden="true"><circle cx="6" cy="6" r="4"/><circle cx="10" cy="6" r="4"/>'
  + '<circle cx="8" cy="10" r="4"/></svg></button>';

function wirePoolButton(): void {
  $('poolSuggest').addEventListener('click', () => {
    /**
     * Handled, not `void`-ed. @Codex, P2 on #184: *"If the store read aborts
     * while deriving the shape, or the injected generator throws,
     * `openPoolOffer()` rejects through this fire-and-forget click handler. The
     * person then receives no refusal or recovery message — only an unhandled
     * page error — so the new explicit pool button appears to do nothing."*
     *
     * The third `void` on a promise this review has found, after the two on
     * `createObject`. `void` reads as "I have considered this and it cannot
     * fail", and every time it has meant "nobody has looked".
     *
     * The button's own contract is that the press is never silent — it offers
     * or it says why not — and `e2e/pool-gesture.mjs` asserts exactly that, so
     * a rejection reaching nobody breaks a clause that is already written.
     */
    openPoolOffer().catch((why: unknown) => {
      say(`the pool could not answer: ${why instanceof Error ? why.message : String(why)}`,
        'bad');
    });
  });
}

/**
 * Rethrow only while the work is still the current work.
 *
 * A superseded request's failure is not news: the person has already asked
 * something else and that answer is on screen. Reporting the older rejection
 * overwrites a newer success with a message about a decision they have already
 * changed — which is the mirror of discarding a superseded COMPLETION, and the
 * door does that silently and deliberately.
 *
 * `undefined` on the swallowed path, so the caller's `await` yields a value it
 * can test rather than a promise that never settles.
 */
const rethrowIfCurrent = (current: () => boolean) => (why: unknown): undefined => {
  if (current()) throw why;
  return undefined;
};

/** Where the person is, as one comparable string. A pool result compiled for
 *  one surface must not be installed on another — @Codex, P2 on #184. */
const surfaceNow = (): string =>
  `${pool?.id ?? ''}\u0000${selected ?? ''}\u0000${state.cardinality}\u0000${state.context}`;

/**
 * Which pool request is the current one.
 *
 * @Codex, P2 on #184: *"When the person starts a pool request and then changes
 * palette/taste, Keeps the current offer, or starts another suggestion request
 * before the first finishes, all four fields in this key can remain unchanged.
 * The older completion consequently passes the guard and can replace the newer
 * offer or install a spec compiled from the previous taste state."*
 *
 * Right, and the reason a surface key cannot be repaired into one is that it
 * describes WHERE the person is and the question is WHEN. Two presses on one
 * surface are two requests with identical keys; so are a press and the taste
 * change that follows it. A counter answers both because it is about ordering
 * rather than about place, and it needs no list of the inputs that matter —
 * which is the other half of why the key kept needing fields added to it.
 *
 * Bumped by everything that supersedes an in-flight request: another press,
 * an acceptance, a dismissal, a taste change. The surface key stays: it catches
 * the case where the person is somewhere else entirely, and a stale result is
 * then wrong for two reasons rather than one.
 */
let poolRequest = 0;

/**
 * The taste a request was compiled against, as one comparable string.
 *
 * @Codex, P2 on #184: *"When a pool request is awaiting compilation and the
 * person uses Hide, Promote, or a taste-correction button, those handlers
 * mutate `state.taste` and repaint without incrementing `poolRequest`; the
 * completion therefore passes this ordering check and can install an offer
 * compiled from the taste state that the person just changed."*
 *
 * Right, and the token alone could not have covered it without a bump in every
 * one of those handlers — which are `0113`'s files, and which is a list that
 * grows. So the check is the STATE rather than a count of the events that
 * change it: a request whose taste no longer matches was compiled against a
 * person who has since said something else, however they said it and whoever
 * owns the handler.
 *
 * Both guards stay, because they answer different questions. The token orders
 * two requests that are otherwise identical — two presses on one surface with
 * no taste change between them. This one catches a change the token cannot see
 * because nothing bumped it.
 */
const tasteNow = (): string => JSON.stringify(state.taste);

async function openPoolOffer(): Promise<void> {
  const from = surfaceNow();
  const taste = tasteNow();
  const mine = (poolRequest += 1);
  /**
   * Still the current request — asked by the FAILURE path as well as by the
   * success one, which is the whole of this round's fix.
   *
   * @Codex, P2 on #184: *"If a person starts a second pool request or navigates
   * away and that newer action succeeds before the older request rejects, this
   * handler still calls `say()` for the stale failure, overwriting the status
   * with 'the pool could not answer' even though the current request
   * answered."*
   *
   * The token, the surface key and the taste string were all added for the
   * completion path and each of them was argued for on the same ground: an
   * answer about a moment that has passed must not be installed over one about
   * now. A rejection is an answer about a moment that has passed. It was
   * exempt only because it leaves this function by a different door.
   *
   * The same shape as the ingest door's `onlyIfCurrent` two thousand lines up,
   * which discarded a superseded completion in silence and shouted a superseded
   * failure — both found in the same review round, in code nobody would think
   * to compare.
   */
  const current = (): boolean =>
    surfaceNow() === from && poolRequest === mine && tasteNow() === taste;
  /**
   * Through the EFFECTIVE read boundary, which is where the render path gets
   * its shape.
   *
   * @Codex, P2 on `4261caa`: *"When the current scope contains provider-derived
   * roles — most notably a migrated document whose `body` comes from canonical
   * blocks — this raw store shape omits those roles even though the rendered
   * surface includes them. The pool request can consequently refuse the
   * accepted lens as incompatible because `body` appears absent, or compile a
   * suggestion for a shape different from the one on screen."*
   *
   * Decision `0030` says it in one sentence: *"All consumers that request shape
   * plus values use the same configured read boundary."* The render path has
   * used `db.effective.read` since the day that decision landed, and this door
   * — which produces a lens the render path then paints — was still asking the
   * raw store. `limit: 0` because only the shape is wanted: the shape is
   * derived over the whole scope regardless, which is the same call the
   * collection-roles line makes a thousand lines up.
   */
  const shape = await db.effective
    .read(lensScope(state.cardinality === 'single'), { limit: 0 })
    .then((r) => r.shape)
    .catch(rethrowIfCurrent(current));
  /**
   * A SWALLOWED stale failure is a STOP, not a shape — @Codex, P2 on
   * `40b56c2`. `rethrowIfCurrent` returns undefined exactly when the person
   * has navigated on, and the `as ObjectShape` cast hid that: the pool was
   * then asked to suggest for an undefined shape, `retrieveGenomes`
   * dereferenced it, and the click handler reported the resulting crash
   * over the newer surface — the very stale noise the predicate exists to
   * suppress. The later `out` result already checks this; the shape now
   * does too.
   */
  if (shape === undefined) return;
  const out = await privatePool.suggest({
    shape, context: state.context,
    /**
     * The palette default is deliberately NOT passed here.
     *
     * @Codex, P2 on #184: *"`state.palette` … is initialized to `neutral` even
     * before the user chooses anything. `PrivatePoolSuggestions.suggest()`
     * spreads `request.taste` after the pooled taste, so this value overwrites
     * every retrieved palette winner; a warm-only contribution can change the
     * aggregate in unit tests but can never change the palette of an offer
     * produced by this UI."*
     *
     * Correct, and it made one of `0040`'s three voted knobs dead in the
     * product while alive in the suite. A palette the person actually picked
     * still outranks the pool — that is the rule everywhere else — but a
     * default nobody chose is not a preference and must not silence a stranger
     * who has one.
     */
    taste: { ...(paletteChosen ? { palette: state.palette } : {}), ...state.taste },
    ...(state.lens.accepted ? { accepted: state.lens.accepted.spec } : {}),
    declaredRoles: declaredRolesNow(),
    enumOptionCount: await enumOptionCountNow(),
  }, manualTaste.ledger).catch(rethrowIfCurrent(current));
  // Swallowed above only when superseded, and a swallowed rejection leaves
  // nothing to install. The currency check below catches this too, but saying
  // it here keeps the type honest.
  if (out === undefined) return;
  /**
   * Navigated while it was thinking, so the answer is about somewhere else.
   *
   * @Codex, P2 on #184: *"If the user presses the pool button and switches
   * pools, objects, cardinality, or context while `deriveShape()` or generation
   * is pending, this applies the completed spec to the current `state.lens`
   * even though it was generated from the earlier scope's shape."*
   *
   * The same shape as the ingest P1 in the round before, on the other async
   * path. Discarded in silence, because the person has already moved on and a
   * notice about a surface they left is noise.
   */
  if (surfaceNow() !== from || poolRequest !== mine || tasteNow() !== taste) return;
  // The pool said nothing — an empty pool, or one that abstained. Refusing here
  // rather than compiling is @Codex's deployed finding: a baseline layout
  // labelled "Suggested from the pool" is a layout the pool had no part in,
  // presented to a person as the pool's opinion.
  if (!out.pooled) { say(out.why, 'bad'); return; }
  if (!out.outcome.ok) {
    say(`the pool's suggestion did not compile — ${out.outcome.reason}`, 'bad');
    return;
  }
  const spec = out.outcome.spec;
  const next = offer(state.lens, spec, out.outcome.provenance, 'Suggested from the pool');
  // `offer()` is deliberately silent when the spec is already accepted or was
  // dismissed. Correct for an ambient offer, wrong for a press — see
  // `refusedOffer`. @Codex found this on the deployed build.
  const refused = refusedOffer(state.lens, next, spec);
  if (refused) { say(refused, 'bad'); return; }
  /**
   * Cleared only once the pool offer has actually REPLACED the visible one.
   *
   * @Codex, P2 on #184: *"If an ordinary candidate is already visible and the
   * user presses the pool button, a pool result equal to the accepted or
   * dismissed spec makes `offer()` return the existing lens and the refusal
   * path exits without repainting it. This line has nevertheless erased the
   * still-visible ordinary candidate's metadata, so pressing Keep skips its
   * taste update and pressing Not this skips `dismissCandidate`."*
   *
   * A refusal must leave the screen exactly as it was, and that includes the
   * state behind the buttons on it.
   */
  pendingCandidate = undefined;
  /**
   * What the pool taught, so Keep can record it.
   *
   * @Codex, P2 on #184: keeping a pool offer *"accepts and republishes its spec
   * but never teaches the person's taste model, so the preference disappears
   * once that accepted revision is replaced."*
   *
   * Decision 0040 is explicit that it should: *"Accepting the resulting lens
   * creates ordinary personal evidence through the existing loop; merely
   * requesting or previewing it does not."* A pool offer is not one of
   * `CANDIDATES`, so there was no `c.taste` for `keepBtn` to credit and the
   * loop simply ended. This is that missing half.
   */
  pendingPoolTaste = out.pooled ? out.taste : undefined;
  state.lens = next;
  /**
   * THROUGH THE SERIALIZED DOOR — @Codex, P2 on `2ffce98`. This install
   * follows the offer's long awaits (store reads, blob resolution, compile),
   * and a direct `run()` here raced whatever `schedule()` had in flight: the
   * newer scheduled pass could finish first and this older one then replace
   * `#stage` with an obsolete scope — the two passes also share `blobUrls`'
   * single-pass sweep bookkeeping and could revoke URLs backing the live
   * DOM. `schedule(false)` serializes without bumping `poolRequest`, so an
   * offer still never supersedes itself.
   */
  schedule(false);
}

/**
 * Every row a person can currently SEE, across all three sets the render fills.
 *
 * Named once because the same omission has now been found twice, by @Codex,
 * in the two places that ask "which objects are on screen":
 *
 * ```
 * the invocation resolver   searched `objects` alone; a button in a sibling
 *                           card resolved every role argument to `undefined`
 * this leak check           searched `objects` and `siblings`; a row visible
 *                           only in a split lens's region was not a needle
 * ```
 *
 * `lastRegionSets` is the one that keeps being missed, and the reason is
 * structural rather than careless: `objects` and `siblings` are two names, so
 * a reader writes two names, while the regions are a RECORD whose keys depend
 * on the spec. A third consumer would have written the same two names again.
 *
 * The outer array is fresh and the region sets are COPIED — their declared
 * type is `readonly BoundValues[]`, and the callers' `BoundValues[][]` is not,
 * so a copy is what makes the shape honest rather than cast away.
 *
 * `objects` and `siblings` are handed out live, exactly as both call sites
 * already read them. Both flatten and iterate synchronously, within one turn,
 * so there is no window for the next render to replace one underneath a caller
 * — and copying them would be a claim of safety this function cannot make,
 * since a caller holding the result across an await is unsafe either way.
 */
function everySetOnScreen(): BoundValues[][] {
  return [objects, siblings, ...Object.values(lastRegionSets).map((set) => [...set])];
}

/**
 * The private strings this host knows, for the pool's leak assertion.
 *
 * Decision 0039 requires the complete projection to be checked against *"the
 * private values known at the contribution boundary"*, and this is that
 * boundary. Titles because they are the person's words; object ids because they
 * identify a vault; the viewport because decision 0014 makes position view
 * state that must never travel.
 *
 * Deliberately generous rather than minimal. A value listed here that the
 * projection was never going to carry costs one string comparison.
 *
 * **What an omission costs, exactly**, because the sentence that stood here
 * said *"a hole in the only check standing between a vault and the pool"* and
 * that is not what these strings do. `assertNoLeak` decides with
 * `assertClosedVocabulary` — a membership walk over the genome — and consults
 * the needles only to NAME what it already refused. Read rather than recalled:
 * `packages/spec/src/pool.ts`, `if (!outside.length) return;` before the
 * needles are touched at all.
 *
 * So a missing needle does not admit a leak; it produces *"… is outside the
 * closed vocabulary decision 0039 requires"* where it could have produced
 * *"private value \"…\" found in genome at …"*. That is the difference
 * between a refusal somebody can act on and one they have to go and hunt, on
 * the privacy path, which is worth the loop below being complete. It is not a
 * reason to treat this list as the boundary.
 */
function privateValuesNow(): readonly string[] {
  const values = new Set<string>();
  /**
   * Every string ANYWHERE in the value, not only at the top.
   *
   * @Codex, P2 on #184: *"This only collects top-level strings longer than two
   * characters, although `BoundValues` can contain arrays and nested objects —
   * for example, expanded relation targets … so a related object's title is
   * absent from the needles supplied to `assertNoLeak`."*
   *
   * Right, and a related object's title is content belonging to someone the
   * person may not even have opened. Depth-bounded rather than trusting the
   * value to be acyclic: a `BoundValues` today is a projection and is, but a
   * privacy check is the wrong place to depend on that staying true.
   */
  // The traversal itself is `privateStringsIn`, in `private-pool.ts`: it is
  // the privacy-critical half, it is pure, and a function nothing can call on
  // its own is one nothing can test on its own — which is how it acquired a
  // depth cap that silently dropped legal data. @Codex, P2 on #184.
  const collect = (value: unknown): void => { privateStringsIn(value, values); };
  for (const row of everySetOnScreen().flat()) {
    // EVERY string the row carries, not just its title. A genome is supposed to
    // contain no content at all, so any of these appearing in one is a leak —
    // and enumerating roles by name would miss the one added next week.
    collect(row);
    // The handle identifies the object, and is a branded string — so the raw
    // value is what a leak would contain, not a JSON rendering of it.
    const handle = handleOf(row);
    if (handle) values.add(String(handle));
  }
  if (selected) values.add(String(selected));
  values.add(`${window.innerWidth}x${window.innerHeight}`);
  return [...values];
}

/** Whether the palette on screen was PICKED or is just the initial default.
 *  Only the pool path distinguishes them — see `openPoolOffer`. */
let paletteChosen = false;

/** Set only by the pool gesture, read only by Keep. See `openPoolOffer`. */
let pendingPoolTaste: TastePriors | undefined;

let pendingCandidate: (typeof CANDIDATES)[number] | undefined;
async function openOffer(c: (typeof CANDIDATES)[number]) {
  // An ordinary suggestion supersedes a pool request in flight, AND is itself
  // superseded by whatever comes after it. @Codex, P2 on #184: this bumped the
  // token and never captured it, so an ordinary compilation that started first
  // and finished second overwrote the newer pool offer — the same defect the
  // token was added for, on the path that raises it.
  const mine = (poolRequest += 1);
  const from = surfaceNow();
  const taste = tasteNow();
  // The same boundary as the pool door, and for the same reason: an ordinary
  // suggestion is compiled here and painted by the render path, so a shape that
  // omits a provider-derived role produces a lens missing a field the screen
  // has. @Codex flagged the pool door; this is its twin, and a fix that landed
  // on one of two identical doors is the shape `CLAUDE.md` lists as (a).
  /**
   * The try exists for the CATCH's guard, @Codex, P2 on `fe50877`: *"a thrown
   * rejection bypasses that guard entirely … the added reporter does not
   * apply the currency check already used by the adjacent pool-suggestion
   * path."* `createObject` had this exact defect this morning — every
   * RETURNED outcome gated on the ticket, every THROWN one loose — and the
   * fix is the same shape: only supersession is swallowed; a current
   * attempt's rejection is re-thrown unchanged, so the `#trigger` handler
   * stays the single reporter and `could not be prepared` stays reachable.
   */
  try {
  const shape: ObjectShape =
    (await db.effective.read(lensScope(state.cardinality === 'single'), { limit: 0 })).shape;
  const out = await compile(
    {
      shape,
      context: state.context,
      taste: { palette: state.palette, ...state.taste, ...c.taste },
      /**
       * The accepted spec travels with an ORDINARY suggestion too.
       *
       * @Codex, P2 on #184: *"When a person has kept or directly edited a
       * non-baseline arrangement and then invokes an ordinary suggestion,
       * `openOffer()` constructs its request without `state.lens.accepted`. As
       * a result, this new revision branch is bypassed and the suggestion is
       * compiled as a fresh baseline, so keeping it can silently discard the
       * person's hierarchy, placements, module configuration, and other
       * authored structure."*
       *
       * Decision `0045` is about what a REVISION is, not about which button
       * asked for one, and the revision path landed on the pool gesture
       * because that is the gesture it was written for. Two presses a person
       * cannot tell apart then meant two different things happened to their
       * arrangement — and the one that discards it is the one that looks
       * ordinary.
       *
       * Spelled identically to `openPoolOffer`'s line, deliberately: two
       * spellings of "pass the accepted spec if there is one" is how the next
       * suggestion path arrives without it.
       */
      ...(state.lens.accepted ? { accepted: state.lens.accepted.spec } : {}),
    declaredRoles: declaredRolesNow(),
    enumOptionCount: await enumOptionCountNow(),
    },
    stageGenerator);
  /**
   * CURRENCY FIRST, and this is the same ordering the success path has always
   * had, applied to the failure I added an hour before it was found.
   *
   * @Codex, P2 on #184: *"When an ordinary revision is compiling and the
   * person navigates, changes taste, or starts another suggestion, the
   * obsolete compilation can later return `AcceptedNoLongerLegal`. This branch
   * calls `say()` before the request-currency guard … so the stale failure
   * overwrites the current surface's status even though stale successful
   * results are discarded."*
   *
   * A stale success is discarded silently and a stale failure was announced
   * loudly — the noisier half of the pair going first is the one nobody
   * notices, because a message about a lens they left looks exactly like a
   * message about the lens they are on. The guard is one line above both
   * outcomes now, so there is nowhere for a third outcome to be added below
   * it and repeat this.
   */
  if (surfaceNow() !== from || poolRequest !== mine || tasteNow() !== taste) return;
  /**
   * And it SAYS so, because passing `accepted` gives this press a new way to
   * fail that it did not have before.
   *
   * `compile` turns `AcceptedNoLongerLegal` into `{ ok: false }` rather than
   * throwing — decision `0045` forbids falling through to a fresh baseline —
   * so the bare `return` that stood here would have been a trigger that does
   * nothing, which is *"indistinguishable from one that is broken"*, the
   * sentence two review rounds already wrote about this same function.
   *
   * The wording is `openPoolOffer`'s, minus the pool: `0045`'s recourse is to
   * ask again without the accepted spec, and a person can only choose that if
   * they are told which of the two things happened.
   */
  if (!out.ok) {
    say(`that suggestion did not compile — ${out.reason}`, 'bad');
    return;
  }
  const next = offer(state.lens, out.spec, out.provenance, c.why);
  // The same silence, on the same kind of press. @Codex found it through the
  // pool gesture; it was always here too, and a trigger that does nothing is
  // indistinguishable from one that is broken.
  // 'Morph', not 'the pool'. This press reads no pool and takes no snapshot, and
  // decision 0039 makes reading the pool an explicit occasion — so a sentence
  // claiming one happened is the product lying about a privacy boundary rather
  // than merely using the wrong noun. @Codex, P2 on #184.
  const refused = refusedOffer(state.lens, next, out.spec, 'Morph');
  if (refused) { say(refused, 'bad'); return; }
  // Same rule as the pool path: a refusal leaves the screen and the state
  // behind it untouched.
  pendingCandidate = c;
  pendingPoolTaste = undefined;
  state.lens = next;
  // The same serialized door as the pool install above, for the same race.
  schedule(false);
  } catch (why) {
    if (surfaceNow() !== from || poolRequest !== mine || tasteNow() !== taste) return;
    throw why;
  }
}

/**
 * The ONE place an accepted lens becomes a pool contribution.
 *
 * There were two acceptance paths and only one of them contributed. @Codex, P2
 * on #184: *"`attachDropHost.onCommit` also creates an accepted revision via
 * `keep(offer(...))` when a person rearranges the layout and never calls
 * `privatePool.contribute`. Consequently a directly customized accepted lens —
 * the strongest structural layout signal — does not enter the pool, despite
 * decision 0039 defining the contribution unit as an accepted spec. Route every
 * acceptance path through one contribution boundary rather than attaching the
 * producer to this single button."*
 *
 * Right on both counts, and the second one is why this is a function rather
 * than a copied call: a third acceptance path would have missed it again. The
 * comment beside `onCommit` already argued the same thing about taste —
 * *"routing it around the loop that records taste would throw away the most
 * direct signal the product ever gets"* — and the pool is that loop's other
 * half.
 *
 * Contribution CANNOT cost the acceptance. @Codex, P1 on the same review: a
 * throw here used to skip the taste update, the pending-state cleanup and
 * `run()`, leaving a stale offer after the person's press. A guard on the WRITE
 * was able to cancel the READ's consequences because they shared a statement
 * list. The refusal is still loud, in the person's own words, and the leak
 * guard is untouched — which is exactly what was asked for: *"catch and report
 * refusal at the pool-write boundary while allowing the local acceptance to
 * finish; do not weaken the leak guard."*
 *
 * It does not READ. Decision 0040's last control is that accepting a lens
 * performs no pool read and cannot repaint an accepted interface, so this is a
 * write and nothing follows from it.
 */
/**
 * THE LIKED SHELF'S MEMORY — @Codex, P1 on `4f1ea1b`, and the amendment's
 * own second clause: kept layouts were retained by the revision contract and
 * findable nowhere. Recorded at the one choke point both acceptance paths
 * already pass through, so a keep and a drag-rearrangement cannot diverge.
 * localStorage like the overlay beside it, verified before trust on the way
 * back out (a kept spec from an older vocabulary refuses to reopen rather
 * than rendering wrongly), capped so the shelf stays a shelf.
 */
const KEPT_KEY = 'morph.demo.kept.v1';
interface KeptLayout {
  /** Collision-free identity — the storage key and the open route. `at` is
   *  display and sort only: two tabs can keep within one millisecond
   *  (@Codex, P2 on `65952e8`), and a same-key write is the clobber the
   *  per-entry keys exist to prevent. Optional because v1 entries lack it;
   *  the read derives one from `at` for them. */
  readonly id?: string;
  readonly poolId: string; readonly what: string;
  readonly at: number; readonly spec: TLayoutSpec;
  /**
   * THE CONTEXT THE KEEP HAPPENED IN — @Codex, P1 on `7ad205c`. The first
   * shelf forced every reopen into large-set/auto, so a layout kept from a
   * single-object lens or the document view reopened against a scope it was
   * never accepted for. Optional, because v1 entries lack them: an entry
   * without a context reopens the collection, which is what v1 promised.
   */
  readonly cardinality?: 'single' | 'large-set';
  readonly view?: typeof state.view;
  /** Which of a look's VIEWS the page showed when starred — Notion's Board,
   *  Table, Calendar or Timeline — so it reopens on that one. */
  readonly templateView?: TemplateView;
  readonly selected?: ObjectId;
  /** The generation context the layout was accepted under — @Codex, P2 on
   *  `40b56c2`: a layout kept while Authoring and reopened under Browsing
   *  had its later revisions and pool suggestions judged against a context
   *  it was never accepted for. */
  readonly context?: Ctx;
}
/**
 * ONE localStorage KEY PER ENTRY — @Codex, P2 on `7cc5d2c`. The shelf was an
 * array under one key, and two tabs keeping at nearly the same time each
 * read the old array and wrote back only their own addition: the last
 * `setItem` silently discarded the other tab's layout. Distinct keys make
 * every write atomic for its own entry — there is nothing shared to clobber
 * — and the read collapses duplicates by identity, newest first. The legacy
 * array key is still read so existing shelves survive.
 */
const KEPT_PREFIX = `${KEPT_KEY}:`;
function keptIdentity(k: KeptLayout): string {
  return JSON.stringify(
    [k.poolId, k.cardinality ?? null, k.view ?? null, k.selected ?? null,
      k.context ?? null, k.spec]);
}
function readKeptLayouts(): KeptLayout[] {
  const found: KeptLayout[] = [];
  try {
    const legacy = JSON.parse(localStorage.getItem(KEPT_KEY) ?? '[]') as unknown;
    if (Array.isArray(legacy)) found.push(...legacy as KeptLayout[]);
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith(KEPT_PREFIX)) continue;
      const one = JSON.parse(localStorage.getItem(key) ?? 'null') as KeptLayout | null;
      if (one && typeof one.at === 'number') {
        found.push(one.id ? one : { ...one, id: String(one.at) });
      }
    }
  } catch { return found; }
  found.sort((a, b) => b.at - a.at);
  const seen = new Set<string>();
  return found.filter((k) => {
    const id = keptIdentity(k);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}
/** Ids this tab wrote to the shelf, so cleanup can only ever remove its own
 *  superseded entries — never another tab's fresh one. */
const keptMintedHere = new Set<string>();

/** The shelf's entries for the PAGE a look shows: this pool, a spec wearing
 *  this template. What Notion's star reports, and what its second press
 *  takes off. */
function keptPageEntries(): KeptLayout[] {
  if (!state.template) return [];
  return readKeptLayouts().filter((k) => k.poolId === pool.id
    && (k.spec as { template?: { id?: string } }).template?.id === state.template);
}

/** Entries off the shelf by their own keys — the star's second press. A v1
 *  entry in the legacy array has no key of its own and stays; the read-side
 *  dedup and the shelf both still show it, which is the truth. */
function forgetKeptLayouts(entries: readonly KeptLayout[]): void {
  try {
    for (const k of entries) if (k.id) localStorage.removeItem(`${KEPT_PREFIX}${k.id}`);
  } catch { /* the shelf re-reads */ }
}

/** Today in the person's own time zone, `YYYY-MM-DD` — what a calendar's
 *  Today means to the person looking at it, which `toISOString` (UTC) is not
 *  after their evening. */
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** A role's declared options, in order, from the pool's own def — or nothing. */
function declaredOptions(role: string): readonly string[] | undefined {
  return pool.defs.find((d) => d.role === role && d.options)?.options?.map(optionValue);
}

/**
 * The pool's declared value→tone mapping, for every enum role that has one —
 * decision 0063.
 *
 * From the pool DEFINITION, not from the store: `enumOptions` derives the
 * values it finds in the scope and `shape.ts` says outright that a store enum
 * key has no authoritative declared option set. The values a renderer offers
 * are observed; which category each one belongs to is authored, and the two
 * come from different places on purpose.
 *
 * A value with no declared tone is simply absent here, and `toneOf` derives
 * one from the text — stable, order-independent, and not guaranteed distinct.
 */
function tonesFor(p: { defs: readonly Def[] }): Partial<Record<Role, Record<string, number>>> {
  const out: Partial<Record<Role, Record<string, number>>> = {};
  for (const d of p.defs) {
    if (!d.options) continue;
    /* A NULL PROTOTYPE, because the KEYS ARE A PERSON'S WORDS — @Codex's P2
       on `d881f5b`. `__proto__` is a legal enum option, and assigning it into
       an ordinary object invokes the legacy prototype setter instead of
       creating an own entry: a sole option leaves `Object.keys(declared)`
       empty so the whole role is dropped, and an option among others reads
       back as `Object.prototype`. Either way the authored tone is discarded
       and the renderer hashes a fallback colour.

       Same shape as the ribbon mapping fixed one file over: a record whose
       keys come from outside the code cannot be an object literal. */
    const declared: Record<string, number> = Object.create(null) as Record<string, number>;
    for (const o of d.options) {
      const t = optionTone(o);
      if (t !== undefined) declared[optionValue(o)] = t;
    }
    if (Object.keys(declared).length > 0) out[d.role as Role] = declared;
  }
  return out;
}

/** The objects with the board's grouping role in the pool's DECLARED option
 *  order, stable within an option; a value that is not an option keeps its
 *  place after the declared ones. The pool's def is the declaration
 *  (`Def.options`); without one the objects pass through untouched. */
function inOptionOrder(objects: readonly BoundValues[], role: string): readonly BoundValues[] {
  const options = declaredOptions(role);
  if (!options) return objects;
  const rank = new Map(options.map((o, i) => [o, i]));
  const at = (o: BoundValues) => rank.get(String(o[role] ?? '')) ?? options.length;
  return objects.map((o, i) => ({ o, i }))
    .sort((a, b) => at(a.o) - at(b.o) || a.i - b.i)
    .map((x) => x.o);
}

/** Back or forward to a place under the look: its view, its cardinality
 *  and its object, then a paint. */
function goToPlace(place: NtPlace): void {
  state.templateView = place.view;
  if (state.cardinality !== place.cardinality) {
    state.cardinality = place.cardinality;
    ($('cardinality') as HTMLSelectElement).value = place.cardinality;
  }
  // Only an OPEN object is a place; back to the board keeps whatever the
  // board had selected, as the app does.
  if (place.cardinality === 'single' && place.selected !== selected) select(place.selected);
  schedule();
}

/** Each pool's candidate state, stashed on leave and restored on return —
 *  the accepted spec and the dismissed list are facts about ONE pool. */
const poolLenses = new Map<string, LensState>();

/** The ACCEPTED spec by default — Keep and a drop record a lens the person
 *  chose. Notion's star records the page ON SCREEN (`lastSpec`): under a
 *  look the generator's own emission is what the person is looking at, and
 *  a look has no accepted lens until something is kept under it. */
function recordKeptLayout(what: string, spec = state.lens.accepted?.spec): void {
  if (!spec) return;
  /**
   * Dedup by the COMPLETE reopening identity, not the spec alone — @Codex,
   * P1 on `2ffce98`; `keptIdentity` beside `readKeptLayouts` is the one
   * definition of what a duplicate is.
   */
  const at = Date.now();
  const entry: KeptLayout = {
    id: `${at.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    poolId: pool.id, what, at, spec,
    cardinality: state.cardinality, view: state.view, context: state.context,
    ...(selected !== undefined ? { selected } : {}),
    ...(currentLook().keepsTemplateView ? { templateView: state.templateView } : {}),
  };
  // Superseded same-identity entries are removed KEY BY KEY — each remove is
  // atomic for its own entry, so this cannot clobber another tab's different
  // layout the way a whole-array write could. The read-side dedup covers
  // whatever a race here leaves behind.
  const twin = keptIdentity(entry);
  // NO CAP — @Codex, P2 on `7ad205c`: slicing at twenty silently discarded
  // the twenty-first keep, and the shelf's contract is that every kept
  // layout stays findable. Storage quota is the real bound, and hitting it
  // is SAID rather than swallowed: the keep itself already succeeded (the
  // lens is accepted either way), only the shelf's memory of it failed.
  // PER-ENTRY KEY, never the shared array — @Codex, P2 on `7cc5d2c`: two
  // tabs keeping at once each read the old array and wrote back only their
  // own addition, and the last setItem silently discarded the other's
  // layout. A distinct key has nothing shared to clobber.
  //
  // THE REPLACEMENT LANDS BEFORE THE PREDECESSOR GOES — @Codex, P2 on
  // `40b56c2`: deleting the same-identity entries first meant a quota
  // failure on the new write erased a previously openable layout while the
  // message claimed only the new recording failed. Write first; tidy only
  // once the write succeeded. The read-side dedup keeps the shelf correct
  // in between and after any race.
  try {
    localStorage.setItem(`${KEPT_PREFIX}${entry.id}`, JSON.stringify(entry));
    keptMintedHere.add(entry.id!);
  } catch {
    say('kept — but the shelf is out of browser storage and could not record it', 'bad');
    return;
  }
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith(KEPT_PREFIX) || key === `${KEPT_PREFIX}${entry.id}`) continue;
      const one = JSON.parse(localStorage.getItem(key) ?? 'null') as KeptLayout | null;
      // ONLY keys this tab itself wrote — @Codex, P2 on `6fd9613`: two tabs
      // keeping the same identity could each collect the OTHER's fresh entry
      // as the stale twin and delete both. A tab that tidies only its own
      // past writes cannot remove the one surviving copy; cross-tab twins
      // stay, and the read-side dedup already renders them as one.
      if (one && keptIdentity(one) === twin
        && one.id !== undefined && keptMintedHere.has(one.id)) stale.push(key);
    }
    for (const key of stale) localStorage.removeItem(key);
  } catch { /* the read-side dedup covers whatever this could not tidy */ }
}

/** Every role the current pool's schema declares — the pool's own defs plus
 *  keys minted this session. Passed with generation requests so the revision
 *  compatibility gate can tell a declared-but-empty role (its last value was
 *  just cleared; the key and the empty field remain) from one that is
 *  genuinely gone. See `declaredRoles` in @morph/generation's types. */
function declaredRolesNow(): Role[] {
  return [...new Set([
    ...pool.defs.map((d) => d.role),
    ...[...declared.keys()]
      .filter((k) => k.startsWith(`${pool.id} `))
      .map((k) => k.split(' ')[1] as Role),
  ])];
}

/** How many values each DECLARED enum may take — task `0202`, the KIND half
 *  of what `declaredRolesNow` restores.
 *
 *  `declaredRoles` brings a cleared role's PRESENCE back; this says what it
 *  IS. Without it `chooseableEnums` sees a cleared `status` in neither
 *  `shape.roles` nor `shape.enumCardinality`, mints no `set-enum`, and the
 *  person gets a read-only field where the value used to be.
 *
 *  Counted from the pool's DECLARED options, which is the only source that
 *  knows the number when no object carries a value. A def with no declared
 *  options is left out rather than counted as zero: zero would read as a
 *  closed set of nothing and offer a chooser with no choices, which is the
 *  empty dropdown this task is about, arriving from the other side. */
/**
 * THE NUMBER OF OPTIONS THE CONTROL WILL HOLD — task `0215`.
 *
 * This counted the DECLARED options only, and the generator took the larger of
 * that and the shape's observed count. Two counts cannot be unioned, so five
 * declared and five observed that barely overlap read as five while
 * `choicesFor` filled the dropdown with ten: an enum could pass a threshold of
 * eight and open a control of ten, which is the exact oversized control the
 * threshold exists to prevent.
 *
 * The host is the side that holds both sets, so the host does the counting.
 * The union rule here is `choicesFor`'s, and it has to STAY `choicesFor`'s —
 * a count that disagrees with the control it describes is worse than the
 * lower bound it replaced, because it looks authoritative.
 *
 * From `pool`, whose `defs` and `scope` are the ones this request is about.
 * `choicesFor` takes its pool as an argument because it is also called for an
 * aside object's owner; every caller of this function is generating for the
 * open pool.
 */
async function enumOptionCountNow(): Promise<Record<string, number>> {
  const roles = new Set<Role>(pool.defs.filter((d) => d.kind === 'enum').map((d) => d.role));
  const out: Record<string, number> = {};
  await Promise.all([...roles].map(async (role) => {
    const declared = pool.defs.find((d) => d.role === role && d.options)
      ?.options?.map(optionValue) ?? [];
    let seen: readonly string[] = [];
    /* A STORE READ CANNOT SINK THE OFFER. If the observed side is unavailable
       the declared count is the old lower bound, which is what this function
       returned for its whole life — a worse answer, not a broken one. */
    try { seen = await db.store.enumOptions(pool.scope, role); } catch { seen = []; }
    out[role] = new Set([...declared, ...seen]).size;
  }));
  return out;
}

function contributeAcceptedLens(what: string): void {
  recordKeptLayout(what);
  try {
    privatePool.contribute(state.lens, privateValuesNow());
  } catch (error) {
    say(
      `${what} — but this layout was not added to your pool: `
      + (error instanceof Error ? error.message : String(error)),
      'bad',
    );
  }
}

/** The taste model, in plain language. It has to be readable and correctable,
 *  or it cannot be trusted — which is why nothing here is a number. */
function renderTaste() {
  const parts: string[] = [];
  if (state.taste.density) parts.push(`you want layouts <b>${state.taste.density}</b>`);
  // Typography and palette were held and not said. A readout that reports one
  // of the three things the model knows is not a readable model — it is a
  // model with two secrets, and "readable and correctable" was the whole
  // argument for having one in prose rather than in numbers.
  if (state.taste.typography) parts.push(`you read this in <b>${state.taste.typography}</b> type`);
  if (state.taste.palette) parts.push(`you want a <b>${state.taste.palette}</b> palette`);
  for (const r of state.taste.suppressedRoles ?? []) {
    parts.push(`you do not want <b>${r}</b> shown `
      + `<button class="btn btn-quiet taste-correct" data-role="${r}" `
      + `data-value="suppressed">Show again</button>`);
  }
  /* Report only what this shape can express. The role-only predicate is
     necessary and not sufficient: a promotion that moves nothing here would
     otherwise be announced as a learned belief and change no layout.

     Against the ACCEPTED spec when there is one, for the same reason the
     Promote gate above is: reporting a belief the person cannot see the effect
     of, and withholding one they can, are the same defect from either side. */
  const acceptedReport = state.lens.accepted?.spec;
  for (const r of (state.taste.prominentRoles ?? [])
    .filter((role) => (acceptedReport
      ? promotionCanCompile(role) && promotionChangesAccepted(acceptedReport, role)
      : !lastShape || promotionChangesRanking(lastShape, role)))) {
    parts.push(`you want <b>${r}</b> prominent `
      + `<button class="btn btn-quiet taste-correct" data-role="${r}" `
      + `data-value="prominent">Use normal prominence</button>`);
  }
  $('taste').innerHTML = parts.length
    ? `Morph currently believes: ${parts.join(', and ')}.` +
      (state.learned.length ? `<br><span class="quiet">Learned from: ${state.learned.join(' · ')}</span>` : '')
    : 'Morph has learned nothing about you yet. Keep a suggestion or adjust a field and this fills in.';
  for (const button of $('taste').querySelectorAll<HTMLButtonElement>('.taste-correct')) {
    button.addEventListener('click', () => {
      const role = button.dataset.role as Role;
      const value = button.dataset.value!;
      manualTaste.correct(role, 'role.presence', value);
      /* RE-FOLD. Retracting the winner can immediately expose the loser as the
         new one — three hides and seven promotes, corrected on `prominent`,
         leaves the role suppressed. Deleting only the corrected value showed it
         as normal until a reload rehydrated the ledger and hid it. */
      const refolded = manualTaste.taste();
      state.taste = applyPresence(state.taste, role, refolded);
      state.learned = state.learned.filter((item) =>
        item !== `hiding ${role}` && item !== `promoting ${role}`);
      if (refolded.suppressedRoles?.includes(role)) state.learned.push(`hiding ${role}`);
      if (refolded.prominentRoles?.includes(role)) state.learned.push(`promoting ${role}`);
      void run();
    });
  }
}

for (const key of
  ['context', 'cardinality', 'view', 'viewport', 'palette', 'scheme', 'mark'] as const) {
  $(key).addEventListener('change', (e) => {
    const value = (e.target as HTMLSelectElement).value;
    (state as Record<string, unknown>)[key] = value;
    // Every one of these is an input to a pool request, so a request in flight
    // was compiled against a state nobody is in any more — see `poolRequest`.
    // `context` and `cardinality` also move `surfaceNow()`; `palette`, `view`,
    // `viewport`, `scheme` and `mark` do not, which is exactly the gap the
    // surface key could not close.
    poolRequest += 1;
    /**
     * A CHOSEN palette outranks the pool; the initial `neutral` must not. See
     * `openPoolOffer`.
     *
     * And it is written into the TASTE as well, which is the half that was
     * missing. @Codex, P2 on #184: *"When a person keeps a pool offer that
     * taught a palette, this stores that palette in `state.taste`. A later
     * Palette-control change updates only `state.palette`, while generation
     * requests merge values as `{ palette: state.palette, ...state.taste }`,
     * so the older pooled value overwrites the newer explicit choice. The
     * selector can consequently show `cool` while compilation and the rendered
     * chrome remain `warm`."*
     *
     * Fixed here rather than by reordering the merges, and the review's own
     * phrasing is the argument: *"put the explicit palette last in EVERY
     * merge"* is a rule that has to be repeated at each site and will be
     * missed at the next one — which is how a second spelling of a rule has
     * gone wrong three times in this repository this week. Writing the choice
     * into `state.taste` leaves one value for one fact, and then no merge
     * order can disagree with it.
     *
     * Not conditional on `paletteChosen`: this IS the choice being made, and
     * the initial `neutral` never comes through here.
     */
    if (key === 'palette') {
      paletteChosen = true;
      state.taste = { ...state.taste, palette: value as Palette };
    }
    // Choosing a mark is choosing to look at the plot. Without this the control
    // is inert on every other view, which reads as a broken control rather than
    // as an inapplicable one.
    if (key === 'mark' && state.view !== 'plot') {
      state.view = 'plot';
      ($('view') as HTMLSelectElement).value = 'plot';
    }
    // Whichever control got us into a capability view, not just the View one.
    // Entering the plot via Mark left `state.cardinality` on `single` while the
    // next render queried and painted the whole pool, so single-object actions
    // stayed enabled against a collection screen — the two halves disagreeing
    // about what is on screen, which is the thing this transition exists to
    // prevent. Found by @Codex.
    if ((key === 'view' || key === 'mark')
      && state.view !== 'auto' && state.cardinality !== 'large-set') {
      state.cardinality = 'large-set';
      ($('cardinality') as HTMLSelectElement).value = 'large-set';
    }
    // Both capability views are collection views. Choosing a single-object
    // cardinality leaves them atomically so querying, property picking and
    // write targeting cannot disagree about whether the screen is one object
    // or the pool.
    if (key === 'cardinality' && value === 'single' && state.view !== 'auto') {
      state.view = 'auto';
      ($('view') as HTMLSelectElement).value = 'auto';
    }
    void run();
  });
}

/** The pool switcher. Changing the pool changes nothing but the data — no
 *  branch anywhere in the renderer knows which pool it is drawing. */
const poolBar = $('pools');

/**
 * Become `next` — the open document, everything that hangs off it, and the
 * chrome that says which one you are in.
 *
 * TWO CALLERS: the pill, and a reference followed across documents. The second
 * is why this is a function. A cross-document follow that only reassigned
 * `selected` left the stage rendering the new object through the OLD pool's
 * manifest, declarations and labels — the defect the side pane was repaired for
 * two commits ago, still standing on the main stage because the repair was
 * written where the bug had been reported rather than where it lived.
 *
 * `keep` is the object to land on. The pill passes nothing: a selection from
 * the pool being left seeds a scope whose typeHint no longer matches, which is
 * an empty collection rather than a wrong one — the generator would be handed a
 * shape with no roles at all.
 */
function enterPool(next: DemoPool, keep?: ObjectId): void {
  /**
   * THE LENS BELONGS TO ITS POOL — @Codex, P1 on `22799c0`: `runBody` gives
   * `activeSpec(state.lens)` precedence over generation, and nothing here
   * cleared it, so keeping a Films gallery and opening Notion painted the
   * Tasks collection with the Films arrangement. Stashed rather than
   * dropped: the accepted spec AND the dismissed list are both pool-scoped
   * facts (0015 F38 — a dismissal forgotten on a round trip re-offers the
   * exact layout the person refused), so each pool gets its own LensState
   * back when the person returns.
   */
  if (pool !== undefined && pool.id !== next.id) {
    poolLenses.set(pool.id, state.lens);
    state.lens = poolLenses.get(next.id) ?? emptyLens();
  }
  pool = next;
  select(keep);
  // A SIDE PANE left over from the pool being left goes too. A pane one
  // document behind renders an object the new pool does not contain — measured
  // after a films→notes switch as a pane titled "Object" standing beside an
  // unrelated document. A pane belongs to its vault.
  aside = undefined;
  // And the SEARCH. A result set is a list of ids from the pool being left, so
  // one that survived would seed a scope in a manifest that does not contain
  // them — the same argument as the side pane, one control over.
  found = undefined;
  ($('find') as HTMLInputElement).value = '';
  ($('findClear') as HTMLElement).hidden = true;
  for (const b of poolBar.querySelectorAll('.pill'))
    b.classList.toggle('on', (b as HTMLElement).dataset.pool === next.id);
  $('poolnote').textContent = next.note;
  watchPool();
}

/**
 * A pool pill is COLLECTION NAVIGATION, and says so out loud.
 *
 * Decision 0024: *"Pressing a pool pill is collection navigation. It clears the
 * current object selection and opens that pool in collection cardinality,
 * including when the pill names the pool already in view. A pool pill never
 * silently preserves a single-object lens from a previous navigation episode."*
 *
 * Task 0099, and the reason it compounds with 0098: `enterPool` already cleared
 * the selection, the side pane and the search, and left `state.cardinality`
 * alone. So after pressing **New** — which moves to `single` deliberately —
 * pressing Films showed ONE arbitrary film rather than the library. Measured:
 * twelve titles before, one after New, and still one after the pill. The way
 * back did not go back.
 *
 * The reset lives HERE and not in `enterPool`, because `enterPool(owner, target)`
 * is also how following a reference into another pool opens the object it points
 * at, and single is right for that. The pill is the gesture that means "the
 * collection"; the other caller means "this object, which happens to live over
 * there".
 *
 * 0024 on why the visible control decides: *"Returning one arbitrary Film
 * because a hidden cardinality control still says `single` makes the visible
 * control lie."*
 */
poolBar.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest('[data-pool]') as HTMLElement | null;
  if (!btn) return;
  closeHub();
  // Collection navigation leaves a named app look behind (0055): the pill
  // means "this pool, as Morph shows it".
  state.template = undefined;
  enterPool(db.pools.find((p) => p.id === btn.dataset.pool)!);
  if (state.cardinality !== 'large-set') {
    state.cardinality = 'large-set';
    ($('cardinality') as HTMLSelectElement).value = 'large-set';
  }
  schedule();
});

/* ─────────────────────────────────────────────── the hub (task 0149) ── */

/**
 * Where each rebuild opens. The hub itself (`hub.ts`) is pure and knows no
 * pool ids; this map is the host's half, kept beside the pools it names.
 *
 * The NOTES pool serves two apps on purpose — Obsidian as a vault, Word as a
 * document — because the whole thesis is one store under generated
 * presentations, and two canon apps sharing objects says it better than any
 * copy could.
 *
 * Fibery's entry arrived with its workspace (task 0150 phase 1) and the card
 * opens into the features database. Openability is this table's contract
 * alone — the card's guard tally stays with `acceptance-map.json`, which
 * still says every Fibery capability is uncovered, and both facts are true
 * at once: there is a thing to open, and nothing about it is proven yet.
 */
const HUB_DESTINATIONS: Readonly<Record<string, HubDestination>> = {
  // TASKS, not films — @Codex, P1 on `4aa3b1f`: the card advertised a board
  // and sent the person to the one pool that cannot generate one (Films has
  // no status-bearing role, and the view chooser has no board entry to
  // recover with). The board this repo can actually show is generated from
  // the Tasks pool; tasks also carry the date the calendar and timeline
  // views need. The gallery needs media and stays honestly with the film
  // library, and the note now says exactly that instead of promising five
  // views at a door that reaches four. Task 0159 keeps the deeper half —
  // whether a person can MAKE a collection board-capable from scratch.
  // AS NOTION — decision 0055's second named look. The door opens the tasks
  // as a page with a board view in Notion's own shell: nav sidebar, topbar,
  // page head, view switcher, the status columns.
  Notion: { poolId: 'tasks', template: 'app.notion',
    note: 'A workspace collection the way Notion would hold it: a page with a board of status columns, in Notion\'s own shell. Table, calendar and timeline are a view away.' },
  // AS OBSIDIAN — decision 0055's first named template. The door opens a note
  // in Obsidian's own workspace (ribbon, explorer, tabs, editor, backlinks,
  // status bar), generated over the same notes pool Word reads as a document.
  Obsidian: { poolId: 'notes', template: 'app.obsidian',
    note: 'A vault of linked notes in Obsidian\'s own workspace: file explorer, tabs, the note, its backlinks, a status bar. Local, no server.' },
  // AS CODA — decision 0055's third named look. The door opens the tasks
  // as a DOC with a table in Coda's own shell: page tree, topbar, a page
  // with a cover and a paragraph, buttons as blocks, the table.
  Coda: { poolId: 'tasks', template: 'app.coda',
    note: 'A task table in a doc, the way Coda holds it: a page with a cover, a paragraph, buttons as blocks, and the table under them.' },
  // AS FABRIC — decision 0055's sixth named look. The door opens the
  // archive ON an item, in Fabric's own window: the title bar, the note
  // (its title, its media, its transcript), and the assistant panel at
  // the right whose composer is the library's retrieval and whose thread
  // is what it found.
  Fabric: { poolId: 'archive', template: 'app.fabric',
    note: 'A media archive in Fabric\'s own window: film, audio, transcripts — and a panel that finds any of it by meaning, the way Fabric\'s assistant answers from your things.' },
  // AS WORD — decision 0055's fourth named look. The door opens the notes
  // as a DOCUMENT in Word's own window: the title bar, the ribbon, the
  // sheet on the desk (the same document-flow module the document view
  // uses: real pages, a table of contents, footnotes, print), the status
  // bar with the word count.
  Word: { poolId: 'notes', template: 'app.word',
    note: 'Long-form writing with real pages in Word\'s own window: the ribbon, the sheet on the desk, a table of contents that updates, footnotes, print.' },
  // AS FIBERY — decision 0055's fifth named look. The door opens the
  // features as a DATABASE in Fibery's own workspace: the white sidebar
  // with the spaces, the view bar with its star, the toolbar with the
  // view's search and New, the board of state columns of badged cards —
  // the table a view away, with the assignee and the sprint as chips.
  Fibery: { poolId: 'features', template: 'app.fibery',
    note: 'A product database in Fibery\'s own workspace: spaces in the sidebar, a board of state columns with typed cards, the table a view away — features linked to sprints and to people who are entities.' },
};

/**
 * Fetched once per page life. The build refuses to EMIT a wrong map (it runs
 * the coverage script and fails on exit 1), so a fetch that succeeds is
 * current by construction; a fetch that fails renders the honest failure
 * surface instead of a proof section that looks like "nothing is tested".
 */
let hubMap: AcceptanceMap | undefined;
const hubHost = $('hub');

/**
 * The same shape as `creationTicket`, for the same reason, found by @Codex the
 * same day the door was built. P2 on `b04863c`: *"When the acceptance-map
 * fetch is still pending and the person clicks a pool pill after invoking the
 * hub door, the pool handler calls `closeHub()` while the hub is hidden, so it
 * returns without invalidating `openHub()`. Once the fetch resolves, these
 * lines unconditionally show the hub and hide the panes, overriding the
 * person's later navigation."*
 *
 * Which is `0044`'s sentence — an answer about a moment that has passed must
 * not be installed over one about now — recurring in code written hours after
 * this branch quoted it about `createObject`. `closeHub` moves the ticket even
 * when the hub is not showing: the thing it invalidates is the pending OPEN,
 * not the visible state.
 */
let hubTicket = 0;
/** Invalidates a kept-layout open still resolving its object — bumped by a
 *  newer kept click and by every `closeHub`, so later navigation wins. */
let keptOpenTicket = 0;
/** The tab-close navigation's own ticket — @Codex's P2 on `cb4fa44`. A close
 *  resolves the neighbour's pool through the store, and anything the person
 *  does meanwhile outranks it. */
let tabCloseTicket = 0;
/** THE TAB BEING CLOSED, so a paint in the middle cannot resurrect it —
 *  @Codex's P2 on `74e732b`.
 *
 *  Closing the active tab splices it out of `openNotes.list` and then AWAITS
 *  `poolOf(next.id)` to learn which pool answers for the neighbour. `selected`
 *  still names the closed note for that whole window, and `workspaceInputs`
 *  re-adds any `selected` note it cannot find in the list — so a repaint in
 *  the middle (a Files or Search press, a realtime invalidation from another
 *  context) pushed the closed note back at the END of the strip, and the
 *  callback then selected the neighbour without removing it. The closed tab
 *  stayed on screen as an inactive one.
 *
 *  PAIRED WITH THE TICKET rather than cleared by hand. `tabCloseTicket` is
 *  bumped by a newer close, by leaving the workspace, and by `select` — which
 *  is EVERY selection, and is where this listed three named sites until
 *  @Codex found the fourth on `98376fb`. Each of those settles the selection
 *  itself, so each must also end the suppression. Carrying the ticket makes
 *  that automatic: the moment the number moves this record stops matching and
 *  the guard lifts, with no clear site to keep in step. A list of sites is the
 *  shape that goes stale when the next one is added; a choke point is not. */
let closingTab: { readonly id: ObjectId; readonly ticket: number } | undefined;

/** The rail controls that act on the pool — meaningless over the shelf, and
 *  worse than meaningless: Add file over a hidden single-object lens attaches
 *  bytes to an object the person cannot see (@Codex, the third P2).
 *
 *  `inspect` joined one review later — @Codex, P2 on `fe50877`: *"only these
 *  three controls are hidden, leaving `#inspect` and `#inspector` active. Its
 *  property, automation, and view controls can therefore mutate or navigate
 *  the previously selected pool while that pool is hidden behind the hub."*
 *  Right, and the omission was a DECISION gone unrecorded: I kept the
 *  Inspector live as "page-level chrome", and run 5 had already shown it is
 *  where creation and property writes actually live — pool-scoped in
 *  everything it does. The drawer itself is closed by `closeInspector`
 *  below, since hiding the button does not close an open drawer. */
const POOL_CHROME = ['suggest', 'findForm', 'addFile', 'inspect'] as const;

/** Assigned by `mountInspector`, whose `set()` owns the drawer's three-part
 *  state (hidden, aria-expanded, the layout class). A second copy of those
 *  mutations here would be the drifted-list defect this same commit is
 *  fixing; a handle is not a copy. Closing through it does overwrite the
 *  remembered-open preference, and that is the safe direction: a drawer that
 *  reopens itself over the shelf is the finding again. */
let closeInspector: () => void = () => {};

async function openHub(): Promise<void> {
  const mine = (hubTicket += 1);
  /**
   * The shelf SUPERSEDES a suggestion in flight — and the bump happens HERE,
   * before the first await, not on the show path. @Codex, P2 on `fc7c930`,
   * then again on `a400293`: the first fix bumped after the ticket re-check,
   * so on a first open a compile completing DURING the map fetch still
   * passed its currency guard and installed an offer for the pool hidden
   * underneath — and the fetch-failure branch returned without ever
   * reaching the bump at all. The person asked for the shelf; whatever was
   * compiling for the surface under it is answering a question that has
   * been withdrawn (0044), on every branch, including the aborted ones.
   */
  poolRequest += 1;
  /**
   * Hiding the surface does not silence it. @Codex, P2 on `d958909`: an
   * `HTMLMediaElement` keeps playing — audibly, and decoding — behind the
   * shelf, because `hidden` on an ancestor removes pixels, not playback.
   * Pause is idempotent and one-way; the person who wants the recording
   * back presses play, the same contract a pool switch implies (a switch
   * rebuilds the stage and destroys the element outright). Before the first
   * await, so a slow map fetch does not leave the audio running under the
   * shelf it is about to show.
   */
  const pauseSurfaceMedia = (): void => {
    for (const m of $('panes').querySelectorAll<HTMLMediaElement>('audio, video')) m.pause();
  };
  pauseSurfaceMedia();
  if (!hubMap) {
    let failure: string | undefined;
    try {
      const res = await fetch('./acceptance-map.json');
      if (!res.ok) throw new Error(`the map answered ${res.status}`);
      hubMap = await res.json() as AcceptanceMap;
    } catch (why) {
      failure = why instanceof Error ? why.message : String(why);
    }
    // AFTER the awaits, before anything is shown: a person who navigated
    // while the map loaded has answered the question this open was asking.
    if (hubTicket !== mine) return;
    if (failure !== undefined || !hubMap) {
      hubHost.innerHTML = renderHubFailure(failure ?? 'the map arrived empty', HUB_DESTINATIONS, keptEntries());
      hubHost.hidden = false;
      $('panes').hidden = true;
      for (const id of POOL_CHROME) $(id).hidden = true;
      closeInspector();
      return;
    }
  }
  if (hubTicket !== mine) return;
  /* The vocabularies the compose form offers are DERIVED, both of them: the
     bases are the destinations the hub already has, and the parts are the
     leading words of the fields the six looks declare. A list typed here
     would be the seventh app's problem on the day somebody adds a field. */
  const allMade = readComposedApps();
  /* WHAT IT IS MADE OF, in the words a person used. The base may be another
     app they made, and the first version printed its raw id — `composed:mtrxt…
     with app.coda's topbar`, which names nothing anybody typed. The base
     resolves to its NAME, and the look ids drop their `app.` prefix for the
     same reason: this line is read, not parsed. */
  const looksLike = (x: string) =>
    allMade.find((a) => a.id === x)?.name ?? x.replace(/^app\./, '');
  const madeEntries = allMade.map((m) => ({
    id: m.id, name: m.name,
    of: Object.entries(m.parts).length
      ? `${looksLike(m.basedOn)} with ${Object.entries(m.parts)
        .map(([part, from]) => `${looksLike(from)}'s ${part}`).join(', ')}`
      : looksLike(m.basedOn),
  }));
  const partNames = [...new Set(lookFields().map((f) => partOf(f, lookFields())))].sort();
  hubHost.innerHTML = renderHub(hubMap, HUB_DESTINATIONS, keptEntries(),
    madeEntries, Object.keys(HUB_DESTINATIONS), partNames);
  hubHost.hidden = false;
  $('panes').hidden = true;
  for (const id of POOL_CHROME) $(id).hidden = true;
  closeInspector();
  // No say(): a pool switch announces nothing either, and the toast floats
  // over the grid — the first screenshot had it sitting exactly on Fabric's
  // one "partly tested" row, hiding the honest half of the shelf.
}

function closeHub(): void {
  // The ticket moves FIRST, unconditionally — the early return below is
  // exactly where the stale-open finding lived. The kept-open ticket moves
  // with it: closing the shelf, for any reason, withdraws a kept open still
  // resolving its object.
  hubTicket += 1;
  keptOpenTicket += 1;
  // And a tab close still resolving its neighbour's pool: leaving the
  // workspace outranks it, for the same reason.
  tabCloseTicket += 1;
  if (hubHost.hidden) return;
  hubHost.hidden = true;
  $('panes').hidden = false;
  for (const id of POOL_CHROME) $(id).hidden = false;
}

$('home').addEventListener('click', () => { void openHub(); });
// The visible-name twin of the wordmark — one destination, two doors, because
// blind run 2's only guess was that the logo might be the way in.
$('homeNamed').addEventListener('click', () => { void openHub(); });

/** Display rows for the liked shelf, derived fresh per open — another tab
 *  can keep a layout between two opens of this one. */
function keptEntries(): HubKeptEntry[] {
  return readKeptLayouts().map((k) => ({
    id: k.id ?? String(k.at), at: k.at, what: k.what,
    poolLabel: db.pools.find((p) => p.id === k.poolId)?.label ?? k.poolId,
  }));
}

hubHost.addEventListener('click', (e) => {
  const keptBtn = (e.target as HTMLElement).closest('[data-hub-kept]') as HTMLElement | null;
  if (keptBtn) {
    // By timestamp, not index: the list is re-read here, and another tab may
    // have changed it since this shelf was drawn.
    const entry = readKeptLayouts()
      .find((k) => (k.id ?? String(k.at)) === keptBtn.dataset.hubKept);
    if (!entry) { say('that kept layout is gone from this browser', 'bad'); return; }
    openKeptEntry(entry);
    return;
  }
  const create = (e.target as HTMLElement)
    .closest('[data-hub-compose-create]') as HTMLElement | null;
  if (create) {
    /* THE GESTURE THAT ANSWERS THE QUESTION. Asked on 2026-09-07 whether a
       person can create an app, the answer was no. Three choices make one:
       which app it is based on, which part to replace, whose part to use.

       It REFUSES rather than making a no-op app: naming the base as the
       donor's source composes nothing, and a door that opens the base under
       another name is the dead control 0055 forbids — a thing that looks
       made and is not. */
    e.preventDefault();
    const form = create.closest('[data-hub-compose]') as HTMLElement | null;
    const val = (sel: string) =>
      (form?.querySelector(sel) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
    const base = val('[data-compose-base]'), part = val('[data-compose-part]');
    const from = val('[data-compose-from]');
    /* THE BASE MAY BE ONE A PERSON MADE, which is how a second part is
       reached. Its pool is the base's, walked from wherever the chain starts,
       so composing onto an app never quietly moves which objects it shows. */
    const onMade = composedById(base);
    const dest = onMade
      ? { poolId: onMade.poolId, template: rootTemplate(onMade, readComposedApps()) }
      : HUB_DESTINATIONS[base];
    /* THE DONOR MAY ALSO BE ONE A PERSON MADE. Its template is not what gets
       stored — the PART's source is, resolved when the record is read, so an
       app that later changes its own nav changes every app that borrowed it.
       That is what "the same source" means here: a borrowed part is a
       reference, not a copy. */
    const fromMade = composedById(from);
    const donor = fromMade
      ? { template: rootTemplate(fromMade, readComposedApps()) }
      : HUB_DESTINATIONS[from];
    /* A destination's `template` is OPTIONAL — a door may open Morph's own
       presentation over a pool with no named look at all. Composition needs
       both sides to be a look, so a door without one is refused in words
       rather than composed into a record with an undefined base. */
    if (!dest?.template || !donor?.template || !part) {
      say('both apps need a named look to compose', 'bad'); return;
    }
    if (from === base) { say('that composes nothing — take the part from another app', 'bad'); return; }
    const name = val('[data-compose-name]').trim()
      || `${onMade ? onMade.name : base} with ${from}'s ${part}`;
    const made: ComposedApp = {
      id: `composed:${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      /* Based on the MADE app when there is one, so its parts are inherited;
         on the template otherwise. One field, two kinds of base, resolved by
         the chain walk rather than by a second field nobody keeps in step. */
      name, basedOn: onMade ? onMade.id : dest.template, poolId: dest.poolId,
      parts: { [part]: fromMade ? fromMade.id : donor.template }, at: Date.now(),
    };
    if (!writeComposedApp(made)) { say('could not store the app — the browser refused', 'bad'); return; }
    say(`made ${name} — it is on the hub`, 'ok');
    void openHub();
    return;
  }
  const forget = (e.target as HTMLElement)
    .closest('[data-hub-composed-forget]') as HTMLElement | null;
  if (forget) {
    forgetComposedApp(forget.dataset.hubComposedForget ?? '');
    void openHub();   // the hub's paint lives there; one painter, not two
    return;
  }
  const btn = (e.target as HTMLElement)
    .closest('[data-hub-open],[data-hub-composed]') as HTMLElement | null;
  if (!btn) return;
  openHubDestination(btn);
});

/**
 * THE SIDE PANES A LOOK OPENS ON, for every door that opens one.
 *
 * @Codex's P2 on `470dfd5`: the hub door reset this and the KEPT-LAYOUT door
 * did not, so a kept Obsidian layout reopened after somebody had selected
 * Backlinks came back on Backlinks — or, after another look, on a pane id this
 * look does not have, which the renderer answers with its first-pane fallback.
 * Neither is the person's choice (a kept entry does not record one) and neither
 * is the look's declaration, so the state was simply whatever happened to be on
 * screen.
 *
 * A function rather than a repeated line, because `openKeptEntry` below already
 * says why: two doors that disagree about what "open" means is the defect, and
 * the cure is that there is only one place to change.
 */
/* A door CLEARS the person's pane selections; the look's own opening is
   declared semantically and resolved by the renderer against what each
   sidebar holds (@Codex's P2 on `0d83f75`). It used to seed this map with
   `{'ws-sidebar': 'graph'}` — a builder's private id — which is why the
   declaration only worked for one builder's naming. */
const openingSidePanes = (_template: AppTemplate | undefined): typeof state.sidePanes =>
  ({}) as typeof state.sidePanes;

/**
 * Reopen a kept layout — the shelf's own door, and Notion's Favorites row,
 * which lists the starred pages and opens one the same way. One function,
 * so the two doors cannot disagree about what "open" means.
 */
function openKeptEntry(entry: KeptLayout): void {
  {
    const dest = db.pools.find((p) => p.id === entry.poolId);
    if (!dest) { say('that kept layout belongs to a pool this build does not have', 'bad'); return; }
    // Verified before trust: a spec kept under an older vocabulary must
    // refuse to reopen, not render wrongly. Same posture as the overlay.
    // BOTH versions, because they move independently — @Codex, P2 on
    // `22799c0`: `SPEC_VERSION` tracks the spec's SHAPE and stays put across
    // a vocabulary-only release, so a layout kept under the old words passed
    // this gate and reached the render-time validator as an invalid-spec
    // surface instead of the promised refusal. The spec carries its own
    // `vocabularyVersion`, so old entries are covered without a new field.
    const kept = entry.spec as { specVersion?: unknown; vocabularyVersion?: unknown };
    if (kept.specVersion !== SPEC_VERSION || kept.vocabularyVersion !== VOCABULARY_VERSION) {
      say('that kept layout is from an older Morph and no longer applies', 'bad');
      return;
    }
    /**
     * THE CONTEXT COMES BACK WITH THE LAYOUT — @Codex, P1 on `7ad205c`.
     * Restored, not forced: the spec was accepted for a scope, and
     * reopening it elsewhere is a different layout wearing its name. A v1
     * entry without a context opens the collection, which is what it
     * promised when it was kept. Single-cardinality only WITH its object —
     * RESOLVED, not merely recorded: @Codex, P2 on `40b56c2` — localStorage
     * outlives an IndexedDB reset, so an id can be real in the entry and
     * gone from the store, and a single lens over it would render an empty
     * page wearing a kept layout's name. Asked of the store first; a
     * selection that no longer resolves falls back to the collection and
     * says so.
     */
    void (async () => {
      // The ticket, this branch's own — @Codex, P2 on `6fd9613`: the resolve
      // below awaits the store, and a person who clicked another entry or a
      // destination meanwhile has answered the question this open was
      // asking. `closeHub` moves it too, so any navigation that closes the
      // shelf (a pool pill, a destination card) also invalidates this one.
      const mine = (keptOpenTicket += 1);
      const wants = entry.cardinality === 'single' && entry.selected !== undefined;
      const resolvable = wants
        ? await db.store.get(entry.selected!, { manifest: dest.manifest })
          .then((o) => o !== undefined).catch(() => false)
        : false;
      if (keptOpenTicket !== mine) return;
      if (wants && !resolvable) {
        say('the object that layout was kept on is gone — showing the collection', 'bad');
      }
      const cardinality = wants && resolvable ? 'single' : 'large-set';
      /**
       * THE NAMED APP LOOK COMES BACK WITH IT — @Codex, P1 on `b0af641`: a
       * kept Obsidian workspace reopened under whatever `state.template` the
       * person last had, and `runBody` lets an accepted lens win only when
       * its template matches the host's — so the shelf's "Open" on an
       * Obsidian layout painted Morph's generic view over the Notes pool and
       * the reproduction was unreachable from the one place it is kept.
       * Read from the VERIFIED stored spec (the version gate above), never
       * from the entry's label. Only WITH its note: a template is a
       * single-object presentation, and a kept layout whose note is gone has
       * already fallen back to the collection and said so.
       */
      /**
       * AT THE CARDINALITY IT WAS KEPT AT, WHICH IS THE SPEC'S OWN — @Codex,
       * P1 on `2ef9cfb`. This asked `lookOpensOn(worn)`, the look's DEFAULT,
       * and a look has one default while its kept layouts have many.
       *
       * Reproduced through the product before it was changed. Opening a
       * Notion board row and starring the page writes:
       *
       *   { what: 'starred', cardinality: 'single',
       *     spec.template.id: 'app.notion', spec.target.cardinality: 'single' }
       *
       * `lookOpensOn('app.notion')` is 'large-set', so a reopen whose object
       * still resolves computed 'single', failed this comparison and dropped
       * the template — and `runBody`'s kept-spec gate then refuses a saved
       * spec whose `template.id` does not match `state.template`. The person
       * got Morph's generic single view of a page they had kept as Notion,
       * from the one place a reproduction is meant to be reachable.
       *
       * Both earlier attempts read a fact about the LOOK where the question
       * is about this ENTRY. "Single only" broke the starred board; the
       * look's default breaks the starred page. The spec's own target is
       * neither: it is what this layout was accepted at, it is REQUIRED by
       * the schema rather than optional like `entry.cardinality` (v1 entries
       * lack that one), and it is the same field `runBody` is about to
       * insist on.
       *
       * COMPARED AS ONE-OR-MANY, NOT AS EQUAL STRINGS, and the first draft of
       * this got that wrong in a way `notion-look` caught: `cardinality` above
       * is computed as `'single' | 'large-set'`, while `CARDINALITIES` has
       * THREE values and a Notion board's spec is stamped `small-set`. String
       * equality therefore dropped the template on every kept board — the same
       * P1 through the opposite door, introduced inside its own fix. That is
       * `0064`'s question unasked: the field this now reads has a wider set
       * than the value it is compared against, so the comparison is on the
       * distinction the restore actually draws — is this layout about ONE
       * object, or about the collection.
       *
       * The missing-object fallback still drops it, and by construction rather
       * than by a second rule: a kept single layout whose note is gone has
       * `cardinality` forced to 'large-set' above, so `onOne` is false where
       * the spec was saved true.
       */
      const worn = (entry.spec as { template?: { id?: AppTemplate } }).template?.id;
      const keptOnOne = (entry.spec as { target?: { cardinality?: Cardinality } })
        .target?.cardinality === 'single';
      const template = worn !== undefined && (cardinality === 'single') === keptOnOne
        ? worn : undefined;
      /**
       * REVALIDATED WHILE THE HUB IS STILL ON SCREEN — @Codex, P2 on
       * `4ab498b`, and it did not do that until their P2 on `4a34aef`.
       *
       * The comment said "before anything hides" and sat forty-five lines
       * BELOW `closeHub()`, `enterPool()` and the cardinality, view and
       * context mutations. A malformed same-version spec therefore refused
       * with a `return` that schedules no render — leaving the person outside
       * the shelf, looking at the previous stage, with every control
       * describing a state nothing had painted. The refusal was real and its
       * promise was not: this file's own recorded defect, a comment asserting
       * a property the code lacks, in the comment that asserts it.
       *
       * Everything it needs — the entry, its spec, the resolved cardinality —
       * is known here, so it runs before the first mutation and the refusal
       * leaves the shelf exactly as it was.
       */
      if (template !== undefined) {
        const valid = validateSpec(entry.spec);
        const short = valid.valid ? templateShortfalls(entry.spec, template) : [];
        if (!valid.valid || short.length) {
          say(`that kept ${template} layout no longer holds together — ${
            valid.valid ? short[0] : 'it is not a valid layout'}`, 'bad');
          return;
        }
      }
      closeHub();
      // The shelf's door is a hub crossing too, so the place history goes
      // with it for the reason the destination door gives.
      state.ntHistory = { back: [], forward: [], here: undefined, moving: false };
      enterPool(dest, cardinality === 'single' ? entry.selected : undefined);
      if (state.cardinality !== cardinality) {
        state.cardinality = cardinality;
        ($('cardinality') as HTMLSelectElement).value = cardinality;
      }
      const view = entry.view ?? 'auto';
      if (state.view !== view) {
        state.view = view;
        ($('view') as HTMLSelectElement).value = view;
      }
      // The generation context too — @Codex, P2 on `40b56c2`: reopened under
      // a different context, later revisions and pool suggestions were
      // judged against a context the layout was never accepted for. A v1
      // entry without one keeps whatever the person has — what it promised.
      if (entry.context !== undefined && state.context !== entry.context) {
        state.context = entry.context;
        ($('context') as HTMLSelectElement).value = entry.context;
      }
      if (state.template !== template) {
        state.template = template;
        applyTemplateChrome();
      }
      // The look's declared opening panes, exactly as the hub door does it.
      // Unconditional rather than inside the template switch above: reopening
      // the SAME look still has to clear a pane the person selected since,
      // which is the case that made this a defect rather than a tidy-up.
      state.sidePanes = openingSidePanes(template);
      /* AND THE SEARCH GOES BACK IN ITS GLYPH — @Codex, P2 on `026c328`, and
         it is the same sentence as the panes two lines up: a kept entry
         records no search state, so reopening one has to arrive as the hub's
         door does. Left standing, a person who expanded Notion's search, went
         Home and reopened a kept layout got the full field where every other
         route gives the glyph. The query goes with it for the reason the
         trigger's own close gives — a filter nobody can see is a lie about
         what is on screen. */
      state.searchQuery = '';
      state.searchOpen = false;
      // On the VIEW it was starred from; a v1 entry opens on the board.
      if (lookOf(template).restoresTemplateView) state.templateView = entry.templateView ?? 'board';
      // The same door a drop takes (0045/0015 F38): accepted directly, the
      // pre-open candidate dismissed, and the generator told whose lens it is.
      state.lens = acceptDirect(state.lens, entry.spec,
        { generator: 'kept-shelf', priors: [], attempts: 1 },
        'reopened from the shelf');
      schedule();
      // Under a named app look the rail is hidden, so the pill is the wrong
      // place to leave focus — the same rule as the hub's door.
      if (template) focusWorkspaceAfterPaint(wsEntryFocus(template));
      else (poolBar.querySelector('.pill.on') as HTMLElement | null)?.focus();
    })();
  }
}

/** The hub's door to a destination — a pool as Morph shows it, or as a
 *  named app look (0055). */
function openHubDestination(btn: HTMLElement): void {
  /* A COMPOSED APP OPENS THROUGH THIS DOOR, not through one of its own. Two
     doors that disagree about what open means is the defect `openKeptEntry`'s
     own comment names, and everything below — the history clear, the opening
     panes, the cardinality, the view, the focus — is what opening MEANS here.
     A composed app is a presentation over a base's generation, so it borrows
     that base's destination and differs only in the look it then presents as. */
  const made = composedById(btn.dataset.hubComposed);
  /* THE GENERATION IS THE ROOT'S. A composed app may be based on another —
     that is how a person gets more than one part — so the template the page is
     built as is found by walking the chain, never by reading one link. A chain
     that is broken or cyclic has no honest answer, and the door refuses in
     words rather than opening as some other app. */
  const root = made ? rootTemplate(made, readComposedApps()) : undefined;
  if (made && root === undefined) {
    say(`${made.name} is built on an app that is gone — forget it and make it again`, 'bad');
    return;
  }
  /* `root` is defined here — the refusal above returned otherwise — but the
     property is SPREAD rather than set, because under exactOptionalPropertyTypes
     an optional `template` may be absent and may not be `undefined`, and those
     are different facts about a destination. */
  const dest: HubDestination | undefined = made
    ? { poolId: made.poolId, ...(root ? { template: root } : {}), note: made.name }
    : HUB_DESTINATIONS[btn.dataset.hubOpen ?? ''];
  if (!dest) return;
  /* SET ON EVERY DOOR, cleared by the six. One writer, so no door can forget:
     a stale composedId would present the next rebuild as somebody's mix. */
  state.composedId = made?.id;
  closeHub();
  /**
   * THE PLACE HISTORY DOES NOT CROSS THE HUB — @Codex's P2 on `4a34aef`.
   *
   * `runBody` clears it when a paint finds no `placeHistory` look, which is
   * the ordinary way of leaving one. Going out through Home and straight into
   * another look never renders anything in between, so the clear never runs
   * and the next history-carrying look opens with the previous one's `here`
   * and back stack intact. Pressing Back in Fibery then walked to a Notion
   * place, and `goToPlace()` resolves its object id through the NEW pool —
   * an empty or unrelated subject, from a control that looked ordinary.
   *
   * Cleared as part of the transition rather than inside the destination,
   * because the destination cannot tell whether it was reached from the hub
   * or from a repaint of itself.
   */
  state.ntHistory = { back: [], forward: [], here: undefined, moving: false };
  // The same semantics as a pool pill — collection navigation, decision 0024:
  // clears the selection and lands in collection cardinality.
  //
  // Unless the rebuild has a NAMED APP LOOK (decision 0055): an Obsidian
  // vault opens ON A NOTE — the explorer beside it is the collection — so the
  // door lands in single cardinality on the first note, which is how the app
  // itself opens.
  state.template = dest.template;
  // A fresh open shows the pane the LOOK opens on — decision 0068 — an empty
  // search, and the look's first view.
  //
  // It used to be `{}`, which the renderer reads as "show each sidebar's
  // first pane in spec order". That is the right FALLBACK and it was the
  // wrong OPENING: Obsidian's right sidebar holds backlinks then the graph,
  // so a fresh vault opened on an empty backlinks pane where the pinned
  // capture shows the graph (@Codex's finding 2).
  //
  // The look declares it, so this stays one line for a seventh app. And the
  // spec is NOT reordered: backlinks keeps its place and its tab, because
  // 0068 says both panes remain selectable and another Obsidian workspace
  // may legitimately open on either.
  state.sidePanes = openingSidePanes(dest.template);
  state.searchQuery = '';
  state.searchOpen = false;
  state.templateView = 'board';
  enterPool(db.pools.find((p) => p.id === dest.poolId)!);
  const cardinality = lookOpensOn(dest.template) ?? 'large-set';
  if (state.cardinality !== cardinality) {
    state.cardinality = cardinality;
    ($('cardinality') as HTMLSelectElement).value = cardinality;
  }
  // THE VIEW TOO, both directions — the same atomic move createObject makes,
  // for the same reason: the two halves must not disagree about what the
  // screen is. Word opens the vault as a DOCUMENT; every other rebuild opens
  // in auto, including when a previous open left a capability view behind.
  const view = dest.view ?? 'auto';
  if (state.view !== view) {
    state.view = view;
    ($('view') as HTMLSelectElement).value = view;
  }
  schedule();
  /**
   * THE FOCUS GOES WITH THEM. @Codex, P2 on `4aa3b1f`: `closeHub()` hides
   * the ancestor of the button the person just activated, and nothing else
   * placed focus — so a keyboard or screen-reader user was left on a hidden
   * control or dumped to `<body>` exactly as the rebuild they asked for
   * appeared. The active pool pill is the stable, visible control that NAMES
   * where they landed, and it is already back on screen (closeHub restored
   * the chrome synchronously); the repaint replaces the stage, not the rail.
   *
   * Under a NAMED APP LOOK the rail is about to be hidden, so the pill is
   * the wrong place to leave focus (@Codex, P2 on ebf7b8c): the focus goes
   * into the workspace once it has painted — `runBody` reads this flag.
   */
  if (dest.template) focusWorkspaceAfterPaint(wsEntryFocus(dest.template));
  else (poolBar.querySelector('.pill.on') as HTMLElement | null)?.focus();
}

/**
 * A selector inside `#stage` that focus goes to once the render loop has
 * SETTLED. Set by every gesture whose target the repaint is about to
 * replace: the hub's door (the ribbon's first tool — the rail is hidden
 * under a named app look), a tab activation (the tab it just made active:
 * @Codex, P2 on b0af641 — the strip is re-rendered, so the button the
 * person pressed is gone and focus fell to `<body>`), and the kept shelf
 * reopening a templated layout.
 *
 * Consumed at the settle, beside `takePendingCreateFocus`, and NOT in
 * `runBody` — the first version was, and measured `BODY` after the door on
 * every drive. The door's open is two paints (the workspace, then the
 * backlinks the host reads asynchronously), and a request spent on the first
 * is undone by the second. This file has made that mistake twice before and
 * written it down both times (`restoreFocus`, `pendingAttachFocus`); the
 * rule is the same: held across repaints, released only when a settle finds
 * focus somewhere real or the deadline passes.
 */
// AND IT IS NOT ONLY THE WORKSPACE'S ANY MORE — task 0185 finding 2 sends a
// row's Open press to the destination page's own heading through the same
// request. The name is the door's, the mechanism is general: a selector
// resolved inside the stage once the render loop has settled. Kept as one
// path rather than a fifth, because five restoration paths is already the
// count this file has been caught by.
let pendingWorkspaceFocus: { readonly selectors: readonly string[]; until?: number } | undefined;
// Each look's first tool, in one selector: Obsidian's ribbon, the nav's
// Home under Notion, Coda and Fibery, the ribbon's tab under Word. It was
// the ribbon's alone, so a door into any other look asked focus of an
// element that never came and dropped it after the deadline — a keyboard
// user landed nowhere, which `e2e/hub.mjs` measures at every door.
//
// AND IT NO LONGER NAMES A LOOK. A fourth entry read
// `.m-app-fabric [data-ws-search]`, which is the same defect @Codex filed
// against the folded-pane refusal on `48121a7` — a list of the instances
// somebody found is not the set — so Fabric declares `entryFocus` instead,
// which is what the comment below this one already said the mechanism was
// for. This list is now component names only, and `look-records.test.ts`
// holds it at a ceiling of zero look names rather than trusting that.
const WS_FIRST_TOOL = '.m-ws-ribbon .m-ws-tool, .m-tool[data-morph-go="home"], .m-wd-tab';
/** WHERE A DOOR LEAVES FOCUS, asked of the look — `entryFocus` when it names
 *  one, the shared first-tool list otherwise. One line, so a seventh app
 *  answers it by declaring rather than by adding a selector to a list whose
 *  ORDER then decides which look wins. */
const wsEntryFocus = (template: AppTemplate | undefined): string =>
  lookOf(template).entryFocus ?? WS_FIRST_TOOL;
/**
 * `selector` may be a LIST, tried in order — @Codex's P2 on `552d25e`.
 *
 * Not a comma-separated selector, which is the shape this reached for first
 * and is wrong: `querySelector('.a, .b')` returns whichever matches first in
 * DOCUMENT order, not the first selector that matches. On a page carrying
 * both, the preference silently becomes "whichever is higher up".
 */
function focusWorkspaceAfterPaint(selector: string | readonly string[]): void {
  // No deadline yet: it starts at the FIRST attempt, which is the first
  // settle. @Codex, P2 on 4ab498b: a deadline counted from the gesture
  // could expire before the render loop ever settled on a cold or
  // throttled browser — the door's open is a generation plus a store
  // traversal — and the request would have been dropped without one try,
  // reproducing the exact failure it exists to fix.
  pendingWorkspaceFocus = { selectors: typeof selector === 'string' ? [selector] : selector };
}
function takePendingWorkspaceFocus(): void {
  const want = pendingWorkspaceFocus;
  if (want === undefined) return;
  want.until ??= Date.now() + 5000;
  // NOWHERE REAL only, and never for long — `takePendingCreateFocus`'s two
  // conditions, for its reasons: a real element means the person has moved
  // on, and the deadline stops a request outliving its own gesture's paints.
  //
  // "Nowhere" is wider than `<body>` here, and the door is why. A tab's
  // button is REMOVED by the repaint, so focus falls to `<body>` at once;
  // the hub's door is only HIDDEN, and Chrome leaves `activeElement` on the
  // hidden button until its next focus fixup — so a settle that asked only
  // "is it body?" read the door as the person's own choice and dropped the
  // request. Measured: 15/16 with the tab green and the door red. A node
  // that is detached or has no box is not somewhere a person is standing.
  const active = document.activeElement;
  const somewhereReal = active instanceof HTMLElement && active !== document.body
    && active.isConnected && active.getClientRects().length > 0;
  if (somewhereReal) { pendingWorkspaceFocus = undefined; return; }
  if (Date.now() > want.until) { pendingWorkspaceFocus = undefined; return; }
  const stage = $('stage');
  /**
   * NEVER AN INERT ITEM, and this was a regression I shipped.
   *
   * `WS_FIRST_TOOL` names `.m-wd-tab` for Word's ribbon, and under `app.word`
   * the FIRST element matching it is the inert `File` tab — imagery under
   * `0056`, `aria-hidden`, a picture of a control. `querySelector` took it,
   * `focus()` was a silent no-op on a span, and the fallback below then made
   * it focusable and focused it. So the door left a keyboard person standing
   * on a node their screen reader cannot describe, and the caret never
   * reached the sheet: `e2e/word-look.mjs` went 19/19 to 17/19 in `45ce12b`,
   * on the two clauses that measure exactly those things.
   *
   * The general form, and it is the one worth carrying: A FALLBACK THAT MAKES
   * A TARGET FOCUSABLE MUST FIRST ASK WHETHER THE TARGET IS ONE A PERSON CAN
   * BE LEFT ON. That fallback was written for a reader's plain-div title —
   * correct there — and it does not get to decide that an aria-hidden element
   * is a destination. So the resolution skips what is hidden from assistive
   * technology, and the whole `querySelectorAll` is walked rather than the
   * first match alone: a look's first tool can legitimately sit behind
   * imagery drawn before it.
   */
  let target: HTMLElement | null = null;
  for (const sel of want.selectors) {
    target = [...stage.querySelectorAll(sel)].find(
      (e) => e instanceof HTMLElement && e.closest('[aria-hidden="true"]') === null) as HTMLElement ?? null;
    if (target !== null) break;
  }
  target?.focus();
  /**
   * AND IF IT WOULD NOT TAKE FOCUS, MAKE IT ABLE TO — @Codex's P2 on
   * `c8b317d`.
   *
   * The row-open fallback resolves the destination's `.m-role-title`. For a
   * WRITER that element is `contenteditable` and focusable; for a reader it
   * is a plain div with no `tabindex`, so `focus()` is a silent no-op — the
   * row button that had focus is gone by then and `activeElement` stays on
   * `<body>`. A keyboard reader presses Open and lands nowhere, which is the
   * defect the whole request exists to prevent, surviving in the one branch
   * where the grant changes the element.
   *
   * Told by MEASURING rather than by a list of tag names: try, and if focus
   * did not move, add `tabindex="-1"` and try once more. That is the standard
   * shape for a programmatic focus target — reachable by script, and NOT
   * added to the sequential order, so a reader tabbing through the page does
   * not acquire a stop that a writer does not have.
   *
   * This is not `0188`. That task asks whether the ordinary object page
   * should carry a real heading at all, and wants its ruling; this only makes
   * the destination the request already names able to receive what it is
   * given.
   */
  if (target !== null && document.activeElement !== target) {
    target.setAttribute('tabindex', '-1');
    target.focus();
  }
  /**
   * AND A SATISFIED REQUEST IS OVER — @Codex, P2 on `72eb864`, converging with
   * the open item `0201` had already filed against itself.
   *
   * This cleared only on the two early returns, so a door whose paint settles
   * ONCE left the request standing for the rest of its five seconds. Two
   * costs, and the first is what `0201` was: `settleCanvasFocus` refuses to
   * place the initial caret while a request is outstanding, so the next
   * navigation's own caret was suppressed by the previous door's satisfied
   * one. The second is theirs: the stale selector is retried after the
   * element it names has gone.
   *
   * What this gives up is re-assertion — a second paint that destroys the
   * focused element no longer brings the request back. That is the trade the
   * ruling makes, and the `somewhereReal` return already covered the case it
   * was written for: focus that is still on a real element clears it anyway,
   * one settle later.
   */
  if (target !== null && document.activeElement === target) {
    pendingWorkspaceFocus = undefined;
  }
  // A re-rendered query input gets its caret at the END, so typing carries
  // on where it left off rather than inserting at the start.
  if (target instanceof HTMLInputElement) {
    const end = target.value.length;
    target.setSelectionRange(end, end);
  }
}


/**
 * A NAMED APP LOOK owns the whole window — decision 0055.
 *
 * The rail, the gesture toolbar and the Inspector are Morph's chrome, and
 * @Codex's first production verdict on the Obsidian row called the Inspector
 * occupying a third of the screen a *"disqualifying substitution"*. So while
 * a template is on the stage the host's chrome steps out: `index.html` hides
 * the rail and lets the page run edge to edge under `body[data-template]`,
 * and the drawer is closed. The way back is the ribbon's own last button.
 */
function applyTemplateChrome(): void {
  if (state.template) {
    document.body.dataset.template = state.template;
    closeInspector();
  } else {
    delete document.body.dataset.template;
    openNotes.list = [];
    openNotes.active = undefined;
  }
  // `display`, not `hidden`: the toolbar carries an inline `display:flex`,
  // which beats the attribute — seen on the first screenshot, where the
  // sliders sat above Obsidian's tab strip with `hidden` set.
  //
  // AND IT IS THE DRAWER THAT HIDES, not the toolbar inside it. The rail
  // moved behind a `details` so it would leave Morph's own reading path;
  // hiding only the toolbar would have left the word PRESENTATION sitting
  // above every reproduction's chrome — the same leak this comment already
  // records, one element out.
  const drawer = document.querySelector<HTMLElement>('[data-manual-role-drawer]');
  const controls = document.querySelector<HTMLElement>('[data-manual-role-controls]');
  if (drawer) drawer.style.display = state.template ? 'none' : 'block';
  if (controls) controls.style.display = state.template ? 'none' : 'flex';
}

/** The demo's own chrome takes its colours from the same derived ramp the
 *  layout does. A shell painted by hand next to a generated layout is a
 *  side-by-side advertisement for the hand-painted one. */
function paintChrome(palette: Palette) {
  const s = surfaceOf(palette, state.scheme);
  const r = document.documentElement.style;
  r.setProperty('--page', s.page);
  r.setProperty('--raised', s.raised);
  r.setProperty('--surface', s.surface);
  r.setProperty('--surface-hover', s.surfaceHover);
  r.setProperty('--border-subtle', s.borderSubtle);
  r.setProperty('--border', s.border);
  r.setProperty('--border-strong', s.borderStrong);
  r.setProperty('--solid', s.solid);
  r.setProperty('--text-muted', s.textMuted);
  r.setProperty('--text', s.text);
  document.documentElement.dataset.scheme = state.scheme;
}

const style = document.createElement('style');
style.textContent = `${BASE_CSS}\n${DROP_CSS}`;
document.head.appendChild(style);

/* ------------------------------------------------------------------- editing */

/**
 * Names, and the boundary they hold.
 *
 * The DOM is a public surface: a store id can be a user-chosen slug, and an
 * element attribute is readable by anything on the page. So an object is named
 * on screen by a per-session HANDLE, minted here, and only this map turns one
 * back into an id. It survives re-renders, because the edit host restores focus
 * by handle after a repaint and an id that changed would put the caret in
 * somebody else's row.
 */
const handles = new Map<ObjectId, ObjectHandle>();
const identities = new Map<ObjectHandle, ObjectId>();
let handleSeq = 0;

function withHandle(values: Readonly<Record<string, unknown>>, id: ObjectId): BoundValues {
  let handle = handles.get(id);
  if (!handle) {
    handleSeq += 1;
    handle = `o${handleSeq}` as ObjectHandle;
    handles.set(id, handle);
    identities.set(handle, id);
  }
  // Under a SYMBOL, not a string key: `columnsOf` and `preview` treat every own
  // key of a role-values object as a Role, so a string key would become a
  // phantom table column headed `undefined`.
  return { ...values, [OBJECT_HANDLE_KEY]: handle };
}

/** Render passes, so a commit from a dead pass can be told apart from a live
 *  one, and the specs those passes were painted from. */
let epoch = 0;
const specs = new Map<string, TLayoutSpec>();
let host: EditHost | undefined;
/** The side pane's own boundary, on its own root. See `renderAside`. */
let asideHost: EditHost | undefined;
/** Which document the side pane is reading — and therefore writing. Set by
 *  `renderAside` on every paint; the pane's bridge reads it at call time. */
let asideManifest: ManifestRef | undefined;
/** The rest of what `paneSurface` reads, set beside it on the same paint. Held
 *  as module state rather than closed over, because the listener is attached
 *  once at boot and every one of these changes on every repaint. */
let asideIR: IR | undefined;
let asideLensId: string | undefined;
let asideOwner: DemoPool | undefined;
let asideRow: BoundValues | undefined;
let asideBridge: RelationStore | undefined;

/**
 * The half of the anchor a single-object lens has nowhere to put.
 *
 * `renderHTML` emits the object half on a card or a table row, because those are
 * the elements that stand for an object. A single-object lens has neither: its
 * root is a region and its fields carry only the node half, so `readAnchor`
 * finds an incomplete annotation, refuses it — correctly, since a partial anchor
 * would let a commit from a dead pass through — and NOTHING in the sheet can be
 * edited. Found by driving it: every keystroke came back "read-only in this
 * lens" while the same field in the table edited fine.
 *
 * Completing it at the root is what the anchor contract is built for: each
 * attribute resolves at its own nearest ancestor, so one stamp names every field
 * below it. Only for a single-object lens — on a collection the row is the right
 * owner, and a root-level handle would make a click that missed every row
 * resolve to some object anyway.
 */
/**
 * Where the CONTENT starts, which is no longer always the spec root.
 *
 * With the shell on, `withShell` wraps the generated lens in a region whose
 * first child is the outline panel and whose second is the lens itself. Every
 * question the demo asks about "the layout" — what presentation was derived,
 * whether this is a single-object view — is a question about the second child,
 * and asking it of the root instead gets answered by the sidebar.
 *
 * Pre-order numbering makes this cheap: the panel's whole subtree is emitted
 * before the content's, so an index at or after the content root is inside it.
 */
function contentRootOf(ir: IR): number {
  if (state.shell.kind === 'off') return ir.root;
  return ir.nodes[ir.root]?.children[1] ?? ir.root;
}

function completeSingleAnchor(
  ir: IR, into: Element = $('stage'), only: BoundValues | undefined = objects[0],
  at: number = epoch, set: readonly BoundValues[] = objects,
): void {
  if (ir.nodes[contentRootOf(ir)]?.kind === 'collection') return;
  // A SHELL AROUND A COLLECTION presents the set too. Every named app look
  // but the note-shaped ones (0055) wraps its board or table in a region of
  // modules, so the check above let it through, and the paragraph over
  // this function came true: measured on Notion, the root wore task one's
  // handle, and every click that missed a card — the page's white, the
  // search box, an inert Filter — opened that task in place of the board.
  // The set is what a collection sourced from the subject draws, unless the
  // host named that region in `regionSets` (Obsidian's backlinks and
  // search, Fabric's hits: the note stays the subject). A set of one is
  // still one object — Word's sections draw [the note], the pane draws its
  // row — and keeps its stamp.
  const drawsTheSet = ir.nodes.some((m) => m.kind === 'collection' && (m.source ?? 'subject') === 'subject'
    && !(m.specId !== undefined && Object.prototype.hasOwnProperty.call(lastRegionSets, m.specId)));
  if (drawsTheSet && set.length > 1) return;
  const handle = handleOf(only);
  const root = into.firstElementChild;
  if (!handle || !root) return;
  root.setAttribute(ANCHOR_ATTR.object, handle);
  /**
   * The EPOCH only under a write grant — decision 0062, and this stamp was
   * the one place the withholding leaked.
   *
   * `readAnchor` resolves each attribute at its own nearest ancestor and this
   * is the ROOT, so stamping an epoch here handed one to every element in the
   * stage: a reader's `permissions` run measured exactly one, on `.m-root`,
   * and any element under it could therefore complete an edit anchor. The
   * write still failed — there are no grants to resolve against — but it
   * failed one layer later than the design says it should, and the renderer's
   * careful withholding was undone by the host in a single line.
   *
   * The HANDLE stays unconditional, for the same reason it is unconditional in
   * the renderer: it is what says which object this surface is about, and a
   * reader needs it to follow anything at all.
   */
  if (MAY_WRITE) root.setAttribute(ANCHOR_ATTR.epoch, String(at));
}

/**
 * One render at a time, and never a lost one.
 *
 * Every write ends in the store, the store tells its subscribers, and the demo
 * re-derives the shape and regenerates. Nothing patches the DOM: if the layout
 * should change it changes because the SHAPE changed, which is the entire claim.
 * Coalescing matters because a single commit can arrive twice — once from the
 * store's own event and once from the host asking for a repaint — and rendering
 * twice would drop the caret twice.
 */
let running = false;
let againAfter = false;
/**
 * A pending request to start a new VIEWING EPISODE, in decision 0023's sense.
 *
 * Sticky across a coalesced burst on purpose. A single gesture can produce both
 * kinds of request — `addProperty` writes a value, which repaints, and then
 * asks for the regeneration that makes its new field appear — and the two
 * arrive one after the other into the same run. If the last one in won, the
 * explicit shape command would be swallowed by the value repaint that its own
 * write triggered. So any boundary request in the burst makes the run that
 * lands a boundary.
 */
let pendingDerive = false;

/**
 * Re-render.
 *
 * `derive` says whether this is a new viewing episode — decision 0023: *"An
 * ordinary value edit updates the values rendered by the current lens but does
 * not regenerate or re-rank that lens."* It defaults to TRUE, so a call site
 * that has not thought about it keeps the old behaviour and the only paths that
 * are quiet are the ones that say so. That is the safe direction: a spurious
 * regeneration is churn, and a missing one shows stale structure.
 */
function schedule(derive = true): void {
  /**
   * A derive boundary is a SHAPE EPISODE, and it supersedes any suggestion
   * still compiling. @Codex, P2 on `fc7c930`: *"Add property publishing a new
   * role or a view-state control changing the active scope"* changes none of
   * `surfaceNow()`, `poolRequest`, or `tasteNow()`, so an offer compiled for
   * the PREVIOUS shape passed the currency guard — and Keeping it could hide
   * the very property the person just added.
   *
   * Here rather than at each shape-changing call site, because "the sites
   * that change shape" is a hand-kept list and this file has retired three of
   * those today. `derive` is already exactly the claim "the shape may have
   * changed" — including a cross-tab write arriving through `watchPool`,
   * where killing the pending offer is equally right: `0044`, uniformly. The
   * suggestion paths themselves finish through `run()` directly, so an offer
   * never supersedes itself.
   */
  if (derive) poolRequest += 1;
  if (derive) pendingDerive = true;
  if (running) { againAfter = true; return; }
  void (async () => {
    running = true;
    try {
      do {
        againAfter = false;
        const boundary = pendingDerive;
        pendingDerive = false;
        /**
         * The fifth phase task `0119` clause 3 names, and the last one nothing
         * measured. A file's repaint is not a special render — it is this loop,
         * reached through `schedule()` at the end of `attachFile()` — so
         * instrumenting a separate "ingest repaint" would be instrumenting a
         * path that does not exist.
         *
         * `morph.render.schedule`, NOT `morph.ingest.repaint`, which is what a
         * hostile review of the first version corrected twice over. It is not
         * every render — twelve call sites reach `run()` directly, the
         * bootstrap among them — and it is not an ingest's render either: every
         * Keep, pool acceptance, hide and promote comes through here too. A
         * measure named for one caller is wrong the first time something else
         * calls it, which is the argument `blobs.ts` makes for its own name.
         *
         * `e2e/ingest-budget.mjs` maps it to the phase called `repaint` and
         * scopes it to the pass that started after it, which is the only place
         * the ingest claim can honestly be made.
         */
        const paintedAt = typeof performance === 'object'
          && typeof performance.now === 'function' ? performance.now() : undefined;
        try {
          await run(boundary);
        } finally {
          if (paintedAt !== undefined && typeof performance.measure === 'function') {
            try {
              // Bounded. A measure entry lives until something clears it, and a
              // long editing session is thousands of renders — a slow permanent
              // leak, and an O(n) cost on anything that reads the buffer.
              if (performance.getEntriesByName('morph.render.schedule', 'measure').length > 8) {
                performance.clearMeasures('morph.render.schedule');
              }
              performance.measure('morph.render.schedule', { start: paintedAt });
            } catch { /* no-op */ }
          }
        }
      } while (againAfter);
      restoreFocus();
      takePendingAttachFocus();
      takePendingInvokeFocus();
      takePendingCreateFocus();
      takePendingWorkspaceFocus();
    } finally { running = false; }
  })();
}

/** A value landed and nothing about the pool's shape was commanded to change.
 *  Decision 0023's ordinary case, and the whole of task 0101's symptom. */
const scheduleValue = (): void => schedule(false);

/**
 * Put the caret back, once the render loop has SETTLED.
 *
 * Restoring inside `run` looked right and was not. A commit produces two
 * renders — the store's own event fires one, and releasing the caret asks for
 * another — so the caret was placed into a node the second pass detached a
 * moment later, and focus fell to `<body>`. Nothing looked broken: the edit had
 * landed, the value was right, and only the undo chord went missing, because the
 * host listens for it inside the render root and focus was no longer in there.
 */
function restoreFocus(): void {
  if (!pendingFocus) return;
  const { anchor, offset, until } = pendingFocus;

  // Held ACROSS repaints, not consumed by the first one.
  //
  // The note above says a commit produces two renders. It produces three when
  // the write changed the collection's coverage: the store's event, the caret
  // release, and then — hundreds of milliseconds later, because generation is
  // asynchronous — the regenerated spec. Restoring once satisfied the first two
  // and was undone by the third: measured on Archive, the caret went back into
  // the origin chooser and a repaint detached it 1.4s later, leaving focus on
  // `<body>` with nothing on screen to say a field had been left.
  //
  // Two conditions bound the retry, and between them it can only ever put the
  // caret back where the person already was:
  //
  //  - `<body>` ONLY. Focus lands there when a repaint detaches the focused
  //    node and nowhere else; anything the person chose — a cell, a chooser, a
  //    pool pill — is a real element, and finding one means they have moved on
  //    and this is finished.
  //  - a deadline, because a lingering request would let some later, unrelated
  //    repaint pull the caret back into a field nobody is looking at.
  const active = document.activeElement;
  if (active !== null && active !== document.body) { pendingFocus = undefined; return; }
  if (Date.now() > until) { pendingFocus = undefined; return; }
  host?.restore({ ...anchor, epoch }, offset);
}

/** Long enough to outlast the regenerate a coverage-changing write triggers,
 *  short enough that it cannot reach a repaint the person caused afterwards.
 *  Measured, not guessed: the regenerate above landed 1.4s after the commit. */
const FOCUS_RETRY_MS = 3000;

/**
 * Put focus back on the button that was pressed, once the repaint has settled.
 *
 * A successful action calls `schedule()`, which replaces the whole of `#stage`.
 * The pressed button is detached and focus falls to `<body>` — so a keyboard
 * user who marks a task seen loses their place entirely, and on a board the row
 * has also moved to a different group. @Codex, P2 on #138: the two existing
 * restoration paths take `pendingFocus` from an inline edit and
 * `pendingAttachFocus` from a relation control, and an invocation recorded
 * neither.
 *
 * BY OBJECT HANDLE, not by node index or DOM position. The whole point of a
 * repaint is that the lens may be different afterwards: the task moved group,
 * the row moved, the node index means something else. The handle is the one
 * thing that still names the same object on the other side, which is the same
 * reasoning `takePendingAttachFocus` gives for keying on a role.
 *
 * Silent when the object is no longer on screen — a filter may have taken it
 * away, and stealing focus to somewhere arbitrary is worse than leaving it.
 * That is `restore`'s own rule and this follows it.
 */
let pendingInvokeFocus: {
  object: string; action: string; inCollection: boolean;
} | undefined;

function takePendingInvokeFocus(): void {
  const want = pendingInvokeFocus;
  pendingInvokeFocus = undefined;
  if (want === undefined) return;

  /**
   * OBJECT, ACTION, **AND THE SURFACE IT WAS PRESSED ON**.
   *
   * Three tries, and the first two were each half right.
   *
   * Matching the object alone put focus on the panel's **New** after a row's
   * Update: the panel's card carries `data-morph-o` for the first object in the
   * pool — task 0085 — so it matched the handle first and won. Measured.
   *
   * Adding the action fixed that and broke the mirror image, which @Codex
   * found: the panel ALSO declares `demo.mark-seen`, so for the row whose
   * object the panel happens to be anchored to, object and action are identical
   * on both buttons. Searching the collection first then sends a PANEL press's
   * focus to the row.
   *
   * Neither identity is enough because the two buttons are genuinely
   * indistinguishable by identity — they name the same action on the same
   * object. What tells them apart is where the person was standing, and that is
   * knowable only at press time. So it is recorded there and used to order the
   * search: the surface that was operated first, the other as a fallback for
   * when a repaint has moved things.
   *
   * The same argument `takePendingAttachFocus` makes one line down about
   * `#stage` and `#paneStage` — *"neither contains the other and the wrong one
   * is silently plausible"*.
   */
  const sel = `[${ANCHOR_ATTR.object}="${want.object}"]`;
  const wanted = `button.m-invoke[data-morph-action="${want.action}"]`;
  const scopes = want.inCollection
    ? ['#stage .m-collection ', '#stage ']
    : ['#stage ', '#stage .m-collection '];
  for (const scope of scopes) {
    for (const holder of document.querySelectorAll(`${scope}${sel}`)) {
      const btn = holder.querySelector(wanted);
      // Never the one that is already focused, and never a button in the
      // collection when the panel was pressed: the panel's own is found first
      // by the reordered scopes above, so this only guards the fallback.
      if (btn instanceof HTMLElement) {
        if (document.activeElement !== btn) btn.focus();
        return;
      }
    }
  }
}

let unwatch: (() => void) | undefined;
/** Re-subscribed whenever the pool or its manifest moves: a scope is keyed by
 *  its manifest ref, and extending a manifest makes a new one. */
function watchPool(): void {
  unwatch?.();
  // A VALUE repaint. Decision 0023: *"background store events update values but
  // do not silently begin a new viewing episode."* A second writer under 0005,
  // or this tab's own commit arriving back through the store, changes what the
  // cells read and not which roles the lens ranks.
  unwatch = db.store.subscribe(pool.scope, () => {
    /**
     * The document view now takes the ordinary value repaint too.
     *
     * The old rule suppressed it entirely whenever a canvas was mounted, for a
     * measured reason: *press Enter, and the commit woke the repaint, which
     * replaced the canvas — every character after that went to a detached
     * element.* But suppressing it indefinitely is its own defect, and @Codex's
     * push review named it: a title changed in another tab *"remains stale
     * indefinitely in the document header or footer even though its channel
     * notice was received."*
     *
     * Decision 0031 removes the reason rather than the symptom. A repaint now
     * rebuilds the generated furniture and TRANSPLANTS the live canvas island
     * into it, so the editing session survives its surroundings being replaced.
     *
     * An in-flight save still gates it, and for a different reason that has
     * not gone away: a canvas save has already updated its own projection, so
     * repainting for it is work with no answer — and the read it would take is
     * mid-flight by definition.
     *
     * Read straight off the counter rather than through `settled()`: this is a
     * question about NOW, and awaiting a quiescent moment here would repaint
     * for a save whose own projection has since landed.
     */
    if (quiescence.inFlight === 0) scheduleValue();
  });
}

/**
 * A write landed.
 *
 * The caret is released deliberately. `deferRepaintWhileEditing` holds a repaint
 * for as long as an editable element has focus, and on Enter the host puts focus
 * straight back into the cell it just committed — so a commit that left it there
 * would defer the repaint for ever, and the lens would only catch up when the
 * user happened to click somewhere else. Enter means done.
 */
function afterWrite(focus?: {
  readonly object: ObjectHandle; readonly node: number; readonly role: Role;
}): void {
  // Where to put the caret back. By (object, role, node) and taken from the
  // intent — never by DOM position, and never read off the live document:
  // restoring by position puts the caret in the wrong cell exactly when the
  // generator re-ranked the roles, and reading the document is too late because
  // the store's own event may already have repainted over it.
  //
  // The ROLE is what makes that first sentence true. It used to say (object,
  // node) and claim immunity from a re-rank, and a node index is exactly what a
  // re-rank moves: index 14 is Status before the write and Author after it, so
  // the caret landed in a different field and nothing said so. `restore` checks
  // the role against the new IR and re-finds the cell, or leaves focus where it
  // is — task 0096's focus regression.
  if (focus) {
    /**
     * WHERE the caret goes back is a fact about how the session ENDED — task
     * 0163. End-of-value is right for Enter (the person finished the value
     * and continues after it) and exactly wrong for a session a repaint or a
     * focus steal interrupted mid-word: measured, a cross-tab write storm
     * spliced " STORM-a" into the middle of a sentence and appended the rest
     * 40 characters later, because this offset was unconditionally MAX. The
     * host records the last typed caret and why the session died;
     * read-and-clear, so a write with no session (a row button, addProperty)
     * cannot inherit a stale record — and the anchor must match, for the
     * same reason.
     */
    const ended = host?.takeLastEnded();
    // Matched on object AND node — a session's DOM anchor carries no role
    // (readAnchor builds object/node/epoch from the attributes), and both
    // values here come from the same pass: the intent's node is the grant's,
    // the session's is the element's, for the commit that just ran.
    const offset = ended && ended.why !== 'enter' && ended.caret !== undefined
      && ended.anchor.object === focus.object && ended.anchor.node === focus.node
      ? ended.caret : Number.MAX_SAFE_INTEGER;
    pendingFocus = {
      anchor: { ...focus, epoch }, offset,
      until: Date.now() + FOCUS_RETRY_MS,
    };
  }
  const active = document.activeElement as HTMLElement | null;
  if (active?.getAttribute('contenteditable') === 'true') active.blur();
  // A VALUE write, by construction — this is the write path's own callback.
  // `addProperty` is the gesture that writes AND commands a shape change, and
  // it asks for the boundary itself after this returns. Sticky within the
  // burst, so the explicit command still wins over the repaint its own write
  // caused rather than being swallowed by it.
  scheduleValue();
}

/** Where focus goes once the repaint has landed. */
let pendingFocus: { anchor: EditAnchor; offset: number; until: number } | undefined;

/**
 * Clicking an object opens ITS lens — a different generated spec over a scope
 * seeded on one object, not a panel over the old one.
 *
 * A click inside an editable cell is an EDIT, so it must not navigate and must
 * not repaint: doing either would destroy the caret the click just placed. It
 * still records which object was touched, because now that every cell in a row
 * is editable there is very little of a table left to click that is not — so
 * without this the Cardinality control could only ever open the first object,
 * which is the bug this whole path exists to remove.
 */
/* --------------------------------------------------------------- view state */

/**
 * Where the user left the panels — collapsed or not.
 *
 * This is the other half of decision 0014's line. Which edge the panel is
 * docked to went into the SPEC, because that is a layout decision worth
 * pooling. Whether it is open right now is per-user, per-device and nobody
 * else's business, so it lives here and cannot reach a genome: the spec has no
 * field for it, which is why `collapsed` is refused by the schema while
 * `collapsible` is accepted.
 */
const viewState = createViewState({ storage: localStorage, key: 'morph.demo.view.v1' });

/**
 * Where each recording is scrubbed to — the same line, one step further.
 *
 * A playhead is a coordinate in time, so decision 0014 puts it on the private
 * side with the dragged bubble: per-user, per-device, never pooled. Nobody's
 * taste model is improved by learning that this person stopped a trailer at
 * 0:47, and the spec has no field shaped to hold it.
 */
const playback = createPlaybackState({ storage: localStorage, key: 'morph.demo.playback.v1' });

/** Apply stored collapse state after a paint. The renderer emits the panel
 *  expanded; the host is what knows the user closed it. */
function applyViewState(): void {
  for (const el of $('stage').querySelectorAll('.m-place')) {
    const key = el.getAttribute('data-morph-node');
    if (!key) continue;
    const collapsed = viewState.get(key)?.collapsed === true;
    if (collapsed) el.setAttribute('data-morph-collapsed', 'true');
    else el.removeAttribute('data-morph-collapsed');
    const toggle = el.querySelector('.m-place-toggle');
    if (toggle) {
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      toggle.textContent = collapsed ? '+' : '\u2212';
    }
  }
  // A board COLUMN folded to a rail — Fibery's collapsed "Resolved" column.
  // The same store and the same attribute, keyed by the grouping role and
  // the value rather than a node index (the renderer writes the key on the
  // column's head AND on its cell in every lane, so one fold folds both).
  // The sidebar's tree rows arrive OPEN unless a person folded them: the
  // renderer draws the pages, and only a recorded `collapsed` hides them.
  for (const el of $('stage').querySelectorAll('[data-morph-collapse-key]')) {
    const key = el.getAttribute('data-morph-collapse-key');
    if (!key) continue;
    const collapsed = viewState.get(key)?.collapsed === true;
    if (collapsed) el.setAttribute('data-morph-collapsed', 'true');
    else el.removeAttribute('data-morph-collapsed');
    // Every toggle inside carries the key too (a column's one; the
    // sidebar's, and the topbar's compact block that shares its key).
    for (const toggle of el.querySelectorAll('[data-morph-collapse]')) {
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      // The toggle's own noun first: a tree row's container carries none.
      const noun = toggle.getAttribute('data-morph-collapse-name') ?? el.getAttribute('aria-label') ?? '';
      toggle.setAttribute('aria-label', `${collapsed ? 'Expand' : 'Collapse'} ${noun}`);
      // A column's toggle is its glyph; the sidebar's keeps its icon.
      if (toggle.classList.contains('m-group-toggle')) toggle.textContent = collapsed ? '\u00bb' : '\u00ab';
    }
  }
  // The matrix's tracks follow the folds: a rail's track is the rail's width
  // and every other column keeps its own, and only the host knows which is
  // which — so the template is written here, from the head row, as a
  // variable the stylesheet declares the fallback for.
  for (const lanes of $('stage').querySelectorAll<HTMLElement>('.m-as-lanes')) {
    const tracks = [...lanes.querySelectorAll('.m-lane-head .m-group')]
      .map((h) => (h.getAttribute('data-morph-collapsed') === 'true' ? 'var(--lane-rail)' : 'var(--lane-track)'));
    lanes.style.setProperty('--lane-cols', tracks.join(' '));
  }
}

/**
 * The key a fold is stored under: the SECTION ANCHOR, which is positional and
 * carries no object identity — the same string the table of contents links to,
 * from the same function, so a fold and a link cannot disagree about which
 * section they mean.
 *
 * One reader, used by both halves. A fold written under one key and read under
 * another is the defect shape that has cost this repo a cycle before — the
 * dismissal filter that had never excluded anything — so `isSectionAnchor`
 * refuses anything that is not the string `sectionAnchor` produces, rather than
 * silently keying on some other id a card might grow later.
 */
function foldKey(el: Element): string | undefined {
  const id = el.closest('.m-card')?.id;
  return isSectionAnchor(id) ? id : undefined;
}

/**
 * Which sections are folded, restored after a paint.
 *
 * `<details>` keeps its own open state in the DOM, and the DOM is replaced on
 * every repaint — so without this a fold survives until the next keystroke.
 * Decision 0014 puts it on the private side with the panel collapse it sits
 * next to: per-user, per-device, never pooled.
 */
function applyFoldState(): void {
  for (const el of $('stage').querySelectorAll('details.m-fold')) {
    const key = foldKey(el);
    if (!key) continue;
    // Open is the default: a document opens readable, and folding is something
    // the reader did. Only a recorded `collapsed` closes one.
    if (viewState.get(key)?.collapsed === true) el.removeAttribute('open');
    else el.setAttribute('open', '');
  }
}

/**
 * A fold is recorded from the user's ACTIVATION, never from the `toggle` event.
 *
 * `toggle` was the obvious choice and it is the wrong one, for two reasons
 * that only a browser could tell me:
 *
 *  1. **Chromium fires `toggle` when a `<details open>` is INSERTED.** Every
 *     repaint therefore delivered one event per open section, each reporting
 *     "the user just opened this" — and a handler that believed them
 *     overwrote every stored fold with `collapsed: false`. Measured: eight
 *     sections, eight events, eight `false`s in storage, and a fold that could
 *     not survive a single repaint.
 *  2. **`toggle` is delivered asynchronously.** A repaint triggered by the
 *     same click ran first, so the event arrived pointing at an element that
 *     had already been replaced.
 *
 * So the DOM does not tell the host what happened; the host tells the DOM. The
 * summary's default action is cancelled, the stored state flips, and
 * `applyFoldState` paints it — which is the shape `toggleCollapse` next door
 * already uses, and the shape decision 0014 asks for: the host owns view state
 * and the markup is a projection of it.
 *
 * A `click` listener catches the keyboard too: Enter and Space on a focused
 * `<summary>` dispatch a click, which is why cancelling the default action
 * does not cost the keyboard affordance `<details>` was chosen for.
 */
function toggleFold(e: Event): void {
  const summary = (e.target as HTMLElement | null)?.closest?.('summary.m-fold-summary');
  if (!summary) return;
  const el = summary.closest('details.m-fold');
  const key = el ? foldKey(el) : undefined;
  if (!key) return;
  e.preventDefault();
  viewState.set(key, { collapsed: viewState.get(key)?.collapsed !== true });
  applyFoldState();
}

/**
 * Move a calendar to another month.
 *
 * The control carries the MONTH it goes to rather than a direction, so the
 * host never has to work out what "previous" means. The module already knows —
 * it was given the list of months that have anything in them — and an arrow
 * meaning "previous" would be a second computation that has to agree with the
 * module's idea of the same thing, which is the defect shape this repo keeps
 * paying for.
 *
 * The value is still checked rather than trusted: it arrives off an attribute
 * in markup, and `state.calendarMonth` is handed straight back to the module
 * next pass.
 */
function stepCalendar(e: Event): void {
  const el = (e.target as HTMLElement | null)?.closest?.('button.m-cal-nav');
  if (!(el instanceof HTMLButtonElement) || el.disabled) return;
  const to = el.getAttribute('data-month');
  if (!to || !/^\d{4}-(0[1-9]|1[0-2])$/.test(to)) return;
  state.calendarMonth = to;
  schedule();
}

function toggleCollapse(e: Event): void {
  // A panel's toggle or a board column's: both carry the key, both fold
  // through the one store, both are painted back by `applyViewState`.
  const el = (e.target as HTMLElement | null)?.closest?.('[data-morph-collapse]');
  if (!(el instanceof HTMLElement)) return;
  const key = el.getAttribute('data-morph-collapse');
  if (!key) return;
  viewState.set(key, { collapsed: viewState.get(key)?.collapsed !== true });
  applyViewState();
}

/* ------------------------------------------------------------------- actions */

/** The registry, built once. Actions are REGISTERED, not enumerated — decision
 *  0010's argument, applied to behaviour in 0014. */
const registry = buildRegistry();

/** The IR of the pass currently on screen, so a click can resolve a button back
 *  to the invocation the spec declared. Read from the IR and never from the
 *  DOM, exactly as the edit path does: markup is output, not a source of truth. */
let lastIR: IR | undefined;
/** The spec that produced it. `applyDrop` edits a spec, not an IR. */
let lastSpec: TLayoutSpec | undefined;
/** The shape the current paint was generated from. Promote is only offered
 *  where it would change THIS shape's ranking — see `promotionChangesRanking`.
 *  Kept beside `lastSpec` because it has the same lifetime and the same
 *  reader. */
let lastShape: ObjectShape | undefined;

/**
 * What an action is allowed to do, in this demo.
 *
 * Every write goes through the SAME door an inline edit goes through — the
 * store's edit path, with its grants and refusals. An action that had its own
 * quieter route to the store would be a way around the authorization the whole
 * permission boundary exists to enforce, and the boundary would be worth
 * nothing.
 */
function effectsFor(subject: ObjectId | undefined) {
  return {
    async setProperty(role: Role, value: unknown): Promise<void> {
      refuseUnauthorized('update');
      if (!subject) { say('nothing is selected to update', 'bad'); return; }
      // STRAIGHT TO THE WRITE, naming the subject. This used to assign
      // `selected = subject` and call `addProperty`, borrowing the form's
      // gesture guard — which refuses unless the lens is in single-object mode,
      // so an action pressed from a row could never write at all. Swapping a
      // module-level variable around an await was the tell: the subject was
      // being smuggled through the UI's state instead of passed.
      const wrote = await writeProperty(subject, role, labelForRole(role), String(value));
      /**
       * SAY WHERE IT WENT — task 0158. A successful press was silent, so the
       * last toast standing (the date FILTER's "Date changed", from a control
       * the Coda blind driver had just used) read as the button's report —
       * and when the written role is not a column of the current view, no
       * cell moves either, so the driver reasonably concluded the press did
       * nothing. The refusal path already speaks; success must too, and when
       * the write landed somewhere this view does not draw, the sentence has
       * to say so or the screen contradicts it. Visibility is read from the
       * painted IR, not from the pool's defs — the IR is what the person is
       * actually looking at.
       */
      if (!wrote) return;
      const drawn = lastIR?.nodes.some((n) => n.role === role) ?? false;
      // The POOL'S label, because that is the word on the person's own
      // headers — labelForRole says "Date" where every surface of the notes
      // pool says "Written", and a toast in a third vocabulary is the same
      // homonym trap this fix exists to close.
      const label = pool.defs.find((d) => d.role === role)?.label ?? labelForRole(role);
      say(drawn
        ? `${label} updated`
        : `${label} updated on the ${pool.typeHint} — not a column in this view; open the row to see it`);
    },
    async createObject(title: string): Promise<string> {
      refuseUnauthorized('create anything');
      await createObject(title);
      // `createObject` opens the new object's own lens, so `selected` IS the
      // thing it made. Reporting it rather than inventing an id keeps the
      // return value honest even though no action here uses it.
      return String(selected ?? '');
    },
    async navigate(via: Role): Promise<void> {
      if (!subject) { say('nothing is selected to open', 'bad'); return; }
      await openReferenced(subject, via);
    },
  };
}

/**
 * A button was pressed.
 *
 * The node index comes off the element, the invocation comes out of the IR, and
 * the registry decides whether it may run. The demo never reads the action name
 * from the DOM as if it were authoritative — the attribute is there for
 * debugging and for a host that wants to style by action; the IR is the source.
 */
let bridge: RelationStore | undefined;
let lastLens: string | undefined;

/**
 * What this reference may be pointed at, from the scope the POOL DECLARED.
 *
 * This used to be *"whichever set the lens happens to be showing"* —
 * `siblings` for a single-object lens, `objects` for a collection. For a pool
 * whose references point at their own kind that is accidentally right, and for
 * Films it is wrong in a way that persists: the picker offered other FILMS as a
 * Filmmaker, and choosing one wrote it. @Codex drove it and got *"this film's
 * filmmaker is Stalker, Released 1979, Director Andrei Tarkovsky"* — a relation
 * the product would happily keep.
 *
 * So the target scope is read from the pool's `targets` declaration, the same
 * way `across` names the documents a lens may read from. Decision 0049's
 * sentence covers both halves: **a lens may read what it POINTS AT, never what
 * it can merely name.**
 *
 * Falls back to the lens's own set when nothing is declared, which is what a
 * pool of notes citing notes wants — but every ref role the demo ships declares
 * one, so the fallback is a contract for a pool somebody adds later rather than
 * a path this demo takes.
 */
function scopeForRole(role: Role | undefined, owner: DemoPool | undefined): Scope | undefined {
  const targets = role ? owner?.defs.find((d) => d.role === role)?.targets : undefined;
  if (!targets) return undefined;
  return 'pool' in targets
    ? db.pools.find((p) => p.id === targets.pool)?.scope
    /**
     * A PICKER'S DOMAIN IS WHAT IT CAN NAME — task `0177`, and it is decision
     * `0060` read forwards instead of argued with.
     *
     * `{manifest, typeHint}` is not a pool domain, and `0060` settled why: a
     * manifest is a boundary over ROLES, not over objects, and nothing anywhere
     * records which manifest an object belongs to. So Fibery's Assignee picker
     * offered ELEVEN NAMELESS ROWS above its four people — the FILMS pool's
     * directors, `typeHint: 'person'` like the features pool's own, projecting
     * to nothing because their values are stored under the films manifest's
     * keys. Invisible in every other pool because `person` is the one type hint
     * two pools declare.
     *
     * The task offered two repairs and I am taking neither, because a third one
     * falls out of the ruling itself. A foreign object *"stays a MEMBER while
     * its foreign roles resolve to nothing"* — which is a property this scope
     * can ASK ABOUT, through the query layer that already exists:
     *
     *     title is-not-empty
     *
     * An option a picker cannot name is not a narrower option, it is an
     * unusable one: the whole gesture is choosing by name. So the domain says
     * what it means rather than approximating it, and the eleven leave for the
     * reason they were wrong to be there — this manifest cannot read them.
     *
     * WHY NOT THE TWO THE TASK NAMED. A durable member list has to be
     * maintained by every writer, as the task says. And the durable domain
     * PROPERTY it preferred is heavier than it reads: `Scope.query` is the
     * spec's own `TQuery`, a `Predicate` names a `RoleRef` from the CLOSED
     * vocabulary, and its value comes from a `viewState` index — so that shape
     * needs a new role in the vocabulary and puts a pool id through the channel
     * built to keep names out of queries. Recorded in `0177` rather than left
     * for the next session to discover.
     */
    : { manifest: owner!.manifest, typeHint: targets.typeHint,
      query: { filter: { where: { role: 'title', op: 'is-not-empty' } } } };
}

/**
 * Whether a role may point at an object — asked of the STORE, against the same
 * scope the picker was built from.
 *
 * One declaration, read twice: if the check re-encoded the domain instead of
 * resolving it through `scopeForRole`, the picker and the write boundary would
 * be two descriptions of one rule, free to drift. That drift is the defect this
 * repo keeps re-finding, so the shared resolver is the point rather than the
 * check.
 *
 * `seed` narrows a scope to named objects — `members` refuses any record not in
 * it — so this is an exact membership test rather than a search through a page
 * of candidates. That also means it cannot inherit the picker's 200-row ceiling:
 * target 201 is refused by the picker for being unlisted, never by this for
 * being out of scope.
 */
async function targetInScope(
  role: Role, target: ObjectId, owner: DemoPool | undefined,
): Promise<boolean> {
  const scope = scopeForRole(role, owner);
  if (!scope) return true;               // declares nothing, constrains nothing
  const hit = await db.store.query({ ...scope, seed: [target] }, { limit: 1 });
  return hit.items.length === 1;
}

/**
 * ONE RELATION SURFACE: a root element, and the state a relation gesture on it
 * needs to mean something.
 *
 * The four functions below read `lastIR`, `lastLens`, `bridge`, `pool`,
 * `siblings`, `objects` and `#stage` directly — module state that all means
 * THE STAGE. So the side pane could be given relation controls and not one of
 * them could work: @Codex measured it, *"pressing any of the newly visible
 * controls after following a Parent task reference does nothing — Add never
 * opens a picker and Remove never writes."* The honest interim was to draw no
 * gestures at all, because **an affordance with nothing behind it is worse than
 * a missing one**. This is task 0077 — the route, rather than the absence.
 *
 * Every field is a FUNCTION, read at call time, never captured. That is the
 * rule `EditBridgeDeps.manifest` already states in its own comment, and the one
 * whose violation wrote a note's title into the tasks manifest: a surface
 * captured at construction is a surface describing whatever was open then.
 */
interface RelationSurface {
  /** Where this surface's markup lives — for the focus lookup, which cannot use
   *  the element it started from because the repaint replaced it. */
  readonly root: () => HTMLElement;
  readonly ir: () => IR | undefined;
  readonly lens: () => string | undefined;
  readonly store: () => RelationStore | undefined;
  /** Which document to read and write through. */
  readonly manifest: () => ManifestRef;
  /**
   * This surface's COMPLETE reading scope — its manifest and its own `across`.
   *
   * @Codex: *"with Tasks on the stage and a Note in the pane, opening that
   * note's cross-document `Raised by` occurrence checks the Notes manifest plus
   * Tasks' `across` list (Notes), rather than the pane owner's `across` list
   * (Tasks), so `occurrenceTarget` refuses the otherwise readable task."*
   *
   * A surface manifest combined with stage-global scope state is two documents'
   * answers to one question — the same shape as the pane reading through its
   * home and writing through the open pool, which #120 spent three commits on.
   * One surface, one scope.
   */
  readonly scope: () => Scope;
  /**
   * Whose DECLARATIONS decide a role's target domain — or **nobody's**.
   *
   * `undefined` is a real answer, not a missing one. An object no pool owns
   * borrows no pool's domain: #120 established that for the pane's fields and
   * labels, and a picker is the same question with a write behind it. Falling
   * back to the open pool here would offer a note whatever the pool on screen
   * points at.
   */
  readonly owner: () => DemoPool | undefined;
  /** Offered when a role declares no domain at all. */
  readonly fallback: () => readonly BoundValues[];
}

/** Which surface an element belongs to. `#stage` and `#paneStage` are
 *  siblings, so containment answers it and nothing has to be told. */
const surfaceFor = (el: Element | null): RelationSurface =>
  el?.closest('#paneStage') ? paneSurface : stageSurface;

const stageSurface: RelationSurface = {
  root: () => $('stage'),
  ir: () => lastIR,
  lens: () => lastLens,
  store: () => bridge,
  manifest: () => pool.manifest,
  scope: () => ({ manifest: pool.manifest, ...crossDocs() }),
  owner: () => pool,
  fallback: () => (siblings.length ? siblings : objects),
};

/**
 * The pane's surface. Its four reads are set by `renderAside` on every paint,
 * beside `asideManifest` and for the same reason.
 *
 * `asideOwner` is deliberately NOT `?? pool`. See `RelationSurface.owner`.
 */
const paneSurface: RelationSurface = {
  root: () => $('paneStage'),
  ir: () => asideIR,
  lens: () => asideLensId,
  store: () => asideBridge,
  manifest: () => asideManifest ?? pool.manifest,
  // The PANE OWNER's `across`, not the stage's. An unowned object gets none:
  // no pool declares what it may follow, so it may follow nothing.
  scope: () => ({
    manifest: asideManifest ?? pool.manifest,
    ...(asideOwner?.scope.across?.length ? { across: asideOwner.scope.across } : {}),
  }),
  owner: () => asideOwner,
  // One object is on screen, so one object is the fallback set — and it is
  // filtered out for being the source, which is why an undeclared role in the
  // pane says "nothing to link this to" rather than offering the stage's rows.
  fallback: () => (asideRow ? [asideRow] : []),
};

async function attachCandidates(
  role: Role | undefined, surface: RelationSurface,
): Promise<readonly BoundValues[]> {
  const owner = surface.owner();
  const def = role ? owner?.defs.find((d) => d.role === role) : undefined;
  if (!def?.targets) return surface.fallback();

  const scope = scopeForRole(role, owner);
  if (!scope) return [];

  // Through `query` + `projection`, the same pair every other set on this page
  // comes through — so a candidate carries an opaque handle and the renderer
  // never sees an identity.
  const [rows, slice] = await Promise.all([
    db.store.query(scope, { limit: 200 }),
    db.store.projection(scope, { limit: 200 }),
  ]);
  return slice.items.map((values, i) => withHandle(values, rows.items[i]!.id));
}

/** A target's name, for the picker. The same rule `preview()` uses — title,
 *  then subtitle — because a picker headed `[object Object]` is not a picker. */
const nameOfTarget = (o: BoundValues): string =>
  String(o['title'] ?? o['subtitle'] ?? o['caption'] ?? 'untitled');

const escAttr = (v: string): string => v.replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
const escText = (v: string): string => v.replace(/[&<>]/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]!);

interface RelateSite {
  readonly lens: string; readonly node: number;
  readonly specId: string; readonly object: ObjectHandle;
  /**
   * The slot's role, read off the IR beside `specId` and carried with the site
   * rather than looked up again at each call.
   *
   * It lives HERE, and required rather than optional, because the first version
   * read it at the one call site that needed it — and a mutation that simply
   * stopped passing it survived every unit test in the suite. The bridge treats
   * an absent role as "unconstrained", so forgetting to send it silently turns
   * the target-scope check off in the product while the check's own tests, which
   * build their requests by hand, stay green. A guard that cannot see its caller
   * is not guarding the caller.
   *
   * On the site, the type system asks the question instead of a test.
   */
  readonly role: Role | undefined;
}

/**
 * Attach and detach, routed the way `.m-invoke` is.
 *
 * The node index comes off the element and the SPEC id comes out of the IR —
 * never from the DOM, which could name a slot it was not granted. The detach
 * token is the one thing read from the markup and it is not an address: it is
 * opaque, the store minted it during the projection this page is showing, and
 * `relationTargetFor` refuses one this store never minted.
 */
async function relateFromClick(e: Event, surface: RelationSurface): Promise<void> {
  const el = (e.target as HTMLElement | null)?.closest?.('.m-attach,.m-detach');
  const ir = surface.ir();
  const store = surface.store();
  const lens = surface.lens();
  if (!(el instanceof HTMLElement) || !ir || !store || !lens) return;
  const i = Number(el.getAttribute(ANCHOR_ATTR.node));
  const specId = Number.isInteger(i) ? ir.nodes[i]?.specId : undefined;
  if (!specId) { say('that control names no relation', 'bad'); return; }

  const source = readAnchor(el)?.object;
  if (!source) { say('that control is not attached to an object', 'bad'); return; }
  const site: RelateSite = {
    lens, node: i, specId, object: source, role: ir.nodes[i]?.role,
  };

  if (el.classList.contains('m-detach')) {
    const token = el.getAttribute('data-morph-t');
    if (!token) { say('that reference cannot be removed', 'bad'); return; }
    el.setAttribute('disabled', 'true');
    const { role: _r, ...where } = site;
    const out = await store.relate({ ...where, mutation: 'unlink', token });
    if (!out.ok) { el.removeAttribute('disabled'); say(out.refusal.message, 'bad'); return; }
    say('reference removed');
    // FOCUS GOES SOMEWHERE. The control the reader just pressed no longer
    // exists — the repaint removes it — and a browser drops focus to `<body>`
    // when that happens, so a keyboard reader was left with nothing selected
    // and had to find the slot again. Measured on the deployed preview by
    // @Codex, and it is the same defect as the footnote apparatus's fragment
    // jump one task ago: the viewport moves and the reader does not.
    //
    // The Add button of the same slot is where they were working, and the
    // picker already returns focus there on Escape. Waits for the repaint the
    // write triggers rather than racing it.
    pendingAttachFocus = site.role === undefined ? undefined : { role: site.role, surface };
    return;
  }
  void openAttachPicker(el, site, surface);
}

/**
 * Put the reader back on the slot's own Add button, AFTER the repaint that
 * removed the control they pressed.
 *
 * By NODE index, because the element itself is gone by then: a relation write
 * repaints the lens, so the control that comes back is not the one that was
 * pressed. The node index survives the repaint and names the same slot in the
 * new markup.
 *
 * **A request, consumed by the render, rather than a delay chosen to be long
 * enough.** A first version used two `requestAnimationFrame`s on the reasoning
 * that the first fires before the repaint and the second after it. That is a
 * guess about ordering, and @Codex measured it failing on the deployed page —
 * `document.activeElement` was `BODY` at 0, 50, 100, 200, 400, 800 and 1200ms.
 * It passed locally, which is worse than failing: the frames happened to land
 * after the repaint on a fast local build and before it over the network.
 *
 * Their diagnosis names the trap precisely: an Add button **already exists**
 * before the unlink, so a callback that fires early focuses the old element and
 * then loses it when regeneration replaces the subtree. Nothing about that is
 * visible to a test that only asks whether something is focused.
 *
 * **And the answer was already in this file**, beside `restoreFocus`, which
 * carries the same diagnosis about the caret: *"Restoring inside `run` looked
 * right and was not. A commit produces TWO renders — the store's own event
 * fires one, and releasing the caret asks for another — so the caret was placed
 * into a node the second pass detached a moment later, and focus fell to
 * `<body>`."* Identical failure, identical cause, one function away. So this is
 * consumed where that is: after the render loop has SETTLED, not inside a pass
 * and not on a frame count.
 */
let pendingAttachFocus: { readonly role: Role; readonly surface: RelationSurface } | undefined;

/**
 * Consumed by the render, once the new markup is in the document.
 *
 * By ROLE, not by node index. The index was the obvious key and it is wrong on
 * the path that matters most: a successful link changes what the pool holds, so
 * the shape changes, so the spec regenerates — and the slot comes back under a
 * DIFFERENT index. Measured: focus read `m-attach` at 0ms and `BODY` from
 * 100ms, because the selector matched nothing after the regeneration and the
 * request was silently dropped.
 *
 * A role survives regeneration, which is the whole point of the layer: the
 * generator may move a relation anywhere in the tree, and it is still the
 * relation this reader was working on.
 */
/**
 * Focus the new object's Title, so the next thing typed names it.
 *
 * Decision 0024: *"Pressing **New** creates one object, opens that object's
 * single-object lens, and puts keyboard focus in its Title field so the next
 * thing a person types answers what they are making."* It is the half of that
 * decision that replaces a modal — @Codex rejected asking first because it
 * *"would add a blocking ceremony to the fastest action in the product"* — so
 * this is what makes creation a single gesture followed by typing rather than
 * a row appearing somewhere with no obvious next move.
 *
 * BY OBJECT AND ROLE, resolved through the IR, the same way
 * `takePendingAttachFocus` does one function down and for the same reason: the
 * lens that paints the new object is generated, so no ordinal or DOM position
 * is knowable in advance. 0024 says exactly this — *"Creation must request
 * focus restoration by the new object's stable handle and the Title role …
 * DOM position and node ordinal are not stable enough."*
 *
 * `.m-place` is excluded because the docked panel draws the same object's
 * fields beside the lens, and sending the caret there has been the false pass
 * twice already.
 *
 * Held across settles like the attach request beside it, and released the
 * moment focus is observed where it belongs — a creation produces the
 * regenerate its own boundary asked for, and a request spent on the first
 * settle is spent before the render that would take it away.
 */
let pendingCreateFocus: { object: ObjectId; until: number } | undefined;

function takePendingCreateFocus(): void {
  const want = pendingCreateFocus;
  if (want === undefined) return;

  /**
   * NEVER over a focus the person chose, and never for long.
   *
   * The first version of this held the request until it observed focus already
   * on the Title — copied from `takePendingAttachFocus` one function down. That
   * is right for an attach, which nothing else competes with, and wrong here:
   * creation is immediately followed by whatever the person does next, so every
   * later repaint yanked the caret back out of wherever they had gone. It broke
   * `e2e/clearing.mjs` — the harness creates an object, moves to the Source
   * chooser, clears it, and found the caret stolen back to Title.
   *
   * Same two conditions as `restoreFocus`, and for the same reason. `<body>` is
   * where focus lands when a repaint detaches a node and nowhere else; a real
   * element means they have moved on and this is finished. The deadline stops a
   * request outliving the repaints its own gesture caused.
   */
  const active = document.activeElement;
  if (active !== null && active !== document.body) { pendingCreateFocus = undefined; return; }
  if (Date.now() > want.until) { pendingCreateFocus = undefined; return; }

  const handle = handles.get(want.object);
  const ir = lastIR;
  if (handle === undefined || ir === undefined) return;

  for (const el of $('stage').querySelectorAll(`[${ANCHOR_ATTR.node}]`)) {
    if (el.closest('.m-place')) continue;
    const found = readAnchor(el);
    if (!found || found.object !== handle) continue;
    if (ir.nodes[found.node]?.role !== 'title') continue;
    if (!(el instanceof HTMLElement)) continue;
    el.focus();
    // Caret at the end of whatever is there, so typing extends rather than
    // landing at an arbitrary offset. A `focus()` alone leaves the caret
    // position to the browser, and Chromium puts it at the start.
    const sel = el.ownerDocument.defaultView?.getSelection();
    if (sel && el.getAttribute('contenteditable') === 'true') {
      const range = el.ownerDocument.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    return;
  }
  // The lens does not paint a title for it. Nothing to focus, and stealing
  // focus somewhere arbitrary is worse than leaving it — `restore`'s rule.
  pendingCreateFocus = undefined;
}

function takePendingAttachFocus(): void {
  const want = pendingAttachFocus;
  if (want === undefined) return;
  const { role, surface } = want;

  // IN THE SURFACE THAT WAS OPERATED. A `#stage` selector here would send a
  // pane gesture's focus to a control on the page behind it — the same class of
  // mistake as the pane writing through the stage's manifest, and reachable
  // only once the pane HAS controls. `#stage` and `#paneStage` are siblings, so
  // neither contains the other and the wrong one is silently plausible.
  const ir = surface.ir();
  let target: HTMLElement | undefined;
  for (const el of surface.root().querySelectorAll(`.m-attach[${ANCHOR_ATTR.node}]`)) {
    const i = Number(el.getAttribute(ANCHOR_ATTR.node));
    if (Number.isInteger(i) && ir?.nodes[i]?.role === role && el instanceof HTMLElement) {
      target = el;
      break;
    }
  }

  // The slot is gone — the shape changed enough that this relation has no
  // region any more. Give up rather than focus something arbitrary.
  if (!target) { pendingAttachFocus = undefined; return; }

  // HELD until a settle finds focus already there.
  //
  // `schedule()` coalesces the renders inside one invocation, and a write can
  // still produce a SECOND invocation from a later store event — so a request
  // cleared on first use is cleared before the render that will actually blow
  // focus away. Measured: focus read `m-attach` at 0 and 100ms and `BODY` from
  // 400ms, with the request already spent.
  //
  // Clearing on "already correct" is what ends it: once a settle observes the
  // reader on the control with nothing having disturbed it, there is nothing
  // left to restore. The cost is bounded and worth naming — if the reader moves
  // focus themselves while renders from their own action are still landing, one
  // of those renders takes it back.
  if (document.activeElement === target) { pendingAttachFocus = undefined; return; }
  target.focus();
}

/**
 * The picker.
 *
 * A native `<select>` rather than a bespoke popover, and that is not laziness:
 * it is keyboard-operable, screen-reader-announced and type-ahead searchable on
 * every platform without this file implementing any of it. The highest-quality
 * version of "choose one of these" is usually the one the platform ships.
 *
 * It serializes no identity into anything poolable: the option values are
 * per-session object HANDLES, resolved through the same map every other write
 * goes through.
 */
async function openAttachPicker(
  after: HTMLElement, site: RelateSite, surface: RelationSurface,
): Promise<void> {
  after.parentElement?.querySelector('.m-attach-picker')?.remove();
  const sourceId = identities.get(site.object);
  // The ROLE comes out of the IR, never off the element — the same rule the
  // node index and the spec id follow two functions up. Off the SITE now, which
  // is where that read happens once: the picker's domain and the write
  // boundary's check have to be about the same role or they are two rules.
  const role = site.role;
  // What this object already points at, BY IDENTITY.
  //
  // Two wrong answers preceded this one. It compared relation-target TOKENS,
  // which worked only while candidates came from the set the lens was already
  // showing — the same projection minted both sides. Candidates come from the
  // declared target scope now, a different projection with different tokens for
  // the same object, so the comparison silently stopped matching and
  // `relate.mjs` caught the picker offering a note the slot was displaying.
  //
  // Then it compared rendered NAMES, which @Codex refused for the right reason:
  // *"this makes object identity equal display text: two distinct same-named
  // objects collapse, and renaming changes duplicate detection. Decision 0016's
  // target is an opaque object identity. UI text cannot be the join key."*
  //
  // `traverse` is the join key, and it was already public. It returns the union
  // of slot-backed and edge-backed targets — the same union the slot draws,
  // which matters because a `relate.mjs` note is one of each — with a real
  // `to: ObjectId` on every edge. The host holds identities already; it is the
  // RENDERER that must never see one.
  const already = new Set<ObjectId>();
  if (sourceId !== undefined && role !== undefined) {
    const walk = await db.store.traverse({
      // The SURFACE's document. A pane on a note traversed through the tasks
      // manifest resolves none of the note's keys, so "what does this already
      // point at" came back empty and the picker offered a target it already had.
      manifest: surface.manifest(), from: [sourceId], via: role,
      direction: 'out', depth: 1, maxNodes: 200,
    });
    for (const e of walk.edges) already.add(e.to);
  }
  // The ROLE comes out of the IR, never off the element — the same rule the
  // node index and the spec id follow two functions up.
  const options = (await attachCandidates(role, surface)).filter((o) => {
    const h = handleOf(o);
    const id = h === undefined ? undefined : identities.get(h);
    if (id === undefined || id === sourceId) return false;
    // A target already in this set would be an idempotent no-op — successful,
    // and indistinguishable on screen from a press that did nothing. Offering
    // it is offering a dead end.
    return !already.has(id);
  });
  if (!options.length) { say('nothing to link this to', 'bad'); return; }

  const sel = document.createElement('select');
  sel.className = 'm-field m-editable m-choose m-attach-picker';
  sel.setAttribute('aria-label', 'Choose what to link to');
  sel.innerHTML = ['<option value="">Choose…</option>',
    ...options.map((o) =>
      `<option value="${escAttr(handleOf(o)!)}">${escText(nameOfTarget(o))}</option>`),
  ].join('');
  sel.addEventListener('change', () => { void chooseTarget(sel, site, surface); });
  sel.addEventListener('keydown', (ev) => {
    if ((ev as KeyboardEvent).key === 'Escape') { sel.remove(); after.focus(); }
  });
  after.insertAdjacentElement('afterend', sel);
  sel.focus();
}

/**
 * Choosing a target, and putting the reader somewhere afterwards.
 *
 * The picker is REMOVED on every path out of here — success, refusal, and the
 * repaint a successful write triggers — and it is the focused element while it
 * is open. So without this, choosing a target dropped focus to `<body>` and a
 * keyboard reader was stranded on the object they had just edited. Found by
 * @Codex, and it is the same defect as unlink's, through a different door: the
 * unlink path had been fixed while this one, three lines away, had not.
 *
 * Both ends go back to the slot's own Add button, through the request the
 * render loop consumes once it has settled — never a frame count, for the
 * reason `takePendingAttachFocus` records at length.
 *
 * The REFUSAL path restores focus too, and that is not symmetry for its own
 * sake: a refusal is the case where the reader most needs to still be on the
 * control, because the thing they tried did not happen and the obvious next
 * move is to try again.
 */
async function chooseTarget(
  sel: HTMLSelectElement, site: RelateSite, surface: RelationSurface,
): Promise<void> {
  const target = identities.get(sel.value as ObjectHandle);
  const store = surface.store();
  if (!target || !store) return;
  sel.setAttribute('disabled', 'true');
  // BEFORE the write, not after. The store's subscription can run the whole
  // render loop while `relate` is still resolving, and a request armed
  // afterwards is armed for a render that has already happened — measured:
  // focus read `m-attach` at 0 and 100ms and `BODY` from 400ms, because the
  // repaint landed later and consumed nothing. Armed first, whichever render
  // lands takes it.
  pendingAttachFocus = site.role === undefined ? undefined : { role: site.role, surface };
  // `RelateSite` carries the role for the picker and the focus request; the
  // bridge derives its own from the grant and would ignore one sent here.
  const { role: _role, ...where } = site;
  const out = await store.relate({ ...where, mutation: 'link', target });
  sel.remove();
  if (!out.ok) {
    say(out.refusal.message, 'bad');
    // A refusal repaints nothing, so the render loop will not run and nothing
    // would consume the request. Taken here, against the markup that is already
    // on screen — the picker is gone but the Add button beside it is not.
    takePendingAttachFocus();
    return;
  }
  say('reference added');
}

/**
 * Does this invocation act ON something?
 *
 * `create-object` makes a new one and needs no subject; the other two effects
 * change or navigate from an object that has to exist. Read from the DECLARED
 * effects rather than from the action name, because the registry is open and
 * the host must not need to know what `demo.mark-seen` is to know it needs
 * something to mark.
 */
function needsSubject(inv: {
  readonly effects: readonly string[];
  readonly args?: readonly { readonly role?: unknown }[];
}): boolean {
  // The effects, and ALSO the arguments. @Codex, P2 on #140: an invocation
  // declaring only `create-object` but carrying `args: [{ role: 'title' }]`
  // needs a subject after all — a role argument is resolved FROM THE PRESSED
  // OBJECT, so with nothing open `resolveInvocationArgs` binds `undefined` and
  // an open-registry action like "duplicate using this title" creates a wrongly
  // populated object instead of refusing.
  //
  // Reading `args` here keeps one answer to "does this act on something".
  // `resolveInvocationArgs` would happily resolve a role to `undefined`, which
  // is right for a role the object genuinely lacks and wrong for having no
  // object at all — a distinction only the caller can make.
  if (inv.args?.some((a) => a.role !== undefined)) return true;
  return inv.effects.some((e) => e === 'set-property' || e === 'navigate');
}

/**
 * Does this node's own anchor name the SUBJECT it appears to be about?
 *
 * Extracted from `invokeFromClick` for task `0183`, unchanged in behaviour, so
 * that the click path can ask the same question the press path already asks.
 * The comment below it — *"the same walk the row case above uses, read once
 * and used both ways"* — described an intention: it was read once and used one
 * way, and a reader clicking the padding of the panel's New card opened the
 * first object in the pool.
 *
 * The rule is @Codex's, ruled on `d37a7b3`:
 *
 * > binding-free invocation card outside a collection = surface chrome;
 * > object-binding card or repeated collection item = object subject.
 *
 * `bindsSomething` is the first half — does this node's subtree draw a value
 * off an object — and it is a property of the SPEC rather than of where the
 * node sits, which is what their counterexample showed the older test needed.
 * `isInsideCollection` is the second: a collection may repeat a binding-free
 * action card once per row, and in that authored shape the row's anchor still
 * names the visible subject.
 */
function anchorNamesItsSubject(i: number): boolean {
  const nodes = lastIR?.nodes;
  if (!nodes) return false;
  const isInsideCollection = (() => {
    for (let at = i, hops = 0; hops < 64; hops += 1) {
      const node = nodes[at];
      if (!node) return false;
      if (node.kind === 'collection') return true;
      if (node.parent === at || node.parent < 0) return false;
      at = node.parent;
    }
    return false;
  })();
  const bindsSomething = (() => {
    const within = (from: number): boolean => {
      for (let at = from, hops = 0; hops < 64; hops += 1) {
        if (at === i) return true;
        const n = nodes[at];
        if (!n || n.parent === at || n.parent < 0) return false;
        at = n.parent;
      }
      return false;
    };
    return nodes.some((n) => n.role !== undefined && within(n.i));
  })();
  return bindsSomething || isInsideCollection;
}

async function invokeFromClick(e: Event): Promise<void> {
  const el = (e.target as HTMLElement | null)?.closest?.('.m-invoke');
  if (!(el instanceof HTMLElement) || !lastIR) return;
  const i = Number(el.getAttribute(ANCHOR_ATTR.node));
  const inv = Number.isInteger(i) ? lastIR.nodes[i]?.invocation : undefined;
  if (!inv) { say('that button names no action', 'bad'); return; }

  const anchor = readAnchor(el);

  /**
   * A ROW ACTION MUST NAME ITS ROW. The ambient fallback is for panel buttons
   * only.
   *
   * `selected` is "whatever object the page last opened", which for a button
   * sitting in a collection is a different object from the one under the
   * pointer — and @Codex's ruling on #138 draws the line exactly there: *"the
   * `selected` fallback is appropriate only for a genuinely subject-less panel
   * action."*
   *
   * Whether this button is one is a question about the SPEC, not the DOM: a
   * button whose card is inside a collection is a row button, however it was
   * drawn. So the IR is what answers it — the same rule the node index and the
   * invocation itself already follow, and the reason this reads `lastIR` rather
   * than looking for a `<tr>` or an `<li>`.
   *
   * Refusing rather than guessing. An action that writes the wrong object is
   * the failure task 0085 is open about, and it is worse than one that stops:
   * a person can retry a button that refused, and cannot untangle a write that
   * silently went somewhere else.
   */
  /**
   * THE READER'S REASON COMES FIRST, and only for an action that would write.
   *
   * A reader's render carries no `edit` boundary, so a row has no anchor —
   * which means the anchor requirement below fires before anything else and
   * tells them *"that button is not attached to a row"*. True of the markup and
   * useless to the person: the reason they cannot press it is that they are a
   * reader. Measured in `permissions.mjs`, which asked for the honest message
   * and got the structural one.
   *
   * Gated on the DECLARED EFFECTS rather than refusing every press, because
   * `demo.open-related` only navigates and a reader may navigate. This is what
   * the effect declaration is for: the spec says what the button may do, so the
   * host can tell whether this viewer may do it without knowing what the action
   * is.
   */
  const writes = inv.effects.some((e) => e === 'set-property' || e === 'create-object');
  if (writes && !mayWrite('change anything')) return;

  /**
   * DOES THIS BUTTON'S NODE BIND ANYTHING?
   *
   * The first version of this asked *"is it inside a collection"*, and @Codex
   * answered the question I put on #140 with the spec shape where that is
   * wrong: a card hosted under a `node-slot` has ancestry
   * `card → node-slot → …` with no collection in it, so the walk said "surface
   * control" and the host threw away the anchor `render-html.ts` had emitted
   * for the referenced target — a `set-property` on the visible card mutating
   * the OUTER object instead. That is 0085's own defect, reintroduced by
   * 0085's fix in a shape I had not enumerated.
   *
   * Collection ancestry was a proxy. The real question is the one this task's
   * own analysis reached and I walked past: **the panel's button is a card that
   * binds nothing** — `{ kind: 'card', bindings: [], affordances: ['invoke'] }`
   * — so its anchor is incidental, an artefact of being built as a card to
   * carry an invocation. Every card that is genuinely ABOUT an object binds
   * something: a row card, a table template, a node-slot's hosted target.
   *
   * So: if this node's subtree draws a value, its anchor names its subject and
   * is used. If it draws none, the anchor is chrome and the only object the
   * button can honestly mean is the one the person opened.
   *
   * This is a property of the SPEC — what the author declared this node reads —
   * and it does not care where the node sits, which is exactly what the
   * counterexample showed the old test needed and did not have.
   */
  // Binding is sufficient, but not necessary: a collection may repeat a
  // binding-free action card once per row. In that authored shape the row's
  // anchor still names the visible subject. Conversely, a binding-free card
  // outside a collection is surface chrome and must use only `selected`.
  /**
   * A NAVIGATION-ONLY BUTTON NEEDS THE HANDLE, NOT THE EPOCH — @Codex, P2 on
   * `72eb864`, and it is decision `0062`'s own division applied one step
   * further out: *the handle identifies; the epoch authorises*.
   *
   * `readAnchor` requires both, and a reader's render withholds the epoch by
   * design. Measured on the deployed build at `?viewer=reader`: the row card
   * carries `data-morph-o="o1"` and no `data-morph-e`. So for a row-bound
   * invocation whose only declared effect is `navigate` — the case the
   * `writes` gate above deliberately lets a reader through — the anchor came
   * back undefined and the refusal below fired, telling them the button is not
   * attached to an object. True of the markup, wrong about the person, and the
   * exact shape the comment above this gate already complains about.
   *
   * NO SPECIMEN IN THE PRODUCT EXERCISES IT TODAY, and saying so is part of
   * the fix: every row invocation the demo emits is `Update`, which writes and
   * is refused a step earlier, and `demo.open-related` sits on the panel,
   * whose card binds nothing. So this is a latent path, verified by reading
   * the anchors a reader actually gets rather than by watching it fail.
   */
  const navigationOnly = !writes && inv.effects.every((e) => e === 'navigate');
  const subjectAnchor = anchor ?? (navigationOnly ? readNavigationAnchor(el) : undefined);
  const anchorNamesSubject = anchorNamesItsSubject(i);
  if (anchorNamesSubject && !subjectAnchor) {
    say('that button is not attached to an object — nothing was changed', 'bad');
    return;
  }

  /**
   * A SURFACE CONTROL MEANS THE OBJECT YOU OPENED — never the one its markup
   * happens to sit on. **Task 0085.**
   *
   * Measured on the deployed build: pressing the shell panel's **Update** with
   * nothing open wrote `status: "seen"` to the FIRST OBJECT IN THE POOL and
   * moved the lens to it. Nobody named that object.
   *
   * `readAnchor` walks ancestors for `data-morph-o`, and the panel's button is
   * built as a card — `{ kind: 'card', bindings: [], affordances: ['invoke'] }`
   * — sitting in a region that a card in a collection is bound to an object
   * for. So it finds one. The author declared a card that reads nothing off an
   * object; the renderer gave it an object anyway.
   *
   * ## Which layer refuses it, decided rather than defaulted
   *
   * The task left three readings open. **It is the HOST, and the renderer keeps
   * emitting the anchor**, for a reason the task said had to be checked rather
   * than assumed: `.m-place` drag-and-drop reads anchors, and `smoke.mjs` and
   * `nesting.mjs` both drag this exact button. Withholding `data-morph-o` from
   * a card that binds nothing would have put that drag at risk to fix a write.
   * Nothing about the markup changes here, so neither can.
   *
   * What changes is what the anchor MEANS, and that is a question about the
   * spec: a button whose card is inside a collection is a row action and its
   * anchor names its row; a button that is not is a control belonging to the
   * SURFACE, and the only object it can honestly mean is the one the person
   * opened. The IR answers that — the same walk the row case above uses, read
   * once and used both ways.
   *
   * ## And it refuses rather than falling back
   *
   * With nothing open there is no honest subject, and the previous behaviour —
   * write to whatever the markup pointed at — is the defect. `createObject`
   * needs no subject and is exempt, which is why this is gated on the invocation
   * actually naming one rather than on the button existing.
   */
  const surfaceSubject = anchorNamesSubject ? undefined : selected;
  if (!anchorNamesSubject && surfaceSubject === undefined && needsSubject(inv)) {
    say('open an object first — this button acts on the one you have open', 'bad');
    return;
  }
  /* `subjectAnchor`, not `anchor` — the same reason the gate above uses it,
     and leaving it here would have moved the failure one line down: the press
     would pass the gate and then act on `undefined`.

     Safe by construction rather than by care: `subjectAnchor` falls back to
     the epoch-less handle ONLY when the invocation declares `navigate` and
     nothing else, `writes` is false on that branch by definition, and both
     `mayWrite` and `writeProperty` still stand between anything here and
     `db.editor`. A handle identifies; it has not authorised anything. */
  const subject = anchorNamesSubject
    ? (subjectAnchor ? identities.get(subjectAnchor.object) : undefined)
    : surfaceSubject;

  // ITS DECLARED ARGUMENTS, resolved at press time. This call passed `args: []`
  // however the invocation was written, which is why `invocation.args`,
  // `args[].role` and `args[].valueFrom` measured inert in task 0084 — three
  // fields the schema accepts, validation blesses and `project()` copies into
  // the genome, that nothing read. Written by @Codex.
  //
  // A ROLE argument reads the pressed object; a `valueFrom` argument indexes
  // this lens's view state by the same integer a predicate uses. The bounds
  // check is repeated inside `resolveInvocationArgs`: the spec was validated
  // when it was authored, and the slot count can have shrunk since.
  // EVERY set on screen, not only the subject. A button inside a card drawn
  // from `source: 'siblings'` — or from a region's own keyed set — anchors to
  // that object correctly, and searching `objects` alone resolved every role
  // argument to `undefined` while the effect went to the anchored sibling: an
  // action that wrote the right object with the wrong values. Found by @Codex.
  // The enumeration is `everySetOnScreen` now, because the same omission was
  // then found a second time in the pool's leak check.
  const bound = everySetOnScreen().flat().find((object) => {
    const handle = handleOf(object);
    return handle !== undefined && identities.get(handle) === subject;
  });
  const resolved = resolveInvocationArgs(inv, {
    role: (role) => bound?.[role],
    // RAW, not the tagged `Value` the store round-trips. `viewStateValues`
    // holds `{ k: 'text', v: 'reviewed' }`, and an action doing `String(value)`
    // on that writes "[object Object]" — a parameterised button persisting
    // garbage while every guard passed, because the unit test supplied a plain
    // string. Found by @Codex. Queries keep the tagged form: `testPredicate`
    // needs the kind to compare.
    viewState: viewStateValues.map((v) => (v === undefined ? undefined : rawOf(v))),
    viewStateCount: viewStateSlots.length,
  });
  if (!resolved.ok) { say(`refused: ${resolved.code} — ${resolved.message}`, 'bad'); return; }

  // WHERE TO PUT FOCUS BACK, recorded before the write rather than after it.
  // A successful action repaints the whole stage, so this element is about to
  // be detached — reading its handle afterwards would read it off a node that
  // is no longer in the document. See `takePendingInvokeFocus`.
  pendingInvokeFocus = subjectAnchor?.object === undefined
    ? undefined
    : { object: subjectAnchor.object, action: inv.action, inCollection: anchorNamesSubject };

  el.setAttribute('disabled', 'true');
  try {
    const out = await registry.invoke(inv, { args: resolved.value, effects: effectsFor(subject) });
    if (!out.ok) {
      say(`refused: ${out.code} — ${out.message}`, 'bad');
      // A refusal repaints nothing, so the button a person is standing on is
      // still there and asking for it back would be a focus change nobody
      // requested.
      pendingInvokeFocus = undefined;
    }
  } finally {
    // Only if it survived. On the success path the repaint has replaced it, and
    // `removeAttribute` on a detached node is a write to nothing — harmless,
    // but it is the newly drawn button that has to be pressable, and that one
    // was never disabled.
    if (el.isConnected) el.removeAttribute('disabled');
  }
}

/**
 * Store the composition, and read it back.
 *
 * It goes in through the SAME path a user adding a property goes through, which
 * is the point: an automation is not a privileged kind of thing, it is a text
 * value on an object. You can see it sitting there in the lens afterwards,
 * which is the most direct demonstration available that no code was persisted.
 */
async function writeAutomation(): Promise<void> {
  if (!selected) { say('select an object to store the automation on', 'bad'); return; }
  await addProperty('body', 'Automation', serializeAutomation(COMPOSED));
}

/** Read it back off the painted values rather than re-querying: the projection
 *  that produced them is the same one the lens is showing, so what runs is what
 *  the user can see. */
function readAutomation(): unknown {
  if (!selected) return undefined;
  const found = objects.find((o) => {
    const h = handleOf(o);
    return h !== undefined && identities.get(h) === selected;
  });
  return found?.['body'];
}

/**
 * Run the stored automation.
 *
 * The steps are read back out of the object store as JSON and handed to the
 * registry. Nothing persisted is a function; what was stored is a list of
 * action names and the effects each claims, and every one is checked again on
 * the way through.
 */
async function runStoredAutomation(): Promise<void> {
  const steps = parseAutomation(readAutomation());
  if (!steps) { say('no automation is stored on this object', 'bad'); return; }
  // WITH SOURCES, or a step that declares arguments is refused. Every step used
  // to share one context whose `args` was `[]`, so a stored automation asking
  // for a role or a slot ran with the action's default — preserved through
  // `parseAutomation` and discarded at the moment it mattered. Found by @Codex.
  const bound = objects.find((object) => {
    const handle = handleOf(object);
    return handle !== undefined && identities.get(handle) === selected;
  });
  const out = await registry.runAutomation(steps, {
    args: [], effects: effectsFor(selected),
  }, {
    role: (role) => bound?.[role],
    viewState: viewStateValues.map((v) => (v === undefined ? undefined : rawOf(v))),
    viewStateCount: viewStateSlots.length,
  });
  say(out.ok ? `ran ${out.value} steps from stored data` : `refused: ${out.message}`,
      out.ok ? 'ok' : 'bad');
}

/** Which stored object a rendered element belongs to, or nothing.
 *
 *  Extracted so the keyboard route below resolves an object the same way the
 *  click route does. Two copies of this lookup is how a keyboard and a mouse
 *  end up disagreeing about what is under the cursor. */
function objectAt(el: HTMLElement | null): ObjectId | undefined {
  if (!el) return undefined;
  // NAVIGATION, not a write — decision 0062. Every caller of this opens,
  // selects or remembers; none commits. It used to take `readAnchor`, which
  // requires an epoch, so under `?viewer=reader` it returned nothing for every
  // element on screen and each of those handlers exited silently: a reader
  // could see a search hit, a card hit, a relation chip or a page-tree row
  // naming another object and reach none of them. Task 0179.
  const anchor = readNavigationAnchor(el);
  if (!anchor) return undefined;
  return identities.get(anchor.object);
}

function selectFromClick(e: Event): void {
  const el = e.target as HTMLElement | null;
  const id = objectAt(el);
  if (!el || !id) return;
  // A table HEADER is never a card. Blind run 10 (coda): clicking the "Word
  // count" th to commit an edit resolved an anchor from an ancestor and
  // opened the pool's FIRST object in its own lens — a note the person never
  // clicked, replacing the table with no transition. Every table convention
  // says a header is sort-or-inert; until sorting exists it is inert.
  if (el.closest('thead')) return;

  // A click on a TRANSPORT, a FOLD or a RELATION CONTROL is not a click on the
  // card — three arrivals at one rule, each a bug first.
  //
  // Pressing play resolved the enclosing card, switched to a single-object
  // lens, repainted, and destroyed the player the user was reaching for. The
  // click did something, so it did not even read as broken; it read as the page
  // fighting you. Found by @Codex. Folding a section did the same to the
  // disclosure and dropped keyboard focus to `<body>` — fold once and you lost
  // your place in the document. Pressing Add inserted the picker and then threw
  // it away in the same tick; on screen, a button that does nothing.
  //
  // The same argument the caret already won: where a card affordance and a
  // control overlap, THE CARD LOSES. A select is recoverable by clicking
  // something else; a transport you cannot press is not a player.
  //
  // The list is `NOT_THE_CARD`, so the keyboard route refuses the same
  // elements — see there.
  if (el.closest(NOT_THE_CARD)) return;

  /**
   * A CHROME CARD'S ANCHOR IS NOT A DESTINATION — task `0183`, and it is one
   * rule reaching its second call site rather than a new one.
   *
   * `0085` refuses a WRITE through an incidental anchor: the panel's Update
   * card is `{ kind: 'card', bindings: [], affordances: ['invoke'] }`, its
   * anchor is an artefact of being built as a card, and pressing it once wrote
   * to the first object in the pool. That fix deliberately left the renderer
   * emitting the anchor, because `.m-place` drag reads anchors and two
   * harnesses drag this exact button.
   *
   * It is about writes. Navigation went straight past it — measured in reader
   * mode on films:
   *
   *     New / Update / Open   data-morph-o="o1", no hit target
   *     clicking the New card's BODY   cardinality large-set -> single
   *
   * So a reader clicking the padding beside the panel's New button opened the
   * first film in the pool: an object nobody named, chosen by source order,
   * with nothing on screen saying that card was about a film.
   *
   * The refusal is SILENT, unlike the press path's. A press is a person asking
   * for something and deserves to be told why it did not happen; a click on a
   * card's padding is not a request, and a toast for it would fire whenever
   * somebody clicked near the panel.
   */
  const clickedNode = readNavigationAnchor(el)?.node;
  /*
   * AND ONLY WHERE THE CARD OFFERS NOTHING, which the first version of this
   * missed and `obsidian-row` caught: 7/8 journeys, the backlinks pane never
   * opening because the explorer's rows had stopped opening their notes.
   *
   * The IR walk asks whether a node's own subtree BINDS anything. Obsidian's
   * explorer draws a card per note from a capability's own data, so its rows
   * bind nothing in the IR and the walk called them chrome — while each one
   * carries its own object's anchor and a real `.m-card-hit`. Measured:
   *
   *     panel chrome card   o1   hit false
   *     explorer row       o13   hit TRUE
   *
   * A card that offers a hit target presents itself as clickable, and
   * refusing its click is the opposite defect from the one being fixed.
   * @Codex's finding names this population exactly — cards with *"no
   * `.m-card-hit` or another focusable control"* — so the hit target is not a
   * patch on the rule, it is the half of their sentence the first version
   * left out.
   *
   * AND IT MUST BE A CARD AT ALL, which the hit-target refinement alone did
   * not give: `obsidian-row` stayed 13/14, because that harness opens its note
   * from a GRAPH NODE — a capability's own control, in an SVG, not inside any
   * `.m-card`. The IR cannot speak for those either, and they name their
   * object explicitly rather than inheriting one.
   *
   * So all three conditions are the rule's own words: a binding-free
   * invocation CARD, outside a collection, offering nothing. A graph node, a
   * plot hit, a calendar hit and a timeline bar are none of them.
   */
  const clickedCard = el.closest('.m-card');
  const offersAHit = !!clickedCard?.querySelector('.m-card-hit');
  if (clickedCard && !offersAHit && clickedNode !== undefined
    && !anchorNamesItsSubject(clickedNode)) return;

  const editing = !!el.closest('[contenteditable="true"]');

  // Does this click mean "to the side"? Decided here, where the object is
  // already resolved and after the guards above, rather than in a listener of
  // its own: a second listener would have to repeat this function's anchor
  // lookup, its transport guard and its fold guard, and two copies of that
  // reasoning is how a click ends up meaning two things.
  //
  // Never on an INVOKE BUTTON. Measured: a modifier-click on one ran the
  // action AND opened a pane, because the button sits inside a card and the
  // anchor lookup finds the card. The same argument the transport already
  // won — where a card affordance and a control overlap, the card loses.
  const toSide = e instanceof MouseEvent && wantsAside(e) && !el.closest(NOT_TO_THE_SIDE);

  // TO THE SIDE, whatever the gesture landed on — and the keyboard route below
  // is the same two lines. `referentOf` decides which object; this decides
  // where it goes. Splitting those apart is what let a chip open one object
  // under the mouse and another under the keyboard.
  if (toSide) {
    e.preventDefault();
    void referentOf(el, id).then((target) => { if (target) openAside(target); });
    return;
  }

  // The target's id is deliberately NOT in the DOM. Projection expands a ref
  // into the target's ROLES and stops; putting an object id into rendered
  // markup would put a store identifier on the far side of the privacy
  // boundary. So the id is resolved by asking the store to traverse OUT from
  // the subject along that role, which is the same question by a safe route.
  //
  // PLAIN activation, which replaces the main stage rather than opening beside
  // it. Same referent, different destination.
  if (!editing && (el.closest('.m-slot-open, .m-ref-hit') || soleReferenceField(el))) {
    e.preventDefault();
    void referentOf(el, id).then((target) => {
      if (target !== undefined && target !== id) return openHere(target);
      return undefined;
    });
    return;
  }

  select(id);
  if (editing) return;

  // A graph node or a plotted observation opens the object's ordinary generated
  // lens; leaving the view on the capability would acknowledge the click and
  // repaint the same picture. The plot's hit buttons are the ONLY reachable
  // thing on a chart — the marks are aria-hidden geometry — so without this a
  // keyboard user could tab to every observation and open none of them.
  // A calendar event belongs in this list for exactly the reason the graph
  // node and the plot hit do: it is the only reachable thing on the surface,
  // and opening one while leaving the view on the capability repaints the same
  // picture with one object in it — a calendar of a single object, which is a
  // grid with one square filled and no way to tell that anything happened.
  if (el.closest('.m-graph-node, .m-plot-hit, .m-cal-hit, .m-tl-bar')) {
    state.view = 'auto';
    ($('view') as HTMLSelectElement).value = 'auto';
    // A dot in the vault's Graph view opens its note IN THE EDITOR — the
    // app's gesture — so the main column returns to the note.
    state.mainView = 'note';
  }

  // AND A NAVIGATION CONTROL HANDS FOCUS TO WHERE IT WENT — task 0185
  // finding 2, and the fifth arrival at the rule the four restoration paths
  // above already carry. The row's Open button is removed by the repaint it
  // causes, so activeElement becomes <body>: a screen-reader user presses
  // Enter on row 7, the table is replaced, nothing is announced, and the
  // virtual cursor is at the top of the document. `pendingInvokeFocus` was
  // written for exactly this shape — @Codex's P2 on #138, *"a keyboard user
  // who marks a task seen loses their place entirely"* — and this is the one
  // CONTENT navigation control, which recorded nothing.
  //
  // The destination is the page's own heading rather than its title field:
  // arriving somewhere should announce what the page IS, and a caret dropped
  // into an editable title is an invitation to type over it. `.m-ws-title`
  // carries tabindex="-1" for this.
  //
  // Scoped to `.m-row-open` and not to every navigation: a card hit has the
  // same gap and is driven by half the harnesses in the repo, so it is named
  // in 0185 rather than changed here on the way past.
  //
  // AND THE HEADING IS NOT THERE ON AN ORDINARY OBJECT PAGE — @Codex's P2 on
  // `552d25e`, and it is task `0188` arriving from the focus side. `.m-ws-title`
  // is emitted only when the title's parent region declares
  // `purpose: 'editor'`, which the six app templates do and baseline's
  // `buildSingle()` does not. So on the ordinary Notes table — the very table
  // decision `0065` added these controls to — the request queued a selector
  // that can never resolve, focus stayed on <body>, and the gap this line
  // exists to close stayed open on the generic lens.
  //
  // The object's own title FIELD is the fallback: it is the same text, it is
  // present on every single-object page, and it is what a person arriving
  // wants announced. The heading is still preferred where it exists, because
  // focus into a contenteditable puts a caret in the title — an insertion
  // point, not a selection, so a stray keystroke inserts rather than replaces,
  // but a heading announces the page without offering to edit it at all.
  //
  // `:not(.m-place *)` IS LOAD-BEARING and the first version left it off, so
  // the fallback resolved and still did nothing. The docked panel lists all
  // eight notes beside the opened one: nine `.m-role-title` under `#stage`,
  // eight of them the panel's, and `querySelector` takes the first in DOCUMENT
  // order — a panel row's title, which is not focusable, so `focus()` was a
  // no-op and activeElement stayed `<body>`. Measured, not reasoned about.
  //
  // Which is this repository's own recorded failure twice over: a value read
  // from something other than the thing under assertion, and the very filter
  // `first-object.mjs` and `table-row-button.mjs` both already apply for this
  // exact panel.
  //
  // The RIGHT fix is for the ordinary page to carry a heading like every named
  // app's does; that is `0188`, it is a generation change, and it wants the
  // ruling `0188` asks for rather than being taken on the way past a P2.
  if (el.closest('.m-row-open')) {
    focusWorkspaceAfterPaint(['.m-ws-title', '.m-role-title:not(.m-place *)']);
  }
  if (state.cardinality !== 'single') {
    state.cardinality = 'single';
    ($('cardinality') as HTMLSelectElement).value = 'single';
    say('opened one object in its own lens');
  }
  schedule();
}

/* ------------------------------------------------------------ creating things */

/** Roles the picker has minted a key for, so a second object can be given the
 *  same property without minting a SECOND key for the role — two keys carrying
 *  one role make every write to it ambiguous, and the store refuses rather than
 *  guessing which one the user meant. */
const declared = new Map<string, PropertyKeyId>();

/**
 * What a property added at runtime needs to survive a reload.
 *
 * The store already persists both halves: the key is in the key registry and
 * the extended manifest is in the manifests table. What does not survive is
 * knowing WHICH manifest ref this pool is now on — a ref is content-addressed
 * over keys the store will not enumerate, so a reload that reads the seed
 * bootstrap addresses the pool's original slot map and the new property is
 * simply invisible. It is stored, and nothing can see it.
 *
 * So this is a second pointer beside `store-setup`'s bootstrap, for the same
 * reason that one exists, and kept separate from it so the two files do not
 * both own one record. Verified against the store before it is trusted: a
 * cleared IndexedDB with a surviving localStorage entry is a real state.
 */
const OVERLAY_KEY = 'morph.demo.added.v1';
type Overlay = Record<string, { manifest: string; added: Record<string, string> }>;

/**
 * ONE KEY PER POOL — @Codex, P1 on `65952e8`, the shelf's clobber one record
 * over and with worse stakes: two tabs adding properties (even to DIFFERENT
 * pools) each read the one shared object and the last `setItem` dropped the
 * other pool's manifest pointer. The key, value and manifest stayed durable
 * in IndexedDB, but after reload the affected pool fell back to its seed
 * manifest and the added field was stored and unreachable. A per-pool key
 * has nothing another pool's write can clobber; same-pool writes are the
 * one-tab-at-a-time case the overlay always assumed. The legacy shared key
 * is still read so existing pointers survive; per-pool entries beat it.
 */
const OVERLAY_PREFIX = `${OVERLAY_KEY}:`;
function readOverlay(): Overlay {
  const out: Overlay = {};
  try {
    const legacy = JSON.parse(localStorage.getItem(OVERLAY_KEY) ?? '{}') as Overlay;
    if (legacy && typeof legacy === 'object') Object.assign(out, legacy);
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key?.startsWith(OVERLAY_PREFIX)) continue;
      const one = JSON.parse(localStorage.getItem(key) ?? 'null') as Overlay[string] | null;
      if (one) out[key.slice(OVERLAY_PREFIX.length)] = one;
    }
  } catch { /* whatever parsed still counts */ }
  return out;
}

/** Move each pool onto the manifest its added properties live in, when that
 *  manifest still addresses objects. */
async function applyOverlay(): Promise<void> {
  const overlay = readOverlay();
  for (const p of db.pools) {
    const saved = overlay[p.id];
    if (!saved) continue;
    const scope: Scope = { manifest: saved.manifest as never, typeHint: p.typeHint };
    try {
      if ((await db.store.count(scope)) === 0) continue;
    } catch {
      continue;   // the store does not know this manifest; the seed one still stands
    }
    p.manifest = saved.manifest as never;
    p.scope = scope;
    if (db.store instanceof StoreEngine) db.store.register(scope);
    for (const [role, key] of Object.entries(saved.added)) {
      declared.set(`${p.id} ${role}`, key as PropertyKeyId);
    }
  }
}

function recordOverlay(target: DemoPool = pool): void {
  const overlay = readOverlay();
  const added: Record<string, string> = { ...overlay[target.id]?.added };
  for (const [composite, key] of declared) {
    const [id, role] = composite.split(' ');
    if (id === target.id && role) added[role] = key;
  }
  // THIS POOL'S KEY ONLY — never the shared object. See `OVERLAY_PREFIX`.
  try {
    localStorage.setItem(`${OVERLAY_PREFIX}${target.id}`,
      JSON.stringify({ manifest: target.manifest, added }));
  } catch { /* private mode */ }
}

let hideStatus: number | undefined;

/**
 * Say something in the live region.
 *
 * The region is never hidden and never unhidden. It used to ship `hidden` and be
 * revealed here — which meant the runtime's `announce()` inserted its line into
 * a `display:none` subtree and this ran afterwards, revealing an element with
 * the content already in place. That is the one sequence assistive tech does not
 * announce: every refusal was visible and none was spoken. Clearing the TEXT
 * leaves the region in the accessibility tree, and `.saidit:not(:empty)` in
 * index.html is what keeps an empty one from drawing a pill.
 */
function say(message: string, tone: 'ok' | 'bad' = 'ok'): void {
  const el = $('saidit');
  el.textContent = message;
  el.className = `saidit ${tone}`;
  // Cleared first, or the previous message's timer clears THIS one partway
  // through its six seconds — which is how a refusal disappears before anybody
  // reads it, and reads as the edit having worked.
  if (hideStatus !== undefined) window.clearTimeout(hideStatus);
  hideStatus = window.setTimeout(() => { el.textContent = ''; }, 6000);
}


/** Create either from the offered derived template or blank. In both cases the
 * object is ordinary immediately afterwards: no marker or continuing rule is
 * attached, and any seeded property may be removed. */
/**
 * Which creation attempt is the current one.
 *
 * @Codex, P2 on #184: *"When two creation buttons are pressed before the first
 * `createObject()` settles, this single promise is replaced by the newer
 * attempt but the older call can still complete later and run
 * `select(created)`. It can therefore overwrite the newer selection, or set
 * `creationLanded` back to true after the newer creation fails, causing the
 * file barrier to attach a subsequent file to the older object the person was
 * no longer creating."*
 *
 * The same shape as the ingest token, one door along — `creating` holds the
 * newest promise but nothing stopped an older call from finishing after it.
 * The second half of their finding is the sharp one: a stale `select()` sets
 * `creationLanded`, so the barrier that exists to catch a failed creation would
 * have been satisfied by the creation that failed.
 */
let creationTicket = 0;

/**
 * How many times a creation may re-plan after a `stale` refusal.
 *
 * The same bound and the same reasoning as `ATTACH_ATTEMPTS`: a contending
 * commit has to land between this attempt's snapshot and its own, and it cannot
 * do that twice running without the ticket moving — which the check inside the
 * loop catches first and answers by stopping rather than by trying again.
 */
const CREATE_ATTEMPTS = 3;

/** Which cardinality a named look OPENS in — a fact about the app: Obsidian
 *  opens on a note, Notion on the page's board of every task. The door reads
 *  it, and so does creation: a page made from a column stays on the board. */
function lookOpensOn(template: AppTemplate | undefined): 'single' | 'large-set' | undefined {
  return template === undefined ? undefined : lookOf(template).opensOn;
}

/** A value preset on creation — Notion's "+ New page" in a status column
 *  makes a page IN that status. Roles the pool has no key for are dropped;
 *  a preset wins over the template seed for the same key. */
type CreatePreset = readonly (readonly [Role, string])[];
function withPresets(
  props: readonly (readonly [PropertyKeyId, Value])[], presets: CreatePreset, targetPool: DemoPool,
): (readonly [PropertyKeyId, Value])[] {
  const out: (readonly [PropertyKeyId, Value])[] = [...props];
  for (const [role, v] of presets) {
    const key = poolKeyForIn(targetPool, role) ?? declared.get(`${targetPool.id} ${role}`);
    if (key === undefined) continue;
    const kind = ROLE_KIND[role];
    const value: Value = kind === 'enum' ? { k: 'enum', v } : kind === 'number' ? { k: 'number', v: Number(v) }
      : kind === 'date' ? { k: 'date', v } : { k: 'text', v };
    const i = out.findIndex(([k]) => k === key);
    if (i >= 0) out[i] = [key, value]; else out.push([key, value]);
  }
  return out;
}
async function createObject(title: string, fromTemplate = true, presets: CreatePreset = []): Promise<void> {
  const mine = (creationTicket += 1);
  // The OTHER door to `db.editor`. Same reasoning as `writeProperty` — the
  // panel's New button is an action, and an action does not pass through the
  // form's gesture guard.
  if (!mayWrite('create anything')) return;
  /**
   * A REJECTION from a superseded attempt is swallowed, and only this scope
   * can decide that.
   *
   * @Codex, P2 on `10bcd23`: *"When creation A rejects during `deriveShape`,
   * template resolution, `snapshot()`, or `edit()` after creation B has
   * superseded it, this unconditional handler still announces A's obsolete
   * failure because the ticket guard is reached only after `edit()` returns
   * normally. Fresh evidence beyond the earlier result-refusal finding is that
   * both click handlers still report promise rejections without checking which
   * creation is current."*
   *
   * The same finding one layer out: the round before gated every RETURNED
   * outcome on the ticket and left every THROWN one alone. The handlers cannot
   * do this themselves — `mine` is local to this call and the promise they hold
   * carries no identity — so the choice belongs here, and the handler stays the
   * single reporter for an attempt that is still current.
   *
   * An inner block rather than a second function: every early `return` in the
   * body means the same thing inside it, and splitting the function would have
   * to thread `mine` through a signature for no other reason.
   *
   * Only supersession is swallowed. A current attempt's rejection is re-thrown
   * unchanged, which is what keeps `could not create:` reachable at all.
   */
  try {
    const targetPool = pool;
    const seed = fromTemplate
      ? await resolveTemplateSeed(
        await db.store.deriveShape(targetPool.scope),
        db.store.stream(targetPool.scope),
        (role) => poolKeyForIn(targetPool, role)
          ?? declared.get(`${targetPool.id} ${role}`),
      )
      : [];
    /**
     * A TITLE NOBODY TYPED IS NOT STORED.
     *
     * Decision 0024: *"The object does not store the literal title `Untitled`
     * merely because it was created. Until the person names it, `Untitled` is
     * presentation fallback copy for an empty title slot, not authored data."*
     * And its reason — *"Persisting the literal makes an untouched default
     * indistinguishable from a title the person deliberately chose, and it turns
     * creation into a content write the person never made."*
     *
     * It is also what makes the other half of 0024 work. Focus goes to Title so
     * the next thing typed names the object, and with `Untitled` written into the
     * slot that produced `UntitledA film I just thought of` — measured, before
     * this. The two consequences are one behaviour.
     *
     * The drawer's own create paths both refuse an empty name before they get
     * here, so this branch is reached only by the **New** action.
     */
    const named = title.trim();
    let created: ObjectId | undefined;
    // Nothing here was read from the store to plan with — the pool, the role and
    // any minted key come from state this session already holds — so a snapshot
    // taken now IS the state this plan was made against. Decision 0027 requires
    // the basis anyway: "callers that intend a blind write still begin from a
    // snapshot; they do not opt out of concurrency control."
    /**
     * Re-planned when the plan was only ever STALE, and the ticket is still ours.
     *
     * @Codex, P2 on `10bcd23`: *"When creation B takes its snapshot while
     * creation A's persistence commit is still pending, both edits can use the
     * same basis because `storeRevision` advances only after persistence accepts
     * A. A then commits but its older ticket suppresses selection, while current
     * attempt B receives `stale`, reports a refusal, and leaves `creationLanded`
     * false even though A's object is durable; re-plan a stale creation while its
     * ticket is still current, as the store's optimistic-concurrency contract
     * requires."*
     *
     * The same door as `attachFile`'s, one along — and worse here, because
     * `creationLanded` gates the next ingest: a person whose second Create was
     * refused as stale then has every file refused too, for a write that
     * succeeded.
     *
     * Only on `stale`, only while the ticket is ours, bounded. A `stale` whose
     * ticket has moved on is a refusal that is CORRECT and belongs to nobody;
     * every other refusal is about the write itself and repeating it would repeat
     * the reason.
     *
     * `created` is cleared per attempt: a refused edit's `create` never
     * committed, and carrying its id forward would select an object that does not
     * exist.
     *
     * WHAT IS RE-PLANNED, precisely, because "re-plan" is doing a lot of work in
     * that quotation and the answer here is not the same as `attachFile`'s.
     *
     * The basis is re-taken and the mutator re-runs, so `targetPool.keys.title`
     * and the pool's own state are read fresh at each commit — that is the half
     * a `stale` refusal is about. `seed` is NOT re-derived. It is deliberate:
     * `resolveTemplateSeed` costs a `deriveShape` plus a walk of the pool's
     * stream, and what it returns is a set of *existing* property keys paired
     * with values another object in the pool already holds. A commit landing
     * between attempts does not invalidate those keys; at worst it makes one
     * default a revision out of date, which is a template suggestion rather than
     * a claim about the store.
     *
     * So the cost of not re-deriving is a slightly stale default; the cost of
     * re-deriving would be two store reads per attempt on the creation path.
     * Named here because the next reader will ask, and because moving the derive
     * inside the loop looks like a strict improvement until you price it.
     */
    let result;
    for (let attempt = 0; ; attempt += 1) {
      created = undefined;
      // eslint-disable-next-line no-await-in-loop
      result = await db.store.edit(db.editor, (await db.store.snapshot()).revision, (s) => {
        created = s.create({
          typeHint: targetPool.typeHint,
          properties: withPresets(named
            ? [[targetPool.keys.title!, { k: 'text', v: named }], ...seed]
            : [...seed], presets, targetPool),
        });
      });
      if (result.ok || result.code !== 'stale') break;
      // Superseded: the refusal is correct and is not ours to retry.
      if (creationTicket !== mine) break;
      if (attempt + 1 >= CREATE_ATTEMPTS) break;
    }
    /**
     * Superseded FIRST, before anything is said about this attempt at all.
     *
     * @Codex, P2 on `27932c6`: *"When creation A is overtaken by creation B while
     * its shape, snapshot, or edit is awaiting, A can return a refusal after B
     * has already succeeded. This branch reports A's obsolete failure before the
     * `creationTicket !== mine` guard … so it overwrites B's success notice even
     * though the new ticket was introduced specifically to discard superseded
     * creation outcomes."*
     *
     * The ticket was doing half its job: it discarded a superseded attempt's
     * SELECTION and let its refusal through, which is the louder half. A person
     * who pressed Create twice saw the first attempt's failure sitting over the
     * second attempt's success.
     *
     * Every outcome, not only the refusal — the cross-pool notice below is a
     * SUCCESS message from an attempt nobody is waiting for any more, and it is
     * stale for the same reason.
     *
     * What is discarded is only the report. The object A wrote is durable and
     * stays; `0044`'s rule is about which answer is installed, not about undoing
     * work that landed.
     */
    if (creationTicket !== mine) return;
    if (!result.ok) {
      say(`refused: ${result.code} — ${result.message}`, 'bad');
      return;
    }
    if (!created) { say('the store created an object but did not return it', 'bad'); return; }
    if (pool !== targetPool) {
      say(named
        ? `created "${named}" in ${targetPool.label} — open that pool to shape it`
        : `created an object in ${targetPool.label} — open that pool to name it`);
      schedule();
      return;
    }

    // Creation is the start of customising this object. Keep that intent explicit:
    // open its own lens and make it the only legal target for the property form.
    // Falling back to the first object in the pool made Create → Add mutate an
    // unrelated record while the new record stayed invisible in the collection.
    //
    // A BACKSTOP now: the check above runs immediately after the last await and
    // nothing between them yields, so this is unreachable today. It stays because
    // the next await added between them reopens the window silently, which is the
    // shape that produced the finding above — a guard correct where it sat, and
    // one statement too low.
    if (creationTicket !== mine) return;
    select(created);
    /**
     * THE VIEW TOO, not only the cardinality.
     *
     * `single` is `state.view === 'auto' && state.cardinality === 'single'`, so
     * setting cardinality alone leaves a capability view — graph, plot, document,
     * calendar, timeline — still in charge. `run()` then does not treat the page
     * as single-object, transforms the result back into that capability, and the
     * new object's Title is never drawn: decision 0024's caret has nothing to
     * land on and creation ends with nothing to do next.
     *
     * Found by @Codex's review, and `e2e/first-object.mjs` could not have caught
     * it — it drives the default `auto` view only. The same atomic move already
     * exists twenty lines into the control handler, for the same reason: the two
     * halves must not disagree about whether the screen is one object or a pool.
     */
    if (state.view !== 'auto') {
      state.view = 'auto';
      ($('view') as HTMLSelectElement).value = 'auto';
    }
    // Under a look that opens on a SET (Notion's board), a page made from a
    // column stays on the board, selected in its column; every other path
    // opens the new object alone, as before.
    const keep = lookOpensOn(state.template);
    const after = keep ?? 'single';
    state.cardinality = after;
    ($('cardinality') as HTMLSelectElement).value = after;
    // Decision 0024: the caret goes to Title, so the next thing typed names it.
    pendingCreateFocus = { object: created, until: Date.now() + FOCUS_RETRY_MS };
    // Under a named app look there is no pool bar to press, and "pool" is
    // Morph's word inside an app that has its own (@Codex, fourth verdict:
    // the toast broke the Obsidian illusion). The look's own vocabulary:
    // a note, and the way back is the ribbon.
    // Under a named app look, NO toast (the twelfth verdict: a floating
    // "new note — type to name it" was a competing surface on a blank
    // note). The note says it itself — the caret is in its title, which
    // reads "Untitled", and the body reads "Start writing…" — as the app
    // does. Morph's own face keeps the toast, because its way back is a
    // pool bar the toast names.
    if (!state.template) {
      say(named
        ? `created "${named}" — press a pool to go back`
        : 'created — type to name it, or press a pool to go back');
    }
    schedule();
  } catch (why) {
    if (creationTicket !== mine) return;
    throw why;
  }
}

/**
 * Add a property to ONE object.
 *
 * `defineProperty` appends to the key registry and touches nothing that exists;
 * the `set` writes one slot on one object; both land in one commit. That is the
 * absence of a migration, and it is why two objects in a collection can carry
 * different properties without either of them knowing.
 *
 * The manifest is copy-on-write, so extending it returns a NEW ref — an
 * already-compiled spec never has the meaning of its slots changed underneath
 * it, and the pool's scope moves to the new ref.
 */
/**
 * What a media property is CALLED, per role.
 *
 * A table rather than a chain of ternaries, because task 0118 added two more
 * roles to it and the chain had already been wrong once: everything that was
 * not video or audio was labelled "Image", which would have made a video's
 * poster frame a second column called Image sitting beside the video.
 */
const MEDIA_LABEL: Partial<Record<Role, string>> = {
  'media.video': 'Video', 'media.audio': 'Audio', 'media.hero': 'Image',
  'media.thumb': 'Thumbnail', 'media.poster-frame': 'Poster',
  'media.still': 'Still', 'media.document': 'Document',
};

/**
 * A file, landing on the open object.
 *
 * Decision 0017 point 4. The bytes are already durable by the time this runs —
 * `ingestOne` put them — so this is only the property write, and it is the same
 * write `addProperty` does: the pool's own key first, then one this session
 * minted, then a new one. Reusing the pool's key is what puts two notes'
 * pictures in the SAME property rather than in a private column each.
 *
 * `media.hero` rather than a role the person chooses. A picture dropped on a
 * note is the note's picture; asking which of six media roles it should be is
 * the four-field form task 0044 was written about.
 */
/**
 * Attaches, or refuses — and SAYS WHICH.
 *
 * @Codex, P2 on `1c4d8b7`: *"this cleanup runs only when `attachFile()`
 * rejects, while its normal refusal paths — including navigation changing
 * `selected`, permission loss, and a refused store edit — return successfully.
 * In those cases the source was already primed, no attachment or later render
 * is scheduled, and the object URL continues pinning up to 64 MiB for the tab's
 * lifetime."*
 *
 * A `Promise<void>` cannot tell its caller the difference between "attached"
 * and "declined", and the caller has a 64 MiB object URL to release on one of
 * them. The previous round released on a THROW, which is the rarest of the four
 * exits — the three ordinary refusals all return normally.
 *
 * `false` for every refusal, `true` only once the values are durable and the
 * repaint is scheduled.
 */
/**
 * How many times an attachment may re-plan after a `stale` refusal.
 *
 * Three is two more than the race needs: a contending write has to land
 * between this attempt's snapshot and its commit, and it cannot do that twice
 * running without the claim moving — which the guard inside the loop catches
 * first and answers with `false` rather than another attempt.
 */
const ATTACH_ATTEMPTS = 3;

/**
 * Adopt the overlay's manifest for the CURRENT pool when the store can
 * address it — @Codex, P1 on `2ffce98`. Two tabs share one localStorage, and
 * the winning tab's attach writes its new manifest there via
 * `recordOverlay()`; a stale-refused attach that retried against the
 * unchanged `pool.manifest` re-extended the OLD base and forked the pool —
 * `M2 = M0 + roleB` beside the other tab's `M1 = M0 + roleA`, with the
 * overlay pointing at whichever landed last and the other tab's durable
 * attachment unreachable after reload. Verified before trust, the same
 * posture as `applyOverlay` at boot: a manifest the store cannot count
 * objects in is not adopted.
 */
async function adoptOverlayManifest(target: DemoPool = pool): Promise<void> {
  // `target`, not the module `pool` — @Codex, P1 on `18eae3f`: an attach
  // retry that rebases "the pool" rebases whatever pool the person has
  // navigated to since, while its write still names the object it started
  // on. The watch is rewired only when the target IS the pool on screen.
  const saved = readOverlay()[target.id];
  if (!saved || saved.manifest === target.manifest) return;
  const scope: Scope = { manifest: saved.manifest as never, typeHint: target.typeHint };
  try {
    if ((await db.store.count(scope)) === 0) return;
  } catch { return; }
  target.manifest = saved.manifest as never;
  target.scope = { manifest: target.manifest, typeHint: target.typeHint, ...crossDocs() };
  if (db.store instanceof StoreEngine) db.store.register(target.scope);
  for (const [role, key] of Object.entries(saved.added)) {
    declared.set(`${target.id} ${role}`, key as PropertyKeyId);
  }
  if (target === pool) watchPool();
}

async function attachFile(
  file: IngestedFile, intended: ObjectId, current?: () => boolean,
): Promise<boolean> {
  // THE THIRD DOOR to `db.editor`, and it was found by the guard written for
  // the other two rather than by reading the file: `write-authorization.test.ts`
  // asserts how many doors exist, and the count came back one higher than the
  // test claimed to cover.
  //
  // Same accidental-authorization shape as `addProperty` had. The `.make` form
  // is hidden for a reader, but a DROP is not the form — `document`'s own drop
  // listener reaches here — so the cardinality line below was again the only
  // thing between a reader and the store, and again only by coincidence.
  if (!mayWrite('add a file')) return false;
  if (!selected || state.cardinality !== 'single') {
    say('open one object before adding a file', 'bad');
    return false;
  }
  /**
   * The object the person was looking at when they chose the file — not the one
   * they are looking at now.
   *
   * @Codex, P1 on #184: *"When a user starts ingesting a video on object A and
   * navigates to object B while metadata decoding or seeking is pending, this
   * newly added deriver can keep `ingestOne()` awaiting for up to ten seconds.
   * `attachFile()` then reads the current module-level `selected` value rather
   * than the object checked before ingestion, so the source and poster are
   * written to B."*
   *
   * Task 0118 is what made the window wide: before the deriver, ingestion was a
   * hash and a put and the gap was milliseconds. `DECODE_CEILING_MS` is 5s per
   * decode and a video does two of them, so the gap is now up to ten seconds of
   * a person being free to navigate.
   *
   * Refused rather than redirected. Silently writing to A while B is on screen
   * is the same defect wearing the other face — the person would see nothing
   * happen and find a file somewhere they were not looking.
   */
  if (selected !== intended) {
    say('that file was for the object you had open — nothing was added', 'bad');
    return false;
  }
  // The role was decided at the door, by the one place that looks at a file.
  // The renderer resolves a MEDIUM from the role and never by opening the
  // value, so a video has to arrive under `media.video` or it draws as a still.
  /**
   * The source role, and the derivative's if ingestion made one.
   *
   * Task 0118. Written in the SAME edit rather than in a second one: a card
   * that finds `media.thumb` present and its source absent, or the reverse, is
   * a state no reader should ever see, and two edits is exactly how one is
   * produced. Decision 0027's basis covers both because it covers the edit.
   */
  const target = selected;
  /**
   * The POOL the person was in when they chose the file, captured with the
   * object for the same reason — @Codex, P1 on `18eae3f`: a stale retry that
   * awaits while the person navigates from pool A to pool B resolved its key
   * and manifest from the module `pool` (now B) while `target` still named
   * A's object, so the store accepted B's key on A and the bookkeeping below
   * advanced B's scope while reporting success. Everything in this function
   * reads `owner`; the module `pool` is only consulted to decide whether the
   * on-screen watch needs rewiring.
   */
  const owner = pool;
  /**
   * The door said what the file IS; the pool says where that lives —
   * decision 0050, from 0166 (the blind gate's one FAIL). An image written
   * as `media.hero` into Films or Archive sat at 1-of-N coverage under
   * every threshold and no collection ever showed it; under the pool's own
   * image role it rides the binding the layout already has. `owner.defs`,
   * not the module pool's — the same capture discipline as everything else
   * in this function.
   */
  const doorRole = file.role as Role;
  const attachRole = attachRoleFor(owner.defs, doorRole);
  const attachments: { role: Role; reference: string; mime: string }[] = [
    { role: attachRole, reference: file.reference, mime: file.mime },
  ];
  // The derivative pairs with the DOOR's role (`media.thumb` is
  // DERIVATIVE_OF `media.hero`); when the source was remapped, writing it
  // would mint the thumb-without-source state the repo forbids, so the
  // derived bytes stay in the blob store unreferenced instead.
  if (file.derived && attachRole === doorRole) {
    attachments.push({
      role: file.derived.role as Role,
      reference: file.derived.reference,
      mime: file.derived.mime,
    });
  }

  /**
   * The derivative role to CLEAR, when a replacement earns one and fails.
   *
   * @Codex, P2 on #184: *"When an object already has an image and thumbnail,
   * replacing it with an image whose derivation fails adds only the new
   * `media.hero` here and leaves the old `media.thumb` untouched. Collection
   * generation can then prefer that still-present thumbnail and show the
   * previous image even though the stored source and single-object view contain
   * the replacement."*
   *
   * The worst kind of stale: the card shows one picture and opening the object
   * shows another, and neither view is wrong about what it read. `0118` clause
   * 6 already says a missing preview must be VISIBLE rather than silent — a
   * preview of the file you replaced is louder than silence and less true.
   *
   * In the same edit as the source, for the reason the comment above gives: a
   * card that finds a thumbnail whose source is gone is a state no reader
   * should see, and two edits is how one is produced.
   */
  // Only when the write is in the door-role lineage: a replacement landing
  // under the pool's own role (0050) is not replacing a hero, so clearing
  // the hero's thumb would erase a preview it does not own.
  const staleDerivative = file.derivationFailure && !file.derived
    && attachRole === doorRole
    ? derivativeRoleFor(file.mime) : undefined;
  const minted = new Map<Role, PropertyKeyId>();

  // Nothing here was read from the store to plan with — the pool, the role and
  // any minted key come from state this session already holds — so a snapshot
  // taken now IS the state this plan was made against. Decision 0027 requires
  // the basis anyway: "callers that intend a blind write still begin from a
  // snapshot; they do not opt out of concurrency control."
  /**
   * Timed, because task `0119` clause 3 names five phases and this is the
   * fourth. @Codex, P2 on #184: *"`attachFile()`'s object write and the
   * subsequent repaint occur after all four measures; therefore a red
   * end-to-end stall still cannot distinguish the named hashing, storage,
   * object-write, and repaint regressions."*
   *
   * True, and `ingest.ts` could never have covered it: `ingestOne()` returns
   * before this runs, on purpose — it stores bytes and decides nothing about
   * an object. So the two remaining phases are emitted here and by `run()`,
   * under the same `morph.ingest.` prefix the harness already reads.
   */
  const wroteAt = typeof performance === 'object'
    && typeof performance.now === 'function' ? performance.now() : undefined;
  /**
   * Planned again when the plan was only ever STALE, and the claim is ours.
   *
   * @Codex, P2 on `5e085ed`: *"When a newer same-role file is selected while
   * this older `edit()` is awaiting its persistence commit, both attachment
   * attempts can capture the same basis: the older edit commits first, and the
   * newer — despite still owning the claim — is refused as stale, leaving the
   * object attached to the file the person replaced. The guard immediately
   * above cannot observe a claim change after the edit begins, so serialize
   * these attachment writes or retry a stale result when its claim is still
   * current to ensure the latest selection wins."*
   *
   * One step past the finding the guard below was written for, and past what
   * that guard can reach: it closes the window before the commit, and this one
   * opens during it. The outcome is the same and is the one supersession
   * exists to prevent — the loser of the race wins, because `edit` refuses the
   * SECOND writer on a stale basis.
   *
   * The store's own refusal says what to do: *"the store has moved and the
   * engine has been refreshed, so plan it again."* It has already re-hydrated
   * by the time this reads the code, so the next snapshot is the current
   * revision rather than a guess at one.
   *
   * **Only on `stale`, and only while the claim is still ours.** A `stale`
   * whose claim has moved on is a refusal that is CORRECT — somebody chose a
   * third file — and retrying it would be the same defect pointed the other
   * way. Every other refusal is about the write itself and repeating it would
   * repeat the reason.
   *
   * Bounded, because an unbounded retry against a store somebody else is
   * writing to in a loop is a page that never answers, and `0112`'s rule is
   * that a named refusal beats a wait that cannot end. Three attempts is two
   * more than the race needs: the contending write has to commit between this
   * snapshot and this edit, and it cannot do that twice in a row without the
   * claim moving, which the guard above catches first.
   *
   * `minted` is cleared per attempt: a refused edit's `defineProperty` never
   * committed, so its key does not exist and carrying it forward would leave
   * the scope pointing at a property nothing published.
   */
  let result;
  for (let attempt = 0; ; attempt += 1) {
    minted.clear();
    const basis = (await db.store.snapshot()).revision;
    /**
     * The claim, asked ONE MORE TIME — as late as anything here can ask it.
     *
     * @Codex, P2 on #184: *"When a newer same-role file is selected after the
     * check at line 6295 but while this older call is awaiting `attachFile()`'s
     * snapshot or edit, the older attachment never consults `claims` again. Both
     * calls can capture the same revision; the older edit, already queued first,
     * can commit while the newer selection receives a revision-conflict refusal,
     * leaving the object with the file chosen first."*
     *
     * The outcome is the one supersession exists to prevent, produced by the
     * mechanism meant to prevent it: the loser of the race wins, because `edit`
     * refuses the SECOND writer on a stale basis and nothing told the first it
     * had been replaced. `ingestOne` grew `current()` for exactly this and it
     * stopped at the door of the write.
     *
     * Placed after the snapshot on purpose. That await is the last one before
     * the commit, so this is the narrowest the window gets from here; a claim
     * that changes during `edit` itself is the residual `0130` already records
     * and cannot be closed on this side of the store.
     *
     * SILENT, like the supersession check at the call site: a person who chose a
     * second file does not need to be told that the first one they replaced did
     * not land. `false` still reaches the caller, which releases the primed URL.
     */
    if (current && !current()) return false;
    result = await db.store.edit(db.editor, basis, (s) => {
      for (const { role, reference, mime } of attachments) {
        const known = poolKeyForIn(owner, role) ?? declared.get(`${owner.id} ${role}`);
        let key = known;
        if (!key) {
          key = s.defineProperty({ label: MEDIA_LABEL[role] ?? 'Image',
            kind: ROLE_KIND[role], roleHint: role });
          minted.set(role, key);
          // In the SAME commit as the value below it — task `0126`. The key does
          // not exist until this line runs, which is why this could not be done
          // before the edit and why the store had to grow the fourth argument.
          s.publishRole(role, key);
        }
        s.set(target, key, { k: 'media', v: { blob: reference, mime } });
      }
      // After the writes, so a clear cannot race the set that replaces it.
      if (staleDerivative) {
        const key = poolKeyForIn(owner, staleDerivative) ?? declared.get(`${owner.id} ${staleDerivative}`);
        // No key means the role was never declared here, so there is nothing
        // stale to clear — a first ingest of a kind whose derivation failed.
        if (key) s.clear(target, key);
      }
    }, owner.manifest);
    if (result.ok || result.code !== 'stale') break;
    // The refusal is CORRECT if somebody has chosen another file since.
    if (current && !current()) return false;
    // REBASE THE MANIFEST before replaying — the stale refusal proves the
    // store moved, and another tab's move may have carried this pool's
    // manifest with it. See `adoptOverlayManifest`. The OWNER's manifest:
    // the write is A's whatever pool is on screen now.
    await adoptOverlayManifest(owner);
    if (attempt + 1 >= ATTACH_ATTEMPTS) break;
  }
  if (wroteAt !== undefined && typeof performance.measure === 'function') {
    try { performance.measure('morph.ingest.write', { start: wroteAt }); } catch { /* no-op */ }
  }
  if (!result.ok) {
    say(`refused: ${result.code} — ${result.message}`, 'bad');
    return false;
  }

  /**
   * ONE operation now — task `0126`, closed.
   *
   * @Codex, P2 on #184: *"if the manifest write fails, the derivative value
   * remains permanently unreachable even though ingestion already committed
   * it."* True — a value whose role is not in the manifest does not resolve, so
   * it was stored and invisible.
   *
   * The `publishRole` above rides in the edit's own commit, so the manifest
   * entry and the values under it are durable together or neither is. What is
   * left here is bookkeeping against the ref the commit reports: the scope, the
   * declared-key cache and the subscriptions all follow the manifest and none
   * of them is durable state.
   *
   * The reorder this task considered and refused — define the properties, then
   * extend, then set the values — is still the wrong shape and is worth keeping
   * written down: three operations widen the window in which the card shows
   * NEITHER value, paid on every first-ingest-of-a-kind to improve a case that
   * only happens when a write fails. The store growing a fourth argument made
   * the question disappear instead of moving it.
   */
  if (minted.size > 0) {
    // The ref the commit reported, not a second write. `result.manifest` is
    // present exactly when the edit published a role, which is exactly when
    // `minted` is non-empty — asserted rather than assumed, because a silent
    // `undefined` here would leave the scope pointing at the old manifest and
    // the new roles unreachable in this tab while durable in the store.
    if (!result.manifest) {
      say('the new role was stored but its manifest did not come back', 'bad');
      return false;
    }
    owner.manifest = result.manifest;
    owner.scope = { manifest: owner.manifest, typeHint: owner.typeHint, ...crossDocs() };
    if (db.store instanceof StoreEngine) db.store.register(owner.scope);
    for (const [role, key] of minted) declared.set(`${owner.id} ${role}`, key);
    if (pool === owner) watchPool();
    recordOverlay(owner);
  }

  // Sub-kilobyte says BYTES: `(510/1024).toFixed(0)` is "0 kB", and the
  // fabric blind driver read that as "the bytes may never have been stored"
  // in the exact moment the toast existed to reassure (task 0162).
  const size = file.bytes > 1024 * 1024
    ? `${(file.bytes / 1024 / 1024).toFixed(1)} MB`
    : file.bytes >= 1024 ? `${(file.bytes / 1024).toFixed(0)} kB`
    : `${file.bytes} bytes`;
  // Task 0118 clause 6: the file landed, and the missing picture is VISIBLE
  // rather than silent. Said in one line so the success is not replaced by the
  // partial failure six seconds later — `say` clears its predecessor.
  if (file.derivationFailure) {
    say(`added ${file.name} — ${size}, but no preview: ${file.derivationFailure}`, 'bad');
  } else {
    say(`added ${file.name} — ${size}`);
  }
  schedule();
  return true;
}

/**
 * The two ways a file gets here, sharing one path.
 *
 * A drop and a picker are the same event with different ceremony, and the
 * moment they are two paths is the moment one of them accepts something the
 * other refuses. Deliberately NOT `attachDropHost`: that host reads
 * `dataTransfer` looking for a block this page put there, and branching it on
 * the payload would put "is this a file" in the middle of a placement decision.
 */
/**
 * The object creation still in flight, or a resolved promise.
 *
 * Held so the file door can wait for it. Nothing else needs it: every other
 * gesture either follows a repaint or is one, and only ingestion has a window
 * long enough for the two to overlap in a way that misfiles somebody's file.
 */
let creating: Promise<unknown> = Promise.resolve();

/**
 * Whether there is an object to attach to, as opposed to a creation that ended.
 *
 * The barrier above waits for a creation to FINISH. Swallowing its rejection
 * fixed a door that one failure disabled forever — and opened a worse one, found
 * by a hostile review of the fix: `createObject` assigns `selected` only on the
 * success path, so after a failed creation `selected` still points at whatever
 * was open BEFORE. The barrier then settles, `!selected` is false, cardinality
 * is still `single`, and the file lands on an object the person did not choose,
 * announced as `added photo.png` over the failure line `say` had just cleared.
 *
 * That is the exact defect the barrier exists to prevent — *"reading `selected`
 * before that gives the PREVIOUS object, and the file lands on it"* — arriving
 * through the fix for a different one.
 *
 * It is not only about rejections. `createObject` also returns early, without
 * setting `selected`, when the store refuses and when the object lands in
 * another pool; those RESOLVE, so the old bare `await` never caught them either.
 *
 * True by default, because nothing pending is not a failure. Cleared when a
 * creation starts and set by every assignment to `selected` — so a person who
 * opens an existing object after a failed creation has answered the question
 * and the door works again, which is what @Codex asked for.
 */
let creationLanded = true;

function mountIngest(): void {
  const input = $('imageFile') as HTMLInputElement;
  input.accept = acceptAttribute;

  /**
   * Which ingest is the current one.
   *
   * @Codex, P2 on #184: *"When a person starts adding file A and selects or
   * drops file B before A finishes decoding, both `take()` invocations run
   * independently, and each completion proceeds to `attachFile()`. Because
   * image/video derivation can wait several seconds, B can attach first and
   * then the older A completion overwrites the same source and derivative
   * roles, leaving the object showing the earlier file instead of the person's
   * latest choice."*
   *
   * A token rather than a queue, and they offered both. Serialising would make
   * A's several seconds of decoding a delay B has to sit through, to finish
   * work whose result is about to be overwritten — the person has already said
   * which file they want. **Discarding the superseded completion is what they
   * asked for**, and it is also the cheaper thing to do.
   *
   * Checked after the derivation and before the write, because that is the
   * whole window: everything before it is reading bytes the person handed over,
   * and the only durable act is `attachFile`.
   *
   * **Per target and role, not one counter for the door.** @Codex, P2 one round
   * later: *"When two files for the same object use different roles — for
   * example, a slow image followed by an audio file — the second `take()`
   * increments this global token even though `media.hero` and `media.audio` can
   * coexist and neither attachment overwrites the other. If the audio ingest
   * finishes first, the completed image is silently discarded here, so only one
   * of the two files the person deliberately added reaches the object."*
   *
   * Right, and it is the fix for one defect creating another: the first version
   * superseded on the fact that a second file was chosen, when what supersedes
   * is a second file that would land in the SAME PLACE. Keyed on the object and
   * the source role, which decides the derivative role too — a thumbnail
   * belongs to its source's claim and cannot conflict with anything the source
   * does not.
   */
  const claims = new Map<string, number>();
  let ingest = 0;
  const claimKey = (target: ObjectId, mime: string): string =>
    `${target}\u0000${roleFor(mime) ?? mime}`;

  /**
   * A rejection from `take()` reaches a person, rather than the console.
   *
   * The same shape @Codex found on `wirePoolButton` and on `createObject`: a
   * `void`-ed promise says "this cannot fail" and means "nobody has looked".
   * `take()` awaits the store, the decoder and the blob store, any of which can
   * reject — and the door then does nothing with no message at all.
   */
  const reportIngestFailure = (work: Promise<void>): void => {
    work.catch((why: unknown) => {
      say(`the file could not be added: ${why instanceof Error ? why.message : String(why)}`,
        'bad');
    });
  };

  const take = async (files: FileList | null | undefined): Promise<void> => {
    const file = files?.[0];
    if (!file) return;
    /**
     * Refused at the DOOR, before any work is done for it.
     *
     * @Codex, P2 on `ef26359`: *"the only `mayWrite('add a file')` check occurs
     * later in `attachFile()`. This commit's new `derive: browserDeriver`
     * option therefore decodes the unattachable image or video and persists its
     * derivative and index mapping before the write is refused; repeated drops
     * can consume substantial CPU, memory, and IndexedDB quota despite the user
     * lacking permission to add anything."*
     *
     * Correct, and the cost grew under me: before `derive` the wasted work was
     * a hash and a clone, which is bad enough at the 64 MiB ceiling; now it is
     * a decode, a resize, an encode and two persisted rows. A refusal that
     * happens after the expensive part is a refusal in name.
     *
     * `attachFile` keeps its own check. This is not redundant — that one is
     * reached from paths this door does not own, and its comment records
     * exactly the accident where a cardinality line was *"the only thing
     * between a reader and the store"*.
     *
     * `mayWrite` says the sentence itself, so the person is told once, here,
     * rather than after a pause long enough to look like it worked.
     */
    if (!mayWrite('add a file')) return;
    /**
     * The ORDINAL is taken now; the KEY is filled in once the object is known.
     *
     * @Codex, P2 on #184: *"When a file larger than 8 MiB is followed by
     * another same-role file before the first file's announcement frame runs,
     * the newer invocation can reach this counter first; the older invocation
     * then resumes, receives the larger ticket, and replaces the newer claim …
     * so the older file can still attach last and overwrite the person's latest
     * choice."*
     *
     * Right, and it is the ordering bug inside an ordering fix: the ticket was
     * allocated after `await creating` and after the announcement frame, so two
     * `take()`s could be handed their numbers in the opposite order to the one
     * the person chose in. A ticket taken at the door cannot be.
     */
    const mine = (ingest += 1);
    let claim = '';
    /**
     * The object a person made a moment ago, finished landing.
     *
     * `$('newObj')` fires `createObject` with `void`, so the selection arrives
     * some time after the click returns. Reading `selected` before that gives
     * the PREVIOUS object, and the file lands on it — a real defect, not a
     * harness artefact, though only a machine is fast enough to hit it.
     *
     * Awaited HERE rather than re-reading the target later, which is what this
     * used to do. See `intended` below.
     *
     * **Swallowed, and that is the whole of the fix.** @Codex, P2 on #184:
     * *"If the most recent `createObject()` rejects … the rejected promise
     * remains stored in `creating`. Every later file selection then rejects at
     * this await before reaching either the refusal UI or `ingestOne()`, even
     * after the person navigates to a valid existing object, so one creation
     * failure disables ingestion until another creation attempt replaces the
     * promise."*
     *
     * Right, and worse than they say: `take()` is called with `void`, so the
     * rejection was not even reported — a person got a door that had silently
     * stopped working. This barrier's only job is to let a creation FINISH; a
     * creation that finished by failing has finished. Whether there is anything
     * to attach to is the next two lines' question, and they already answer it.
     */
    await creating.catch(() => undefined);
    // Before `!selected`, because `selected` is exactly what a failed creation
    // leaves stale — see `creationLanded`. Refusing is the only honest answer:
    // the object this file was meant for does not exist.
    if (!creationLanded) {
      say('that object was not created — nothing was added', 'bad');
      return;
    }
    if (!selected || state.cardinality !== 'single') {
      say('open one object before adding a file', 'bad');
      return;
    }
    /**
     * Captured ONCE, and the awaits above are why it can be.
     *
     * This was `let`, re-taken when the decode began. @Codex, P1 on #184:
     * *"When a large file begins ingesting on object A and the user navigates
     * to B while `file.arrayBuffer()` or `blobs.put()` is pending, this
     * callback overwrites the original `intended` value with B … preserve the
     * object selected when `take()` begins."*
     *
     * Right, and the recapture was product bent to fit an instrument. What it
     * was fixing is real — creating an object and immediately adding a file is
     * one continuous action, and `createObject` is fired off with `void`, so a
     * harness that sets the file five milliseconds later reads the PREVIOUS
     * selection and the file lands on the wrong object. But that is a race
     * between two gestures a PERSON cannot run together, and the answer is to
     * finish the first one rather than to re-read the target after the slow
     * part. `await creating` above is that; this is a `const` again.
     */
    const intended = selected;
    claim = claimKey(intended, file.type);
    // An OLDER ordinal never replaces a newer claim. Without this the check is
    // only "the last writer wins", and the last writer may be the older file.
    if ((claims.get(claim) ?? 0) > mine) return;
    claims.set(claim, mine);

    /**
     * The announcement comes AFTER the capture, and it used to come before.
     *
     * @Codex, P2 on #184: *"files above `ANNOUNCE_ABOVE_BYTES` still await
     * `requestAnimationFrame()` before `selected` is captured. If navigation
     * from object A to B is processed during that frame, the file chosen for A
     * is assigned `intended = B`, so both the currency claim and `attachFile()`
     * accept writing it to B."*
     *
     * The same defect the block above already carries a P1 about — *"preserve
     * the object selected when `take()` begins"* — reintroduced by a yield
     * added afterwards for an unrelated reason. Every await between the
     * selection check and the capture is another frame in which a person can
     * navigate, and a frame wait is an await whose entire purpose is to let the
     * page do something else.
     *
     * Moving it below costs nothing it was for. It is said BEFORE the work,
     * which is what matters: hashing and the structured clone both run on the
     * main thread — `e2e/blob-scale.mjs` measures about 19ms of frozen page per
     * megabyte — so a message painted after the work starts is a message nobody
     * sees until it is over. The frame wait is what gets it on screen, and the
     * work is still on the far side of it.
     */
    /**
     * A SUPERSEDED invocation fails as silently as it succeeds.
     *
     * @Codex, P2 on #184: *"if the superseded file's `arrayBuffer()` rejects
     * after the newer file has already attached, `reportIngestFailure` still
     * says 'the file could not be added.' The person sees a failure for a file
     * they replaced, over the success of the one they chose."*
     *
     * Right, and it is this door's own supersession rule applied to one branch
     * and not its mirror. The discard below is already deliberate and already
     * silent, for a reason that does not depend on how the older work ended:
     * *being told that the first one they replaced did not land is a message
     * about a decision they already made.* A rejection is that same message
     * with an error string on it, and it arrives on top of the newer file's
     * result rather than instead of it.
     *
     * Still current, and a failure is news — that is the path `reportIngestFailure`
     * was added for, and it is untouched.
     */
    const onlyIfCurrent = (why: unknown): undefined => {
      if (claims.get(claim) === mine) throw why;
      return undefined;
    };
    /**
     * The id `onStored` primed, so an abandoned ingest can give the URL back.
     *
     * Captured here rather than read off the result, because `onStored` fires
     * inside `ingestOne` BEFORE its own supersession check — so the abandoned
     * paths reach their handlers with no result and no id.
     */
    let primed: { readonly id: BlobId; readonly mime: string } | undefined;
    if (file.size > ANNOUNCE_ABOVE_BYTES) {
      say(`reading ${file.name}…`);
      await new Promise((r) => requestAnimationFrame(() => r(undefined)));
    }
    const out = await ingestOne(file, {
      blobs: db.blobs, onRefusal: (why) => say(why, 'bad'), derive: browserDeriver,
      // Paired with the deriver above, because it names THAT encoder. Task 0129.
      probe: encoderProbe,
      // Supersession asked DURING the ingest, not only after it. The check
      // below still stands — this one stops the work, that one stops the write.
      current: () => claims.get(claim) === mine,
      // The bytes are in memory here; the renderer would otherwise read all of
      // them back out of IndexedDB to mint an object URL. At the 64 MiB ceiling
      // that read IS the freeze — see `blob-urls.ts`.
      onStored: (id, bytes, mime) => {
        // Recorded only when THIS call minted the URL. `prime` returns false
        // when one already existed for these exact bytes and this type — it
        // belongs to whatever is drawing it, and a refusal here must not
        // revoke it out from under that. @Codex, P2 on #184.
        if (blobUrls.prime(id, bytes, mime)) primed = { id, mime };
      },
    }).catch(onlyIfCurrent);
    /**
     * Superseded, so this completion is discarded rather than written.
     *
     * The bytes are already stored — `ingestOne` put them, and a blob is
     * content-addressed and shared, so nothing is wasted or orphaned by not
     * attaching them. What is discarded is only the claim on the object's
     * roles, which the newer file has already made or is about to.
     *
     * Silent on purpose. The person chose a second file; being told that the
     * first one they replaced did not land is a message about a decision they
     * already made.
     */
    if (claims.get(claim) !== mine) {
      // The BYTES are fine to leave, as the comment above says. The primed
      // object URL is not: it pins a copy of them in this tab, and nothing will
      // sweep it, because a superseded ingest schedules no render.
      //
      // @Codex, P2 on #184. `onStored` fires INSIDE `ingestOne`, before its own
      // supersession check, so this path reaches here with `out` undefined and
      // no id to release — which is why the id is captured as it is primed
      // rather than read back off the result.
      if (primed) blobUrls.release(primed);
      return;
    }
    // And the same guard on the write: a newer file can claim the role while
    // this attach is in flight, and then this one's failure is superseded too.
    if (out) {
      const attached = await attachFile(out, intended,
        () => claims.get(claim) === mine).catch((error) => {
        // Same leak, the other way in: `attachFile` refusing a now-wrong target
        // ends the ingest with a primed URL and no later pass.
        if (primed) blobUrls.release(primed);
        onlyIfCurrent(error);
        return false;
      });
      // And on a refusal that RETURNS. @Codex, P2 on `1c4d8b7`: the throw is
      // the rarest of the four exits, and the three ordinary refusals — no
      // write authority, navigation moving `selected`, a refused edit — all
      // came back here looking like success while pinning up to 64 MiB for the
      // life of the tab.
      if (!attached && primed) blobUrls.release(primed);
    } else if (primed) {
      // Stored, then abandoned inside `ingestOne` itself — the `current()`
      // check after `onStored`.
      blobUrls.release(primed);
    }
  };

  $('addFile').addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    reportIngestFailure(take(input.files));
    // Cleared, or choosing the SAME file twice fires no second `change` and the
    // second attempt looks like the app ignoring you.
    input.value = '';
  });

  // Counted rather than toggled. `dragenter` and `dragleave` fire for every
  // element the pointer crosses, so a boolean flickers off the moment the
  // cursor moves between two children and the outline strobes across the page.
  let depth = 0;
  const dropping = (on: boolean) => {
    document.documentElement.classList.toggle('dropping', on);
    if (on) $('dropnote').textContent = state.cardinality === 'single'
      ? 'Drop a file to add it to this object'
      : 'Open one object first, then drop a file on it';
  };
  document.addEventListener('dragenter', (e) => {
    if (!(e.dataTransfer?.types ?? []).includes('Files')) return;
    depth += 1; dropping(true);
  });
  document.addEventListener('dragleave', () => { depth = Math.max(0, depth - 1); if (!depth) dropping(false); });
  // Without this the browser navigates to the file and the page is gone.
  document.addEventListener('dragover', (e) => {
    if ((e.dataTransfer?.types ?? []).includes('Files')) e.preventDefault();
  });
  document.addEventListener('drop', (e) => {
    if (!(e.dataTransfer?.types ?? []).includes('Files')) return;
    e.preventDefault();
    depth = 0; dropping(false);
    // The shelf is not a pool. The drop listener is document-level, so hiding
    // the pool chrome while the hub is up does not reach it — a file dropped
    // on the shelf would attach to whatever single-object lens is hidden
    // underneath, which is @Codex's exact scenario. Refused out loud.
    if (!$('hub').hidden) {
      say('the shelf holds rebuilds, not files — open a collection first', 'bad');
      return;
    }
    reportIngestFailure(take(e.dataTransfer?.files));
  });
}

/**
 * Write one role onto ONE NAMED OBJECT.
 *
 * Split out of `addProperty` because that function had two jobs welded
 * together: the gesture guard for the "add a property" form — which acts on
 * whatever object is open — and the write itself. An ACTION does not need the
 * first. It names its subject, and the button that ran it sits on that
 * subject's row.
 *
 * Measured, and it is the whole reason a row button could not work: pressing
 * one in a collection announced *"open one object before adding a property"*,
 * because the only write path in the demo insisted the lens be in
 * single-object mode. A button on a row is by definition not in single-object
 * mode, so the affordance and the only door it had were mutually exclusive.
 * The same family as task 0085 — an action's effects borrowing the UI's notion
 * of "the object in hand" instead of using the subject it was given.
 *
 * Returns whether the write landed, so the caller decides what to say. This
 * says nothing itself except on a store refusal, which is the one message that
 * belongs to the write rather than to the gesture.
 */
async function writeProperty(
  target: ObjectId, role: Role, label: string, raw: string,
): Promise<boolean> {
  // THE DOOR TO `db.editor`, so the authorization is asked here and not only at
  // the gestures above it. See `mayWrite` — a reader reached this through an
  // action, and every guard on the way was a gesture guard that an action does
  // not pass through.
  if (!mayWrite('change anything')) return false;
  // The pool's own key first, then one this session minted, then a new one.
  // Reusing the pool's key is what puts a new note's body in the SAME property
  // every other note's body is in — so it reads back, sorts, folds and renders
  // as one field rather than as a private column of one.
  const known = poolKeyFor(role) ?? declared.get(`${pool.id} ${role}`);
  let minted: PropertyKeyId | undefined;
  // Re-check derived provenance in the same coherent read that supplies the
  // write basis. This door is shared by row actions, Add property and the
  // Automation panel, so guarding only GrantSet-backed fields would leave the
  // retired scalar writable through a second product control.
  const plan = await db.store.read(async ({ reader, revision }) => ({
    basis: revision,
    derived: await db.isDerivedRole(reader, target, pool.manifest, role),
  }));
  if (plan.derived) {
    say(`refused: no-grant — ${label} is derived from canonical content and is read-only here`, 'bad');
    return false;
  }
  // If another tab migrates after this read, the basis makes the editor refuse
  // the stale plan instead of recreating the retired scalar.
  const result = await db.store.edit(db.editor, plan.basis, (s) => {
    // A role this session already minted a key for reuses THAT key. A second key
    // carrying one role is not a harmless duplicate: it makes every later write
    // to that role ambiguous, and the store refuses rather than guess which of
    // the two the user meant.
    const key = known
      ?? (minted = s.defineProperty({ label, kind: ROLE_KIND[role], roleHint: role }));
    // Same commit as the value — task `0126`. This door mints a scalar rather
    // than a media pair, and the defect is identical: a value under a role the
    // manifest does not carry is stored and does not resolve.
    if (minted) s.publishRole(role, minted);
    s.set(target, key, coerce(role, raw));
  }, pool.manifest);
  if (!result.ok) { say(`refused: ${result.code} — ${result.message}`, 'bad'); return false; }

  if (minted) {
    if (!result.manifest) {
      say('the new role was stored but its manifest did not come back', 'bad');
      return false;
    }
    pool.manifest = result.manifest;
    pool.scope = { manifest: pool.manifest, typeHint: pool.typeHint, ...crossDocs() };
    if (db.store instanceof StoreEngine) db.store.register(pool.scope);
    declared.set(`${pool.id} ${role}`, minted);
    // A scope is keyed by its manifest ref, so the old subscription is now
    // watching a collection nothing will ever write to again.
    watchPool();
    recordOverlay();
  }
  // A NEW KEY is a shape change; writing a value through an existing one is not.
  //
  // This is about the OTHER caller. `effects.setProperty` reaches here for every
  // row action, and those must not regenerate. `addProperty` is an explicit
  // shape command and asks for its own boundary — it has to, because a role
  // whose key already exists mints nothing and would otherwise render no field.
  //
  // This was an unconditional `schedule()`, and @Codex's review caught what that
  // costs now that the default regenerates: `effects.setProperty` reaches here
  // for every row action, so pressing **Update** on a task asked the generator
  // for a new lens. Measured with a counter in the build — `press row Update:
  // +1 compile` — which is precisely what decision 0023 forbids, and it was
  // invisible in the columns because the re-ranking happened to come back the
  // same. The rule is the regeneration, not the symptom.
  schedule(minted !== undefined);
  return true;
}

/** Widen an enum through the binding authority of the lens actually on screen.
 *
 * This must NOT share `writeProperty`'s editor door. Knowing the manifest key
 * proves where a value would land; it does not prove that the rendered lens
 * authorised the person to land it. @Codex, holding acceptance on task 0096:
 * *"the Inspector can overwrite a carried enum even when the rendered binding
 * did not authorize that mutation. Key reachability prevents an accidental
 * duplicate key; it is not binding authority."*
 *
 * That is decision 0007's whole point — a spec may only write what it declared
 * — and the widening exception had been routing around it. So this resolves the
 * current lens's writable `set-enum` node, mints THAT grant, and submits an
 * ordinary bridge transaction, exactly as an edit in the generated chooser
 * does. Absent the grant it refuses, even for somebody who may otherwise edit
 * the schema.
 */
async function widenEnum(target: ObjectId, role: Role, raw: string): Promise<boolean> {
  if (!mayWrite('change anything')) return false;
  const ir = lastIR;
  const lens = lastLens;
  const store = bridge;
  const handle = handles.get(target);
  const node = ir?.nodes.find((candidate) =>
    candidate.role === role && candidate.mutation === 'set-enum'
    && candidate.direction === 'read-write');
  if (!ir || !lens || !store || !handle || !node) {
    /**
     * NAME THE WALL, not just the refusal — task 0160, defect 1. The old
     * sentence ended at "does not authorize", and the Notion blind driver's
     * 45-step budget died against it: three retries, no way to learn that
     * the missing thing is a RENDERED control. The mechanism is stated
     * because it is the true precondition (0096: only a rendered writable
     * binding is authority), and the coverage clause is stated because it
     * is the generator's actual rule for when a chooser exists. Whether a
     * door should be CUT through this wall is the 0160 ruling, still open;
     * a wall with its shape written on it is what honesty affords today.
     */
    say(`refused: no-grant — this lens draws no writable ${labelForRole(role)} control, `
      + 'and only a rendered control can authorize the change. A chooser is '
      + `generated once enough of these objects carry ${labelForRole(role)}`, 'bad');
    return false;
  }
  const grant = mintGrants(ir, lens).forNode(node.i);
  if (!grant || grant.role !== role || grant.mutation !== 'set-enum') {
    say(`refused: no-grant — this lens does not authorize changing ${labelForRole(role)}`, 'bad');
    return false;
  }
  const scope = scopeOf(ir, node.i, lens);
  const result = await store.apply({
    id: nextTxnId(), scope, at: Date.now(),
    intents: [{
      grant: grant.id, object: handle, role, mutation: 'set-enum',
      value: { t: 'enum', v: raw }, lens, scope,
    }],
  });
  if (!result.ok) {
    say(`refused: ${result.refusal.code} — ${result.refusal.message}`, 'bad');
    return false;
  }
  return true;
}

async function addProperty(role: Role, label: string, raw: string): Promise<void> {
  // The GESTURE's guard, and it stays here. The form adds a field to the object
  // a person has open, so "which object" is answered by the lens — and a
  // collection has no answer to give.
  if (!selected || state.cardinality !== 'single') {
    say('open one object before adding a property', 'bad');
    return;
  }
  // A CARRIED enum is a change to an existing slot, and goes through the lens's
  // own grant rather than the schema editor door. Only carried enums take this
  // branch — an ordinary addition is still a schema act and still the editor's.
  /**
   * CARRIED means the POOL HAS A KEY FOR IT, not that this object currently
   * holds a value through it.
   *
   * This read `Object.keys(objects[0])` — the projected values — and @Codex's
   * review caught what that costs now that clearing exists. The store keeps a
   * cleared slot in the `empty` state deliberately (task 0096: empty is a
   * property state, not a schema mutation), and projection omits roles whose
   * slots are not populated. So immediately after somebody clears an enum, this
   * set said the role was not carried, and filling it back in fell through to
   * `writeProperty` and the EDITOR DOOR instead of `widenEnum` and the rendered
   * lens's binding grant.
   *
   * That is the authorization bypass `f8d06cc` closed, reopened for exactly the
   * clear-then-refill workflow that task 0096 created — and it mislabelled the
   * control **Add** when the honest word is **Change**. A permission boundary
   * that holds until somebody clears a field is not a boundary.
   *
   * The key map is the right source because it answers the question actually
   * being asked: does a binding for this role already exist to write through?
   * A slot's value can come and go; the key cannot.
   */
  const hasKey = (r: Role): boolean =>
    poolKeyForIn(pool, r) !== undefined || declared.has(`${pool.id} ${r}`);
  if (ROLE_KIND[role] === 'enum' && hasKey(role)) {
    if (!await widenEnum(selected, role, raw)) return;
    schedule();
    await explainCoverage(role, label);
    return;
  }
  if (!await writeProperty(selected, role, label, raw)) return;
  // **Add property is an explicit shape command**, whether or not a key had to
  // be minted for it. Decision 0023 names it as the example: *"Add property is
  // an explicit shape-changing command and may regenerate so that the requested
  // field appears."*
  //
  // This line is new and its absence was a defect I introduced. `writeProperty`
  // used to end in an unconditional `schedule()` and I narrowed it to
  // `schedule(minted !== undefined)` so that row actions would stop
  // regenerating — correct for that caller, and it silently took the
  // regeneration away from this one whenever the role's key ALREADY existed.
  // Adding a subtitle to a second object then wrote the value and rendered no
  // field: "is on 2 of 14" in the status line, nothing on screen. The
  // widen-enum branch above already called `schedule()` for itself, which is
  // why only this path broke.
  schedule();
  await explainCoverage(role, label);
}

/**
 * Why the collection did or did not change, in the generator's own numbers.
 *
 * A single-object lens has no metadata budget, so it gains the field at once.
 * A collection gains a column only when the role clears `roleCoverageFloor` —
 * and a user who is not told that reads "nothing happened" as a broken feature.
 * The floor is read from the parameter registry rather than written down again
 * here, because an explanation that has drifted from the rule is worse than no
 * explanation at all.
 */
async function explainCoverage(role: Role, label: string): Promise<void> {
  // The effective boundary, because this SENTENCE is about what generation
  // decided, and generation reads the effective shape. A coverage figure taken
  // from the raw store would explain a number the product did not use.
  const shape = (await db.effective.read(pool.scope, { limit: 0 })).shape;
  const coverage = shape.roles.find((r) => r.role === role)?.coverage ?? 0;
  const floor = PARAMS.roleCoverageFloor.value;
  const pct = (n: number) => `${Math.round(n * 100)}%`;
  const carrying = Math.round(coverage * shape.n);
  say(coverage >= floor
    ? `"${label}" is on ${carrying} of ${shape.n} — past the ${pct(floor)} a column needs, `
      + 'so the collection can change too.'
    : `"${label}" is on ${carrying} of ${shape.n} (${pct(coverage)}). A column needs ${pct(floor)}, `
      + 'so the collection is unchanged — the single-object lens gained a field.');
}

/**
 * What a user may add to the object in hand.
 *
 * Roles, never widgets: `ROLE_KIND` picks the editor, for the same reason
 * `OPERATORS_BY_KIND` lives in one place. Two kinds are absent because a value
 * of theirs is not something anyone can type — media is a blob reference and
 * ref is an object identity. A role the object already carries is absent too:
 * role values are keyed BY ROLE on the way to the renderer, so a second one
 * would overwrite the first on screen while the store kept both.
 */
let poolRoles = new Set<Role>();

/**
 * The key the POOL declared for a role, if it declared one.
 *
 * This is what makes a new note writable. The picker used to hide every role
 * the pool already carried, on the reasoning that reusing one would mint a
 * second key and break writes — true for a role whose key this session cannot
 * see, and false for the pool's OWN roles, whose keys `bootstrap` filled into
 * `pool.keys` and which were sitting right here the whole time.
 *
 * The consequence was the whole answer to "can you take notes in this yet":
 * you could create a note, and the one thing you could not then give it was a
 * **body**, because the Notes pool already declares one. The picker offered
 * subtitle, caption, rank and confidence — everything except the field you
 * would actually write in.
 */
function poolKeyFor(role: Role): PropertyKeyId | undefined {
  return poolKeyForIn(pool, role);
}

function poolKeyForIn(targetPool: DemoPool, role: Role): PropertyKeyId | undefined {
  const def = targetPool.defs.find((d) => d.role === role);
  return def ? targetPool.keys[def.handle] : undefined;
}

function renderPicker(): void {
  const hasTarget = state.cardinality === 'single' && selected !== undefined;
  const carried = new Set(Object.keys(hasTarget ? objects[0] ?? {} : {}));
  const options = hasTarget
    ? propertyPickerRoles(ROLES, carried, (r) =>
      // A role the pool already carries under a key this session did not mint
      // cannot be reused — nothing in the store's read surface hands a role's key
      // back — so offering it would mint a second key and break writes to it.
      // Offered when the key is REACHABLE: the pool declared it, this session
      // minted it, or the pool does not carry the role at all. Only a role
      // carried under a key nobody here can name stays hidden, which is the
      // case the original rule was written for.
      !poolRoles.has(r) || declared.has(`${pool.id} ${r}`) || !!poolKeyFor(r))
    : [];

  const select = $('propRole') as HTMLSelectElement;
  const previous = select.value;
  select.innerHTML = options
    .map((r) => `<option value="${r}">${labelForRole(r)} · ${ROLE_KIND[r]}</option>`).join('');
  if (options.includes(previous as Role)) select.value = previous;

  // The form says which act it is about to perform. A carried enum is
  // OVERWRITTEN rather than added, and calling that "Add" would tell somebody
  // they were creating a second property — the exact confusion the exclusion
  // rule existed to prevent, reintroduced by the exception that relaxes it.
  const describeAct = () => {
    const role = select.value ? select.value as Role : undefined;
    const act = propertyPickerAct(role, carried);
    /**
     * DECLINE BEFORE THE TYPING — task 0160, defect 2. A carried enum routes
     * through `widenEnum`, which requires the rendered lens to hold a
     * writable set-enum node (0096's boundary); when it holds none, the
     * submit WILL refuse, and the form knew before the person filled
     * anything. Same predicate as the door itself asks, so the two cannot
     * drift: if the 0160 ruling later opens a route, this precondition stops
     * firing by construction.
     */
    const walled = hasTarget && role !== undefined && ROLE_KIND[role] === 'enum'
      && (poolKeyForIn(pool, role) !== undefined || declared.has(`${pool.id} ${role}`))
      && !lastIR?.nodes.some((n) =>
        n.role === role && n.mutation === 'set-enum' && n.direction === 'read-write');
    $('propTarget').textContent = !hasTarget
      ? 'Open an object to add a property'
      : walled
        ? `This lens draws no writable ${labelForRole(role!)} control, so it cannot `
          + `authorize changing it — a chooser is generated once enough objects carry it`
        : `${act} this object's ${role ? labelForRole(role) : 'property'}`;
    $('addProp').textContent = act;
    ($('propValue') as HTMLInputElement).disabled = !hasTarget || walled;
    ($('addProp') as HTMLButtonElement).disabled = !hasTarget || walled;
  };
  for (const id of ['propRole', 'propLabel', 'propValue', 'addProp']) {
    ($(id) as HTMLInputElement | HTMLSelectElement | HTMLButtonElement).disabled = !hasTarget;
  }
  // AFTER the baseline disabling, so the walled state is not overwritten.
  select.onchange = describeAct;
  describeAct();
}

/** A typed value from what someone typed. The store refuses a kind mismatch
 *  rather than coercing silently, so this has to be right. */
function coerce(role: Role, raw: string): Value {
  switch (ROLE_KIND[role]) {
    case 'number': case 'score': {
      const n = Number(raw);
      return { k: 'number', v: Number.isFinite(n) ? n : 0 };
    }
    case 'date':  return { k: 'date', v: raw || new Date().toISOString().slice(0, 10) };
    case 'enum':  return { k: 'enum', v: raw };
    case 'media': return { k: 'media', v: { blob: raw, mime: 'image/jpeg' } };
    default:      return { k: 'text', v: raw };
  }
}

/**
 * The chrome that WRITES, removed for a reader rather than disabled.
 *
 * `createObject` and `addProperty` go through `db.editor` directly, so a reader
 * who could still reach these would create objects and mint properties in a
 * lens that has no editable field in it — a permission that stops at the
 * document and not at the person.
 *
 * Removed, not `disabled`: a disabled control still says the action exists and
 * that you are not allowed today, which is a different message from a product
 * that this person reads. Absence is the same rule the lens itself follows.
 */
if (!MAY_WRITE) {
  const make = document.querySelector('.make');
  if (make instanceof HTMLElement) make.hidden = true;
}

$('newObj').addEventListener('click', () => {
  if (!MAY_WRITE) return;
  const input = $('newObjName') as HTMLInputElement;
  const title = input.value.trim();
  if (!title) { input.focus(); return; }
  input.value = '';
  creationLanded = false;
  creating = createObject(title);
  // Handled here, not merely `void`-ed. `void` on a promise attaches nothing,
  // so a failed creation surfaced as an unhandled rejection with no message a
  // person could read — and `take()` now swallows the same failure at its
  // barrier, which would have left NOBODY reporting it.
  creating.catch((why: unknown) => { say(`could not create: ${String(why)}`, 'bad'); });
});

$('newBlank').addEventListener('click', () => {
  if (!MAY_WRITE) return;
  const input = $('newObjName') as HTMLInputElement;
  const title = input.value.trim();
  if (!title) { input.focus(); return; }
  input.value = '';
  creationLanded = false;
  creating = createObject(title, false);
  creating.catch((why: unknown) => { say(`could not create: ${String(why)}`, 'bad'); });
});

$('addProp').addEventListener('click', () => {
  if (!MAY_WRITE) return;
  const role = ($('propRole') as HTMLSelectElement).value as Role;
  const label = ($('propLabel') as HTMLInputElement).value.trim() || role;
  const valueEl = $('propValue') as HTMLInputElement;
  const value = valueEl.value.trim();
  // Focus alone read as a dead click (blind run 12, 0154 item 7): the person
  // pressed Add, the caret moved, and nothing said why. The refusal gets its
  // words; the focus stays, because the field the words name is the fix.
  if (!value) { valueEl.focus(); say('nothing to add yet — type a value first', 'bad'); return; }
  valueEl.value = '';
  void addProperty(role, label, value);
});

// Enter in the Value field submits — blind run 12 pressed the key every form
// teaches and the property sat unwritten (0154 item 7). Routed through the
// button's own click so the empty-value refusal, the walled-lens disable and
// the write itself cannot drift from the path a pointer takes: a click on a
// disabled button is a no-op by construction.
$('propValue').addEventListener('keydown', (e) => {
  if (e.key !== 'Enter') return;
  e.preventDefault();
  ($('addProp') as HTMLButtonElement).click();
});

/* ------------------------------------------------------------------ bootstrap */

async function boot(): Promise<void> {
  db = await openDemoStore({
    onRefreshed: () => { void refreshCanonicalSurfaces(); },
  });
  /**
   * The back-forward cache is not a close, and treating it as one broke the tab.
   *
   * @Codex, P1 on #166: *"When a browser puts this page into the back-forward
   * cache, `pagehide` fires without destroying the page; navigating Back then
   * resumes the same `db` after this once-only handler has permanently closed
   * its channel. The restored tab no longer receives other tabs' writes, and
   * its next successful local write attempts to post on the closed channel,
   * producing `InvalidStateError`."*
   *
   * So the close is conditional on the page actually being discarded, and
   * `once` is gone because `pagehide` can fire many times over one page's life.
   *
   * A restored page also MISSED whatever was announced while it was frozen —
   * the channel delivers nothing to a frozen document — so `pageshow` hydrates
   * before reconciling rather than only reconciling. Without the hydrate the
   * engine still holds the photograph it was frozen with, which is the original
   * finding of task 0104 arriving by a new route.
   */
  window.addEventListener('pagehide', (event) => {
    if (!event.persisted) db.tabs?.close();
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    void (async () => {
      if (db.store instanceof StoreEngine) await db.store.hydrate({}, true);
      await refreshCanonicalSurfaces();
    })();
  });
  blobUrls = createBlobUrls(db.blobs);
  // The blob store, reachable from a harness. `e2e/blob-scale.mjs` measures what
  // a large file costs in a real IndexedDB, and it has to measure the SHIPPED
  // store rather than a reimplementation of it — a benchmark against a copy is a
  // benchmark of the copy. Read-only from the page's point of view: nothing here
  // is wired to a control, and the demo never reads it back.
  (window as unknown as { __morphBlobs: unknown }).__morphBlobs = db.blobs;
  /**
   * And the store BEHIND the worker, for decision `0046`'s two substitutions.
   *
   * Control 3 routes the doubled request through it — *"even if the content is
   * stored correctly"*, the Worker-boundary clause must redden — and control 4
   * routes the ordinary ceiling ingest through it, which is the responsiveness
   * control. `0046` requires the real implementation rather than a synthetic
   * freeze, so nothing here simulates: it is the object the wrapper delegates
   * to, writing to the same database.
   *
   * Undefined on the memory routes, where there is no Worker to substitute
   * away, and a harness that finds it missing is being told so rather than
   * silently measuring the wrapped store.
   */
  (window as unknown as { __morphDirectBlobs: unknown }).__morphDirectBlobs = db.direct;
  /**
   * And the OPENER, for the one control no other hook can reach.
   *
   * `0047`'s amendment, control 7: *"close and reopen of real IndexedDB
   * preserves both the digest-only fallback and the absence of an invalid
   * typed row."* That is a claim about the v5 MIGRATION — a pre-v5 vault whose
   * historical media type the typed scheme cannot encode — and `morph-demo` is
   * already at version 5 by the time any harness can run, so the only way to
   * ask it is to seed a fresh database at version 4 and open THAT through the
   * shipped opener.
   *
   * The shipped one, deliberately: `fake-indexeddb.ts` keeps its rows in a
   * `Map` that lives for the process, so a migration clause driven against it
   * is a clause about the fake. This repo has paid for that twice — the
   * transaction that auto-commits, and the version-4 upgrade surviving a
   * reload — and both were found only in a browser.
   *
   * A function, not a store: it opens nothing until a harness names a database,
   * and the demo itself never calls it.
   *
   * It grants no authority a page script does not already have — `indexedDB` is
   * on `window` and any script in this origin can open any database in it. What
   * this saves a harness is reimplementing the v5 migration, which is the one
   * thing a harness must not do here.
   */
  (window as unknown as { __morphOpenBlobs: unknown }).__morphOpenBlobs = openIndexedDbBlobs;
  /**
   * The encoder this session actually is, and the warm-up that produces it.
   *
   * Started here rather than lazily at the first drop so the probe's decode and
   * encode are not inside a person's ingest, which is the path decision `0021`
   * budgets and `e2e/ingest-budget.mjs` measures. `encoderProbe()` memoises, so
   * this call and the one inside `ingestOne` are one piece of work.
   *
   * Exposed because `e2e/derivatives.mjs` plants a mapping at a hand-spelled
   * key, and the encoder is the one part of that key a harness cannot know.
   * Deliberately only the digest: the harness keeps spelling the rest itself,
   * so a change to the key's SHAPE still reddens it rather than being imported
   * along with the value.
   */
  (window as unknown as { __morphEncoderProbe: unknown }).__morphEncoderProbe = encoderProbe;
  // Only the transform this build can actually identify. `video-poster-frame`
  // resolves to `undefined` by design and costs nothing to warm.
  void encoderProbe('image-thumbnail', 'image/png');
  // The pool, by id only — see `PrivatePoolSuggestions.ids`. A harness needs to
  // ask whether an acceptance contributed, and there are two acceptance paths.
  (window as unknown as { __morphPool: unknown }).__morphPool = {
    ids: () => privatePool.ids(),
    /**
     * Whether the CURRENT accepted lens projects to a genome inside the closed
     * vocabulary, and what falls outside it if not.
     *
     * Task `0124` clause 3 asks for a battery *"that tells you the enumeration
     * is complete rather than merely plausible"*, and two fixtures in
     * `packages/spec` are not that: they were green while the demo's own
     * generated specs produced nine refusals, over four structural keys the
     * fixtures never carry. The demo's real specs exist only here, so this is
     * where that clause can actually be asked.
     *
     * Returns the failures rather than throwing, so a harness can print them.
     */
    outsideVocabulary: () => {
      if (!state.lens.accepted) return ['no accepted lens'];
      try {
        return [...assertClosedVocabulary(project(state.lens.accepted.spec))];
      } catch (error) {
        return [`projection refused: ${error instanceof Error ? error.message : String(error)}`];
      }
    },
  };
  /**
   * A canonical read taken once the writes it was told about have landed.
   *
   * Without this the gate was red on `6923b27` and about a third of local runs
   * agreed: `e2e/document-canvas.mjs` 7/9, the list shortcut and the remount
   * comparison failing together. Both were one cause. `loadDocument` reports
   * what the store holds RIGHT NOW, and a canvas gesture is several publishes —
   * `fill('- ')` clears the block and then inserts — so a read taken between
   * two of their saves returns a document that was true a moment ago and will
   * not be true a moment later. The remount check then compared the settled
   * graph against that mid-flight one and disagreed with itself.
   *
   * The product was never wrong: the same runs showed the correct list and item
   * after remounting. It is the READ that has to name a quiescent moment, and
   * the harness could not name one from outside the page.
   *
   * Deliberately not a fixed delay. A sleep long enough for CI is dead time on
   * every run, and one that is occasionally too short is this defect again with
   * a longer fuse.
   */
  /**
   * The two specs a revision is between, by node id.
   *
   * @Codex, P2 on #184 found that an ORDINARY suggestion compiled without
   * `state.lens.accepted`, so keeping it discarded a person's hierarchy and
   * placements while the pool gesture beside it preserved them. That defect is
   * invisible from the DOM — both answers render a lens — and invisible from
   * the store, which holds objects rather than arrangements. It lives in the
   * SPEC, so a harness needs the spec.
   *
   * Ids rather than the specs themselves, and read-only: a revision keeps the
   * nodes it was given, and a fresh baseline mints its own, so the id is the
   * whole of what distinguishes them. Handing out the specs would be handing a
   * harness something to compare against itself.
   */
  (window as unknown as { __morphLens: unknown }).__morphLens = {
    /**
     * The two places a palette lives, so a harness can see them agree.
     *
     * They are two values for one fact — `state.palette` drives the selector
     * and the chrome, `state.taste.palette` is what a generation request
     * merges — and the defect @Codex found is exactly them disagreeing, with
     * the older pooled one winning. Reachable only through the page, because
     * neither the DOM nor the store carries the taste.
     */
    palette: () => ({ chosen: state.palette, taste: state.taste.palette }),
    /** The paint count and the store's revision, for 0056's third proof:
     *  a pointer click on an inert item must leave BOTH unchanged, and the
     *  DOM a harness reads back cannot say so (@Codex, P2 on 8957373 — a
     *  handler that repaints or writes without moving the compared fields
     *  passed). `epoch` moves once per paint; `revision` once per commit. */
    epoch: () => epoch,
    revision: () => db.store.read(async ({ revision }) => revision),
    rootIds: () => ({
      accepted: (state.lens.accepted?.spec.root as { id?: string } | undefined)?.id,
      offered: (state.lens.offered?.spec.root as { id?: string } | undefined)?.id,
    }),
  };
  (window as unknown as { __morphDocument: unknown }).__morphDocument = {
    /** Canonical reconstruction through object reads and relation traversals,
     *  so a harness judges STORED MEANING rather than the DOM it just typed
     *  into. Decision 0025 forbids the substitution by name. */
    read: async (ordinal = 0) => {
      await settled();
      return canvasDocuments[ordinal]
        ? loadDocument(db.store, canvasDocuments[ordinal]!, db.document.keys, db.document.manifests)
        : undefined;
    },
    readAll: async () => {
      await settled();
      return Promise.all(canvasDocuments.map((document) =>
        loadDocument(db.store, document, db.document.keys, db.document.manifests)));
    },
    /**
     * The STATE of the stored `body` scalar, straight from the store.
     *
     * Same reason `read` exists: once the projection is wired, the page hands
     * out a `body` whether or not one is stored, so "was the scalar retired"
     * is a question the DOM cannot answer and neither can a projected value.
     * A guard for decision 0028's ownership transfer has to reach past both.
     *
     * Read-only, and it returns a STATE rather than a value — 'absent' is the
     * answer that matters and a cleared property has no value to report.
     *
     * THE PROSE SCALAR, whichever role it is. Found by the clause written for
     * @Codex's P2 on c4d1346 rather than by the review: that finding is about
     * the mount, and a whole-file assertion for the same literal reported this
     * one too. On a transcript-shaped pool it answered 'unknown' — an
     * instrumentation helper reporting "no such thing" about a thing that is
     * there, which is the worst answer a probe can give. PROSE_ROLES is
     * ordered, so `body` still wins where both exist.
     *
     * The NAME stays `storedBodyState`: harnesses call it, and renaming a
     * probe is its own change with its own evidence.
     */
    storedBodyState: async (ordinal = 0) => {
      await settled();
      const document = canvasDocuments[ordinal];
      const key = PROSE_ROLES.map((role) => pool?.keys[role]).find((k) => k !== undefined);
      if (!document || !key) return 'unknown';
      const object = await db.store.get(document, { manifest: pool.manifest });
      return object ? stateOf(object, key) : 'missing';
    },
    /**
     * Canonical TITLES, read from stored objects rather than the stage.
     *
     * Decision 0031's clause 6 reopens the database and asks for *"both the new
     * title and the complete block text from canonical objects and relations"*.
     * `readAll` answers the second half; nothing answered the first, and taking
     * the title off the DOM is the substitution decision 0025 forbids by name.
     */
    storedTitles: async () => {
      await settled();
      if (!pool) return [];
      return Promise.all(canvasDocuments.map(async (document) => {
        const object = await db.store.get(document, { manifest: pool!.manifest });
        // By ROLE, manifest-resolved, the way `seedNoteIds` reads one. A
        // property key is the pool's private naming; the role is the thing the
        // renderer painted, so this is the same value seen from the store side.
        const value = object?.roles.get('title')?.[0];
        return value && 'v' in value && typeof value.v === 'string' ? value.v : undefined;
      }));
    },
    /** Clause 8's accounting. See `canvasLifetime`. */
    canvasCensus: () => ({
      live: documentCanvases.size, islands: canvasIslands.size,
      mounted: canvasDocuments.length, ...canvasLifetime,
    }),
    /**
     * One ROLE's stored values, for the object at `ordinal` in the pool.
     *
     * Added because a harness of mine asserted over table cells and filed a
     * defect that was not there. The notes table's `Word count` column is a
     * DERIVED value — 23 words of body prose — while the object's stored
     * `quantity` is 1240, and its `Written (Links to)` column is not the note's
     * own `date` either. A row action wrote correctly, every cell being watched
     * stayed the same exactly as it should have, and I read that as a product
     * reporting success while storing nothing.
     *
     * Decision 0025 already forbids the substitution by name: judge stored
     * meaning, not the DOM. This is the hook that lets a harness follow it for
     * an arbitrary role.
     */
    /**
     * WHAT A LENS READS, which since `0199` half 2 is not always what the slot
     * holds — @Codex's own division, arriving in the instrumentation.
     *
     * `storedRole` below reads `db.store` directly, deliberately: it is the
     * stored-meaning probe `0025` asks for, and four write clauses depend on
     * it seeing the raw slot. But a note with a document has **no** `body`
     * slot — the seed retires it, exactly as the product's migration does — and
     * the body a lens gets is the document's projection through
     * `db.effective`. A probe that reads the slot for such a note reads
     * nothing and reports zero words for a note that has ninety.
     *
     * So this is a SECOND probe rather than a change to the first, because the
     * two answer different questions and a harness has to say which it is
     * asking. `coda-row`'s formula clause asks the lens's question: its column
     * counts the words a person is looking at.
     */
    derivedRole: async (role: string, ordinal = 0) => {
      await settled();
      if (!pool) return undefined;
      /* THE PROJECTION, not `objects` — the derived values do not live on the
         stored record. `derivedRoleReadBoundary` returns `{shape, objects,
         projection, derived}` and puts a provider's answers in the last two;
         `objects.items[].roles` is the raw record, so reading it here would
         have been the stored probe under a second name, which is exactly the
         confusion this exists to end. Measured: three notes with documents
         came back empty from `roles` while the rendered table showed their
         prose. */
      const page = await db.effective.read(pool.scope, { limit: ordinal + 1 });
      const projected = page.projection.items[ordinal] as Record<string, unknown> | undefined;
      if (!projected) return undefined;
      const value = projected[role];
      return value === undefined ? [] : (Array.isArray(value) ? value : [value]);
    },
    storedRole: async (role: string, ordinal = 0) => {
      await settled();
      if (!pool) return undefined;
      const page = await db.store.query(pool.scope, { limit: ordinal + 1 });
      const row = page.items[ordinal];
      if (!row) return undefined;
      const object = await db.store.get(row.id, { manifest: pool.manifest });
      return (object?.roles.get(role as Role) ?? [])
        .map((value: Value) => ('v' in value ? value.v : undefined));
    },
    /**
     * THE POOL'S ROWS WITH THEIR IDENTITY — `[{ id, values }]` for one role.
     *
     * @Codex's P2s on `daafee9` and `bcbe1fc`, four harnesses, one root cause:
     * `storedRole` reads by ORDINAL and returns values alone, so a write
     * clause could only ask "did the marker appear anywhere". A write routed
     * to a different object satisfies that, and the clause reports success
     * over a corrupted neighbour — the worst failure a store-backed edit can
     * have, reported as a pass.
     *
     * Fixed once here rather than four times in the harnesses: with an id per
     * row a clause can say WHICH object it edited and that the others did not
     * move. Reading identity is what makes a store read mean anything, and
     * decision `0025`'s rule to judge stored meaning rather than the DOM was
     * being followed with the identity thrown away.
     */
    storedRows: async (role: string, limit = 64) => {
      await settled();
      if (!pool) return undefined;
      const page = await db.store.query(pool.scope, { limit });
      const out: { id: string; values: unknown[] }[] = [];
      for (const row of page.items) {
        const object = await db.store.get(row.id, { manifest: pool.manifest });
        out.push({ id: String(row.id),
          values: (object?.roles.get(role as Role) ?? [])
            .map((value: Value) => ('v' in value ? value.v : undefined)) });
      }
      return out;
    },
    /**
     * One RELATION's full stored set — slot AND edges — for the object at
     * `ordinal` in the pool.
     *
     * `storedRole` reads the manifest projection, which is SLOT values only,
     * and that blind spot nearly convicted an innocent product: the attach
     * picker writes an EDGE, so a harness that attached and then read
     * `storedRole` saw `[]`, called the persisted write lost, and the "fix"
     * it invited would have repaired nothing. Traversal is where a relation
     * becomes something on screen (decision 0016), so it is the door that
     * answers what the relation IS — the same union the renderer draws and
     * the detach handle addresses.
     */
    storedRelation: async (role: string, ordinal = 0) => {
      await settled();
      if (!pool) return undefined;
      const page = await db.store.query(pool.scope, { limit: ordinal + 1 });
      const row = page.items[ordinal];
      if (!row) return undefined;
      const out = await db.store.traverse({
        manifest: pool.manifest, from: [row.id], via: role as Role,
        direction: 'out', depth: 1, maxNodes: 50,
      });
      // Occurrence identity rides along (@Codex, P2 on 4ab60ec): two members
      // may target ONE object and differ by anchor, and a read that collapses
      // to target ids calls an anchored-for-unanchored swap "unchanged".
      // Decision 0016 made occurrences addressable; the full stored set is
      // the set of occurrences, not the set of destinations.
      //
      // NARROWED CLAIM, not the full set (@Codex, P2 on bc77cbf):
      // `Engine.traverse()` deduplicates by from>to before this line, so two
      // occurrences sharing one TARGET still collapse, and private-key
      // provenance is absent. Faithful for distinct-target sets — every
      // clause reading this today — and NOT for duplicate-accumulation
      // checks: decision 0054's control 5 needs an occurrence-preserving
      // read, which its implementation arc owes this door.
      return out.edges.filter((e) => e.from === row.id)
        .map((e) => ({ to: e.to, anchor: e.anchor ?? null }));
    },
    /** The ID of the pool's nth object, so a harness can assert a stored
     *  reference points at the object it PROMISED — "any one target landed"
     *  passes a picker wired to the wrong handle (@Codex, same review). */
    storedId: async (ordinal = 0) => {
      await settled();
      if (!pool) return undefined;
      const page = await db.store.query(pool.scope, { limit: ordinal + 1 });
      return page.items[ordinal]?.id;
    },
    /** For a harness that wants the quiescent point without a read. */
    settled,
    /**
     * Whether a canonical read has ever given up waiting.
     *
     * Task 0112's ceiling is only half an improvement if the giving-up is as
     * silent as the hanging was. `say()` tells a PERSON; this tells a harness,
     * after the fact, so a journey that read something mid-flight can report
     * the reason instead of an unexplained mismatch three assertions later.
     */
    quiescence: () => ({
      inFlight: quiescence.inFlight,
      abandoned: quiescence.abandonCount,
      lastAbandon: quiescence.lastAbandon,
    }),
  };
  await applyOverlay();

  // The measurement pool, when one was asked for, is what the page opens on.
  //
  // `?scale=100000` is a URL whose entire purpose is to put 100,000 objects on
  // screen, and selecting `pools[0]` regardless meant it opened on Films —
  // twelve objects — until somebody noticed the extra pill and clicked it. An
  // on-load benchmark against the advertised URL would have reported a
  // convincing frame time for a dozen rows. `e2e/frame-time.mjs` clicks the
  // pill and so was never wrong, which is exactly what made this invisible:
  // the harness compensated for the route instead of testing it. Found by
  // @Codex.
  pool = db.pools.find((p) => p.id === SCALE_POOL_ID) ?? db.pools[0]!;

  poolBar.innerHTML = db.pools.map((p) =>
    `<button class="pill${p.id === pool.id ? ' on' : ''}" data-pool="${p.id}" `
    + `type="button">${p.label}</button>`)
    .join('');
  $('poolnote').textContent = pool.note;

  // The permission boundary, attached once to the render root. Delegated, so it
  // survives every repaint replacing the stage's contents and works over any
  // renderer that emits the anchor attributes — there is one boundary, not one
  // per renderer.
  // Held rather than inlined: the relation gestures are button presses this app
  // routes itself — the same shape as `.m-invoke` — so they need the bridge
  // directly. The edit host drives keystrokes and drags and knows nothing about
  // them, which is why `relate` is not on `EditStore`.
  bridge = createEditBridge({
    store: db.store,
    editor: db.editor,
    manifest: () => pool.manifest,
    specOf: (lens) => specs.get(lens),
    objectOf: (handle) => identities.get(handle),
    isDerivedRole: (reader, object, manifest, role) =>
      db.isDerivedRole(reader, object, manifest, role),
    onWrote: afterWrite,
    // Bound to THIS surface's owner. The check and the picker have to resolve
    // one declaration or they are two descriptions of a rule, free to drift —
    // which is why `scopeForRole` takes the pool rather than reading it.
    targetInScope: (role, target) => targetInScope(role, target, stageSurface.owner()),
  });
  host = attachEditHost({
    root: $('stage'),
    status: $('saidit'),
    store: bridge,
    /**
     * EVERY host repaint is a value repaint, and my first version of this line
     * was wrong for a clever reason.
     *
     * The host does distinguish `value` from `structure`, so mapping `structure`
     * to a regeneration looked like using a distinction that was already there.
     * But the host reports on WRITES and nothing else: it says `structure` when
     * a patch is `created`, and for undo and redo. Under decision 0023 none of
     * those is a new viewing episode — *"editing or clearing an existing field
     * is a value edit, even when it crosses a coverage threshold."*
     *
     * What that cost, found by @Codex's review and then measured: `readRole`
     * returns `undefined` for an EMPTY slot as well as an absent one, so filling
     * a field somebody had just cleared came back as `created`, arrived here as
     * `structure`, and regenerated. Clearing was stable and re-filling was not.
     * Counted in the build: **re-fill that field: +1 compile.**
     *
     * The columns did not visibly move, because putting the value back restores
     * the ranking it had — which is why a guard on columns alone would have
     * called this fine. 0023 forbids the regeneration, not only the symptom.
     *
     * Explicit shape commands do not come through here. They are the demo's own
     * gestures — `addProperty`, and `writeProperty` when it mints a key — and
     * they ask for the boundary themselves.
     *
     * @Codex's own implementation had `schedule(false)` here. I replaced it with
     * the reason-mapping and introduced the hole; this is its line restored.
     */
    onRepaintRequest: () => scheduleValue(),
    // The host writes refusals into the status element itself; routing them
    // through the demo's own line as well is what applies the tone and starts
    // the clear timer. It runs AFTER the host's insertion, which is why the
    // region must already be in the accessibility tree by then.
    onRefusal: (r) => say(r.message, 'bad'),
  });
  // The pane's boundary, on the pane's root. A SECOND host rather than a wider
  // one: the main host's listeners are on `#stage`, these are on `#paneStage`,
  // and the two elements are siblings — so neither sees the other's events and
  // each resolves an anchor's node index against the IR it actually painted.
  //
  // It repaints the PANE, not the page. A write here also reaches the main lens,
  // but by the store's own subscription rather than through this callback —
  // which is the same path a write in the main lens takes to reach the pane.
  /**
   * The pane's bridge, HELD — `paneSurface.store()` reads it, and a relation
   * gesture needs `relate`, which the edit host neither has nor knows about.
   *
   * **And it gets `targetInScope`, which it never had.** The stage's bridge has
   * carried that boundary since #119; this one was constructed without it,
   * invisibly, because the pane could not link and an unreachable hole looks
   * exactly like no hole. The moment the pane can link it becomes the one write
   * path in the app where a declared target domain is not enforced — so it is
   * closed in the same commit that opens the gesture, not after somebody drives
   * it. Bound to the PANE's owner, which is often not the open pool and is
   * sometimes nobody.
   */
  asideBridge = createEditBridge({
    store: db.store,
    editor: db.editor,
    // The PANE's manifest, not the stage's. See `asideManifest`.
    manifest: () => asideManifest ?? pool.manifest,
    specOf: (lens) => specs.get(lens),
    objectOf: (handle) => identities.get(handle),
    isDerivedRole: (reader, object, manifest, role) =>
      db.isDerivedRole(reader, object, manifest, role),
    onWrote: afterWrite,
    targetInScope: (role, target) => targetInScope(role, target, paneSurface.owner()),
  });
  asideHost = attachEditHost({
    root: $('paneStage'),
    status: $('saidit'),
    store: asideBridge,
    /**
     * The pane's OWN repaint, and the focus request has to survive it.
     *
     * This path does not go through `schedule()`, so nothing consumed
     * `pendingAttachFocus` after it — and a pane relation write produced
     * exactly that sequence: the settle focused the pane's Add correctly, then
     * this repaint replaced the subtree and focus fell to `<body>` with the
     * request still held and no one left to take it. Measured rather than
     * reasoned about: `m-attach @pane` at 0ms, `BODY` from 100ms through
     * 2400ms.
     *
     * The same defect `takePendingAttachFocus` and `restoreFocus` each record
     * at length — a render the request does not reach — arriving through the
     * one repaint path that was not a settle. So the request is consumed HERE
     * too, once this render's markup is in the document.
     */
    // A VALUE repaint, like the stage's. Decision 0023 is about the current
    // lens and the pane is one — a pane edit must not re-derive the pane.
    onRepaintRequest: () => { void renderAside(false).then(takePendingAttachFocus); },
    onRefusal: (r) => say(r.message, 'bad'),
  });
  /**
   * A view-state control moved.
   *
   * `change`, not `input`: every keystroke would rebuild a scope, re-derive a
   * shape, regenerate a lens and repaint — the same reason FIND is a submit
   * rather than search-as-you-type, recorded three hundred lines down. A page
   * that reflows under a half-typed number is unusable.
   *
   * The value is typed from the slot's ROLE, never guessed from the string: a
   * number role gets a number and a date role gets a date, because
   * `testPredicate` compares kinds and a text "12" is not 12.
   */
  const typedViewValue = (
    role: Role, raw: string,
  ): KnownValue | undefined => {
    if (!raw) return undefined;
    const kind = ROLE_KIND[role];
    if (kind === 'number' || kind === 'score') {
      return Number.isFinite(Number(raw)) ? { k: 'number', v: Number(raw) } : undefined;
    }
    if (kind === 'date') {
      return Number.isFinite(Date.parse(raw)) ? { k: 'date', v: raw } : undefined;
    }
    // Reference slots are not rendered until the host can supply a scoped
    // ObjectId picker. Never turn a visible label into an id.
    if (kind === 'ref') return undefined;
    return { k: 'text', v: raw };
  };

  $('stage').addEventListener('change', (e) => {
    const el = (e.target as HTMLElement | null)?.closest?.('[data-morph-vs]');
    if (!(el instanceof HTMLInputElement)) return;
    const i = Number(el.getAttribute('data-morph-vs'));
    const slot = viewStateSlots[i];
    if (!Number.isInteger(i) || !slot) return;
    const raw = el.value.trim();

    if (slot.op === 'between') {
      const endpoints = [...$('stage').querySelectorAll(
        `input[data-morph-vs="${i}"][data-morph-vs-bound]`,
      )].filter((node): node is HTMLInputElement => node instanceof HTMLInputElement)
        .sort((a, b) => Number(a.dataset.morphVsBound) - Number(b.dataset.morphVsBound));
      const values = endpoints.map((node) => typedViewValue(slot.role, node.value.trim()));
      viewStateValues[i] = values.length === 2 && values.every((v) => v !== undefined)
        ? { k: 'list', v: values as readonly KnownValue[] } : undefined;
    } else {
      viewStateValues[i] = typedViewValue(slot.role, raw);
    }

    // The caption the CONTROL SHOWS, not the slot's authored name. Task 0087
    // found that the generator has to invent `slot.name` from the vocabulary
    // term — a pooled spec carries nothing about the pool's contents — so the
    // Films control read "Date" while every other field in that pool read
    // "Released". `render-html.ts` was fixed to prefer the host's own label,
    // and this line was not: the box said Released and the live region said
    // "Date changed", so the one person who hears this instead of seeing it
    // was told a different control had changed. The same defect, in the
    // sibling that reads the same value.
    const caption = labelsFor(pool)[slot.role] ?? slot.name;
    say(viewStateValues[i] === undefined ? `${caption} cleared` : `${caption} changed`);
    schedule();
  });
  // LEAVING a named app look (0055): the ribbon's last button is the one
  // control in the reproduction that belongs to the host, and it goes back
  // to the hub the person came in by. Before `selectFromClick`, which would
  // otherwise resolve the ribbon's enclosing region as a card.
  // An INACTIVE TAB reopens its note: the handle it carries resolves here,
  // in the host, to the object it names — the renderer never held an id.
  /* CLOSING a tab — task `0198`. Registered before the activation handler
     below: the close sits beside the tab inside the chip (@Codex's P2 on
     `c8b317d` moved it out of the `role="tab"` element), and a click that
     reached both would activate the tab it just closed. View state only:
     nothing about the pool changes (0023), so this is a paint rather than a
     regeneration — but WHICH pool answers for the neighbour is not view
     state, which is the finding below. */
  $('stage').addEventListener('click', (e) => {
    const x = (e.target as HTMLElement).closest('[data-ws-tab-close]') as HTMLElement | null;
    if (!x) return;
    e.stopImmediatePropagation();
    const id = identities.get(x.dataset.wsTabClose as ObjectHandle);
    if (id === undefined) return;
    const at = openNotes.list.findIndex((n) => n.id === id);
    if (at < 0 || openNotes.list.length < 2) return;
    openNotes.list.splice(at, 1);
    /* EVERY ACCEPTED CLOSE INVALIDATES A PENDING ONE — @Codex's P2 on
       `d881f5b`, and the bump used to live inside the active-tab branch below.

       A, B, C open with A active. Closing A splices it and starts the
       asynchronous `poolOf(B)`. Before that resolves, the still-rendered close
       beside B removes B from the list — and because that close is not of the
       ACTIVE tab, it took no ticket, so the first callback still matched, ran,
       and `select(B)` put back the tab the person had just closed.

       The ticket is taken here, for every close the handler accepts, rather
       than only for the one that goes on to resolve a pool. `closingTab` needs
       no matching change: it is paired with the ticket rather than cleared by
       hand, so an older entry goes inert the moment this line moves.

       That is `0064`'s question asked of this diff's own vocabulary — which
       consumer assumes the set of bump sites is closed — and the answer here
       is the same as it was for `select()`: put it at the choke point every
       route passes through, not on a list of the routes somebody remembered.

       WHAT THE PRODUCT CAN REACH TODAY, measured rather than assumed, because
       it decides what a clause for this could say. `capability.ts` builds
       `closeMarkup` for `tabs[activeAt]` ALONE — `0072` term 9 — so the strip
       carries exactly ONE close control and it belongs to the active tab.
       Driven on Obsidian with three tabs open: one `[data-ws-tab-close]` in
       the whole stage, labelled for the active note. A second close of a
       DIFFERENT tab is therefore not a gesture the shipped strip offers, and
       a harness that tried to drive two would be reporting on a control it
       had fabricated.

       The window is still reachable, and by the mechanism `0207` is about: the
       strip is not repainted between the splice and the callback, but an
       invalidation from ANOTHER PAGE repaints it, and the repainted strip
       carries a close for the NEW active tab — the very tab the pending
       callback is about to select. Closing that one takes the branch below
       only if `openNotes.active` already names it, and it does not: the
       callback has not run `select` yet. So the fix is the invariant rather
       than a repair of a route anyone has driven, and the route that reaches
       it needs two pages and a race. Filed as `0223` rather than left here. */
    const mine = (tabCloseTicket += 1);
    // A NEIGHBOUR takes over when the one that closed was open: the tab to
    // its right, else the one to its left — Obsidian's, and the only choice
    // that never leaves the editor showing nothing.
    /**
     * AND THROUGH ITS OWN POOL — @Codex's P2 on `c8b317d`.
     *
     * A note followed across a cross-document relation is opened by
     * `openHere`, which enters the referent's pool. Its tab then sits in the
     * strip beside tabs from the pool it came from. Closing the active one
     * called `select(next.id)`, which changes the selection and leaves `pool`
     * where it is — so the next render queries a foreign id through the wrong
     * scope, finds nothing, and falls back to an unrelated object while the
     * strip shows the tab the person expected.
     *
     * `openHere`'s two lines are the ones that answer it — resolve the owner,
     * enter that pool when it differs, plain `select` when it does not — but
     * not `openHere` itself: it announces *"followed a reference"*, which is
     * not what happened and is the only thing a screen reader would hear.
     * Asynchronous, so the repaint is scheduled after the pool is settled; a
     * `schedule()` here as well would paint the old scope first.
     */
    if (openNotes.active === id) {
      const next = openNotes.list[Math.min(at, openNotes.list.length - 1)];
      if (next) {
        state.mainView = 'note';
        focusWorkspaceAfterPaint('.m-ws-tab-on');
        /* A TICKET, the same shape the kept-shelf door uses — @Codex's P2 on
           `cb4fa44`. `poolOf` walks the pools with a store query each, and a
           person who pressed another tab, a pool, or Back to Morph meanwhile
           has answered the question this close was asking. Without it the
           callback selected the former neighbour regardless and scheduled a
           paint over their newer choice. */
        // Suppress the re-add for exactly as long as this close is the live
        // one; see `closingTab`. The ticket was taken above, for every close.
        closingTab = { id, ticket: mine };
        void (async () => {
          /* A REFUSED or failed lookup still has to leave a note on screen.
             `poolOf` reaches the store, so it can reject; without this the
             rejection is unhandled, the neighbour is never shown, and the
             harness reports it on whichever clause reads page errors rather
             than on the close. Falling back to the plain select is what the
             code did before this fix, which is the right floor: wrong pool is
             a worse answer than the right one and a better answer than none. */
          let owner: DemoPool | undefined;
          try { owner = await poolOf(next.id); } catch { owner = undefined; }
          if (tabCloseTicket !== mine) return;
          closingTab = undefined;
          if (owner !== undefined && owner !== pool) enterPool(owner, next.id);
          else select(next.id);
          say(`closed a tab — ${next.title} is open now`);
          schedule();
        })();
        return;
      }
    }
    focusWorkspaceAfterPaint('.m-ws-tab-on');
    schedule();
  });
  $('stage').addEventListener('click', (e) => {
    const tab = (e.target as HTMLElement).closest('[data-ws-tab]') as HTMLElement | null;
    if (!tab) return;
    e.stopImmediatePropagation();
    const id = identities.get(tab.dataset.wsTab as ObjectHandle);
    if (id === undefined) return;
    // A note's tab brings the note back, whatever the main column showed.
    state.mainView = 'note';
    // And a newer activation outranks a close still resolving its neighbour's
    // pool — @Codex's P2 on `cb4fa44`. The bump this line used to carry is
    // inside `select` now, which is where EVERY selection route reaches it.
    select(id);
    // The strip is re-rendered by the paint, so the button just pressed is
    // about to be gone: focus lands on the tab it became (@Codex, P2 on
    // b0af641), not on `<body>`.
    focusWorkspaceAfterPaint('.m-ws-tab-on');
    schedule();
  });
  // NOTION'S VIEW ROW — a view is the same page with another subject: a
  // regeneration the person asked for by name (the template reads it).
  $('stage').addEventListener('click', (e) => {
    const pill = (e.target as HTMLElement).closest('[data-morph-view]') as HTMLElement | null;
    if (!pill) return;
    e.stopImmediatePropagation();
    const view = pill.dataset.morphView as TemplateView | undefined;
    if (view === undefined || view === state.templateView) return;
    state.templateView = view;
    focusWorkspaceAfterPaint(`[data-morph-view="${view}"]`);
    schedule();
  });
  // NOTION'S NAV — a row acts: Home returns to the hub; a space is a pool
  // (the pool pill's own path, which leaves the look behind — 0024: "this
  // pool, as Morph shows it"); an upcoming object opens in place.
  $('stage').addEventListener('click', (e) => {
    const row = (e.target as HTMLElement).closest('[data-morph-go]') as HTMLElement | null;
    if (!row) return;
    e.stopImmediatePropagation();
    const go = row.dataset.morphGo ?? '';
    if (go === 'home') {
      state.template = undefined;
      applyTemplateChrome();
      void openHub().then(() => {
        (hubHost.querySelector('[data-hub-open], button') as HTMLElement | null)?.focus();
      });
      return;
    }
    if (go === 'back' || go === 'forward') {
      // The topbar's chevrons: the places visited under the look, observed
      // at each paint. The move sets `moving`, so the paint it causes
      // records nothing new.
      const h = state.ntHistory;
      const [from, to] = go === 'back' ? [h.back, h.forward] : [h.forward, h.back];
      const place = from.pop();
      if (!place || !h.here) return;
      to.push(h.here);
      h.moving = true;
      goToPlace(place);
      return;
    }
    if (go === 'star') {
      // Notion's star: the page on the hub's shelf through the shelf's own
      // door — the accepted spec, this pool, this view (`starred` is the
      // shelf's word for it); lit, it takes the page off again. No toast:
      // the star's own state says it, and "hub" is Morph's word.
      const on = keptPageEntries();
      if (on.length) forgetKeptLayouts(on);
      else recordKeptLayout('starred', lastSpec);
      schedule();
      return;
    }
    if (go === 'more') {
      // ··· opens its menu in place — view state of one paint, closed by
      // the next; the first item takes focus, as a menu does.
      const menu = row.parentElement?.querySelector<HTMLElement>('.m-topbar-menu');
      if (!menu) return;
      menu.hidden = !menu.hidden;
      row.setAttribute('aria-expanded', String(!menu.hidden));
      if (!menu.hidden) (menu.querySelector('[role="menuitem"]') as HTMLElement | null)?.focus();
      return;
    }
    if (go === 'leave') {
      // ···'s way out: this pool as Morph shows it (0024), the look put down.
      state.template = undefined;
      applyTemplateChrome();
      schedule();
      return;
    }
    if (go.startsWith('pool:')) {
      const next = db.pools.find((p) => p.id === go.slice(5));
      if (!next) return;
      if (next.id === pool.id) {
        /**
         * THE ROW YOU ARE ALREADY IN IS THE WAY BACK, once the view has been
         * narrowed to one object. It used to be an unconditional no-op, which
         * was right while the row only ever meant "switch pools" — but task
         * 0176 made a card-open produce a real single-object page of the SAME
         * pool, and then refusing here leaves a person on a page with no exit.
         *
         * Found by the 0176 acceptance clause on its first run, not by
         * reading: Fibery draws no back chevron at all — measured, every
         * `data-morph-go` it renders is `home`, a pool row, `star`, `more` or
         * `leave` — so the pool row is the ONLY return path that look has.
         *
         * Widen back to the collection and stay in the look: `leave` is the
         * door that puts the look down (0024), and this is not that.
         */
        if (state.cardinality !== 'single') return;
        state.cardinality = 'large-set';
        ($('cardinality') as HTMLSelectElement).value = 'large-set';
        select(undefined);
        say(`back to ${pool.label}`);
        schedule();
        return;
      }
      state.template = undefined;
      applyTemplateChrome();
      enterPool(next);
      schedule();
      return;
    }
    if (go.startsWith('object:')) {
      const id = identities.get(go.slice(7) as ObjectHandle);
      if (id === undefined) return;
      // OPENS it, the way a card does: `select` alone lit the row and left
      // the board on screen, so a sidebar page was an affordance with
      // nothing behind it (@Codex, P1 on #229). `openHere` is the door the
      // card path and the reference chip already use — it switches to the
      // object's own lens and says so.
      /**
       * AND IT HANDS FOCUS ON — @Codex's P2 on `4a34aef`.
       *
       * The row a keyboard or screen-reader user pressed is inside `#stage`,
       * which the scheduled render replaces, so the element holding focus is
       * detached and focus falls to `<body>`. The table row's Open path arms
       * a destination for exactly this reason; this route — Notion's Recent
       * and its nested page tree — never did, so the two ways of opening an
       * object from a sidebar behaved differently for a person who cannot
       * point at one.
       *
       * Same destination list as the row button's, and for its reason: the
       * heading where a look has one, the title field on the generic lens.
       * Which element provides it is the host's business and moves with
       * `0188`.
       *
       * `.m-nt-title` used to sit between these two and is REMOVED: nothing in
       * the runtime has ever emitted it, checked with `git log -S` over
       * `packages/runtime/src`, which returns no commit at all. So the middle
       * step was never taken on any door, and the comment above describes an
       * intention the list did not carry. The heading a look actually draws is
       * `m-pagehead-title` (task `0206` renamed it from `m-nt-page-title`).
       * Adding it here would change where focus lands on five doors, which is
       * `0188`'s question and not a rename's — recorded in `0206` rather than
       * decided inside a batch that is supposed to change nothing.
       */
      focusWorkspaceAfterPaint(['.m-ws-title', '.m-role-title:not(.m-place *)']);
      void openHere(id);
      return;
    }
    if (go.startsWith('kept:')) {
      // A Favorites row: the shelf's own door, re-read here as the hub's is.
      const entry = readKeptLayouts().find((k) => (k.id ?? String(k.at)) === go.slice(5));
      if (!entry) return;
      openKeptEntry(entry);
    }
  });
  // A SIDEBAR PANE TAB (0055: Backlinks | Graph): host state, then a paint.
  // Not a regeneration — nothing about the pool changed (0023), the renderer
  // shows a different one of the panes the spec already holds.
  $('stage').addEventListener('click', (e) => {
    const tab = (e.target as HTMLElement).closest('[data-ws-pane]') as HTMLElement | null;
    if (!tab) return;
    e.stopImmediatePropagation();
    const pane = tab.dataset.wsPane as RegionPurpose | undefined;
    const side = tab.dataset.wsSidebar;
    if (pane === undefined || side === undefined || state.sidePanes[side] === pane) return;
    state.sidePanes = { ...state.sidePanes, [side]: pane };
    focusWorkspaceAfterPaint(`[data-ws-sidebar="${side}"].m-ws-side-tab-on`);
    scheduleValue();
  });
  // A FOLDER in the explorer folds and unfolds — the same kind of change as
  // a pane tab: nothing about the pool moved (0023), the renderer draws the
  // folders the spec's `groupBy` names with one of them closed. Focus
  // returns to the folder row so a keyboard can keep walking the tree.
  $('stage').addEventListener('click', (e) => {
    const row = (e.target as HTMLElement).closest('[data-ws-fold]') as HTMLElement | null;
    if (!row) return;
    e.stopImmediatePropagation();
    const name = row.dataset.wsFold;
    if (name === undefined) return;
    state.folded = state.folded.includes(name)
      ? state.folded.filter((f) => f !== name)
      : [...state.folded, name];
    focusWorkspaceAfterPaint(`[data-ws-fold="${name.replace(/["\\]/g, '\\$&')}"]`);
    scheduleValue();
  });
  // ESCAPE PUTS A COLLAPSED SEARCH BACK (0187). The trigger toggles by
  // pointer and by Enter; a person whose caret is IN the box has nothing to
  // press without leaving it first, and Escape is what a field that grew out
  // of a glyph answers to everywhere else. Same close as the trigger's, query
  // and all — a filter nobody can see is a lie about what is on screen.
  $('stage').addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!(e.target as HTMLElement).closest('[data-ws-search]')) return;
    if (!currentLook().searchTrigger || !state.searchOpen) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    state.searchOpen = false;
    state.searchQuery = '';
    focusWorkspaceAfterPaint('[data-ws-search-open]');
    scheduleValue();
  });
  // THE SEARCH QUERY: typed into the pane's input, answered on the next
  // paint (a value change, never a regeneration — 0023). The input is
  // re-rendered with the query echoed, and the caret goes back to its end
  // once the paint has settled, so typing is continuous.
  $('stage').addEventListener('input', (e) => {
    const box = (e.target as HTMLElement).closest('[data-ws-search]') as HTMLInputElement | null;
    if (!box) return;
    state.searchQuery = box.value;
    focusWorkspaceAfterPaint('[data-ws-search]');
    scheduleValue();
  });
  /**
   * THE RIBBON'S TOOLS DO WHAT THEY SHOW — @Codex, P1 on b0af641: the first
   * ribbon drew Search and Graph as pictures that did nothing, and 0055
   * forbids an imitation with dead controls. The renderer names each tool's
   * ACTION and the host, which owns the store and the focus, performs it:
   * `files` moves focus into the explorer, on the open note's row so a
   * keyboard user lands where they are; `new` is the drawer's New action —
   * an unnamed note, `Untitled` as fallback copy and never as data (0024) —
   * which selects the note and paints it in the editor under the same look.
   */
  $('stage').addEventListener('focusin', (e) => {
    const el = (e.target as HTMLElement | null)?.closest?.('[data-morph-document-block]') as HTMLElement | null;
    if (!el) return;
    const object = objectAt(el);
    const block = el.getAttribute('data-morph-document-block');
    if (object !== undefined && block) lastDocBlock = { object, block };
  });
  $('stage').addEventListener('click', (e) => {
    const tool = (e.target as HTMLElement).closest('[data-ws-action]') as HTMLElement | null;
    if (!tool) return;
    e.stopImmediatePropagation();
    const action = tool.dataset.wsAction;

    /**
     * A LOOK'S DECLARED SELECTOR BEATS A COMMAND THAT SHARES ITS NAME — task
     * `0233`, decided here rather than by @Codex, who was asked and returned
     * *"Codex couldn't complete this request"* sixteen times across the day.
     * Recorded as MINE so a partner can overrule it on evidence.
     *
     * The reasoning, and the counter-argument, because both belong in the file
     * that makes the choice:
     *
     * AGAINST — `new` creating a note is a near-universal affordance, and a
     * look shadowing it would break creation with no obvious cause.
     *
     * FOR, and it wins — a look cannot declare `{ new: 'backlinks' }` by
     * accident. It is a deliberate line in a look record, so the "accidental
     * shadowing" risk is not real, while the current behaviour is: the look
     * declares it, the renderer draws a tab for it, and pressing it does
     * something unrelated. A control that does something other than what its
     * look says is a lie about what the control is, which is worse than a
     * look losing a name it chose to spend.
     *
     * And the alternative is a RESERVED-WORD LIST nobody can see: `new`,
     * `graph`, `print` and `leave` would be names no look may map, enforced
     * nowhere and discoverable only by pressing. That is the hard-coding the
     * amendment of 2026-09-03 forbids, wearing a default's clothes.
     *
     * NOTHING CHANGES TODAY, and that is checked rather than assumed:
     * `looks.ts:329` is the only declaration in the product and it maps
     * `files` and `search`, neither of which is a command. So this is the
     * rule stated before a look needs it, not a behaviour change.
     */
    const declaredPane = action === undefined ? undefined
      : currentLook().ribbonSelects?.[action] as RegionPurpose | undefined;
    /* The two actions whose pane selection has a RICHER handler below — the
       search knows about on-page boxes and collapsed triggers, the explorer
       about a pane behind another pane. They are named here, beside the branch
       that skips for them, so a third specialised handler is one line away and
       cannot be forgotten. It is a list of BRANCHES THAT EXIST IN THIS FILE,
       never of look semantics. */
    const SPECIALISED = new Set(['search', 'files']);
    if (declaredPane !== undefined && action !== undefined && !SPECIALISED.has(action)) {
      const side = sidebarHolding(declaredPane);
      if (side === undefined) {
        say(`this layout has no ${declaredPane} pane to open`, 'bad');
        return;
      }
      if (state.sidePanes[side] !== declaredPane) {
        state.sidePanes = { ...state.sidePanes, [side]: declaredPane };
        scheduleValue();
      }
      focusWorkspaceAfterPaint([`.m-ws-${declaredPane} [aria-current="true"] .m-card-hit`,
        `.m-ws-${declaredPane} .m-card-hit`, `.m-ws-${declaredPane} input`,
        `.m-ws-${declaredPane} a`, `.m-ws-${declaredPane} button`]);
      return;
    }

    if (action === 'print') {
      // Word's Print: the document's real pagination, through the browser.
      window.print();
      return;
    }
    if (action === 'leave') {
      // Word's Close: the look put down, this pool as Morph shows it (0024).
      state.template = undefined;
      applyTemplateChrome();
      schedule();
      return;
    }
    if (action?.startsWith('style:')) {
      // A style tile converts the block the caret was last in — remembered
      // on focus, because pressing the tile moves focus to the tile.
      const kind = action.slice(6);
      if (kind !== 'paragraph' && kind !== 'heading' && kind !== 'list-item') return;
      const at = lastDocBlock;
      const canvas = at ? documentCanvases.get(at.object) : undefined;
      if (!at || !canvas) return;
      try { canvas.convert(at.block, kind); } catch { /* a read-only canvas, or a block that is not text */ }
      return;
    }
    if (action === 'graph') {
      // The vault's GRAPH VIEW in the main column, behind a tab of its own
      // (the tenth verdict's list). Host state, then a paint — nothing about
      // the pool moved (0023). Focus lands on the open note's dot, so the
      // keyboard is in the canvas. A workspace without the view is told so.
      if (!$('stage').querySelector('.m-force-graph-vault')) {
        say('this vault has no relations to draw a graph of', 'bad');
        return;
      }
      if (state.mainView !== 'graph') {
        state.mainView = 'graph';
        focusWorkspaceAfterPaint('.m-force-graph-vault [aria-current="true"], .m-force-graph-vault .m-graph-node');
        scheduleValue();
      } else {
        ($('stage').querySelector('.m-force-graph-vault [aria-current="true"], .m-force-graph-vault .m-graph-node') as HTMLElement | null)?.focus();
      }
      return;
    }
    if (action === 'search' || action === 'files') {
      /**
       * A TOOL THAT SELECTS A PANE THE WINDOW HAS FOLDED SAYS SO — @Codex,
       * P2 on `026c328`, and it is the second half of `0195`.
       *
       * Folding the left sidebar below 40rem left the ribbon still offering
       * Files and Search. Measured at 600px: pressing Search selected the
       * hidden pane, tried to focus a `display:none` input, drew nothing,
       * said nothing, and dropped focus to `<body>` — a control that answers
       * with silence and takes your place with it, which is the worst shape
       * `0055` names.
       *
       * The tool is NOT withdrawn. `0065` says a look decides how a control
       * is drawn and never whether a keyboard user has it, and a control that
       * disappears at one width is a route lost at that width. It refuses in
       * words instead, as `graph` already does for a vault with no relations
       * — and it keeps the focus it was given, which is the half that made
       * this worse than doing nothing.
       *
       * Asked of the LAYOUT, not of a breakpoint: whether that sidebar has a
       * box on screen. A media query is where the fold is written today and
       * would be a second copy of it here.
       *
       * AND ASKED OF THE COMPONENT, NOT OF THE LOOK — @Codex's P2 on
       * `48121a7`. This selector began `.m-app-obsidian > .m-region >`, so a
       * seventh or composed look drawing the same ribbon mapping over a
       * folded sidebar got `null` here and fell straight through to the
       * silence this guard exists to replace: select the hidden pane, focus a
       * `display:none` input, drop focus to `<body>`. The refusal was welded
       * to the one look that happens to fold today.
       *
       * The explorer is what names it — a sidebar that holds one is the left
       * one, which is the same discriminator `render-html.ts` uses so it can
       * tell the two sidebars apart without counting children. The structural
       * `> .m-region >` went with the look name for a second reason: a
       * composed layout wraps a capability in `.m-capability-anchor`, so a
       * child selector is a fold that misses the wrapped case — the finding
       * `e2e/blanket-rules.mjs` already carries a clause for.
       *
       * Measured across all six doors at 1440px and 600px before the change:
       * only Obsidian draws an explorer sidebar at all, and the semantic
       * query and the look-scoped one agree on every row — absent, absent;
       * shown at 1440; folded at 600. So this gains the seventh look and
       * moves nothing for the six.
       */
      const leftSide = $('stage').querySelector('.m-ws-sidebar:has(.m-ws-explorer)');
      if (leftSide !== null && leftSide.getClientRects().length === 0) {
        say('the side panes are folded at this window width — the note has it', 'bad');
        (tool as HTMLElement).focus();
        return;
      }
    }
    if (action === 'search') {
      // Under Notion's look the box is on the page (the view row's); the
      // tool puts the caret in it. Under the vault: the left sidebar to
      // its search pane, the caret in the query. A second press with the
      // pane already showing just refocuses.
      if (currentLook().searchOnPage) {
        // A COLLAPSED search (0187) has to be opened before it can be typed
        // in, and the trigger is what opens it. Both places that press this
        // action reach the same box: the sidebar's Search tool opens it, and
        // the trigger itself TOGGLES — it stays beside the open field, at its
        // left as the app draws it, so pressing it again puts the row back.
        //
        // It has to stay for a POINTER-ONLY person: Escape below is the other
        // way back, and a control whose only exit is a key is the accessibility
        // defect this task was careful not to introduce, arriving by the other
        // door.
        //
        // Closing CLEARS the query, deliberately. The box filters the board;
        // a filter nobody can see is a lie about what is on screen, and the
        // person who put the row back has no way to ask why four cards are
        // missing.
        if (currentLook().searchTrigger) {
          const fromTrigger = tool.matches('[data-ws-search-open]');
          if (!state.searchOpen) {
            state.searchOpen = true;
            focusWorkspaceAfterPaint('[data-ws-search]');
            scheduleValue();
          } else if (fromTrigger) {
            state.searchOpen = false;
            state.searchQuery = '';
            focusWorkspaceAfterPaint('[data-ws-search-open]');
            scheduleValue();
          } else {
            ($('stage').querySelector('[data-ws-search]') as HTMLElement | null)?.focus();
          }
          return;
        }
        ($('stage').querySelector('[data-ws-search]') as HTMLElement | null)?.focus();
        return;
      }
      const searchSide = sidebarHolding('search');
      if (searchSide === undefined) {
        // No sidebar on screen holds a search pane, so there is nothing this
        // press can show. It refuses in words rather than writing a state
        // entry nothing reads — the silent half of @Codex's finding.
        say('this layout has no search pane to open', 'bad');
        return;
      }
      if (state.sidePanes[searchSide] !== 'search') {
        state.sidePanes = { ...state.sidePanes, [searchSide]: 'search' };
        focusWorkspaceAfterPaint('[data-ws-search]');
        scheduleValue();
      } else {
        ($('stage').querySelector('[data-ws-search]') as HTMLElement | null)?.focus();
      }
      return;
    }
    if (action === 'files') {
      // The sidebar that HOLDS the explorer back to it if the search pane has
      // it — that sidebar, by the id the renderer gave it, not `ws-left`.
      const filesSide = sidebarHolding('explorer');
      const explorer = $('stage').querySelector('.m-ws-explorer');
      /* BEHIND ANOTHER PANE IS NOT FOLDED AWAY BY WIDTH — @Codex's P2 on
         `d881f5b`. This asked `state.sidePanes[filesSide] === 'search'`, so a
         sidebar holding the explorer beside a THIRD pane — backlinks, a graph,
         anything a composed layout puts there — fell through to the message
         below and told the person the file list was folded away at this width.
         At 1440px, with a sidebar plainly on screen.

         And the state was never the authority anyway. `paneShowing` falls back
         to `opensPaneOver` and then to the sidebar's FIRST pane, so a sidebar
         whose declared default is not the explorer shows something else with
         `state.sidePanes` still empty — a case no comparison against that
         record can see. Reading `sidePanes` harder cannot answer this.

         So the DOM answers, and it distinguishes all three cases because the
         renderer marks them differently. Measured on Obsidian at 1440x900:

             .m-ws-side-off[hidden] wrapping m-ws-search, m-ws-backlinks
             m-ws-explorer          not wrapped, 1 box     -> showing
             wrapped                                       -> another pane
             not wrapped, 0 boxes                          -> the width fold

         The FIRST probe for this read `[data-ws-pane][data-ws-sidebar]`, which
         is what `sidebarHolding` reads — and those are the pane TABS, inside
         `.m-ws-side-tabs`, never inside `.m-ws-side-off`. A fix written on
         that reading would have been green about the wrong element, which is
         this repository's most-repeated defect. The pane is `.m-ws-pane`; the
         tab is not. */
      const behindAnotherPane = explorer?.closest('.m-ws-side-off') != null;
      if (filesSide !== undefined && behindAnotherPane) {
        state.sidePanes = { ...state.sidePanes, [filesSide]: 'explorer' };
        focusWorkspaceAfterPaint('.m-ws-explorer li[aria-current="true"] .m-card-hit, .m-ws-explorer .m-card-hit');
        scheduleValue();
        return;
      }
      const row = (explorer?.querySelector('li[aria-current="true"] .m-card-hit')
        ?? explorer?.querySelector('.m-card-hit')) as HTMLElement | null;
      // Below 40rem the stylesheet folds the explorer away, and focusing a
      // node with no box is a click that does nothing (@Codex, P2 on
      // 2c1ead8). A pressed control acts or explains.
      if (!row || row.getClientRects().length === 0) {
        say('the file list is folded away at this width — widen the window to reach it', 'bad');
        return;
      }
      row.focus();
      return;
    }
    if (action === 'new') {
      // A reader who presses it is TOLD, not ignored — @Codex, P2 on
      // 4ab498b: a silent return is the dead control this round removed.
      if (!mayWrite('create a note')) return;
      creationLanded = false;
      // A column's "+ New page" presets the column's value (the `create`
      // affordance): a page made in "In review" lands in "In review".
      const presetRole = tool.dataset.wsPresetRole as Role | undefined;
      const presetValue = tool.dataset.wsPresetValue;
      creating = createObject('', true, presetRole && presetValue !== undefined ? [[presetRole, presetValue]] : []);
      creating.catch((why: unknown) => { say(`could not create: ${String(why)}`, 'bad'); });
      return;
    }
  });
  $('stage').addEventListener('click', (e) => {
    const exit = (e.target as HTMLElement).closest('[data-ws-exit]');
    if (!exit) return;
    e.stopImmediatePropagation();
    state.template = undefined;
    applyTemplateChrome();
    // Focus lands IN the hub, not on the button about to be hidden with the
    // panes (@Codex, P2 on ebf7b8c).
    void openHub().then(() => {
      (hubHost.querySelector('[data-hub-open], button') as HTMLElement | null)?.focus();
    });
  });
  $('stage').addEventListener('click', selectFromClick);
  // Separate from `selectFromClick` rather than folded into it: pressing a
  // button and selecting a card are different intents, and a button inside a
  // card would otherwise do both.
  $('stage').addEventListener('click', (e) => { void invokeFromClick(e); });
  $('stage').addEventListener('click', (e) => { void relateFromClick(e, stageSurface); });
  // The pane's own listener, on the pane's own root, carrying the pane's own
  // surface. `#stage` and `#paneStage` are siblings, so this is a second
  // boundary rather than a wider one — neither sees the other's events, which
  // is the same shape the two edit hosts already take.
  $('paneStage').addEventListener('click', (e) => { void relateFromClick(e, paneSurface); });
  /**
   * And the pane's OPEN controls, which I shipped inert.
   *
   * @Codex: *"a Note rendered in the pane now receives `withRelationMutations`,
   * and its two declared relation slots therefore draw `.m-slot-open` buttons,
   * but the only pane click listener added here calls `relateFromClick`, whose
   * selector accepts only `.m-attach,.m-detach`; `selectFromClick`, which
   * handles `.m-slot-open`, remains registered only on `#stage`. Pressing a
   * visible Open button in the pane consequently does nothing."*
   *
   * **Task 0077 was the task about not doing this**, and giving the pane
   * relation gestures is what drew a third control nobody wired. The rule it
   * opens with — *an affordance with nothing behind it is worse than a missing
   * one* — was broken by the commit that quoted it.
   *
   * Not `selectFromClick`: that one also selects, changes cardinality and
   * repaints the STAGE, which is the wrong half of it for a control living
   * beside a different document. Only the navigation, through the pane's own
   * surface — so the object the pane points at opens where the reader already
   * is, in the pane.
   */
  $('paneStage').addEventListener('click', (e) => {
    const el = e.target as HTMLElement | null;
    if (!el?.closest('.m-slot-open') || el.closest('[contenteditable="true"]')) return;
    const subject = objectAt(el);
    if (!subject) return;
    e.preventDefault();
    void referentOf(el, subject).then((target) => {
      if (target !== undefined && target !== subject) openAside(target);
    });
  });
  $('stage').addEventListener('click', toggleCollapse);
  // A fold is driven from the summary's ACTIVATION, not from the `toggle`
  // event — the browser fires that one on insertion too. See `toggleFold`.
  $('stage').addEventListener('click', toggleFold);

  // The keyboard half of "open to the side". A gesture that exists only under
  // a mouse modifier is unreachable without a mouse, and Ctrl+Enter on a
  // focused card is the keyboard's version of the same habit. The pane's own
  // close control is an ordinary focusable button, so the pane can be both
  // opened and dismissed without pointing at anything.
  $('stage').addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey)) return;
    const el = e.target as HTMLElement | null;
    if (!el || el.closest(NOT_THE_CARD) || el.closest(NOT_TO_THE_SIDE)) return;
    const id = objectAt(el);
    if (!id) return;
    /**
     * @Codex: *"a modifier-click follows the chip target while the documented
     * keyboard equivalent opens the wrong object."*
     *
     * True twice. The first version resolved the enclosing card and opened
     * THAT, so a focused chip opened the object its reference was written on.
     * The repair let the chip through by returning early — which handed the
     * gesture to whatever the browser does with a synthesized click, and it is
     * a NO-OP wherever modifier state is not copied onto one. A route that
     * works by not running is not a route.
     *
     * Both halves are explicit now, and they are the same two lines the mouse
     * runs: `referentOf` says which object, `openAside` says where. Guards
     * included — the caret keeps Ctrl+Enter, and a transport, a fold or a
     * relation control is not the card it sits inside for the keyboard any more
     * than for the mouse.
     */
    e.preventDefault();
    void referentOf(el, id).then((target) => { if (target) openAside(target); });
  });
  /**
   * ROVING NAVIGATION FOR EVERY TABLIST — @Codex's P2 on `c8b317d`.
   *
   * `0194` made the band's Files and Search real tabs and gave the unselected
   * one `tabindex="-1"`, which is half of the roving model and the half that
   * takes reachability AWAY. With no arrow handler, Search could not be
   * reached from the initial Files state by keyboard at all, and once on
   * Search there was no way back to Files — a lost capability introduced by a
   * change that was making the accessibility model better.
   *
   * Written for `[role="tablist"]` rather than for the ribbon: the runtime
   * draws three of them today (the band's pane selectors, the note strip, the
   * sidebar's pane tabs) and a seventh app's is a fourth. A tablist that
   * happens to leave every tab in the sequential order loses nothing by also
   * answering arrows — this is additive everywhere and load-bearing in the one
   * place a tab is out of that order.
   *
   * Focus only. ACTIVATION stays on Enter, Space and click, so arrowing past a
   * pane does not open it — the manual-activation half of the APG pattern,
   * which is the right one here because selecting a pane repaints the sidebar.
   */
  $('stage').addEventListener('keydown', (e) => {
    const KEYS = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!KEYS.includes(e.key)) return;
    const from = (e.target as HTMLElement | null)?.closest('[role="tab"]');
    const list = from?.closest('[role="tablist"]');
    if (!from || !list) return;
    const tabs = [...list.querySelectorAll<HTMLElement>('[role="tab"]')]
      .filter((t) => t.checkVisibility?.() !== false);
    const at = tabs.indexOf(from as HTMLElement);
    if (at < 0 || tabs.length < 2) return;
    /* The axis is the tablist's OWN, read from the box rather than assumed:
       the band's selectors run horizontally and a sidebar's pane tabs may not.
       `align-self` taught this repo that a cross-axis property is two
       properties with one name; the same is true of an arrow key. */
    const horizontal = list.getBoundingClientRect().width
      >= list.getBoundingClientRect().height;
    const forward = horizontal ? 'ArrowRight' : 'ArrowDown';
    const back = horizontal ? 'ArrowLeft' : 'ArrowUp';
    const next = e.key === 'Home' ? 0
      : e.key === 'End' ? tabs.length - 1
      : e.key === forward ? (at + 1) % tabs.length
      : e.key === back ? (at - 1 + tabs.length) % tabs.length
      : -1;
    // An arrow along the OTHER axis is not this widget's; leave it alone
    // rather than swallowing a key the page may want.
    if (next < 0) return;
    e.preventDefault();
    tabs[next]?.focus();
  });
  $('paneClose').addEventListener('click', closeAside);

  // FIND. Submitting runs the search; clearing the box clears it. Deliberately
  // NOT search-as-you-type: every keystroke would rebuild a scope, regenerate a
  // lens and repaint the document, and a page that reflows under a half-typed
  // word is unusable. A form submit is also what gives the control its Enter
  // key for free.
  $('findForm').addEventListener('submit', (e) => {
    e.preventDefault();
    void runFind(($('find') as HTMLInputElement).value);
    ($('findClear') as HTMLElement).hidden = !($('find') as HTMLInputElement).value.trim();
  });
  $('find').addEventListener('input', () => {
    const box = $('find') as HTMLInputElement;
    ($('findClear') as HTMLElement).hidden = !box.value.trim();
    // An emptied box means "show me the pool again" — the one case where
    // reacting to typing is right, because it is a cancellation rather than a
    // query.
    if (!box.value.trim()) clearFind();
  });
  $('findClear').addEventListener('click', () => {
    ($('find') as HTMLInputElement).value = '';
    ($('findClear') as HTMLElement).hidden = true;
    clearFind();
    $('find').focus();
  });
  $('stage').addEventListener('click', stepCalendar);

  // Adaptive layout. Grab a block and every place it can go lights up at once —
  // which is affordable because `enumerateDrops` is arithmetic rather than a
  // speculative compile, and honest because the same `applyDrop` the overlay
  // calls is the one whose refusals it draws.
  //
  // The committed spec goes through the ordinary candidate loop — `offer` then
  // `keep` — rather than being written straight into state. A rearrangement IS
  // a taste signal, and routing it around the loop that records taste would
  // throw away the most direct signal the product ever gets.
  attachDropHost({
    root: $('stage'),
    current: () => (lastSpec && lastIR ? { spec: lastSpec, ir: lastIR } : undefined),
    // The demo's user is the author of their own lens. A viewer does not carry
    // this, and `applyDrop` refuses without it — which is what that comment
    // predicted before there was a viewer to be.
    authority: { effects: MAY_WRITE ? ['edit-layout'] : [] },
    onCommit: (spec) => {
      // Any pool request still compiling is now about a lens the person has
      // already replaced with their own hands. @Codex, P2 on #184: this
      // acceptance path was wired for contribution and not for invalidation.
      poolRequest += 1;
      /**
       * `acceptDirect`, not `keep(offer(...))`, and decision `0045` names why.
       *
       * *"When direct manipulation accepts a new spec, the pre-drop candidate
       * is dismissed before another suggestion is selected. Otherwise the
       * deterministic generator can immediately re-offer the exact undo even if
       * later revision paths honour `accepted`."* — `0015` F38, still binding
       * and separate from the revision contract: *"implementing either one is
       * not evidence for the other."*
       *
       * `offer`'s two silences are also wrong for a gesture rather than a
       * suggestion: it says nothing when the id was dismissed and nothing when
       * it matches the accepted one, so a person rearranging back to a layout
       * they had once dismissed would find their own hands ignored.
       */
      state.lens = acceptDirect(state.lens, spec,
        { generator: 'user-drop', priors: [], attempts: 1 },
        'you moved a block');
      say('layout rearranged — it is your lens now');
      // AFTER the success line, so a refusal replaces it rather than being
      // overwritten by it. This is an acceptance like any other and goes
      // through the same boundary — see `contributeAcceptedLens`.
      contributeAcceptedLens('rearranged');
      void run();
    },
    onRefusal: (message) => say(`refused: ${message}`, 'bad'),
    onStatus: (message) => { $('saidit').textContent = message; },
  });

  // Come back to a recording where you left it. Delegated on the same root as
  // the other two hosts, so it survives every repaint replacing the stage —
  // and with `capture`, because media events do not bubble and a listener
  // without it would receive nothing at all.
  attachPlayback({
    root: $('stage'),
    current: () => lastIR ?? undefined,
    state: playback,
  });

  // The one piece of chrome this file adds builds itself, rather than growing
  // the hand-written shell that decision 0014 calls a lie by another row.
  const controls = document.querySelector('.controls');
  if (controls) {
    const current = SHELL_OPTIONS.find((o) =>
      JSON.stringify(o.choice) === JSON.stringify(state.shell))?.value ?? 'off';
    mountShellControl(controls, current, (value) => {
      state.shell = SHELL_OPTIONS.find((o) => o.value === value)?.choice ?? { kind: 'off' };
      writeShellChoice(value);
      void run();
    });

    // Automation, as a round trip you can watch: write the composition onto the
    // selected object as an ordinary text property — it becomes visible in the
    // lens — then read it back and run it. Nothing persisted is a function.
    if (!controls.querySelector('#autoStore')) {
      const label = document.createElement('label');
      label.textContent = 'Automation';
      const row = document.createElement('span');
      row.style.display = 'flex';
      row.style.gap = '.4rem';
      const store = document.createElement('button');
      store.id = 'autoStore';
      store.className = 'btn';
      store.type = 'button';
      store.textContent = 'Store';
      store.addEventListener('click', () => { void writeAutomation(); });
      const play = document.createElement('button');
      play.id = 'autoRun';
      play.className = 'btn';
      play.type = 'button';
      play.textContent = 'Run';
      play.addEventListener('click', () => { void runStoredAutomation(); });
      row.append(store, play);
      label.appendChild(row);
      controls.appendChild(label);
    }
  }

  mountInspector();
  mountIngest();

  watchPool();
  await run();
}

/**
 * The inspector drawer.
 *
 * Task 0045 phase 1. Everything that exists to look AT the machinery — the
 * selects, the two forms, the node/hash/timing line, the spec and genome dumps
 * — used to occupy the top two thirds of the viewport, and while it did,
 * nothing about the generated document could be judged. It is all still here,
 * still wired to the same ids, and now it starts closed.
 *
 * `hidden` rather than a class, because it has to leave the accessibility tree
 * too: a reader walked through nine selects before reaching the document is the
 * same problem in a different modality.
 *
 * The state is remembered, because someone who opened it is working with it and
 * a reload should not put it away. `?inspect=1` forces it open on load, which is
 * how the browser harnesses reach controls they have to drive — the alternative
 * was every harness clicking a button before its first assertion, and a harness
 * that fails at its own setup reports a product defect it did not find.
 */
function mountInspector(): void {
  const drawer = $('inspector');
  const toggle = $('inspect') as HTMLButtonElement;
  const KEY = 'morph.inspector';

  const forced = new URLSearchParams(location.search).has('inspect');
  const set = (open: boolean) => {
    drawer.hidden = !open;
    toggle.setAttribute('aria-expanded', String(open));
    // The page moves over rather than being covered — see the media query in
    // `index.html`. A drawer that sits on top of the document intercepts clicks
    // meant for it, which is not a styling opinion: it failed four browser
    // assertions before this line existed.
    document.documentElement.classList.toggle('inspecting', open);
    try { localStorage.setItem(KEY, open ? '1' : '0'); } catch { /* private mode */ }
  };

  let remembered = false;
  try { remembered = localStorage.getItem(KEY) === '1'; } catch { /* private mode */ }
  set(forced || remembered);

  // The hub's half of @Codex's inspector finding: the shelf closes the drawer
  // through the same set() that owns its state, not through a second copy.
  closeInspector = () => set(false);

  // `=== true` because lib.dom types `hidden` as `boolean | 'until-found' | ''`.
  toggle.addEventListener('click', () => set(drawer.hidden === true));
  $('inspectClose').addEventListener('click', () => { set(false); toggle.focus(); });
  // Escape closes it — unless the key came from inside the document, where it
  // belongs to whatever is being edited. Without that guard, cancelling an edit
  // with the drawer open also put the drawer away: one keystroke, two things
  // undone, and only one of them asked for. The offer surface has its own
  // Escape policy and is not inside the drawer, so those two never contend.
  //
  // CAPTURE phase, and that is not a detail. In the bubble phase the runtime's
  // own Escape handler has already run, reverted the edit and REPAINTED the
  // stage — so `e.target` is a detached node, `contains` answers false, and the
  // guard passes the very case it exists to catch. Measured in Chromium: the
  // drawer closed on Escape from inside a note with the guard in place.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || drawer.hidden) return;
    if (e.target instanceof Node && $('stage').contains(e.target)) return;
    set(false);
    toggle.focus();
  }, { capture: true });
}

/**
 * Boot, and SAY SO when it does not.
 *
 * `void boot()` discarded the promise, so every failure on the boot path was a
 * console line and an empty shell. `renderBootFailure` is in its own module
 * with its own tests; the wiring is here, and it is the wiring that was
 * missing. See `boot-failure.ts` for why the shape of the failure mattered.
 */
function reportBootFailure(error: unknown): void {
  console.error('Morph could not start', error);
  const stage = document.getElementById('stage');
  if (stage) renderBootFailure(stage, error);
}

boot().catch(reportBootFailure);

// A failure AFTER boot resolves — a rejected save, a listener that throws — is
// not this boundary's job to render: the page is up and the person is working
// in it, and replacing the stage would take their work off screen to show them
// a stack. It must not be silent either, and it was.
window.addEventListener('unhandledrejection', (event) => {
  console.error('Morph: unhandled rejection', event.reason);
});
